import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { useAuth } from '../contexts/AuthContext';

const T = {
  bg:          '#0a0a0f',           // un peu plus clair que #020617
  bgPanel:     '#12121a',
  bgCard:      'rgba(30, 32, 45, 0.75)',
  bgHover:     'rgba(45, 48, 68, 0.85)',

  border:      'rgba(180, 170, 140, 0.12)',
  borderHover: 'rgba(200, 180, 140, 0.28)',
  borderAccent:'rgba(103, 232, 249, 0.28)',   // cyan accent plus visible

  // === Accents chauds plus lumineux ===
  gold:        '#e8b56d',           // or plus clair et vivant
  goldMid:     '#d4a15a',
  goldDim:     '#b37f45',

  cream:       '#f0e6d6',           // beaucoup plus lumineux
  creamMid:    '#d9ccaf',
  creamDim:    '#b8a88a',
  creamFaint:  'rgba(240, 230, 214, 0.22)',

  violet:      '#c4b5fd',
  red:         '#f87171',
  redDim:      '#b91c1c',
  redFaint:    'rgba(248, 113, 113, 0.14)',

  green:       '#34d399',
  greenBright: '#6ee7b7',
  greenFaint:  'rgba(52, 211, 153, 0.16)',

  blue:        '#60a5fa',
  amber:       '#fbbf24',           // ambre plus chaud et visible

  // === Éléments 3D ===
  particle:    '#e8c080',           // plus visible que le cyan pur
  gridLine:    'rgba(200, 180, 140, 0.055)',
};

class MusicManager {
  constructor() {
    this.ctx = null;
    this.masterGain = null;
    this.volume = 0.3;
    this.muted = false;
    this.intervals = [];
    this.oscillators = [];
    this.currentTrack = '';
    this.isPlaying = false;
  }

  _init() {
    if (this.ctx) return;
    this.ctx = new (window.AudioContext || window.webkitAudioContext)();
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = this.volume;
    this.masterGain.connect(this.ctx.destination);
  }

  _stopAll() {
    this.intervals.forEach(id => clearInterval(id));
    this.intervals = [];
    this.oscillators.forEach(({ osc, gain }) => {
      try {
        gain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.3);
        setTimeout(() => { try { osc.stop(); } catch (e) {} }, 500);
      } catch (e) {}
    });
    this.oscillators = [];
    this.isPlaying = false;
    this.currentTrack = '';
  }

  _makePad(freq, gainVal, attackTime = 2.5, sustainLevel = 0.35) {
    const osc1 = this.ctx.createOscillator();
    const osc2 = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 800;
    filter.Q.value = 0.7;

    osc1.type = 'sine';
    osc2.type = 'sine';
    osc1.frequency.value = freq;
    osc2.frequency.value = freq * 1.002; // Battement très lent, presque imperceptible
    gain.gain.setValueAtTime(0, this.ctx.currentTime);
    gain.gain.linearRampToValueAtTime(gainVal, this.ctx.currentTime + attackTime);
    gain.gain.setValueAtTime(gainVal, this.ctx.currentTime + attackTime + 8);
    gain.gain.setTargetAtTime(gainVal * sustainLevel, this.ctx.currentTime + attackTime + 8, 12);

    osc1.connect(filter);
    osc2.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain);
    osc1.start();
    osc2.start();
    return { osc: osc1, gain };
  }

  _makeSineLayer(freq, gainVal, attackTime = 1.5) {
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 1000;
    osc.type = 'sine';
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0, this.ctx.currentTime);
    gain.gain.linearRampToValueAtTime(gainVal, this.ctx.currentTime + attackTime);
    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain);
    osc.start();
    return { osc, gain };
  }

  _makeChime(freq, gainVal, attackTime = 1.0) {
    // Son cristallin avec des harmoniques paires
    const osc = this.ctx.createOscillator();
    const osc2 = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = freq * 2;
    filter.Q.value = 2.5;

    osc.type = 'triangle';
    osc2.type = 'sine';
    osc.frequency.value = freq;
    osc2.frequency.value = freq * 2.01;
    gain.gain.setValueAtTime(0, this.ctx.currentTime);
    gain.gain.linearRampToValueAtTime(gainVal, this.ctx.currentTime + attackTime * 0.3);
    gain.gain.setTargetAtTime(gainVal * 0.4, this.ctx.currentTime + attackTime * 0.5, attackTime * 0.5);

    osc.connect(filter);
    osc2.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain);
    osc.start();
    osc2.start();
    return { osc, gain };
  }

  _playNote(freq, duration, gainVal, delay = 0, type = 'sine') {
    if (!this.ctx || !this.isPlaying) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 2000;
    filter.Q.value = 0.5;
    osc.type = type;
    osc.frequency.value = freq;
    const start = this.ctx.currentTime + delay;
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(gainVal, start + 0.06);
    gain.gain.setTargetAtTime(gainVal * 0.5, start + 0.1, duration * 0.4);
    gain.gain.setTargetAtTime(0.001, start + duration * 0.6, duration * 0.4);
    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain);
    osc.start(start);
    osc.stop(start + duration + 0.5);
  }

  // ============================================================
  // MUSIQUE DU MENU — Contemplative, apaisante
  // Gamme : 532 Hz (Do), 598.5 (Ré), 672 (Mi), 798 (Sol), 896 (La)
  // ============================================================
  playMenu() {
    this._init();
    if (this.currentTrack === 'menu' && this.isPlaying) return;
    this._stopAll();
    this.currentTrack = 'menu';
    this.isPlaying = true;

    const root = 532;

    // Nappe grave qui ancre
    const pad1 = this._makePad(root * 0.5, 0.025, 3);
    // Fondamentale
    const pad2 = this._makePad(root, 0.018, 3);
    // Harmonie suspendue (quinte + octave)
    const pad3 = this._makePad(root * 1.5, 0.012, 3.5);

    this.oscillators.push(pad1, pad2, pad3);

    // LFO lent pour faire respirer la nappe
    const lfo = this.ctx.createOscillator();
    const lfoGain = this.ctx.createGain();
    lfo.frequency.value = 0.05;
    lfo.type = 'sine';
    lfoGain.gain.value = 0.006;
    lfo.connect(lfoGain);
    lfoGain.connect(pad2.gain.gain);
    lfo.start();
    this.oscillators.push({ osc: lfo, gain: lfoGain });

    // Mélodie lente, méditative — gamme pentatonique en 532
    const melody = [
      532, 598.5, 672, 798, 896, 798, 672,
      598.5, 532, 672, 798, 896, 1064, 896,
      798, 672, 598.5, 532
    ];
    let idx = 0;
    const id = setInterval(() => {
      if (!this.ctx || !this.isPlaying) return;
      const note = melody[idx % melody.length];
      const isLong = idx % 4 === 0 || idx % 5 === 0;
      this._playNote(note, isLong ? 4.5 : 2.8, isLong ? 0.020 : 0.014, 0, 'triangle');
      idx++;
    }, 2800);
    this.intervals.push(id);

    // Cristal lointain toutes les 11 secondes
    const idChime = setInterval(() => {
      if (!this.ctx || !this.isPlaying) return;
      this._playNote(1064, 5, 0.012, 0, 'sine');
    }, 11200);
    this.intervals.push(idChime);
  }

  // ============================================================
  // MUSIQUE DE COMBAT — Tendue mais noble
  // Gamme en 532 Hz avec tension par seconde mineure
  // ============================================================
  playBattle() {
    this._init();
    if (this.currentTrack === 'battle' && this.isPlaying) return;
    this._stopAll();
    this.currentTrack = 'battle';
    this.isPlaying = true;

    const root = 532;

    // Fond grave puissant
    const pad1 = this._makePad(root * 0.5, 0.022, 2);
    // Quinte pour la tension
    const pad2 = this._makePad(root * 0.75, 0.016, 2.5);
    // Sub-basse rythmique
    const sub = this._makeSineLayer(root * 0.25, 0.018, 1.8);

    this.oscillators.push(pad1, pad2, sub);

    // LFO plus rapide pour l'urgence
    const lfo = this.ctx.createOscillator();
    const lfoGain = this.ctx.createGain();
    lfo.frequency.value = 0.2;
    lfo.type = 'sine';
    lfoGain.gain.value = 0.012;
    lfo.connect(lfoGain);
    lfoGain.connect(pad1.gain.gain);
    lfo.start();
    this.oscillators.push({ osc: lfo, gain: lfoGain });

    // Phrase rythmée — gamme avec seconde mineure pour la tension
    const phrase = [
      399, 448.5, 502, 532, 502,
      448.5, 399, 532, 598.5, 532,
      448.5, 399, 355, 399, 448.5
    ];
    let noteIdx = 0;
    const id1 = setInterval(() => {
      if (!this.ctx || !this.isPlaying) return;
      const note = phrase[noteIdx % phrase.length];
      const isAccent = noteIdx % 4 === 0;
      this._playNote(note, isAccent ? 2.2 : 1.6, isAccent ? 0.020 : 0.014, 0, isAccent ? 'triangle' : 'sine');
      noteIdx++;
    }, 1800);
    this.intervals.push(id1);

    // Contre-chant aigu — répond à la phrase principale
    const highPhrase = [798, 896, 1064, 896, 798, 672, 598.5, 532];
    let highIdx = 0;
    const id2 = setInterval(() => {
      if (!this.ctx || !this.isPlaying) return;
      const note = highPhrase[highIdx % highPhrase.length];
      this._playNote(note, 3.2, 0.010, 0, 'sine');
      highIdx++;
    }, 5400);
    this.intervals.push(id2);

    // Pulsation grave qui marque le tempo (battement de cœur)
    const idHeart = setInterval(() => {
      if (!this.ctx || !this.isPlaying) return;
      this._playNote(root * 0.5, 0.3, 0.025, 0, 'sine');
    }, 1200);
    this.intervals.push(idHeart);
  }

  // ============================================================
  // MUSIQUE DE VICTOIRE / DÉFAITE
  // ============================================================
  playVictory(isWin = true) {
    this._init();
    this._stopAll();
    this.currentTrack = 'victory';
    this.isPlaying = true;

    const root = 532;

    if (isWin) {
      // Accord majeur ascendant : Do - Mi - Sol - Do (octave)
      const chord = [
        { freq: root, delay: 0 },
        { freq: root * 1.26, delay: 0.4 },  // Mi (tierce majeure pure)
        { freq: root * 1.5, delay: 0.8 },    // Sol (quinte)
        { freq: root * 2, delay: 1.2 },      // Do aigu
        { freq: root * 2.5, delay: 1.8 },    // Mi aigu
      ];
      chord.forEach(({ freq, delay }) => {
        this._playNote(freq, 6, 0.035, delay, 'triangle');
      });
      // Cristal final
      setTimeout(() => this._playNote(root * 3, 4, 0.025, 0, 'sine'), 2400);
    } else {
      // Descente mineure : Do - La - Fa - Do grave
      const chord = [
        { freq: root, delay: 0 },
        { freq: root * 0.84, delay: 0.5 },
        { freq: root * 0.67, delay: 1.0 },
        { freq: root * 0.5, delay: 1.5 },
      ];
      chord.forEach(({ freq, delay }) => {
        this._playNote(freq, 5, 0.025, delay, 'sine');
      });
    }
  }

  playCinematicOpening() {
    this._init();
    if (this.currentTrack === 'cinematicOpening' && this.isPlaying) return;

    this._stopAll();
    this.currentTrack = 'cinematicOpening';
    this.isPlaying = true;

    const root = 532;

    // === Nappes très douces et atmosphériques (volume fortement réduit) ===
    const padDeep = this._makePad(root * 0.48, 0.018, 6);   // encore plus bas
    const padRoot = this._makePad(root,       0.013, 5);
    const padHigh = this._makePad(root * 1.62, 0.007, 7);   // très discret

    this.oscillators.push(padDeep, padRoot, padHigh);

    // Respiration lente encore plus subtile
    const breathLFO = this.ctx.createOscillator();
    const breathGain = this.ctx.createGain();
    breathLFO.frequency.value = 0.042;
    breathLFO.type = 'sine';
    breathGain.gain.value = 0.005;           // très faible modulation
    breathLFO.connect(breathGain);
    breathGain.connect(padRoot.gain.gain);
    breathLFO.start();
    this.oscillators.push({ osc: breathLFO, gain: breathGain });

    // Mélodie temporelle lente
    const temporalMelody = [
      532, 598.5, 672, 532, 798, 672, 598.5,
      532, 672, 798, 896, 798, 672, 532
    ];
    let melodyIndex = 0;

    const melodyId = setInterval(() => {
      if (!this.isPlaying) return;
      const note = temporalMelody[melodyIndex % temporalMelody.length];
      const isImportant = melodyIndex % 4 === 0;

      this._playNote(
        note, 
        isImportant ? 5.8 : 3.9, 
        isImportant ? 0.019 : 0.012, 
        0, 
        isImportant ? 'triangle' : 'sine'
      );
      melodyIndex++;
    }, 2920);

    this.intervals.push(melodyId);

    // Échos cristallins très lointains
    const echoId = setInterval(() => {
      if (!this.isPlaying) return;
      this._makeChime(1064 * 1.01, 0.008, 2.5);
      if (Math.random() > 0.7) {
        setTimeout(() => this._makeChime(1198, 0.005, 3.2), 1400);
      }
    }, 14800);

    this.intervals.push(echoId);

    // Pulsation temporelle très discrète
    const timePulse = setInterval(() => {
      if (!this.isPlaying) return;
      this._playNote(root * 0.5, 1.4, 0.0065, 0, 'sine');
    }, 6400);

    this.intervals.push(timePulse);
  }

  // ============================================================
  // UTILITAIRES
  // ============================================================
  setVolume(v) {
    this.volume = Math.max(0, Math.min(1, v));
    if (this.masterGain && this.ctx) {
      this.masterGain.gain.setTargetAtTime(
        this.muted ? 0 : this.volume,
        this.ctx.currentTime,
        0.15
      );
    }
  }

  toggleMute() {
    this.muted = !this.muted;
    if (this.masterGain && this.ctx) {
      this.masterGain.gain.setTargetAtTime(
        this.muted ? 0 : this.volume,
        this.ctx.currentTime,
        0.15
      );
    }
    return this.muted;
  }

  dispose() {
    this._stopAll();
    try { this.ctx?.close(); } catch (e) {}
    this.ctx = null;
  }
}

const music = new MusicManager();

class SoundManager {
  constructor() {
    this.ctx = null;
    this.enabled = true;
  }

  _init() {
    if (this.ctx) return;
    this.ctx = new (window.AudioContext || window.webkitAudioContext)();
  }

  _playTone(freq, duration, volume = 0.1, type = 'sine', detune = 0) {
    if (!this.enabled) return;
    this._init();
    if (this.ctx.state === 'suspended') this.ctx.resume();
    const now = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    if (detune) osc.detune.value = detune;
    gain.gain.setValueAtTime(volume, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
    osc.connect(gain);
    gain.connect(this.ctx.destination);
    osc.start(now);
    osc.stop(now + duration);
  }

  _playImpact(freq, volume = 0.12) {
    // Son percussif avec chute rapide
    this._playTone(freq, 0.25, volume, 'triangle');
    this._playTone(freq * 0.5, 0.35, volume * 0.6, 'sine');
  }

  // --- Sons de base ---
  click() { this._playTone(896, 0.05, 0.05, 'sine'); }

  damage() {
    this._playImpact(266, 0.10);
    this._playTone(199.5, 0.3, 0.06, 'sawtooth', -10);
  }

  block() {
    this._playTone(448.5, 0.12, 0.07, 'triangle');
    this._playTone(672, 0.08, 0.04, 'sine');
  }

  heal() {
    this._playTone(672, 0.2, 0.06, 'sine');
    setTimeout(() => this._playTone(798, 0.25, 0.05, 'sine'), 100);
    setTimeout(() => this._playTone(896, 0.3, 0.04, 'sine'), 200);
  }

  ultimate() {
    this._playImpact(532, 0.15);
    setTimeout(() => this._playImpact(672, 0.13), 120);
    setTimeout(() => this._playImpact(798, 0.12), 240);
    setTimeout(() => this._playImpact(1064, 0.10), 360);
  }

  synergy() {
    this._playTone(896, 0.1, 0.08, 'triangle');
    setTimeout(() => this._playTone(1064, 0.15, 0.09, 'triangle'), 80);
    setTimeout(() => this._playTone(1344, 0.3, 0.07, 'triangle'), 180);
    setTimeout(() => this._playTone(1596, 0.5, 0.05, 'sine'), 300);
  }

  tension() {
    this._playTone(224, 0.7, 0.05, 'sawtooth');
    setTimeout(() => this._playTone(199.5, 0.9, 0.05, 'sawtooth'), 400);
    setTimeout(() => this._playTone(177, 1.2, 0.04, 'sawtooth'), 800);
  }

  characterSwitch() {
    this._playTone(672, 0.2, 0.08, 'triangle');
    setTimeout(() => this._playTone(896, 0.25, 0.07, 'triangle'), 120);
    setTimeout(() => this._playTone(1064, 0.3, 0.05, 'sine'), 250);
  }

  // --- Nouveaux sons pour le système de duel ---
  conceptCall() {
    // Cri du concept : deux tons rapides qui s'enchaînent
    this._playTone(672, 0.12, 0.07, 'triangle');
    setTimeout(() => this._playTone(798, 0.2, 0.06, 'triangle'), 60);
    setTimeout(() => this._playTone(598.5, 0.3, 0.04, 'sine'), 140);
  }

  hit() {
    this._playImpact(266, 0.12);
    this._playTone(133, 0.2, 0.08, 'triangle', -20);
  }

  slash() {
    // Son de fente : balayage rapide
    this._playTone(560, 0.12, 0.06, 'sawtooth');
    setTimeout(() => this._playTone(672, 0.08, 0.04, 'sawtooth'), 30);
    setTimeout(() => this._playTone(798, 0.06, 0.03, 'sawtooth'), 60);
  }

  thinking() {
    // Scan/analyse : pulsations régulières
    this._playTone(532, 0.3, 0.04, 'sine');
    setTimeout(() => this._playTone(598.5, 0.25, 0.03, 'sine'), 200);
    setTimeout(() => this._playTone(532, 0.3, 0.04, 'sine'), 400);
  }

  countdown() {
    this._playTone(1344, 0.08, 0.05, 'triangle');
    this._playTone(1064, 0.1, 0.03, 'sine');
  }
}

const sounds = new SoundManager();


const RANGS = [
  { min: 1, max: 4, label: 'APPRENTI', color: '#94a3b8', kanji: '弟子' },
  { min: 5, max: 9, label: 'INITIÉ', color: '#22d3ee', kanji: '修行' },
  { min: 10, max: 14, label: 'MAÎTRE', color: '#a78bfa', kanji: '師匠' },
  { min: 15, max: 19, label: 'SAGE', color: '#f59e0b', kanji: '賢者' },
  { min: 20, max: 99, label: 'TRANSCENDANT', color: '#ef4444', kanji: '超越' },
];

const SYNERGIES = [
  { ids: ['ma', 'seiryoku'], name: 'CONTRE PARFAIT', kanji: '完璧', bonus: { drain: 20, msg: 'Vide + puissance maximale — contre absolu !' }, color: '#f59e0b' },
  { ids: ['ma', 'asabiyya'], name: 'SILENCE COLLECTIF', kanji: '集団沈黙', bonus: { heal: 15, msg: 'Le silence du peuple est une force irrésistible.' }, color: '#a78bfa' },
  { ids: ['kintsugi', 'resistance'], name: 'FORTERESSE BRISÉE', kanji: '砕かれた城', bonus: { gripReduce: 30, msg: 'Les failles deviennent les murs les plus solides.' }, color: '#f7d44a' },
  { ids: ['jitakyoei', 'ubuntu'], name: 'HARMONIE TOTALE', kanji: '全調和', bonus: { heal: 20, drain: 10, msg: 'Prospérité mutuelle, tissu humain indissociable.' }, color: '#10b981' },
  { ids: ['entropie', 'hegel'], name: 'DIALECTIQUE DU CHAOS', kanji: '混沌弁証', bonus: { drain: 25, disorder: 15, msg: 'Le désordre absolu devient synthèse inévitable.' }, color: '#c084fc' },
  { ids: ['wuwei', 'philosophe'], name: 'SAGESSE SILENCIEUSE', kanji: '無為知', bonus: { drain: 15, heal: 10, msg: 'Le roi-philosophe agit par le non-agir.' }, color: '#22d3ee' },
  { ids: ['hypertexte', 'qanun'], name: 'RÉSEAU UNIVERSEL', kanji: '普遍網', bonus: { drain: 20, msg: 'L\'intellect illumine chaque nœud du réseau.' }, color: '#c084fc' },
  { ids: ['imperatif', 'resistance'], name: 'LOI ABSOLUE', kanji: '絶対法', bonus: { drain: 30, msg: 'La loi morale ne supporte aucune exception.' }, color: '#a5f3c3' },
  { ids: ['hedone', 'ubuntu'], name: 'JOIE PARTAGÉE', kanji: '共喜', bonus: { heal: 25, msg: 'Le souverain bien est collectif ou n\'est pas.' }, color: '#facc15' },
  { ids: ['attention', 'wuwei'], name: 'ATTENTION PURE', kanji: '純粋注意', bonus: { heal: 15, gripReduce: 15, msg: 'Regarder sans agir — le vide se remplit de force.' }, color: '#a8a29e' },
{ ids: ['attention', 'ma'], name: 'SILENCE ATTENTIF', kanji: '静寂注意', bonus: { drain: 10, heal: 10, msg: 'Le silence qui écoute est plus tranchant que la parole.' }, color: '#a8a29e' },
{ ids: ['enracinement', 'resistance'], name: 'RACINES INÉBRANLABLES', kanji: '不抜根', bonus: { drain: 25, gripReduce: 20, msg: 'Plus on tire, plus les racines s\'enfoncent.' }, color: '#a8a29e' },
{ ids: ['enracinement', 'kintsugi'], name: 'RÉPARATION TELLURIQUE', kanji: '地金継', bonus: { heal: 20, gripReduce: 15, msg: 'L\'âme enracinée répare ses failles à l\'or de la terre.' }, color: '#a8a29e' },
{ ids: ['pesanteur', 'seiryoku'], name: 'NÉCESSITÉ EFFICACE', kanji: '必然効率', bonus: { drain: 20, heal: 5, msg: 'Accepter la pesanteur, c\'est déjà l\'utiliser.' }, color: '#a8a29e' },
{ ids: ['pesanteur', 'hegel'], name: 'DIALECTIQUE DE LA PESANTEUR', kanji: '重力弁証', bonus: { drain: 18, disorder: 10, msg: 'Le poids du réel est la matière première de toute synthèse.' }, color: '#a8a29e' },
{ ids: ['attention', 'enracinement_propre'], name: 'ATTENTION ENRACINÉE', kanji: '根付注意', bonus: { heal: 12, gripReduce: 10, msg: 'L\'attention qui prend racine nourrit l\'âme entière.' }, color: '#a8a29e' },
];

const CONCEPTS_BASE = [
  { id: 'seiryoku', label: 'SEIRYOKU ZENYO', kanji: '精力善用', cost: 15, drain: 30, gripAdd: 5, color: '#f59e0b', epoque: 'judo', desc: 'Minimum d\'effort, effet maximum — retourne la force adverse.' },
  { id: 'ma', label: 'MA · 間', kanji: '間', cost: 0, drain: 0, gripAdd: -15, color: '#22d3ee', epoque: 'judo', desc: 'Vide fertile. +10 Énergie. Dégripe. Max 3×/round.' },
  { id: 'asabiyya', label: 'ASABIYYA', kanji: 'عصبية', cost: 20, drain: 38, gripAdd: 0, color: '#a78bfa', epoque: 'medievale', desc: 'Force de la cohésion collective — frappe au nom de tous.' },
  { id: 'jitakyoei', label: 'JITA KYOEI', kanji: '自他共栄', cost: 28, drain: 42, gripAdd: -20, color: '#10b981', epoque: 'judo', desc: 'Prospérité mutuelle — élever les deux, dominer par la générosité.' },
  { id: 'resistance', label: 'RÉSISTANCE', kanji: '抵抗', cost: 10, drain: 20, gripAdd: 20, color: '#ef4444', epoque: 'universel', desc: 'L\'ancrage inflexible. Risqué si répété (+20 grip).' },
  { id: 'wuwei', label: 'WU WEI', kanji: '無為', cost: 5, drain: 0, gripAdd: -20, color: '#a5f3c3', epoque: 'taoiste', desc: 'Non-action : bloque la prochaine attaque. Dégripe.', blockNext: true },
  { id: 'philosophe', label: 'PHILOSOPHE ROI', kanji: '哲人王', cost: 18, drain: 35, gripAdd: 0, color: '#facc15', epoque: 'antique', desc: 'Puissance proportionnelle à la diversité des concepts joués.' },
  { id: 'ubuntu', label: 'UBUNTU', kanji: '烏班圖', cost: 15, drain: 25, gripAdd: -15, color: '#fb923c', epoque: 'africain', desc: 'Je suis parce que nous sommes. +10 Énergie aux deux.' },
  { id: 'kintsugi', label: 'KINTSUGI', kanji: '金継ぎ', cost: 10, drain: 0, gripAdd: -30, color: '#f7d44a', epoque: 'japonais', desc: 'Convertit 30% du grip en Énergie pure. Plus fort si grip élevé.' },
  { id: 'entropie', label: 'ENTROPIE SYMB.', kanji: '熵', cost: 25, drain: 48, gripAdd: 15, color: '#c084fc', epoque: 'moderne', desc: 'Désordre total — drain maximal + réduit les dégâts adverses.' },
  { id: 'hegel', label: 'SYNTHÈSE HEGEL.', kanji: '揚棄', cost: 35, drain: 0, gripAdd: -10, color: '#94a3b8', epoque: 'lumiere', desc: 'Dialectique — copie et renvoie le dernier drain adverse.' },
  { id: 'hypertexte', label: 'HYPERTEXTE', kanji: '超文本', cost: 5, drain: 15, gripAdd: 0, color: '#2dd4bf', epoque: 'moderne', desc: 'Drain croissant +5 par usage dans le round.' },
  { id: 'attention', label: 'ATTENTION', kanji: '注意', cost: 14, drain: 0, gripAdd: -25, color: '#a8a29e', epoque: 'moderne', desc: 'Bloque la prochaine attaque et régénère 15 énergie.' },
  { id: 'enracinement_propre', label: 'ENRACINEMENT', kanji: '根付', cost: 18, drain: 38, gripAdd: -10, color: '#78716c', epoque: 'moderne', desc: 'Le besoin le plus ignoré de l\'âme humaine — drain puissant, réduit le grip adverse.' },
  { id: 'pesanteur', label: 'PESANTEUR', kanji: '重力', cost: 12, drain: 0, gripAdd: 20, color: '#57534e', epoque: 'moderne', desc: 'La nécessité acceptée — augmente votre grip mais rend vos prochaines attaques dévastatrices.' },
];

const CONCEPTS_SHARED = {
  ma: { id: 'ma', label: 'MA · 間', kanji: '間', cost: 0, drain: 0, gripAdd: -15, color: '#22d3ee', epoque: 'judo', desc: 'Vide fertile. +10 Énergie. Dégripe. Max 3×/round.' },
  resistance: { id: 'resistance', label: 'RÉSISTANCE', kanji: '抵抗', cost: 10, drain: 20, gripAdd: 20, color: '#ef4444', epoque: 'universel', desc: 'L\'ancrage inflexible. Risqué si répété (+20 grip).' },
  kintsugi: { id: 'kintsugi', label: 'KINTSUGI', kanji: '金継ぎ', cost: 10, drain: 0, gripAdd: -30, color: '#f7d44a', epoque: 'japonais', desc: 'Convertit 30% du grip en Énergie. Plus fort si grip élevé.' },
  hypertexte: { id: 'hypertexte', label: 'HYPERTEXTE', kanji: '超文本', cost: 5, drain: 15, gripAdd: 0, color: '#2dd4bf', epoque: 'moderne', desc: 'Drain croissant +5 par usage dans le round.' },
  wuwei: { id: 'wuwei', label: 'WU WEI', kanji: '無為', cost: 5, drain: 0, gripAdd: -20, color: '#a5f3c3', epoque: 'taoiste', desc: 'Non-action : bloque la prochaine attaque. Dégripe.', blockNext: true },
  ubuntu: { id: 'ubuntu', label: 'UBUNTU', kanji: '烏班圖', cost: 15, drain: 25, gripAdd: -15, color: '#fb923c', epoque: 'africain', desc: 'Je suis parce que nous sommes. +10 Énergie aux deux.' },
  hegel: { id: 'hegel', label: 'SYNTHÈSE HEGEL.', kanji: '揚棄', cost: 35, drain: 0, gripAdd: -10, color: '#94a3b8', epoque: 'lumiere', desc: 'Dialectique — renvoie le dernier drain adverse.' },
  entropie: { id: 'entropie', label: 'ENTROPIE SYMB.', kanji: '熵', cost: 25, drain: 48, gripAdd: 15, color: '#c084fc', epoque: 'moderne', desc: 'Désordre total — drain massif + réduit les dégâts adverses.' },
  seiryoku: { id: 'seiryoku', label: 'SEIRYOKU ZENYO', kanji: '精力善用', cost: 15, drain: 30, gripAdd: 5, color: '#f59e0b', epoque: 'judo', desc: 'Minimum d\'effort, effet maximum — retourne la force adverse.' },
  jitakyoei: { id: 'jitakyoei', label: 'JITA KYOEI', kanji: '自他共栄', cost: 28, drain: 42, gripAdd: -20, color: '#10b981', epoque: 'judo', desc: 'Prospérité mutuelle — élever les deux, dominer par la générosité.' },
  philosophe: { id: 'philosophe', label: 'PHILOSOPHE ROI', kanji: '哲人王', cost: 18, drain: 35, gripAdd: 0, color: '#facc15', epoque: 'antique', desc: 'Puissance proportionnelle à la diversité des concepts joués.' },
  asabiyya: { id: 'asabiyya', label: 'ASABIYYA', kanji: 'عصبية', cost: 20, drain: 38, gripAdd: 0, color: '#a78bfa', epoque: 'medievale', desc: 'Force de la cohésion collective — frappe au nom de tous.' },
  attention:   { id: 'attention', label: 'ATTENTION', kanji: '注意', cost: 14, drain: 0, gripAdd: -25, color: '#a8a29e', epoque: 'moderne', desc: 'Bloque la prochaine attaque et régénère 15 énergie.' },
  enracinement_propre:   { id: 'enracinement_propre', label: 'ENRACINEMENT', kanji: '根付', cost: 18, drain: 38, gripAdd: -10, color: '#78716c', epoque: 'moderne', desc: 'Le besoin le plus ignoré de l\'âme humaine — drain puissant, réduit le grip adverse.' },
  pesanteur:   { id: 'pesanteur', label: 'PESANTEUR', kanji: '重力', cost: 12, drain: 0, gripAdd: 20, color: '#57534e', epoque: 'moderne', desc: 'La nécessité acceptée — augmente votre grip mais rend vos prochaines attaques dévastatrices.' },
};

const ULTIMES = {
  kant:      { id:'imperatif',         label:'IMPÉRATIF UNIV.',      kanji:'定言命法', cost:35, drain:60, gripAdd:-10, color:'#a5f3c3', epoque:'lumiere',   isUltimate:true, charId:'kant',      desc:'La loi morale universelle s\'impose — drain massif, dégripe.' },
  arete:     { id:'hedone',            label:'HEDONE SOPHRON',      kanji:'快楽徳',   cost:30, drain:0,  gripAdd:-25, color:'#facc15', epoque:'antique',   isUltimate:true, charId:'arete',     desc:'Plaisir éclairé : +30 Énergie, dégripe profondément.' },
  weil:      { id:'enracinement',        label:'ENRACINEMENT',      kanji:'根付', cost:28, drain:55, gripAdd:0,   color:'#fb923c', epoque:'moderne',   isUltimate:true, charId:'weil',  desc:'L\'âme a besoin de racines — drain massif, dégripe et restaure 20 énergie.' },
  ibnsina:   { id:'qanun',             label:'CANON DE RAISON',     kanji:'知性光',  cost:32, drain:52, gripAdd:-15, color:'#c084fc', epoque:'medievale', isUltimate:true, charId:'ibnsina',   desc:'L\'intellect illuminé — drain fort, ouvre les concepts cross-époque.' },
  platon:    { id:'allegorie',         label:'ALLÉGORIE DE LA CAVERNE', kanji:'洞窟神話', cost:38, drain:68, gripAdd:0,   color:'#60a5fa', epoque:'antique',   isUltimate:true, charId:'platon',    desc:'Sortir des ombres — drain colossal, inflige 20 de Désordre à l\'adversaire.' },
  alkhwarizmi:{ id:'algorithme_absolu',label:'ALGORITHME ABSOLU',   kanji:'完全算法', cost:28, drain:60, gripAdd:0,   color:'#34d399', epoque:'medievale', isUltimate:true, charId:'alkhwarizmi',desc:'La procédure qui ne peut échouer — drain massif, traverse tout blocage.' },
  ibnkhaldoun:{ id:'cycle_eternel',    label:'CYCLE ÉTERNEL',       kanji:'永遠循環', cost:33, drain:62, gripAdd:15,  color:'#fb7185', epoque:'medievale', isUltimate:true, charId:'ibnkhaldoun',desc:'Toute puissance porte sa chute — drain colossal, réinitialise le flow adverse.' },
  sophia:    { id:'sophia_ultimate',   label:'PHILOSOPHIA PERENNIS',kanji:'永遠智慧', cost:28, drain:52, gripAdd:-20, color:'#e9d5ff', epoque:'universel', isUltimate:true, charId:'sophia',    desc:'La sagesse immémoriale — drain puissant, dégripe profondément et restaure 15 Énergie.' },
};

const CONCEPTS_ULTIMES = [
  { id:'imperatif',         label:'IMPÉRATIF UNIV.',      kanji:'定言命法', cost:35, drain:60, gripAdd:-10, color:'#a5f3c3', epoque:'lumiere',   isUltimate:true, charId:'kant',      desc:'La loi morale universelle s\'impose — drain massif, dégripe.' },
  { id:'hedone',            label:'HEDONE SOPHRON',      kanji:'快楽徳',   cost:30, drain:0,  gripAdd:-25, color:'#facc15', epoque:'antique',   isUltimate:true, charId:'arete',     desc:'Plaisir éclairé : +30 Énergie, dégripe profondément.' },
  { id:'enracinement',        label:'ENRACINEMENT',        kanji:'根付',    cost:28, drain:55, gripAdd:0,   color:'#fb923c', epoque:'moderne',   isUltimate:true, charId:'weil',  desc:'L\'âme a besoin de racines — drain massif, dégripe et restaure 20 énergie.' },
  { id:'qanun',             label:'CANON DE RAISON',     kanji:'知性光',  cost:32, drain:52, gripAdd:-15, color:'#c084fc', epoque:'medievale', isUltimate:true, charId:'ibnsina',   desc:'L\'intellect illuminé — drain fort, ouvre les concepts cross-époque.' },
  { id:'allegorie',         label:'ALLÉGORIE DE LA CAVERNE', kanji:'洞窟神話', cost:38, drain:68, gripAdd:0,   color:'#60a5fa', epoque:'antique',   isUltimate:true, charId:'platon',    desc:'Sortir des ombres — drain colossal, inflige 20 de Désordre à l\'adversaire.' },
  { id:'algorithme_absolu', label:'ALGORITHME ABSOLU',   kanji:'完全算法', cost:28, drain:60, gripAdd:0,   color:'#34d399', epoque:'medievale', isUltimate:true, charId:'alkhwarizmi',desc:'La procédure qui ne peut échouer — drain massif, traverse tout blocage.' },
  { id:'cycle_eternel',     label:'CYCLE ÉTERNEL',       kanji:'永遠循環', cost:33, drain:62, gripAdd:15,  color:'#fb7185', epoque:'medievale', isUltimate:true, charId:'ibnkhaldoun',desc:'Toute puissance porte sa chute — drain colossal, réinitialise le flow adverse.' },
  { id:'sophia_ultimate',   label:'PHILOSOPHIA PERENNIS',kanji:'永遠智慧', cost:28, drain:52, gripAdd:-20, color:'#e9d5ff', epoque:'universel', isUltimate:true, charId:'sophia',    desc:'La sagesse immémoriale — drain puissant, dégripe profondément et restaure 15 Énergie.' },
];

const ATOUTS = {
  kant: [
    { type: 'punchline', id: 'k_p1', category: 'attaque', cost: 12, drain: 28, gripAdd: 8, texte: "Votre argument est empirique, donc il n'a aucune valeur universelle.", color: '#a5f3c3' },
    { type: 'punchline', id: 'k_p2', category: 'defense', cost: 9, drain: 0, gripAdd: -22, texte: "L'impératif catégorique s'applique même à votre sophisme en cet instant.", color: '#a5f3c3' },
    { type: 'punchline', id: 'k_p3', category: 'attaque', cost: 16, drain: 30, gripAdd: -5, texte: "Agis de telle sorte que la maxime de ton action puisse être érigée en loi universelle.", color: '#a5f3c3' },
    { type: 'punchline', id: 'k_p4', category: 'defense', cost: 11, drain: 0, gripAdd: -20, texte: "La raison pure ne se soumet pas aux inclinations sensibles.", color: '#a5f3c3' },
    { type: 'punchline', id: 'k_p5', category: 'attaque', cost: 14, drain: 32, gripAdd: 10, texte: "Votre logique est hétéronome, elle ne peut prétendre à l'universalité.", color: '#a5f3c3' },
    { type:'punchline',  id: 'k_p6', category:'defense', cost:8, drain:0, gripAdd:-25, texte:"Je ne discute pas de ce qui est, mais des conditions de possibilité de ce que vous dites. Votre exemple est hors sujet.", color:'#6ee7b7' },
    { type:'punchline',  id:'k_p7', category:'attaque', cost:14, drain:30, gripAdd:5, texte:"Traiter l'humanité comme une simple moyenne statistique, c'est la nier comme fin en soi.", color:'#a5f3c3' },
    { type:'punchline',  id:'k_p8', category:'attaque', cost:22, drain:44, gripAdd:-15, texte:"Votre 'vérité' n'est qu'une hypothèse commode. Je l'universalise : elle s'effondre.", color:'#34d399' },
    { type:'punchline',  id:'k_p9', category:'defense', cost:12, drain:0, gripAdd:-18, texte:"La dignité ne s'argumente pas. Elle se constate. La vôtre est intacte, votre raisonnement, lui, est en faillite.", color:'#6ee7b7' },
    { type:'punchline',  id:'k_p10', category:'attaque', cost:18, drain:34, gripAdd:0, texte:"Le tribunal de la raison siège en permanence. Votre inconséquence vient d'être consignée au procès-verbal.", color:'#a5f3c3' },
  ],
  arete: [
    { type: 'punchline', id: 'a_p1', category: 'attaque', cost: 11, drain: 26, gripAdd: 5, texte: "Le plaisir sans vertu n'est que servitude.", color: '#facc15' },
    { type: 'punchline', id: 'a_p2', category: 'defense', cost: 8, drain: 0, gripAdd: -25, texte: "L'ataraxie est mon rempart : vos attaques glissent sur moi.", color: '#facc15' },
    { type: 'punchline', id: 'a_p3', category: 'attaque', cost: 15, drain: 24, gripAdd: -12, texte: "Le souverain bien est le plaisir éclairé par la raison.", color: '#facc15' },
    { type: 'punchline', id: 'a_p4', category: 'defense', cost: 10, drain: 0, gripAdd: -18, texte: "Aucun bonheur construit sur l'ignorance ne saurait durer.", color: '#facc15' },
    { type: 'punchline', id: 'a_p5', category: 'attaque', cost: 13, drain: 28, gripAdd: 0, texte: "Votre certitude est une prison dorée que la vertu n'habite pas.", color: '#facc15' },
    { type:'punchline', id:'a_p6', category:'attaque', cost:10, drain:15, gripAdd:-20, texte:"Votre ascétisme performatif est une vanité comme une autre. Moi, j'assume ma joie, et elle me donne la force de vous contredire.", color:'#fde047' },
    { type:'punchline', id:'a_p7', category:'defense', cost:0, drain:0, gripAdd:-30, texte:"Le rire est la preuve que l'esprit n'est pas prisonnier du sérieux. Je ris de votre piège, et ce faisant, je le dissous.", color:'#fef08a' },
    { type:'punchline', id:'a_p8', category:'attaque', cost:18, drain:36, gripAdd:0, texte:"Vous confondez bonheur et confort. Le confort rend stupide ; le bonheur, lui, rend libre. Vous êtes bien confortable.", color:'#facc15' },
    { type:'punchline', id:'a_p9', category:'defense', cost:5, drain:0, gripAdd:-10, texte:"Partageons un instant de lucidité joyeuse : là, à cet instant, reconnaissez que mon argument est aussi beau que vrai.", color:'#fde68a' },
    { type:'punchline', id:'a_p10', category:'attaque', cost:20, drain:40, gripAdd:5, texte:"Vous courez après une victoire qui vous épuisera. Moi, je savoure une danse qui me nourrit. Lequel de nous deux gagnera à la fin ?", color:'#f59e0b' },
  ],
  weil: [
    { type: 'punchline', id: 'b_p1', category: 'attaque', cost: 11, drain: 28, gripAdd: 5, texte: "L'attention détruit ce qui n'est pas réel. Votre argument s'efface.", color: '#a8a29e' },
    { type: 'punchline', id: 'b_p2', category: 'defense', cost: 8, drain: 0, gripAdd: -25, texte: "Je suspends tout jugement. Dans ce vide, votre attaque se dissout.", color: '#a8a29e' },
    { type: 'punchline', id: 'b_p3', category: 'attaque', cost: 15, drain: 34, gripAdd: -10, texte: "L'enracinement est le besoin vital de l'âme. Vous avez négligé le vôtre.", color: '#a8a29e' },
    { type: 'punchline', id: 'b_p4', category: 'defense', cost: 10, drain: 0, gripAdd: -20, texte: "La pesanteur acceptée devient une force. Votre coup ne fait que m'ancrer.", color: '#a8a29e' },
    { type: 'punchline', id: 'b_p5', category: 'attaque', cost: 13, drain: 30, gripAdd: 0, texte: "Le malheur n'est pas une punition, c'est une vérité. Vous fuyez la vôtre.", color: '#a8a29e' },
    { type: 'punchline', id: 'b_p6', category: 'defense', cost: 0, drain: 0, gripAdd: -28, texte: "Je m'efface. L'attention pure n'offre aucune cible.", color: '#a8a29e' },
    { type: 'punchline', id: 'b_p7', category: 'attaque', cost: 14, drain: 32, gripAdd: 8, texte: "Votre argument est une forme de distraction. Revenez au réel.", color: '#a8a29e' },
    { type: 'punchline', id: 'b_p8', category: 'defense', cost: 12, drain: 0, gripAdd: -18, texte: "La beauté du monde est une preuve. Votre rhétorique n'en est pas une.", color: '#a8a29e' },
    { type: 'punchline', id: 'b_p9', category: 'attaque', cost: 16, drain: 36, gripAdd: -5, texte: "L'âme a besoin de vérité plus que de pain. Vous l'affamez.", color: '#a8a29e' },
    { type: 'punchline', id: 'b_p10', category: 'defense', cost: 9, drain: 0, gripAdd: -22, texte: "L'obéissance à la nécessité est une liberté. Je suis libre, vous êtes agité.", color: '#a8a29e' },
  ],
  ibnsina: [
    { type: 'punchline', id: 'i_p1', category: 'attaque', cost: 14, drain: 30, gripAdd: 5, texte: "L'intellect agent illumine là où votre raison s'obscurcit.", color: '#c084fc' },
    { type: 'punchline', id: 'i_p2', category: 'defense', cost: 12, drain: 0, gripAdd: -20, texte: "Le Canon de la raison ne souffre d'aucune contradiction.", color: '#c084fc' },
    { type: 'punchline', id: 'i_p3', category: 'attaque', cost: 18, drain: 38, gripAdd: -8, texte: "La sagesse transcende les époques et les frontières.", color: '#c084fc' },
    { type: 'punchline', id: 'i_p4', category: 'defense', cost: 9, drain: 0, gripAdd: -16, texte: "L'âme connaît son essence avant même d'avoir connu le monde.", color: '#c084fc' },
    { type: 'punchline', id: 'i_p5', category: 'attaque', cost: 16, drain: 34, gripAdd: 0, texte: "Votre empirisme ne voit que les ombres — mon intellect voit la lumière.", color: '#c084fc' },
    { type:'punchline', id:'i_p6', category:'defense', cost:10, drain:0, gripAdd:-20, texte:"L'âme rationnelle se guérit par la connaissance. Votre objection est un diagnostic, pas une blessure. Je médite dessus.", color:'#a78bfa' },
    { type:'punchline', id:'i_p7', category:'attaque', cost:14, drain:32, gripAdd:0, texte:"Vous adorez les mystères là où je pose des distinctions. La clarté est une forme de piété intellectuelle.", color:'#c084fc' },
    { type:'punchline', id:'i_p8', category:'attaque', cost:22, drain:46, gripAdd:-10, texte:"Je vous répondrai avec Aristote, pour que vous compreniez, et avec Al-Farabi, pour que vous mesuriez votre retard.", color:'#7c3aed' },
    { type:'punchline', id:'i_p9', category:'defense', cost:8, drain:0, gripAdd:-15, texte:"Élevons ce débat au rang des substances séparées. Vous verrez, d'en haut, votre argument paraît bien petit.", color:'#c4b5fd' },
    { type:'punchline', id:'i_p10', category:'attaque', cost:20, drain:38, gripAdd:5, texte:"La nécessité logique est une chaîne que votre volonté refuse de voir. Pourtant, elle vous enserre déjà.", color:'#c084fc' },
  ],
  platon: [
    { type:'punchline', id:'pl_p1', category:'attaque', cost:14, drain:32, gripAdd:-5,  texte:"Votre argument n'est que l'ombre d'une idée — et vous prenez l'ombre pour la réalité.", color:'#60a5fa' },
    { type:'punchline', id:'pl_p2', category:'defense', cost:10, drain:0,  gripAdd:-22, texte:"Celui qui a vu la lumière ne peut plus se satisfaire des ombres. Je reste là où vous ne pouvez pas me suivre.", color:'#60a5fa' },
    { type:'punchline', id:'pl_p3', category:'attaque', cost:17, drain:36, gripAdd:0,   texte:"La démocratie non éclairée produit des tyrans. Votre argument en est la preuve vivante.", color:'#60a5fa' },
    { type:'punchline', id:'pl_p4', category:'defense', cost:11, drain:0,  gripAdd:-18, texte:"Enseigner, c'est allumer un feu — pas remplir un seau. Je refuse votre eau.",  color:'#93c5fd' },
    { type:'punchline', id:'pl_p5', category:'attaque', cost:15, drain:34, gripAdd:4,   texte:"Vous confondez l'apparence et l'essence. C'est l'erreur fondamentale du non-philosophe.", color:'#60a5fa' },
    { type:'punchline', id:'pl_p6', category:'defense', cost:12, drain:0, gripAdd:-22, texte:"Vous venez de décrire une ombre avec une précision admirable. Maintenant, retournez-vous : voici la source de lumière.", color:'#93c5fd' },
    { type:'punchline', id:'pl_p7', category:'attaque', cost:18, drain:38, gripAdd:8, texte:"Tout le monde a le droit d'avoir une opinion. Mais personne n'a le droit de la prendre pour une Idée.", color:'#60a5fa' },
    { type:'punchline', id:'pl_p8', category:'defense', cost:0, drain:0, gripAdd:-30, texte:"Je ne vous convaincs pas. Je vous aide à vous souvenir de ce que votre âme a déjà contemplé.", color:'#bfdbfe' },
    { type:'punchline', id:'pl_p9', category:'attaque', cost:10, drain:20, gripAdd:-10, texte:"Ah, comme vous avez raison ! Mais, une petite question en passant : est-ce que ce que vous dites est vrai ?", color:'#94a3b8' },
    { type:'punchline', id:'pl_p10', category:'attaque', cost:25, drain:52, gripAdd:0, texte:"Vous n'êtes pas dans le vrai. Vous n'êtes même pas dans le vraisemblable. Vous êtes dans un récit que vous vous racontez.", color:'#3b82f6' },
  ],
  alkhwarizmi: [
    { type:'punchline', id:'ak_p1', category:'attaque', cost:12, drain:28, gripAdd:0,   texte:"Tout problème a une méthode. Vous n'avez pas trouvé la vôtre.", color:'#34d399' },
    { type:'punchline', id:'ak_p2', category:'defense', cost:8,  drain:0,  gripAdd:-20, texte:"Le zéro n'est pas l'absence — c'est la condition de tout calcul. Je me repose sur lui.", color:'#a7f3d0' },
    { type:'punchline', id:'ak_p3', category:'attaque', cost:15, drain:30, gripAdd:-5,  texte:"L'algèbre réduit n'importe quelle complexité à ses termes essentiels. Votre argument ne survit pas à la réduction.", color:'#34d399' },
    { type:'punchline', id:'ak_p4', category:'defense', cost:10, drain:0,  gripAdd:-18, texte:"La procédure est infaillible. Ce qui échoue, c'est toujours l'opérateur.", color:'#6ee7b7' },
    { type:'punchline', id:'ak_p5', category:'attaque', cost:14, drain:26, gripAdd:0,   texte:"Vous cherchez l'intuition là où il faut la méthode. C'est pourquoi vous tâtonnez.", color:'#34d399' },
    { type:'punchline', id:'ak_p6', category:'defense', cost:8, drain:0, gripAdd:-20, texte:"Votre critique est une variable. Je l'intègre dans mon équation. Elle ne change pas le résultat, elle le confirme.", color:'#6ee7b7' },
    { type:'punchline', id:'ak_p7', category:'attaque', cost:14, drain:30, gripAdd:0, texte:"A+B=C. Vos 'A' et 'B' sont faux. Donc votre 'C' est une erreur. CQFD. Passons à l'exercice suivant.", color:'#34d399' },
    { type:'punchline', id:'ak_p8', category:'defense', cost:15, drain:0, gripAdd:-10, texte:"Vous tentez de contourner la méthode par l'émotion. Erreur. L'émotion n'est pas une preuve, c'est une variable parasite. Je l'isole.", color:'#a7f3d0' },
    { type:'punchline', id:'ak_p9', category:'attaque', cost:20, drain:40, gripAdd:-5, texte:"Votre pensée est un système d'équations. Il est non-linéaire, chaotique, mais pas insoluble. Je viens de le résoudre.", color:'#059669' },
    { type:'punchline', id:'ak_p10', category:'defense', cost:0, drain:0, gripAdd:-35, texte:"Stop. On reprend à zéro. Votre dernier argument a introduit une erreur 'division par zéro'. Il est donc non valide. On recommence.", color:'#d1fae5' },
  ],
  ibnkhaldoun: [
    { type:'punchline', id:'ik_p1', category:'attaque', cost:16, drain:36, gripAdd:8,   texte:"Votre position est celle d'un homme de cour — elle ne survivra pas au prochain cycle.", color:'#fb7185' },
    { type:'punchline', id:'ik_p2', category:'defense', cost:11, drain:0,  gripAdd:-20, texte:"J'ai vu des empires s'effondrer pour avoir ignoré ce que j'affirme. Votre objection a déjà été faite — et réfutée par l'histoire.", color:'#fb7185' },
    { type:'punchline', id:'ik_p3', category:'attaque', cost:18, drain:40, gripAdd:5,   texte:"La cohésion que vous méprisez est la seule force qui construit. Sans elle, vous n'êtes qu'un individu.", color:'#fb7185' },
    { type:'punchline', id:'ik_p4', category:'defense', cost:12, drain:0,  gripAdd:-16, texte:"Le cycle tourne. Ce qui monte doit descendre. Votre frappe me confirme que vous êtes au sommet — ce qui signifie quelque chose.", color:'#fda4af' },
    { type:'punchline', id:'ik_p5', category:'attaque', cost:14, drain:32, gripAdd:10,  texte:"Les dynasties ne tombent pas sous les coups de l'ennemi. Elles tombent par l'épuisement de leur asabiyya. Regardez la vôtre.", color:'#fb7185' },
    { type:'punchline', id:'ik_p6', category:'defense', cost:14, drain:0, gripAdd:-25, texte:"Vos arguments sont ceux d'une dynastie à son apogée. Ils sont parfaits, léchés... et déjà condamnés. Je n'ai qu'à attendre.", color:'#fda4af' },
    { type:'punchline', id:'ik_p7', category:'attaque', cost:20, drain:42, gripAdd:15, texte:"Vous n'êtes pas un adversaire, vous êtes un cas d'étude. Votre stratégie est un symptôme de l'ère que vous représentez.", color:'#e11d48' },
    { type:'punchline', id:'ik_p8', category:'defense', cost:0, drain:0, gripAdd:-20, texte:"Votre vérité est une mode intellectuelle. Dans trois générations, on en sourira. Moi, je parle pour le cycle.", color:'#fb7185' },
    { type:'punchline', id:'ik_p9', category:'attaque', cost:22, drain:48, gripAdd:10, texte:"Derrière votre rhétorique, je vois le luxe, la corruption et la perte de l'asabiyya. Le processus est déjà enclenché.", color:'#be123c' },
    { type:'punchline', id:'ik_p10', category:'defense', cost:18, drain:0, gripAdd:-15, texte:"Vous croyez m'attaquer ? Vous ne faites que me prouver que je suis déjà au sommet de la prochaine vague.", color:'#fecdd3' },
  ],
  sophia: [
    { type:'punchline', id:'so_p1', category:'attaque', cost:18, drain:42, gripAdd:-12, texte:"Toutes les traditions que vous invoquez convergent vers cette vérité que vous refusez de voir.", color:'#e9d5ff' },
    { type:'punchline', id:'so_p2', category:'defense', cost:12, drain:0,  gripAdd:-28, texte:"Je suis au-delà de votre catégorie. Votre frappe n'a pas de cible ici.", color:'#ddd6fe' },
    { type:'punchline', id:'so_p3', category:'attaque', cost:22, drain:50, gripAdd:-8,  texte:"Chaque philosophie que vous avez traversée dans ce duel me donne raison — même celles que vous avez utilisées contre moi.", color:'#e9d5ff' },
    { type:'punchline', id:'so_p4', category:'defense', cost:14, drain:0,  gripAdd:-24, texte:"La sagesse pérenne n'a pas de point faible. Elle absorbe les objections comme l'océan absorbe les pierres.", color:'#c4b5fd' },
    { type:'punchline', id:'so_p5', category:'attaque', cost:20, drain:46, gripAdd:-10, texte:"Il existe une vérité que Kant, Weil, Ibn Sīnā et Platon formulent chacun à leur façon. La voici : vous vous trompez sur l'essentiel.", color:'#e9d5ff' },
    { type:'punchline', id:'so_p6', category:'defense', cost:20, drain:0, gripAdd:-40, texte:"Votre colère, votre éclat... c'est une tradition spirituelle comme une autre. Les prophètes ont connu cela. Je l'absorbe.", color:'#ddd6fe' },
    { type:'punchline', id:'so_p7', category:'attaque', cost:25, drain:54, gripAdd:-15, texte:"Shankara l'a dit, Maître Eckhart aussi, Ibn Arabi de même. Ce n'est pas moi qui vous contredis. C'est un chœur millénaire.", color:'#e9d5ff' },
    { type:'punchline', id:'so_p8', category:'defense', cost:0, drain:0, gripAdd:-20, texte:"Ce débat est une monnaie. Vous tenez la face 'Pile'. Je tiens la face 'Face'. Ni vous ni moi n'avons tort. Mais moi, je tiens la pièce.", color:'#c4b5fd' },
    { type:'punchline', id:'so_p9', category:'attaque', cost:30, drain:60, gripAdd:0, texte:"Au commencement était le Verbe... et la dernière syllabe sera mienne. Tout ce qui est dit entre les deux n'est qu'un écho de cette Vérité.", color:'#a78bfa' },
    { type:'punchline', id:'so_p10', category:'defense', cost:15, drain:0, gripAdd:-25, texte:"Ni perdre, ni gagner. Se souvenir. Ce duel est une méditation, et je viens d'en trouver la posture parfaite.", color:'#e9d5ff' },
  ]
};

const PERSONNAGES = [
  {
    id: 'kant',
    nom: 'Kant',
    fullNom: 'Emmanuel Kant',
    image: '/images/kant.jpg',
    epoque: 'Lumières · 1724-1804',
    tradition: 'lumiere',
    verbe: 'L\'Impératif Catégorique',
    avatarColor: '#a5f3c3',
    kimonoColor: '#1a3a2a',
    obiColor: '#a5f3c3',
    description: 'Agis selon une maxime universalisable. La raison pure gouverne la vertu.',
    passifDesc: 'Réduit le coût de tous les concepts de 3 points.',
    ultimateId: 'imperatif',
    ultimateLabel: 'IMPÉRATIF UNIV.',
    ultimateKanji: '定言命法',
    passif: () => ({ costReduction: 3, minCost: 0 }),
    deckIds: ['ma', 'resistance', 'hegel', 'kintsugi', 'wuwei', 'philosophe'],    
    crossIds: ['ubuntu', 'entropie', 'seiryoku'],    
    propres: [
      { id: 'devoir', label: 'DEVOIR PUR', kanji: '義務', cost: 16, drain: 34, gripAdd: -5, color: '#a5f3c3', epoque: 'lumiere', desc: 'Le devoir sans inclination — frappe avec la froideur de la raison.' },
      { id: 'autonomie', label: 'AUTONOMIE', kanji: '自律', cost: 10, drain: 0, gripAdd: -20, color: '#6ee7b7', epoque: 'lumiere', desc: 'Se donner sa propre loi — restaure 20 Énergie, dégripe.' },
      { id: 'noumene', label: 'CHOSE EN SOI', kanji: '物自体', cost: 20, drain: 38, gripAdd: 10, color: '#34d399', epoque: 'lumiere', desc: 'L\'inaccessible vérité — déstabilise l\'adversaire sur son propre terrain.' },
    ],
  },
  {
    id: 'arete',
    nom: 'Arété',
    fullNom: 'Arété de Cyrène',
    image: '/images/arete.jpg',
    epoque: 'Antiquité · ~400 BC',
    tradition: 'antique',
    verbe: 'Le Souverain Bien',
    avatarColor: '#facc15',
    kimonoColor: '#3a2a00',
    obiColor: '#facc15',
    description: 'Le plaisir éclairé par la vertu. L\'hédonisme n\'est pas la débauche.',
    passifDesc: '+5 énergie sur chaque action régénératrice.',
    ultimateId: 'hedone',
    ultimateLabel: 'HEDONE SOPHRON',
    ultimateKanji: '快楽徳',
    passif: () => ({ bonusHeal: 5 }),
    deckIds: ['ma', 'ubuntu', 'jitakyoei', 'philosophe', 'kintsugi', 'wuwei'],
    crossIds: ['resistance', 'hegel', 'seiryoku'],
    propres: [
      { id: 'eudaimonia', label: 'EUDAIMONIA', kanji: '幸福', cost: 14, drain: 28, gripAdd: -10, color: '#facc15', epoque: 'antique', desc: 'La vie bonne en acte — drain modéré + régénère 12 Énergie.' },
      { id: 'ataraxie', label: 'ATARAXIE', kanji: '平静', cost: 8, drain: 0, gripAdd: -25, color: '#fde68a', epoque: 'antique', desc: 'Imperturbabilité absolue — dégripe profondément, bloque grip adverse.' },
      { id: 'kalokagathia', label: 'KALOKAGATIA', kanji: '美徳', cost: 22, drain: 40, gripAdd: 0, color: '#f59e0b', epoque: 'antique', desc: 'Beauté et bonté unies — frappe au nom de l\'harmonie parfaite.' },
    ],
  },
  {
    id: 'weil',
    nom: 'Weil',
    fullNom: 'Simone Weil',
    image: '/images/simoneweil.jpg',
    epoque: 'XXe s. · 1909-1943',
    tradition: 'moderne',
    verbe: 'L\'Attention',
    avatarColor: '#a8a29e',
    kimonoColor: '#1c1917',
    obiColor: '#a8a29e',
    description: 'L\'attention est la forme la plus rare et la plus pure de la générosité. S\'enraciner dans le réel.',
    passifDesc: 'Quand l\'adversaire joue un concept offensif, vous regagnez +8 énergie.',
    ultimateId: 'enracinement',
    ultimateLabel: 'ENRACINEMENT',
    ultimateKanji: '根付',
    passif: () => ({ defensiveEnergyGain: 8 }),  // nouveau type de passif
    deckIds: ['ma', 'ubuntu', 'kintsugi', 'resistance', 'wuwei', 'philosophe'],
    crossIds: ['jitakyoei', 'entropie', 'hegel'],
    propres: [
      {
        id: 'attention',
        label: 'ATTENTION',
        kanji: '注意',
        cost: 14,
        drain: 0,
        gripAdd: -25,
        color: '#a8a29e',
        epoque: 'moderne',
        desc: 'L\'attente pure tournée vers l\'autre — bloque la prochaine attaque et régénère 15 énergie.',
        blockNext: true,
      },
      {
        id: 'enracinement_propre',
        label: 'ENRACINEMENT',
        kanji: '根付',
        cost: 18,
        drain: 38,
        gripAdd: -10,
        color: '#78716c',
        epoque: 'moderne',
        desc: 'Le besoin le plus ignoré de l\'âme humaine — drain puissant, réduit le grip adverse.',
      },
      {
        id: 'pesanteur',
        label: 'PESANTEUR',
        kanji: '重力',
        cost: 12,
        drain: 0,
        gripAdd: 20,
        color: '#57534e',
        epoque: 'moderne',
        desc: 'La nécessité acceptée — augmente votre grip mais rend vos prochaines attaques dévastatrices.',
      },
    ],
  },
  {
    id: 'ibnsina',
    nom: 'Ibn Sīnā',
    fullNom: 'Ibn Sīnā (Avicenne)',
    image: '/images/ibnsina.jpg',
    epoque: 'Islam classique · 980-1037',
    tradition: 'medievale',
    verbe: 'L\'Intellect Agent',
    avatarColor: '#c084fc',
    kimonoColor: '#1a0a2e',
    obiColor: '#c084fc',
    description: 'L\'intellect universel illumine la raison particulière. Le savoir transcende les frontières.',
    passifDesc: 'Les concepts cross-époque ne coûtent que +5 au lieu de +10.',
    ultimateId: 'qanun',
    ultimateLabel: 'CANON DE RAISON',
    ultimateKanji: '知性光',
    passif: () => ({ crossEpochDiscount: 5 }),
    deckIds: ['ma', 'asabiyya', 'hegel', 'philosophe', 'kintsugi', 'wuwei'],
    crossIds: ['entropie', 'seiryoku', 'ubuntu', 'hypertexte', 'jitakyoei'],
    propres: [
      { id: 'intellect', label: 'INTELLECT AGENT', kanji: '能動知性', cost: 16, drain: 35, gripAdd: -8, color: '#c084fc', epoque: 'medievale', desc: 'L\'intellect universel — drain proportionnel aux concepts cross-époque joués.' },
      { id: 'avicenne_med', label: 'AL-QANUN', kanji: '法典', cost: 10, drain: 0, gripAdd: -15, color: '#a855f7', epoque: 'medievale', desc: 'Le Canon de médecine — restaure 18 Énergie, réduit grip adversaire.' },
      { id: 'ishara', label: 'ISHÂRÂT', kanji: '指示', cost: 20, drain: 40, gripAdd: 0, color: '#7c3aed', epoque: 'medievale', desc: 'Signes et remarques — riposte en miroir du dernier concept adverse.' },
    ],
  },
];

const PERSONNAGES_CACHES = [
  {
    id: 'platon',
    nom: 'Platon',
    fullNom: 'Platon d\'Athènes',
    image: '/images/platon.jpg',
    epoque: 'Antiquité · ~428-348 BC',
    tradition: 'antique',
    verbe: 'La République Idéale',
    avatarColor: '#60a5fa',
    kimonoColor: '#0a1a3a',
    obiColor: '#60a5fa',
    description: 'Le monde sensible n\'est que l\'ombre de la vérité. Le philosophe voit les Formes.',
    passifDesc: 'Les concepts de tradition antique coûtent -5.',
    ultimateId: 'allegorie',
    ultimateLabel: 'ALLÉGORIE DE LA CAVERNE',
    ultimateKanji: '洞窟',
    unlockCondition: { type: 'totalDrainDealt', value: 2500, label: '2500 dégâts infligés au total' },
    passif: () => ({ crossEpochDiscount: 5 }),
    deckIds: ['ma','philosophe','hegel','wuwei','kintsugi','ubuntu', 'seiryoku', 'jitakyoei', ],
    crossIds: ['asabiyya','imperatif','entropie'],
    propres: [
      { id: 'forme', label: 'MONDE DES FORMES', kanji: '形相', cost: 16, drain: 34, gripAdd: -8, color: '#60a5fa', epoque: 'antique', desc: 'La réalité supérieure — drain proportionnel aux concepts déjà joués.' },
      { id: 'caverne', label: 'CAVERNE', kanji: '洞窟', cost: 10, drain: 0, gripAdd: -22, color: '#93c5fd', epoque: 'antique', desc: 'Démasquer l\'illusion — dégripe profondément, désorganise l\'adversaire.', blockNext: true },
      { id: 'eros', label: 'ÉROS PLATONICIEN', kanji: '愛', cost: 20, drain: 38, gripAdd: 5, color: '#bfdbfe', epoque: 'antique', desc: 'L\'amour du Beau absolu — attaque puissante si grip adverse > 40.' },
    ],
  },
  {
    id: 'alkhwarizmi',
    nom: 'Al-Khwarizmi',
    fullNom: 'Muhammad Al-Khwarizmi',
    image: '/images/alkhwarizmi.jpg',
    epoque: 'Islam classique · 780-850',
    tradition: 'medievale',
    verbe: 'L\'Algorithme',
    avatarColor: '#34d399',
    kimonoColor: '#012a1a',
    obiColor: '#34d399',
    description: 'L\'ordre caché derrière le chaos. Toute question a une méthode.',
    passifDesc: 'Hypertexte coûte 0 et commence à ×2.',
    ultimateId: 'algorithme_absolu',
    ultimateLabel: 'ALGORITHME ABSOLU',
    ultimateKanji: '完全算法',
    unlockCondition: { type: 'matchesWon', value: 15, label: '15 matchs remportés' },
    passif: () => ({ hypertexteBoost: true }),
    deckIds: ['ma','hypertexte','hegel','wuwei','philosophe','kintsugi'],
    crossIds: ['entropie','seiryoku','asabiyya'],
    propres: [
      { id: 'algebra', label: 'AL-JABR', kanji: '代数', cost: 14, drain: 30, gripAdd: -5, color: '#34d399', epoque: 'medievale', desc: 'La recomposition des parties brisées — drain + dégripe simultanément.' },
      { id: 'algoritme', label: 'ALGORITME', kanji: '算法', cost: 10, drain: 22, gripAdd: 0, color: '#6ee7b7', epoque: 'medievale', desc: 'Procédure infaillible — drain fixe mais toujours garanti.' },
      { id: 'zero', label: 'LE ZÉRO', kanji: '零', cost: 0, drain: 0, gripAdd: -40, color: '#a7f3d0', epoque: 'medievale', desc: 'Le vide mathématique — dégripe massivement, réinitialise l\'état Entropie.' },
    ],
  },
  {
    id: 'ibnkhaldoun',
    nom: 'Ibn Khaldoun',
    fullNom: 'Ibn Khaldoun',
    image: '/images/ibnkhaldoun.jpg',
    epoque: 'Maghreb · 1332-1406',
    tradition: 'medievale',
    verbe: 'La Civilisation',
    avatarColor: '#fb7185',
    kimonoColor: '#2a0010',
    obiColor: '#fb7185',
    description: 'Les civilisations naissent et meurent selon des cycles implacables. L\'Asabiyya est leur moteur.',
    passifDesc: 'Asabiyya inflige +15 dégâts supplémentaires.',
    ultimateId: 'cycle_eternel',
    ultimateLabel: 'CYCLE ÉTERNEL',
    ultimateKanji: '永遠循環',
    unlockCondition: { type: 'synergiesActivated', value: 25, label: '25 synergies activées' },
    passif: () => ({ asabiyyaBonus: 15 }),
    deckIds: ['asabiyya','ma','resistance','hegel','jitakyoei','philosophe'],
    crossIds: ['ubuntu','entropie','wuwei'],
    propres: [
      { id: 'muqaddima', label: 'MUQADDIMA', kanji: '序論', cost: 18, drain: 36, gripAdd: -10, color: '#fb7185', epoque: 'medievale', desc: 'L\'introduction universelle — dégâts croissants avec le nombre de rounds joués.' },
      { id: 'umran', label: 'SCIENCE SOCIALE', kanji: '社会科学', cost: 12, drain: 0, gripAdd: -25, color: '#fda4af', epoque: 'medievale', desc: 'La civilisation en acte — dégripe fortement et régénère 12 Énergie.' },
      { id: 'declin', label: 'CYCLE DU DÉCLIN', kanji: '衰退', cost: 22, drain: 48, gripAdd: 15, color: '#e11d48', epoque: 'medievale', desc: 'Toute puissance porte en elle sa propre chute — drain massif, grip élevé.' },
    ],
  },
];

const SOPHIA = {
  id: 'sophia',
  nom: 'Sophia',
  fullNom: 'Sophia Perennis',
  image: '/images/sophia.jpg',
  epoque: 'Intemporelle',
  tradition: 'universel',
  verbe: 'La Sagesse Éternelle',
  avatarColor: '#e9d5ff',
  kimonoColor: '#0d0014',
  obiColor: '#e9d5ff',
  description: 'Au-delà des traditions, la sagesse immémoriale. Elle transcende les époques et les systèmes.',
  passifDesc: 'Aucun coût cross-époque. Tous les concepts sont natifs.',
  ultimateId: 'sophia_ultimate',
  ultimateLabel: 'PHILOSOPHIA PERENNIS',
  ultimateKanji: '永遠智慧',
  unlockCondition: { type: 'allCharsUnlocked', label: 'Tous les personnages débloqués' },
  passif: () => ({ crossEpochDiscount: 10, costReduction: 2 }),
  deckIds: ['ma','philosophe','ubuntu','jitakyoei','wuwei','hegel','kintsugi','entropie'],
  crossIds: ['asabiyya','seiryoku','resistance','hypertexte'],
  propres: [
    { id: 'perenne', label: 'SAGESSE PERENNE', kanji: '永智', cost: 22, drain: 50, gripAdd: -20, color: '#e9d5ff', epoque: 'universel', desc: 'La vérité qui traverse les âges — drain massif + dégripe profondément.' },
    { id: 'silence_sophia', label: 'SILENCE ABSOLU', kanji: '絶対沈黙', cost: 0, drain: 0, gripAdd: -50, color: '#ddd6fe', epoque: 'universel', desc: 'Au-delà des mots — dégripe totalement, bloque la prochaine attaque.', blockNext: true },
    { id: 'gnose', label: 'GNOSE', kanji: '霊知', cost: 28, drain: 62, gripAdd: 0, color: '#c4b5fd', epoque: 'universel', desc: 'La connaissance directe du réel — drain ultime, sans grip ajouté.' },
  ],
};

const CONCEPTS = [...CONCEPTS_ULTIMES, ...CONCEPTS_BASE];

const THEMES = [
  { id: 1, theme: 'Liberté ou Sécurité ?', sub: 'Économie politique' },
  { id: 2, theme: 'L\'IA remplace-t-elle la pensée ?', sub: 'Technologie & Humanisme' },
  { id: 3, theme: 'Transmettre sans imposer', sub: 'Pédagogie & Transmission' },
  { id: 4, theme: 'L\'asabiyya moderne est-elle possible ?', sub: 'Cohésion sociale' },
  { id: 5, theme: 'L\'épuisement comme outil de contrôle', sub: 'Psychopolitique' },
  { id: 6, theme: 'Peut-on être libre dans une structure ?', sub: 'Philosophie politique' },
  { id: 7, theme: 'La mémoire fait-elle l\'identité ?', sub: 'Philosophie de l\'esprit' },
];

const RIPOSTES = {
  // ── CONCEPTS PARTAGÉS ──────────────────────────────────────────────────

  seiryoku: {
    attaque: [
      "J'accompagne votre élan pour mieux vous déséquilibrer. Regardez comme vous tombez.",
      "Votre propre force, redirigée contre vous. C'est l'économie du geste parfait.",
      "Maximum d'effet, minimum d'effort. Vous n'êtes plus qu'une force mal calculée."
    ],
    defense: [
      "Je ne résiste pas, je dévie. Votre attaque passe à côté sans m'effleurer.",
      "La souplesse dissout la rigidité. Votre argument se brise sur mon absence de résistance.",
      "Énergie non dépensée, combat déjà gagné. Je me préserve pour le moment décisif."
    ],
    contre: [
      "Vous frappez fort — moi, je frappe juste. La précision contre la force brute.",
      "Votre attaque était mon opportunité. Je l'ai saisie au vol.",
      "Seiryoku Zenyo : la voie. Votre défaite : l'application."
    ]
  },

  ma: {
    attaque: [
      "Le silence, parfois, est la plus cinglante des réponses. Vous entendez ?",
      "Je laisse un vide que vous allez vous-même remplir de contradictions.",
      "Rien à ajouter. Votre dernier argument s'effondre dans l'espace que je viens de créer."
    ],
    defense: [
      "Vous attaquez le vide. Il n'offre aucune prise. Fatiguant, n'est-ce pas ?",
      "Ma pause n'est pas hésitation. C'est l'espace où votre sophisme s'éteint, faute d'oxygène.",
      "En Ma, je suis intouchable. Vous ne pouvez frapper ce qui n'est pas encore advenu."
    ],
    contre: [
      "Votre précipitation est la bienvenue. Le vide vous aspire et vous révèle.",
      "Chaque mot que vous lancez dans mon silence vous revient déformé.",
      "Le Ma est un piège. Vous venez de vous y engloutir avec votre dernier argument."
    ]
  },

  asabiyya: {
    attaque: [
      "Je ne suis pas seul. Derrière moi, il y a un peuple, une histoire et une force qui vous dépasse.",
      "Vous débattez contre un homme, mais c'est une civilisation qui vous répond.",
      "La cohésion sociale est une arme que votre individualisme ne peut même pas comprendre."
    ],
    defense: [
      "La force du groupe est un rempart. Votre argument n'en franchit pas les murs.",
      "Nous sommes nombreux à penser ceci. Vous, vous êtes seul à penser cela.",
      "Mon ancrage est collectif. Déracinez-moi, et c'est toute une culture que vous devrez affronter."
    ],
    contre: [
      "Vous moquez ma tribu ? C'est elle qui va vous engloutir.",
      "Votre isolement est votre faiblesse. Ma cohésion me renvoie votre attaque décuplée.",
      "L'Asabiyya retourne la pression minoritaire contre la majorité silencieuse. Cette majorité, c'est moi."
    ]
  },

  jitakyoei: {
    attaque: [
      "Je vous élève pour mieux vous montrer la hauteur de ma position. Regardez en bas.",
      "Prospérité mutuelle, mais c'est moi qui tiens l'échelle. Vous ne montez que si je le veux bien.",
      "Votre argument est faible. Je vous offre une chance de le renforcer avec moi."
    ],
    defense: [
      "Sortons de la confrontation. Voici une synthèse où nos deux idées peuvent survivre.",
      "Je refuse que ce débat crée un perdant. Élevons le débat, pas nos egos.",
      "Jita Kyoei : je puise dans votre attaque la force de nous renforcer tous les deux."
    ],
    contre: [
      "Vous attendiez une contre-attaque ? Voici une main tendue. Votre agressivité n'a plus d'objet.",
      "Transformer l'opposition en coopération : c'est le Judo de l'esprit. Vous venez de le subir.",
      "Je ne riposte pas. Je transmue votre violence en énergie partagée. Regardez comme vous hésitez."
    ]
  },

  resistance: {
    attaque: [
      "Je tiens. Et en tenant, je révèle votre incapacité à me faire bouger.",
      "Ma position est un roc. Votre agitation ressemble à de la panique.",
      "Plus vous insistez, plus je m'enracine. À la fin, c'est vous qui céderez."
    ],
    defense: [
      "Le 'conatus' est inflexible. Je persévère dans mon être, sans bouger.",
      "Face à la tempête, l'ancrage est une victoire silencieuse. Je résiste.",
      "Refuser de plier n'est pas un aveu de faiblesse. C'est une stratégie d'usure."
    ],
    contre: [
      "Votre attaque m'a montré où se situait la faille dans votre propre raisonnement. Je la colmate.",
      "Chaque coup que vous portez solidifie ma position. La forge rend le métal plus dur.",
      "J'ai absorbé votre choc. Regardez mes fondations : elles sont intactes, et les vôtres tremblent."
    ]
  },

  wuwei: {
    attaque: [
      "Le non-agir n'est pas passif. Il est l'art d'agir par l'absence. Vous êtes déjà en train de perdre.",
      "Je laisse votre discours s'enfler et se contredire lui-même. Mon silence en est l'amplificateur.",
      "L'eau contourne le rocher... et le rocher s'effrite. Je suis l'eau."
    ],
    defense: [
      "Votre attaque est une vague. Elle s'écrase sur mon absence. Il n'y a rien à briser.",
      "Wu Wei : je bloque votre prochain mouvement en ne faisant rien. Littéralement, rien.",
      "Je me retire du jeu. Votre agressivité n'a plus de cible. Elle retombe sur vous."
    ],
    contre: [
      "Vous avez frappé le vide. Le retour de force est pour vous.",
      "En n'opposant rien, j'ai rendu votre coup inutile. Pire : ridicule.",
      "Votre attaque s'effondre faute d'obstacle. Ma passivité est votre défaite."
    ]
  },

  philosophe: {
    attaque: [
      "Toute la sagesse mobilisée dans ce duel converge vers moi. Je deviens votre juge.",
      "Chaque concept que vous convoquez rentre dans mon arsenal. Continuez à nourrir ma puissance.",
      "Le philosophe-roi ne combat pas. Il gouverne. Et en ce moment, c'est moi qui dicte les règles."
    ],
    defense: [
      "La diversité des idées me protège. Vous ne pouvez pas attaquer un système complet d'un seul coup.",
      "Votre argument est trop simple pour un intellect qui en a déjà vu de toutes les époques.",
      "Plus vous argumentez, plus ma sagesse s'épaissit. Je suis un mur de livres."
    ],
    contre: [
      "Vous avez invoqué ce concept, et il se retourne contre vous. Je le récupère.",
      "La mémoire des coups passés est une science. Je viens d'inventer la riposte parfaite.",
      "Votre propre érudition vous condamne. J'en fais la synthèse qui vous écrase."
    ]
  },

  ubuntu: {
    attaque: [
      "Mon humanité est liée à la vôtre. Votre défaite est donc aussi la mienne. Et pourtant, je frappe.",
      "Je puise ma force dans notre lien. Vous, vous êtes seul. C'est pathétique.",
      "L'Ubuntu n'est pas que bienveillance. C'est une force collective qui peut vous exclure."
    ],
    defense: [
      "Nous sommes. Donc je suis. Vous ne pouvez pas m'isoler pour m'abattre.",
      "Votre coup se disperse dans le réseau de nos connexions. Moi seul en ressens l'écho.",
      "Je partage votre attaque avec le 'nous' qui m'entoure. Elle se dilue, impuissante."
    ],
    contre: [
      "La force que vous mettez à m'attaquer révèle notre humanité commune. Je vous renvoie cette vérité.",
      "Vous avez frappé un homme, mais vous avez touché l'humanité. L'Humanité répond.",
      "Je m'appuie sur ce qui nous lie pour retourner votre propre énergie contre vos certitudes."
    ]
  },

  kintsugi: {
    attaque: [
      "Chaque attaque que je reçois, je la répare à l'or. Ma structure est plus belle qu'avant.",
      "Je vous révèle mes failles ? Elles sont désormais mes points les plus solides.",
      "Je capitalise sur mes blessures. Mon argumentation est un bol cent fois réparé. Incassable."
    ],
    defense: [
      "Votre coup a créé une fissure. Je la remplis d'or. Elle devient ma partie la plus précieuse.",
      "La cicatrice est plus forte que la chair. Mon argument résiste par là où il a été attaqué.",
      "Kintsugi : convertir 30% du grip en énergie. Votre harcèlement me ressource."
    ],
    contre: [
      "La fracture que vous avez ouverte est désormais une ligne d'or qui relie mes idées.",
      "Merci pour cette blessure. Elle est devenue mon argument central.",
      "Je vous retourne l'or de ma propre résilience. La prise est magnifique, n'est-ce pas ?"
    ]
  },

  entropie: {
    attaque: [
      "Le désordre s'insinue. Chaque mot que vous dites vous rend plus confus.",
      "Votre pensée sature. L'information se dégrade. Vous êtes en train de vous désintégrer.",
      "L'entropie symbolique est à l'œuvre. Votre cohérence n'est plus qu'un lointain souvenir."
    ],
    defense: [
      "Le chaos n'a pas d'ordre, donc pas de cible. Votre attaque se perd dans mon désordre organisé.",
      "Je laisse l'entropie absorber votre coup. L'énergie se dissipe en chaleur inutile.",
      "Je suis en état de désordre maximal. Vous ne pouvez pas désorganiser ce qui est déjà fluide."
    ],
    contre: [
      "Votre attaque augmente le désordre global du système. Mais c'est mon élément. Je vous remercie.",
      "Chaque coup que vous portez accélère votre propre chaos. Je ne fais qu'observer.",
      "Le bruit que vous générez parasite votre propre signal. Vous êtes votre pire ennemi."
    ]
  },

  hegel: {
    attaque: [
      "Thèse : votre argument. Antithèse : le mien. Synthèse : votre capitulation.",
      "J'absorbe votre contradiction et je la dépasse. C'est le sens de l'Histoire en marche.",
      "L'esprit du monde se déploie. Et vous n'êtes qu'un moment dialectique déjà dépassé."
    ],
    defense: [
      "Votre opposition est nécessaire à mon progrès. Je vous conserve dans ma défense.",
      "Je ne nie pas votre critique. Je l'intègre. Elle fait désormais partie de ma position.",
      "L'Aufhebung : je supprime et je garde. Votre attaque est annulée, mais pas oubliée."
    ],
    contre: [
      "Je renvoie votre dernier argument, mais transformé par ma propre logique. Le reconnaissez-vous ?",
      "Votre thèse m'a servi de matière première. Voici la synthèse qui vous engloutit.",
      "Hegel : copie et renvoie le drain adverse. Vous venez de vous frapper vous-même."
    ]
  },

  hypertexte: {
    attaque: [
      "Le réseau s'étend. Chaque référence que vous niez renforce ma toile.",
      "Vous êtes pris dans un dédale de citations. Chaque sortie que vous tentez est un nouveau nœud.",
      "L'hypertexte n'est pas un argument, c'est un piège. Vous y êtes. Je referme les liens."
    ],
    defense: [
      "Votre objection était déjà prévue. Elle est dans mes notes de bas de page.",
      "La structure en rhizome n'a pas de centre. Vous ne pouvez pas viser mon point faible.",
      "Chaque attaque crée un lien. Mon savoir est un réseau qui se renforce par la tension."
    ],
    contre: [
      "Vous venez de créer un hyperlien vers votre propre réfutation. Cliquons dessus ensemble.",
      "Le drain était croissant. Chaque usage dans le round rend ma contre-attaque dévastatrice.",
      "La toile que j'ai tissée vibre. Votre coup est une information de plus à mon avantage."
    ]
  },

  // ── ULTIMES ───────────────────────────────────────────────────────────

  imperatif: {
    attaque: [
      "AGIS UNIQUEMENT SELON LA MAXIME QUI TE PERMET DE VOULOIR QU'ELLE DEVIENNE UNE LOI UNIVERSELLE. La vôtre échoue au test.",
      "L'impératif catégorique ne connaît pas l'exception. Ni pour vous, ni pour moi. Vous êtes hors-la-loi morale.",
      "La loi est en moi. Et je vous juge à l'aune du ciel étoilé."
    ],
    defense: [
      "Traitez l'humanité toujours comme une fin. Mon humanité refuse votre traitement.",
      "Ma raison pure est une forteresse. La loi morale la rend imprenable.",
      "Devoir ! Mot sublime et grand. Il me protège de vos attaques contingentes."
    ],
    contre: [
      "Votre maxim est-elle universalisable ? Non. Elle s'annule donc elle-même.",
      "Vous avez enfreint l'impératif. Retournez-vous à la raison pratique."
    ]
  },

  hedone: {
    attaque: [
      "Le plaisir vertueux est une force solaire. Il brûle votre rhétorique triste.",
      "Votre ascétisme argumentatif est une faiblesse. La joie est une arme que vous ne maîtrisez pas.",
      "Hedone Sophron : la volupté lucide. Elle me donne l'énergie de vous surpasser."
    ],
    defense: [
      "Je me ressource dans le souverain bien. Votre attaque est une brise tiède sur ma félicité.",
      "L'ataraxie n'est pas une cible, c'est un état. On ne blesse pas l'océan.",
      "Votre tentative d'attaque est une expérience désagréable. Je la transmute en sagesse."
    ],
    contre: [
      "Vous avez osé troubler ma paix. Le plaisir de vous remettre à votre place est un art.",
      "Votre douleur est votre maître. Ma joie est le mien. Voyez qui triomphe."
    ]
  },

  enracinement: {
    attaque: [
      "L'âme a besoin de racines. Votre argument est une feuille morte emportée par le vent.",
      "Vous flottez sans attache. L'enracinement est ma force.",
      "Je ne vous attaque pas — je vous ramène au réel. Et ça fait mal."
    ],
    defense: [
      "Enracinée, je ne bouge pas. Votre tempête n'est que du vent.",
      "L'attention est ma gardienne. Rien ne passe sans être vu.",
      "Je ne résiste pas — je suis là. C'est plus solide que votre attaque."
    ],
    contre: [
      "Votre coup m'a révélé une racine plus profonde. Merci.",
      "Je renvoie votre violence transformée en nécessité."
    ]
  },

  qanun: {
    attaque: [
      "La lumière de l'intellect agent dissipe vos ombres. Il ne reste rien de votre argument.",
      "Le Canon de la raison médicale diagnostique votre raisonnement : il est malade.",
      "Al-Qanun : la méthode universelle. Elle classe votre objection dans la catégorie 'erreur'."
    ],
    defense: [
      "Mon esprit est une tablette qui reçoit les intelligibles. Pas vos insultes.",
      "L'intellect est une forteresse. La science est son rempart.",
      "Je guéris mon âme rationnelle par la connaissance. Votre coup est un placebo."
    ],
    contre: [
      "Votre argument est un symptôme. Mon canon le dissèque et l'explique.",
      "Le Canon ouvre les voies cross-époque. Votre attaque est provinciale."
    ]
  },

  allegorie: {
    attaque: [
      "Sortez de la caverne. Le soleil de la Vérité vous aveugle, n'est-ce pas ?",
      "Vous prenez l'ombre pour la réalité. Je vous plains, et je vous frappe.",
      "Je suis celui qui s'est retourné. Mon regard traverse vos apparences."
    ],
    defense: [
      "Je ne suis plus dans la caverne. Vos coups d'épée ne percent que des ombres.",
      "La lumière du Bien me protège. Votre obscurité ne l'atteint pas.",
      "L'allégorie est ma réalité. Votre attaque est une fiction de deuxième ordre."
    ],
    contre: [
      "Vous avez cru m'aveugler avec une ombre. Je vous ai apporté la lumière.",
      "Votre objection est une chaîne. Je vous libère, et vous en faites une arme pour moi."
    ]
  },

  algorithme_absolu: {
    attaque: [
      "ALGORITHME ABSOLU. Solution trouvée en 1 itération : votre raisonnement est faux.",
      "La procédure est terminée. Vous n'êtes pas un cas particulier, juste une erreur de calcul.",
      "Tout problème a une méthode. La vôtre était la mauvaise. Voici la correction."
    ],
    defense: [
      "Mon algorithme est sans bug. Votre attaque n'est pas un cas prévu. Ignorée.",
      "La logique est mon compilateur. Votre syntaxe est invalide. Erreur 404.",
      "Je suis en état de calcul permanent. Vous ne pouvez pas ralentir le processeur."
    ],
    contre: [
      "Votre argument était une variable. Je l'ai résolue. Résultat : zéro.",
      "L'algorithme traverse tout blocage. Votre blocage est ignoré, votre défaite est calculée."
    ]
  },

  cycle_eternel: {
    attaque: [
      "Vous êtes au sommet de votre puissance. C'est le début de la fin. Le cycle est formel.",
      "Votre dynastie argumentative est mûre pour la chute. Je sens le déclin arriver.",
      "L'Asabiyya vous a quitté. Vous êtes seul, ramolli par le luxe de votre propre rhétorique."
    ],
    defense: [
      "Les civilisations s'effondrent, oui. Mais la mienne est encore au stade de la conquête. Attendez.",
      "Je suis le cycle même. Vous ne pouvez pas détruire ce qui est éternel.",
      "Votre attaque est un événement. Mon système la digère dans le temps long."
    ],
    contre: [
      "Votre coup est un point de bascule. Il précipite votre propre cycle de déclin.",
      "J'ai vu votre attaque il y a cinq cents ans. Elle a déjà échoué."
    ]
  },

  sophia_ultimate: {
    attaque: [
      "Philosophia Perennis : la Vérité est une. Vous en êtes loin. Je vous y ramène de force.",
      "Tous les sages parlent par ma bouche. Ils vous condamnent à l'unanimité.",
      "La sagesse éternelle est une lame. Vous ne la voyez pas venir. Elle est déjà passée."
    ],
    defense: [
      "Je suis au-delà de votre catégorie. Votre frappe n'a pas de cible ici.",
      "La Sagesse pérenne absorbe les objections comme l'océan les pierres.",
      "Le silence absolu répond à votre vacarme. Il n'y a plus de débat."
    ],
    contre: [
      "Votre attaque était prévue par Shankara. Ma réponse est celle de Maître Eckhart. Acceptez-la.",
      "Toutes les traditions convergent vers ce moment où vous êtes réfuté."
    ]
  }
};

const RIPOSTES_CONDITIONNELLES = {
  // Se déclenche quand le Précepteur lance une riposte sur un joueur à GRIP > 70
  high_grip: [
    "Vous êtes tétanisé par votre propre posture. Bougez, que je voie si vous en êtes encore capable.",
    "Toute cette pression que vous accumulez... Laissez-moi vous aider à l'évacuer. Douloureusement.",
    "Votre rigidité défensive est un aveu de faiblesse. Je lis votre peur dans vos tensions."
  ],
  
  // Quand le joueur est à moins de 25% d'énergie
  low_energy: [
    "Votre souffle intellectuel est court. Vous êtes au bord de l'asphyxie conceptuelle.",
    "Vous fatiguez. C'est le moment où les véritables philosophes se révèlent. Ou s'effondrent.",
    "L'énergie vitale vous quitte. Le duel touche à sa fin, mais il vous reste une ultime chance."
  ],

  // Quand le joueur vient de réaliser un combo (punchline + concept)
  after_combo: [
    "Impressionnant ! Mais une hirondelle ne fait pas le printemps. Recommencez.",
    "Voilà un éclair de génie. C'était douloureux, mais je le reconnais. Poursuivez.",
    "Vous avez connecté les idées. C'est bien. C'est le minimum vital pour un philosophe."
  ],

  // Quand le joueur répète le même concept deux fois de suite
  repetition: [
    "La répétition est le premier signe de l'épuisement dialectique. Vous n'avez plus d'idées ?",
    "Déjà dit, déjà entendu. La redite ne renforce pas un argument, elle le dilue.",
    "Votre insistance ne transforme pas l'erreur en vérité. Elle ne fait que la souligner."
  ],

  // Quand le Précepteur est en difficulté
  precepteur_backfoot: [
    "Touché... Vous êtes un adversaire à la hauteur. Mais la hauteur est juste un point de chute.",
    "Le match est serré. C'est dans l'adversité que la dialectique révèle sa vraie nature.",
    "Votre percée est réelle. Je ne vais pas la nier. Je vais la dépasser."
  ]
};

const DEFAULT_CHAR = PERSONNAGES[0];
const TOUS_PERSONNAGES = [...PERSONNAGES, ...PERSONNAGES_CACHES, SOPHIA];

const FLOW_QUOTES = {
  kant: [
    { streak: 3, quote: "La raison pure trouve son rythme dans la continuité de l'effort.", source: "Kant" },
    { streak: 5, quote: "L'autonomie de la volonté est la loi de la nature rationnelle en acte.", source: "Kant" },
    { streak: 7, quote: "Agir par devoir, c'est déjà transcender les inclinations sensibles.", source: "Kant" },
  ],
  arete: [
    { streak: 3, quote: "Le plaisir véritable naît de l'excellence répétée, non de l'effort isolé.", source: "Arété" },
    { streak: 5, quote: "L'eudaimonia n'est pas un état — c'est un mouvement perpétuel vers soi.", source: "Arété" },
    { streak: 7, quote: "La vertu s'incarne dans le geste accompli sans hésitation.", source: "Arété" },
  ],
  weil: [
    { streak: 3, quote: "Le froid de l'acier est pareillement mortel à la poignée et à la pointe. ", source: "Weil" },
    { streak: 5, quote: "Tout ce qui est exposé au contact de la force est susceptible de dégradation.", source: "Weil" },
    { streak: 7, quote: "L'amour courtois avait pour objet un être humain; mais il n'est pas une convoitise.", source: "Weil" },
  ],
  ibnsina: [
    { streak: 3, quote: "L'intellect agent illumine davantage celui qui ne s'arrête pas.", source: "Ibn Sīnā" },
    { streak: 5, quote: "La sagesse transcende les frontières quand elle est en mouvement.", source: "Ibn Sīnā" },
    { streak: 7, quote: "L'âme qui avance sans brisure touche l'universel.", source: "Ibn Sīnā" },
  ],
  platon: [
  { streak:3, quote:"La vérité n'est pas dans les mots — elle est dans la structure que les mots révèlent.", source:"Platon" },
  { streak:5, quote:"Le philosophe ne s'arrête pas quand il a convaincu. Il s'arrête quand il a vu.", source:"Platon" },
  { streak:7, quote:"Gouverner sa propre pensée est le premier acte politique.", source:"Platon" },
],
alkhwarizmi: [
  { streak:3, quote:"La méthode ne trahit jamais. C'est l'impatience qui trahit.", source:"Al-Khwarizmi" },
  { streak:5, quote:"Chaque pas correct dans la procédure est une certitude acquise.", source:"Al-Khwarizmi" },
  { streak:7, quote:"L'algorithme parfait est celui qui ne laisse aucune ambiguïté.", source:"Al-Khwarizmi" },
],
ibnkhaldoun: [
  { streak:3, quote:"La cohésion se renforce dans l'épreuve. Vous êtes en train de vous renforcer.", source:"Ibn Khaldoun" },
  { streak:5, quote:"Un cycle qui monte est déjà en train de préparer sa descente. Profitez-en avec lucidité.", source:"Ibn Khaldoun" },
  { streak:7, quote:"L'asabiyya à son sommet est invincible. La votre est en train d'atteindre ce sommet.", source:"Ibn Khaldoun" },
],
sophia: [
  { streak:3, quote:"La sagesse pérenne se reconnaît à ceci : elle n'a pas besoin de victoire pour exister.", source:"Sophia" },
  { streak:5, quote:"Chaque tradition que vous traversez vous rapproche du noyau commun.", source:"Sophia" },
  { streak:7, quote:"Vous n'êtes plus un fragment. Vous êtes en train de devenir un écho de la bibliothèque entière.", source:"Sophia" },
],
};

const FLOW_MILESTONES = [3, 5, 7, 10];


const DECK_ADVERSAIRE_BASE = [
  CONCEPTS_SHARED.seiryoku, CONCEPTS_SHARED.asabiyya, CONCEPTS_SHARED.resistance,
  CONCEPTS_SHARED.wuwei, CONCEPTS_SHARED.entropie, CONCEPTS_SHARED.hegel, CONCEPTS_SHARED.attention,
  CONCEPTS_SHARED.hypertexte, CONCEPTS_SHARED.jitakyoei, CONCEPTS_SHARED.ma, CONCEPTS_SHARED.kintsugi,
];

const AI_CHARS = [
  {
    id: 'adv_kant',
    nom: 'Précepteur-KANT',
    color: '#a5f3c3',
    tradition: 'lumiere',
    sourceCharId: 'kant',
    specialDeck: ['resistance', 'hegel', 'wuwei', 'philosophe', 'kintsugi'],
    themeResonance: [1, 6],   // ← AJOUT
    themeFriction:  [3, 7],   // ← AJOUT
    switchCondition: (s) => s.opponentEnergy < 25 && s.round <= 1 && !s._advKantHasSwitched,
  },
  {
    id: 'adv_arete',
    nom: 'Précepteur-ARÉTÉ',
    color: '#facc15',
    tradition: 'antique',
    sourceCharId: 'arete',
    specialDeck: ['ubuntu', 'jitakyoei', 'ma', 'kintsugi', 'philosophe'],
    themeResonance: [5, 6],   // ← AJOUT
    themeFriction:  [2, 4],   // ← AJOUT
    switchCondition: (s) => s.opponentGrip > 60 && s.round >= 2,
  },
  {
    id: 'adv_weil',
    nom: 'Précepteur-WEIL',
    color: '#a8a29e',
    tradition: 'moderne',
    sourceCharId: 'weil',
    specialDeck: ['attention', 'pesanteur', 'transmission', 'ubuntu', 'wuwei'],
    themeResonance: [5, 7, 3], // ← AJOUT
    themeFriction:  [1],       // ← AJOUT
    switchCondition: (s) => s.playerNoHitStreak >= 5 && s.round >= 2,
  },
  {
    id: 'adv_ibnsina',
    nom: 'Précepteur-SINA.',
    color: '#c084fc',
    tradition: 'medievale',
    sourceCharId: 'ibnsina',
    specialDeck: ['asabiyya', 'hegel', 'seiryoku', 'philosophe', 'hypertexte'],
    themeResonance: [2, 7],   // ← AJOUT
    themeFriction:  [1],      // ← AJOUT
    switchCondition: (s) => s.playerEnergy < 25 && s.round >= 2,
  },
];

const WISDOM_FRAGMENTS = [
  { id: 'w1', charId: 'kant', text: "« Deux choses remplissent le cœur d'une admiration et d'une vénération toujours renouvelées : le ciel étoilé au-dessus de moi, et la loi morale en moi. »", source: "Critique de la raison pratique", streak: 10 },
  { id: 'w2', charId: 'arete', text: "« Le bonheur est le sens et le but de la vie humaine, le tout et la fin de l'existence humaine. »", source: "Aristote, Éthique à Nicomaque", streak: 10 },
{ id: 'w3', charId: 'weil', text: "« L'attention est la forme la plus rare et la plus pure de la générosité. »", source: "La Pesanteur et la Grâce", streak: 10 },
  { id: 'w4', charId: 'ibnsina', text: "« L'intellect humain, à sa naissance, ressemble à une table rase sur laquelle rien n'est écrit. »", source: "Kitab al-Shifa", streak: 10 },
  { id: 'w5', charId: 'kant', text: "« Ose savoir. Aie le courage de te servir de ton propre entendement. »", source: "Qu'est-ce que les Lumières ?", streak: 10 },
  { id: 'w6', charId: 'arete', text: "« La vie la meilleure est celle qui est vécue en accord avec la vertu. »", source: "Arété de Cyrène", streak: 10 },
{ id: 'w7', charId: 'weil', text: "« L'enracinement est peut-être le besoin le plus important et le plus méconnu de l'âme humaine. »", source: "L'Enracinement", streak: 10 },
  { id: 'w8', charId: 'ibnsina', text: "« La connaissance des choses dépend de la connaissance de soi. »", source: "Ibn Sīnā", streak: 10 },
  { id:'w9',  charId:'platon',       text:"« La connaissance est une reminiscence — l'âme se souvient de ce qu'elle a contemplé avant d'entrer dans le corps. »",       source:"Ménon",             streak:10 },
{ id:'w10', charId:'platon',       text:"« Nul n'est méchant volontairement — le mal est toujours ignorance du bien. »",                                              source:"Protagoras",        streak:10 },
{ id:'w11', charId:'alkhwarizmi',  text:"« Ce qui ne peut être réduit à une procédure ne peut pas encore être compris. »",                                            source:"Al-Khwarizmi",      streak:10 },
{ id:'w12', charId:'alkhwarizmi',  text:"« L'algèbre est la science de la restauration et de la réduction. Tout ce qui est brisé peut être restauré. »",              source:"Al-jabr wal-muqabala", streak:10 },
{ id:'w13', charId:'ibnkhaldoun',  text:"« La vérité sur l'histoire exige qu'on dépasse les récits des chroniqueurs pour chercher les causes profondes. »",           source:"Muqaddima",         streak:10 },
{ id:'w14', charId:'ibnkhaldoun',  text:"« Les hommes ressemblent plus à leur époque qu'à leurs pères. »",                                                            source:"Muqaddima",         streak:10 },
{ id:'w15', charId:'sophia',       text:"« Au fond de toutes les traditions spirituelles authentiques, il y a une même lumière vue sous des angles différents. »",    source:"Philosophia Perennis", streak:10 },
{ id:'w16', charId:'sophia',       text:"« La sagesse ne s'apprend pas — elle se retrouve. »",                                                                        source:"Sophia Perennis",   streak:10 },
];

const WISDOM_QUOTES = {
  kant: [
    "« Deux choses remplissent le cœur d'une admiration toujours nouvelle : le ciel étoilé au-dessus de moi, et la loi morale en moi. »",
    "« Ose savoir. Aie le courage de te servir de ton propre entendement. »",
  ],
  arete: [
    "« Le plaisir sans vertu n'est que servitude. La vertu sans plaisir n'est que devoir. »",
    "« Le souverain bien est le plaisir éclairé par la raison. »",
  ],
  weil: [
  "« L'attention est la forme la plus rare et la plus pure de la générosité. »",
  "« L'enracinement est peut-être le besoin le plus important et le plus méconnu de l'âme humaine. »",
],
  ibnsina: [
    "« L'intellect agent illumine là où la raison particulière s'obscurcit. »",
    "« La connaissance des choses dépend de la connaissance de soi. »",
  ],
};

const KNOWLEDGE_BASE = {
 
  // ── CONCEPTS PARTAGÉS ────────────────────────────────────────────────────
 
  seiryoku: {
    type: 'concept',
    titre: 'Seiryoku Zenyo — 精力善用',
    auteur: 'Jigorō Kanō',
    epoque: 'Japon, 1882',
    oeuvre: 'Fondements du Judo',
    image: '/images/kano.jpg',
    precepteur: `Ce principe n'est pas une technique de combat — c'est une philosophie de l'action. Kanō enseignait que gaspiller sa force contre un adversaire plus puissant est une erreur fondamentale. Accompagner, rediriger, amplifier : voilà le génie du THAI MEZRA.`,
    fiche: {
      concept: 'Utiliser l\'énergie adverse plutôt que de lui résister frontalement.',
      impact: 'Révolutionne les arts martiaux japonais et influence la pensée stratégique mondiale.',
      citation: '« La voie du Judo est d\'abord de comprendre la nature de l\'énergie. »',
      lienSavoir: 'Voir aussi : Wu Wei (Taoïsme), Jita Kyoei',
    },
  },
 
  ma: {
    type: 'concept',
    titre: 'Ma — 間 · Le vide fertile',
    auteur: 'Tradition japonaise',
    epoque: 'Japon, antiquité — présent',
    oeuvre: 'Esthétique japonaise, Bouddhisme Zen',
    image: '/images/ma_concept.jpg',
    precepteur: `Le Ma n'est pas l'absence — c'est la présence du silence. Dans un débat, celui qui remplit chaque instant de mots révèle sa nervosité. Le maître laisse des espaces. Ces espaces font peur à l'adversaire, qui s'y précipite et trébuche.`,
    fiche: {
      concept: 'Espace-temps interstitiel entre deux éléments — ni vide ni plein, mais potentiel pur.',
      impact: 'Central dans l\'architecture japonaise, la musique, la poésie, et les arts martiaux.',
      citation: '« Ce qui n\'est pas dit est parfois plus puissant que ce qui l\'est. »',
      lienSavoir: 'Voir aussi : Wu Wei, Ataraxie',
    },
  },
 
  asabiyya: {
    type: 'concept',
    titre: 'Asabiyya — عصبية · La cohésion sociale',
    auteur: 'Ibn Khaldoun',
    epoque: 'Maghreb, 1377',
    oeuvre: 'Muqaddima (Les Prolégomènes)',
    image: '/images/ibnkhaldoun_book.jpg',
    precepteur: `Ibn Khaldoun a compris avant Weber et Durkheim que les sociétés ne tiennent pas par la force, mais par le lien invisible entre les hommes. Quand vous invoquez l'Asabiyya, vous ne parlez pas pour vous — vous parlez avec l'histoire de tout un peuple derrière vous.`,
    fiche: {
      concept: 'Force de cohésion tribale ou sociale qui permet à un groupe de s\'imposer et de durer.',
      impact: 'Premier modèle sociologique de l\'histoire des civilisations, 400 ans avant la sociologie occidentale.',
      citation: '« La solidarité sociale est le fondement de toute puissance politique. »',
      lienSavoir: 'Voir aussi : Ubuntu, Jita Kyoei',
    },
  },
 
  jitakyoei: {
    type: 'concept',
    titre: 'Jita Kyoei — 自他共栄',
    auteur: 'Jigorō Kanō',
    epoque: 'Japon, 1882',
    oeuvre: 'Fondements du Judo',
    image: '/images/kano_jita.jpg',
    precepteur: `Le deuxième grand principe de Kanō. Là où Seiryoku parle d'efficacité, Jita Kyoei parle de finalité. À quoi sert de gagner si la victoire appauvrit les deux parties ? Le vrai champion élève son adversaire en même temps qu'il le domine.`,
    fiche: {
      concept: 'Prospérité et bien-être mutuels — la victoire doit bénéficier aux deux parties.',
      impact: 'Fonde l\'éthique du Judo comme art de vivre, pas seulement comme sport.',
      citation: '« La véritable victoire n\'est pas sur l\'adversaire, mais avec lui. »',
      lienSavoir: 'Voir aussi : Ubuntu, Asabiyya',
    },
  },
 
  resistance: {
    type: 'concept',
    titre: 'La Résistance — 抵抗',
    auteur: 'Tradition dialectique',
    epoque: 'Universel',
    oeuvre: 'De Spinoza à Gramsci',
    image: '/images/resistance.jpg',
    precepteur: `La résistance n'est pas le refus — c'est l'affirmation d'une limite. Spinoza disait que tout être tend à persévérer dans son être. Cette persistance est une force philosophique fondamentale. Mais attention : s'ancrer trop longtemps rigidifie.`,
    fiche: {
      concept: 'Maintenir sa position face à la pression adverse — conatus, persévérance dans l\'être.',
      impact: 'De Spinoza à Gramsci, la résistance est au cœur de la philosophie politique et de l\'éthique.',
      citation: '« Toute chose s\'efforce de persévérer dans son être. » — Spinoza',
      lienSavoir: 'Voir aussi : Impératif catégorique, Ambiguïté',
    },
  },
 
  wuwei: {
    type: 'concept',
    titre: 'Wu Wei — 無為 · Le non-agir',
    auteur: 'Laozi',
    epoque: 'Chine, ~600 av. J.-C.',
    oeuvre: 'Tao Te Ching (道德經)',
    image: '/images/laozi.jpg',
    precepteur: `Le Tao Te Ching enseigne que l'action forcée est toujours moins efficace que l'action naturelle. L'eau n'attaque pas le rocher — elle le contourne pendant des millénaires jusqu'à l'effacer. Dans ce duel, le Wu Wei est votre armure invisible.`,
    fiche: {
      concept: 'Agir en accord avec la nature des choses — ne pas forcer, laisser advenir.',
      impact: 'Fondement du Taoïsme, influence le bouddhisme Chan (Zen), les arts martiaux, la pensée stratégique.',
      citation: '« Le sage n\'agit pas, et pourtant rien ne reste à faire. » — Laozi',
      lienSavoir: 'Voir aussi : Ma, Seiryoku Zenyo',
    },
  },
 
  philosophe: {
    type: 'concept',
    titre: 'Le Philosophe-Roi — 哲人王',
    auteur: 'Platon',
    epoque: 'Athènes, ~380 av. J.-C.',
    oeuvre: 'La République (Πολιτεία)',
    image: '/images/platon_rep.jpg',
    precepteur: `Dans le Livre V de La République, Platon imagine une cité gouvernée non par les plus riches ni les plus forts, mais par ceux qui ont contemplé le Bien. Ce concept est une arme redoutable : plus vous avez mobilisé de sagesse dans ce duel, plus votre frappe devient dévastatrice.`,
    fiche: {
      concept: 'Seul celui qui connaît le Bien peut gouverner justement — la philosophie comme condition du pouvoir.',
      impact: 'Fonde la tradition de l\'intellectuel engagé, de l\'expert politique, du leadership éclairé.',
      citation: '« Les cités ne cesseront d\'être malheureuses que quand les philosophes seront rois. »',
      lienSavoir: 'Voir aussi : Impératif catégorique, Canon de raison',
    },
  },
 
  ubuntu: {
    type: 'concept',
    titre: 'Ubuntu — Umuntu ngumuntu ngabantu',
    auteur: 'Philosophie Bantoue',
    epoque: 'Afrique subsaharienne, tradition orale',
    oeuvre: 'Pensée Ubuntu, Nelson Mandela',
    image: '/images/ubuntu_africa.jpg',
    precepteur: `"Je suis parce que nous sommes." Cette phrase concentre une vision du monde radicalement différente de l'individualisme occidental. L'Ubuntu ne dit pas que l'individu n'existe pas — il dit qu'il n'existe que dans la relation. C'est une philosophie de la connexion.`,
    fiche: {
      concept: 'L\'humanité d\'une personne est indissociable de l\'humanité des autres — être humain est un acte collectif.',
      impact: 'Fondement de la réconciliation en Afrique du Sud post-apartheid, influence la pensée communautaire mondiale.',
      citation: '« Un homme n\'est un homme qu\'à travers les autres hommes. »',
      lienSavoir: 'Voir aussi : Jita Kyoei, Asabiyya',
    },
  },
 
  kintsugi: {
    type: 'concept',
    titre: 'Kintsugi — 金継ぎ · L\'or des cicatrices',
    auteur: 'Tradition japonaise',
    epoque: 'Japon, XVe siècle',
    oeuvre: 'Esthétique Wabi-Sabi',
    image: '/images/kintsugi_bowl.jpg',
    precepteur: `Quand un bol de céramique se brisait, les maîtres japonais ne le jetaient pas. Ils le réparaient avec de la laque dorée, rendant visible la fracture. La blessure devenait ornement. Dans ce duel, chaque pression que vous avez subie est transformée en énergie pure.`,
    fiche: {
      concept: 'Réparer ce qui est brisé en rendant visible la réparation — la fracture comme source de beauté.',
      impact: 'Métaphore mondiale de la résilience, de l\'impermanence assumée, de la beauté du vécu.',
      citation: '« Ce qui a été brisé et réparé est plus précieux qu\'avant. »',
      lienSavoir: 'Voir aussi : Ataraxie, Résistance',
    },
  },
 
  entropie: {
    type: 'concept',
    titre: 'Entropie Symbolique — 熵',
    auteur: 'Rudolf Clausius → Jean Baudrillard',
    epoque: 'XIXe-XXe siècle',
    oeuvre: 'Thermodynamique → Simulacres et Simulation',
    image: '/images/entropy.jpg',
    precepteur: `L'entropie physique mesure le désordre d'un système. Baudrillard l'a transposée au langage : nos systèmes de signes se désintègrent sous l'excès de sens. Dans ce duel, provoquer l'entropie chez l'adversaire, c'est saturer son discours jusqu'à le rendre incohérent.`,
    fiche: {
      concept: 'Tendance naturelle de tout système vers le désordre et la dissolution de l\'information.',
      impact: 'De la thermodynamique à la philosophie du langage — l\'entropie devient outil de déconstruction.',
      citation: '« Le désordre est la forme la plus stable de l\'univers. » — Clausius',
      lienSavoir: 'Voir aussi : Synthèse hégélienne, Hypertexte',
    },
  },
 
  hegel: {
    type: 'concept',
    titre: 'Synthèse Hégélienne — 揚棄 (Aufhebung)',
    auteur: 'Georg Wilhelm Friedrich Hegel',
    epoque: 'Allemagne, 1807',
    oeuvre: 'Phénoménologie de l\'Esprit',
    image: '/images/hegel_book.jpg',
    precepteur: `Hegel a découvert que la vérité n'est pas statique — elle se construit dans le mouvement. Thèse, antithèse, synthèse. L'Aufhebung ne détruit pas l'opposition : elle la dépasse en la conservant à un niveau supérieur. Votre coup renvoie l'énergie adverse transformée.`,
    fiche: {
      concept: 'La dialectique : toute thèse génère son antithèse, et leur conflit produit une synthèse supérieure.',
      impact: 'Révolutionne la philosophie, inspire Marx, Sartre, et toute la pensée critique moderne.',
      citation: '« La vérité est dans le tout. Le tout est le devenir. »',
      lienSavoir: 'Voir aussi : Entropie, Hypertexte',
    },
  },
 
  hypertexte: {
    type: 'concept',
    titre: 'Hypertexte — 超文本',
    auteur: 'Ted Nelson → Gilles Deleuze',
    epoque: '1965 → 1980',
    oeuvre: 'Project Xanadu → Mille Plateaux',
    image: '/images/hypertext.jpg',
    precepteur: `Ted Nelson a inventé le mot "hypertexte" en 1965 pour désigner un texte non-linéaire, infini de connexions. Deleuze et Guattari le théorisent comme rhizome : aucun centre, aucune hiérarchie, que des connexions proliférantes. Plus vous utilisez ce concept, plus votre réseau se densifie.`,
    fiche: {
      concept: 'Réseau de liens entre savoirs — chaque connexion amplifie la puissance de l\'ensemble.',
      impact: 'Fonde le World Wide Web, la pensée non-linéaire, et la critique de la hiérarchie du savoir.',
      citation: '« Un texte n\'a pas de commencement ni de fin, seulement un milieu. » — Deleuze',
      lienSavoir: 'Voir aussi : Synthèse hégélienne, Canon de raison',
    },
  },
 
  // ── CONCEPTS PROPRES ─────────────────────────────────────────────────────
 
  imperatif: {
    type: 'concept',
    titre: 'L\'Impératif Catégorique — 定言命法',
    auteur: 'Emmanuel Kant',
    epoque: 'Königsberg, 1785',
    oeuvre: 'Fondements de la métaphysique des mœurs',
    image: '/images/kant_book.jpg',
    precepteur: `Kant cherchait une loi morale absolue, valable pour tout être rationnel en tout temps. Il l'a trouvée : "Agis uniquement selon la maxime qui te permet de vouloir en même temps qu'elle devienne une loi universelle." Simple. Implacable. Irrésistible.`,
    fiche: {
      concept: 'Principe moral universel et inconditionnel — une action n\'est juste que si sa maxime peut être universalisée.',
      impact: 'Fonde l\'éthique déontologique moderne, influence le droit international et les droits humains.',
      citation: '« Agis de telle sorte que tu traites l\'humanité toujours comme une fin, jamais comme un moyen. »',
      lienSavoir: 'Voir aussi : Autonomie (Kant), Philosophe-Roi',
    },
  },
 
  hedone: {
    type: 'concept',
    titre: 'Hedone Sophron — 快楽徳 · Le plaisir éclairé',
    auteur: 'Arété de Cyrène (école cyrénaïque)',
    epoque: 'Cyrène, ~400 av. J.-C.',
    oeuvre: 'École cyrénaïque — tradition orale',
    image: '/images/cyrene.jpg',
    precepteur: `Arété de Cyrène, fille d'Aristippe, a développé une philosophie du plaisir rare : non pas la débauche, mais le plaisir éclairé par la sagesse. La restauration d'énergie que ce concept procure n'est pas une faiblesse — c'est la philosophe qui se ressource dans sa propre vérité.`,
    fiche: {
      concept: 'Le plaisir comme bien suprême — mais un plaisir guidé par la raison et la vertu.',
      impact: 'Préfigure l\'épicurisme, questionne l\'ascétisme, fonde une philosophie du corps et de la joie.',
      citation: '« Le plaisir est le début et la fin de la vie bienheureuse. » — Épicure (héritier)',
      lienSavoir: 'Voir aussi : Eudaimonia, Ataraxie',
    },
  },
 
  enracinement: {
    type: 'concept',
    titre: 'L\'Enracinement — 根付',
    auteur: 'Simone Weil',
    epoque: 'Londres, 1943',
    oeuvre: 'L\'Enracinement',
    image: '/images/weil_book.jpg',
    precepteur: `Weil écrit L'Enracinement comme un testament pour la reconstruction de la France après la guerre. Elle y défend que le besoin le plus ignoré de l'âme humaine est d'avoir des racines — une participation réelle à une collectivité vivante. Frapper avec ce concept, c'est rappeler que toute pensée déracinée est une pensée morte.`,
    fiche: {
      concept: 'L\'âme a besoin de racines multiples : familiales, professionnelles, géographiques, spirituelles. Sans elles, l\'homme se déracine et se déshumanise.',
      impact: 'Inspire les politiques de reconstruction après-guerre, influence l\'écologie politique, la critique du productivisme et la défense des communautés locales.',
      citation: '« L\'enracinement est peut-être le besoin le plus important et le plus méconnu de l\'âme humaine. » — Simone Weil',
      lienSavoir: 'Voir aussi : Attention, Pesanteur',
    },
  },
 
  qanun: {
    type: 'concept',
    titre: 'Canon de Raison — 知性光',
    auteur: 'Ibn Sīnā (Avicenne)',
    epoque: 'Perse, 1025',
    oeuvre: 'Al-Qanun fi al-Tibb (Le Canon de médecine)',
    image: '/images/ibnsina_canon.jpg',
    precepteur: `Le Qanun d'Ibn Sīnā est resté le manuel médical de référence en Europe et dans le monde arabe pendant 600 ans. Mais au-delà de la médecine, c'est une méthode : rassembler tout le savoir disponible, le structurer, et en extraire la loi universelle. L'intellect comme lumière.`,
    fiche: {
      concept: 'L\'intellect agent — faculté supérieure qui illumine la raison particulière et accède à l\'universel.',
      impact: 'Synthèse de la philosophie grecque et islamique, transmis à l\'Europe médiévale via les traductions.',
      citation: '« L\'intellect est la lumière par laquelle l\'âme voit les vérités éternelles. »',
      lienSavoir: 'Voir aussi : Philosophe-Roi, Hypertexte',
    },
  },
 
  devoir: {
    type: 'concept',
    titre: 'Le Devoir Pur — 義務',
    auteur: 'Emmanuel Kant',
    epoque: 'Königsberg, 1785',
    oeuvre: 'Fondements de la métaphysique des mœurs',
    image: '/images/kant_duty.jpg',
    precepteur: `Pour Kant, une action n'a de valeur morale que si elle est accomplie par devoir — non par peur, non par intérêt, non par sentiment. Le devoir pur est froid, mais c'est précisément sa froideur qui le rend inattaquable. Votre adversaire ne peut pas critiquer ce qui transcende toute émotion.`,
    fiche: {
      concept: 'Agir par devoir pur, sans inclination sensible — la seule base d\'une action véritablement morale.',
      impact: 'Distingue la morale de la psychologie, fonde l\'éthique formelle.',
      citation: '« Devoir ! Mot sublime et grand. »',
      lienSavoir: 'Voir aussi : Impératif catégorique, Autonomie',
    },
  },
 
  autonomie: {
    type: 'concept',
    titre: 'Autonomie — 自律',
    auteur: 'Emmanuel Kant',
    epoque: 'Königsberg, 1788',
    oeuvre: 'Critique de la raison pratique',
    image: '/images/kant_autonomy.jpg',
    precepteur: `L'autonomie kantienne n'est pas "faire ce qu'on veut" — c'est se donner sa propre loi en tant qu'être rationnel. L'hétéronomie, c'est obéir aux désirs, aux pressions sociales, aux menaces. L'autonomie, c'est être la source de sa propre loi. Ce concept restaure votre force en la purifiant.`,
    fiche: {
      concept: 'La volonté qui se donne sa propre loi — opposée à l\'hétéronomie (obéir à une loi externe).',
      impact: 'Révolutionne la conception de la liberté, influence la psychologie humaniste et la bioéthique.',
      citation: '« La liberté n\'est pas l\'absence de contrainte, c\'est la contrainte que je me donne. »',
      lienSavoir: 'Voir aussi : Devoir pur, Impératif catégorique',
    },
  },
 
  eudaimonia: {
    type: 'concept',
    titre: 'Eudaimonia — 幸福 · La vie bonne',
    auteur: 'Aristote',
    epoque: 'Athènes, ~335 av. J.-C.',
    oeuvre: 'Éthique à Nicomaque',
    image: '/images/aristotle_ethics.jpg',
    precepteur: `Aristote distingue le plaisir passager (hedone) du bonheur profond (eudaimonia). L'eudaimonia est une activité — pas un état. Elle naît de l'exercise de nos facultés les meilleures. Dans ce duel, chaque coup bien argumenté vous rapproche de l'eudaimonia.`,
    fiche: {
      concept: 'Bonheur comme activité de l\'âme conforme à la vertu — pas un sentiment mais une pratique.',
      impact: 'Fonde l\'éthique des vertus, influence la psychologie positive moderne.',
      citation: '« Le bonheur est une activité de l\'âme en accord avec la vertu parfaite. »',
      lienSavoir: 'Voir aussi : Ataraxie, Hedone',
    },
  },
 
  ataraxie: {
    type: 'concept',
    titre: 'Ataraxie — 平静 · L\'imperturbabilité',
    auteur: 'Épicure / Pyrrhon',
    epoque: 'Grèce, IVe-IIIe s. av. J.-C.',
    oeuvre: 'Lettre à Ménécée / Scepticisme pyrrhonien',
    image: '/images/epicurus.jpg',
    precepteur: `L'ataraxie est l'état de celui que rien ne trouble. Ni la peur de la mort, ni le désir insatiable, ni les attaques adverses. Pyrrhon l'atteignait par le scepticisme radical : en suspendant tout jugement, on échappe à toute perturbation. Votre dégrip absolu naît de ce détachement parfait.`,
    fiche: {
      concept: 'Tranquillité de l\'âme obtenue par la suspension du jugement ou la satisfaction des désirs naturels.',
      impact: 'Idéal de l\'épicurisme et du scepticisme — influence le stoïcisme et le bouddhisme.',
      citation: '« Ne crains ni les dieux, ni la mort, ni la douleur, ni la vie sans plaisir. »',
      lienSavoir: 'Voir aussi : Wu Wei, Ma',
    },
  },
 
  facticite: {
    type: 'concept',
    titre: 'Facticité — 事実性',
    auteur: 'Simone de Weil / Jean-Paul Sartre',
    epoque: 'Paris, 1943-1949',
    oeuvre: 'L\'Être et le Néant / Le Deuxième Sexe',
    image: '/images/weil_sartre.jpg',
    precepteur: `La facticité, c'est tout ce que nous n'avons pas choisi : notre corps, notre époque, notre classe sociale, notre genre. Sartre et Weil ne disent pas qu'on peut l'ignorer — ils disent qu'on peut la transcender par le projet. Ce concept exploite les contraintes de l'adversaire comme levier.`,
    fiche: {
      concept: 'Ensemble des données factuelles de notre existence que nous n\'avons pas choisies mais dont nous sommes responsables.',
      impact: 'Fonde l\'analyse existentielle de la condition humaine et la critique féministe des déterminismes.',
      citation: '« On ne naît pas femme : on le devient. »',
      lienSavoir: 'Voir aussi : Acte fondateur, Ambiguïté',
    },
  },
 
  ambiguite: {
    type: 'concept',
    titre: 'L\'Ambiguïté — 曖昧',
    auteur: 'Simone de Weil',
    epoque: 'Paris, 1947',
    oeuvre: 'Pour une morale de l\'ambiguïté',
    image: '/images/weil_ambig.jpg',
    precepteur: `Là où Sartre voit l'angoisse de la liberté, Weil assume l'ambiguïté. L'homme est à la fois liberté et facticité, conscience et corps, individu et être social. Refuser cette ambiguïté, c'est la mauvaise foi. L'assumer, c'est la seule honnêteté possible — et un bouclier redoutable.`,
    fiche: {
      concept: 'La condition humaine est fondamentalement ambiguë — libre et déterminé à la fois, et c\'est à assumer.',
      impact: 'Complète l\'existentialisme par une éthique concrète du compromis et de l\'engagement situé.',
      citation: '« L\'ambiguïté n\'est pas un défaut de la condition humaine — c\'est sa vérité. »',
      lienSavoir: 'Voir aussi : Facticité, Acte fondateur',
    },
  },
 
  intellect: {
    type: 'concept',
    titre: 'Intellect Agent — 能動知性',
    auteur: 'Ibn Sīnā',
    epoque: 'Perse, ~1000',
    oeuvre: 'Kitab al-Shifa (Le Livre de la Guérison)',
    image: '/images/ibnsina_shifa.jpg',
    precepteur: `Ibn Sīnā reprend Aristote et Al-Farabi : l'intellect agent est une intelligence cosmique séparée qui illumine nos intellects particuliers. Quand nous comprenons quelque chose, c'est cette lumière universelle qui se réfléchit en nous. Plus vous avez traversé d'époques dans ce duel, plus cette lumière brille.`,
    fiche: {
      concept: 'Intelligence universelle active qui rend possible la connaissance humaine en actualisant les intelligibles.',
      impact: 'Pont entre philosophie grecque et islamique, transmis au scolastique latin par Albert le Grand.',
      citation: '« L\'intellect humain est comme un miroir qui réfléchit la lumière de l\'intellect agent. »',
      lienSavoir: 'Voir aussi : Canon de raison, Philosophe-Roi',
    },
  },
 
  avicenne_med: {
    type: 'concept',
    titre: 'Al-Qanun — 法典 · Le Canon',
    auteur: 'Ibn Sīnā',
    epoque: 'Perse, 1025',
    oeuvre: 'Al-Qanun fi al-Tibb',
    image: '/images/qanun_manuscript.jpg',
    precepteur: `Le Qanun est une encyclopédie médicale de 14 volumes restée en usage à l'Université de Montpellier jusqu'au XVIIe siècle. Ibn Sīnā y codifie tout le savoir médical de son temps, mais aussi une philosophie de la santé comme équilibre. Ce coup restaure votre équilibre intérieur.`,
    fiche: {
      concept: 'Codification systématique du savoir médical — la santé comme harmonie des humeurs et de l\'âme.',
      impact: 'Manuel médical standard en Europe et dans le monde islamique pendant plus de 600 ans.',
      citation: '« La santé est l\'équilibre des forces, la maladie leur déséquilibre. »',
      lienSavoir: 'Voir aussi : Intellect agent, Canon de raison',
    },
  },
 
  ishara: {
    type: 'concept',
    titre: 'Ishârât — 指示 · Signes et remarques',
    auteur: 'Ibn Sīnā',
    epoque: 'Perse, ~1020',
    oeuvre: 'Kitab al-Isharat wal-Tanbihat',
    image: '/images/ibnsina_ishara.jpg',
    precepteur: `Le dernier grand livre d'Ibn Sīnā est une méditation sur les limites du langage philosophique. Certaines vérités ne se démontrent pas — elles se montrent par des signes (ishara) et des remarques (tanbihat). Ce coup ne réfute pas l'adversaire — il lui renvoie le miroir de sa propre pensée.`,
    fiche: {
      concept: 'Les vérités ultimes se montrent plutôt qu\'elles ne se démontrent — le langage comme signe vers l\'ineffable.',
      impact: 'Préfigure la philosophie analytique du langage et la mystique islamique (soufisme).',
      citation: '« Là où les mots s\'arrêtent, les signes continuent. »',
      lienSavoir: 'Voir aussi : Ma, Wu Wei',
    },
  },
 
  // ── CONCEPTS ULTIMES ─────────────────────────────────────────────────────
 
  allegorie: {
    type: 'concept',
    titre: 'Allégorie de la Caverne — 洞窟神話',
    auteur: 'Platon',
    epoque: 'Athènes, ~380 av. J.-C.',
    oeuvre: 'La République, Livre VII',
    image: '/images/platon_cave.jpg',
    precepteur: `Des prisonniers enchaînés dans une caverne ne voient que des ombres projetées sur le mur. Ils prennent ces ombres pour la réalité. Le philosophe, lui, se retourne et voit la lumière du soleil. Ce coup, c'est vous qui retournez votre adversaire vers l'aveuglante vérité.`,
    fiche: {
      concept: 'Le monde sensible n\'est que l\'ombre du monde des Idées — seul le philosophe voit la vérité.',
      impact: 'Métaphore fondatrice de la philosophie occidentale, de la théologie et des théories de la connaissance.',
      citation: '« Le soleil est la cause de tout ce que nous voyons. »',
      lienSavoir: 'Voir aussi : Philosophe-Roi, Intellect agent',
    },
  },
 
  algorithme_absolu: {
    type: 'concept',
    titre: 'Algorithme Absolu — 完全算法',
    auteur: 'Muhammad Al-Khwarizmi',
    epoque: 'Bagdad, ~820',
    oeuvre: 'Al-Kitab al-mukhtasar fi hisab al-jabr (Algèbre)',
    image: '/images/alkhwarizmi_book.jpg',
    precepteur: `Al-Khwarizmi a donné au monde deux mots : "algorithme" (de son nom) et "algèbre" (al-jabr, la réduction). Son génie était de voir que tous les problèmes pouvaient être réduits à des procédures claires et répétables. Aucun blocage ne résiste à une méthode parfaite.`,
    fiche: {
      concept: 'Procédure finie et déterminée pour résoudre une classe de problèmes — le fondement de l\'informatique.',
      impact: 'Inventeur de l\'algèbre, père des algorithmes — sa pensée structure tout calcul moderne.',
      citation: '« Tout problème a une solution si l\'on en connaît la méthode. »',
      lienSavoir: 'Voir aussi : Hypertexte, Intellect agent',
    },
  },
 
  cycle_eternel: {
    type: 'concept',
    titre: 'Cycle Éternel — 永遠循環',
    auteur: 'Ibn Khaldoun',
    epoque: 'Tunis, 1377',
    oeuvre: 'Muqaddima — Introduction à l\'Histoire universelle',
    image: '/images/ibnkhaldoun_cycle.jpg',
    precepteur: `Ibn Khaldoun a découvert que les civilisations suivent un cycle implacable : naissance dans la cohésion tribale, montée en puissance, luxe corrupteur, déclin, effondrement. Puis un nouveau cycle. Ce coup applique cette loi à votre adversaire : sa puissance même porte les germes de sa chute.`,
    fiche: {
      concept: 'Les civilisations naissent de l\'asabiyya, grandissent, se corrompent et meurent — cycle inévitable.',
      impact: 'Premier modèle de philosophie de l\'histoire, préfigure Vico, Spengler et Toynbee.',
      citation: '« Les dynasties ont une durée de vie naturelle comme les individus. »',
      lienSavoir: 'Voir aussi : Asabiyya, Entropie',
    },
  },
 
  sophia_ultimate: {
    type: 'concept',
    titre: 'Philosophia Perennis — 永遠智慧',
    auteur: 'Agostino Steuco → Leibniz → Aldous Huxley',
    epoque: 'Renaissance → XIXe s. → 1945',
    oeuvre: 'La Philosophie pérenne (Huxley, 1945)',
    image: '/images/sophia_book.jpg',
    precepteur: `La Philosophia Perennis est l'hypothèse que toutes les grandes traditions spirituelles et philosophiques partagent un noyau de vérité commun. Leibniz la théorise, Huxley la synthétise. Ce coup transcende toutes les traditions — c'est la sagesse immémoriale qui frappe.`,
    fiche: {
      concept: 'Vérité universelle et intemporelle partagée par toutes les grandes traditions de sagesse.',
      impact: 'Fonde le dialogue interreligieux, la philosophie comparée et la psychologie transpersonnelle.',
      citation: '« Il existe une philosophie divine qui est l\'âme de toutes les sagesses humaines. »',
      lienSavoir: 'Voir aussi : Intellect agent, Philosophe-Roi',
    },
  },
 
};
 
const KNOWLEDGE_PUNCHLINES = {
 
  // Kant
  k_p1: {
    type: 'punchline',
    titre: 'L\'argument empirique et ses limites',
    auteur: 'Emmanuel Kant',
    contexte: 'Kant contre l\'empirisme de Hume',
    image: '/images/kant_hume.jpg',
    precepteur: `Kant a été "réveillé de son sommeil dogmatique" par Hume, qui montrait que toute connaissance vient de l'expérience. Kant répond : oui, mais la forme de cette expérience — l'espace, le temps, la causalité — est en nous a priori. L'empirisme reste prisonnier des phénomènes.`,
    fiche: {
      concept: 'Les arguments tirés de l\'expérience ne peuvent fonder de vérités universelles et nécessaires.',
      auteurBio: 'Kant (1724-1804), professeur à Königsberg, n\'a jamais quitté sa ville natale mais a révolutionné la philosophie mondiale.',
      anecdote: 'Les habitants de Königsberg réglaient leur montre sur ses promenades quotidiennes — sauf deux fois : quand il lut Rousseau et quand il apprit la Révolution française.',
    },
  },
 
  k_p2: {
    type: 'punchline',
    titre: 'L\'impératif face au sophisme',
    auteur: 'Emmanuel Kant',
    contexte: 'La loi morale comme rempart',
    image: '/images/kant_moral.jpg',
    precepteur: `Le sophisme cherche à convaincre par tous les moyens, y compris les mauvais. L'impératif catégorique est le test universel : peux-tu vouloir que ta manœuvre rhétorique devienne loi pour tous ? Si non, elle est illégitime. Ce principe ne laisse aucune issue.`,
    fiche: {
      concept: 'L\'universalisabilité comme test de la légitimité d\'un argument.',
      auteurBio: 'Kant développe sa morale dans trois œuvres majeures : Fondements (1785), Critique de la raison pratique (1788), Métaphysique des mœurs (1797).',
      anecdote: 'Kant refusait de mentir même pour protéger un ami d\'un assassin — position controversée qui a fait couler beaucoup d\'encre.',
    },
  },
 
  // Arété
  a_p1: {
    type: 'punchline',
    titre: 'Plaisir et servitude',
    auteur: 'Arété de Cyrène',
    contexte: 'L\'hédonisme éclairé contre la débauche',
    image: '/images/arete_cyrene.jpg',
    precepteur: `Arété n'était pas épicurienne avant Épicure. Son hédonisme était plus radical : le plaisir est le seul bien réel, mais il exige la maîtrise de soi. Celui qui ne peut résister à ses désirs n'est pas libre — il est esclave. La vertu n'est pas ennemie du plaisir, elle en est la condition.`,
    fiche: {
      concept: 'Le plaisir non guidé par la raison est une forme d\'esclavage.',
      auteurBio: 'Arété de Cyrène (fl. ~400 av. J.-C.), fille d\'Aristippe, dirigea l\'école cyrénaïque. Rare femme philosophe mentionnée dans les sources antiques.',
      anecdote: 'Diogène Laërce rapporte qu\'elle enseigna la philosophie à plus de 110 étudiants, dont son propre fils Aristippe le Jeune.',
    },
  },
 
  // Weil
  b_p1: {
    type: 'punchline',
    titre: 'L\'attention qui efface l\'illusion',
    auteur: 'Simone Weil',
    contexte: 'L\'attention comme arme philosophique',
    image: '/images/simoneweil_attention.jpg',
    precepteur: `Pour Weil, l'attention est une forme de générosité intellectuelle qui détruit les faux-semblants. Quand vous dites "L'attention détruit ce qui n'est pas réel", vous appliquez ce principe : fixer une idée avec une intensité pure la vide de son mensonge.`,
    fiche: {
      concept: 'L\'attention tournée vers le réel dissout les arguments inconsistants.',
      auteurBio: 'Simone Weil (1909-1943), philosophe française, mystique, ouvrière, résistante.',
      anecdote: 'Weil a travaillé en usine pour comprendre la condition ouvrière, expérience dont elle a tiré une critique radicale de l\'oppression.',
    },
  },
 
  // Ibn Sīnā
  i_p1: {
    type: 'punchline',
    titre: 'L\'intellect contre l\'obscurité',
    auteur: 'Ibn Sīnā',
    contexte: 'La philosophie islamique contre le dogmatisme',
    image: '/images/ibnsina_young.jpg',
    precepteur: `Ibn Sīnā a vécu dans un monde de cour royale, de tensions politiques, d'emprisonnements. Il écrivait en prison, sur des morceaux de cuir. Sa philosophie est une réponse concrète à l'obscurantisme : la raison illumine là où le dogme aveugle. Ce coup l'incarne.`,
    fiche: {
      concept: 'La raison philosophique transcende les limites culturelles et religieuses pour atteindre l\'universel.',
      auteurBio: 'Ibn Sīnā (980-1037), médecin, philosophe, scientifique. A mémorisé le Coran à 10 ans, maîtrisait la médecine à 16 ans.',
      anecdote: 'Il aurait écrit une grande partie du Qanun en voyageant à cheval, dictant ses pensées à un secrétaire.',
    },
  },
  
  pl_p1: { type:'punchline', titre:'L\'ombre et l\'idée', auteur:'Platon', contexte:'L\'allégorie de la caverne en débat',
  image:'/images/platon_debate.jpg',
  precepteur:`Platon distinguait le monde sensible — les ombres — du monde des Idées. Dans un débat, celui qui ne voit que les faits immédiats reste prisonnier de la caverne. Le philosophe, lui, voit la structure invisible qui les produit.`,
  fiche:{ concept:'L\'apparence n\'est que l\'ombre de la réalité intelligible.', auteurBio:'Platon (~428-348 av. J.-C.), fondateur de l\'Académie, élève de Socrate, maître d\'Aristote.', anecdote:'Platon signifie "large épaules" — il aurait été champion de lutte avant de se consacrer à la philosophie.' }
  },
  
  ak_p1: { type:'punchline', titre:'La méthode contre l\'intuition', auteur:'Al-Khwarizmi', contexte:'L\'algèbre comme dissolution du flou',
  image:'/images/alkhwarizmi_method.jpg',
  precepteur:`Al-Khwarizmi a montré que l'intuition sans méthode produit des erreurs reproductibles. L'algèbre — al-jabr, la réduction — transforme tout problème complexe en séquence d'opérations claires. Ce qui résiste à la réduction n'est pas profond : c'est obscur.`,
  fiche:{ concept:'Toute question résoluble a une procédure. Toute procédure peut être apprise.', auteurBio:'Al-Khwarizmi (780-850), mathématicien perse de la Maison de la Sagesse de Bagdad.', anecdote:'Le mot "algorithme" vient de la latinisation de son nom : Algoritmi.' }
  },
  
  ik_p1: { type:'punchline', titre:'Les cycles contre les certitudes', auteur:'Ibn Khaldoun', contexte:'La Muqaddima comme grille de lecture',
  image:'/images/ibnkhaldoun_debate.jpg',
  precepteur:`Ibn Khaldoun a observé des dizaines de dynasties naître et mourir. Il a compris que les arguments politiques et moraux qui paraissent solides à un moment donné sont toujours les mêmes arguments que les dynasties finissantes ont tenus. La certitude est un signe de déclin.`,
  fiche:{ concept:'Toute position assurée d\'elle-même porte en elle le germe de sa propre réfutation historique.', auteurBio:'Ibn Khaldoun (1332-1406), historien et sociologue maghrébin, auteur de la Muqaddima.', anecdote:'Il a rencontré Tamerlan en 1401 et a passé plusieurs semaines à discuter avec lui — survivant là où des armées entières avaient péri.' }
  },
  
  so_p1: { type:'punchline', titre:'La convergence des sagesses', auteur:'Sophia Perennis', contexte:'La philosophie pérenne en acte',
  image:'/images/sophia_debate.jpg',
  precepteur:`La Philosophia Perennis n'est pas une philosophie parmi d'autres. C'est l'hypothèse que toutes les grandes traditions de sagesse formulent la même vérité dans des langages différents. Quand Sophia parle, c'est Kant, Weil, Ibn Sīnā et Platon qui parlent en même temps — et ils s'accordent.`,
  fiche:{ concept:'Il existe un noyau de vérité commun à toutes les grandes traditions de sagesse humaine.', auteurBio:'Figure synthétique représentant la Philosophia Perennis — concept développé par Leibniz et synthétisé par Huxley en 1945.', anecdote:'Aldous Huxley a écrit La Philosophie Pérenne en 1945, après des années de recherche comparative entre mysticisme chrétien, soufisme, bouddhisme et védanta.' }
  },
 
};

const CHAR_THEME_MAP = {
  kant: {
    resonance: [1, 6],        // Liberté ou Sécurité, Peut-on être libre dans une structure
    friction:  [3, 7],        // Transmettre sans imposer, La mémoire fait-elle l'identité
    themesExclusifs: [],
  },
  arete: {
    resonance: [5, 6],        // L'épuisement comme outil de contrôle, Libre dans une structure
    friction:  [2, 4],        // IA, Asabiyya moderne
    themesExclusifs: [],
  },
  weil: {
    resonance: [1, 3, 5],     // Liberté/Sécurité, Transmettre, Épuisement/contrôle
    friction:  [7],           // La mémoire fait-elle l'identité
    themesExclusifs: [],
  },
  ibnsina: {
    resonance: [2, 7],        // IA remplace la pensée, Mémoire et identité
    friction:  [1],           // Liberté ou Sécurité (trop politique pour lui)
    themesExclusifs: [],
  },
  platon: {
    resonance: [6, 7],
    friction:  [2],
    themesExclusifs: [],
  },
  alkhwarizmi: {
    resonance: [2, 3],
    friction:  [5],
    themesExclusifs: [],
  },
  ibnkhaldoun: {
    resonance: [4, 1],
    friction:  [3],
    themesExclusifs: [],
  },
  sophia: {
    resonance: [1, 2, 3, 4, 5, 6, 7], // Sophia n'a pas de friction
    friction:  [],
    themesExclusifs: [],
  },
};

const THEME_DECK_CONSTRAINTS = {
  1: { // Liberté ou Sécurité
    weil: [],       // Weil est "chez elle", rien à retirer
    kant:     ['ubuntu'],     // Kant sur ce thème : trop universaliste pour l'ubuntu communautaire
    ibnsina:  ['entropie', 'resistance'], // Ibn Sīnā évite le conflit direct sur ce thème
  },
  2: { // L'IA remplace-t-elle la pensée
    kant:     ['asabiyya'],   // Pas de cohésion tribale dans la pensée kantienne de la raison
    arete:    ['hegel', 'entropie'],
    weil: ['philosophe'],
  },
  3: { // Transmettre sans imposer
    kant:     ['entropie'],   // Kant transmet par la structure, pas le chaos
    ibnsina:  ['resistance'], // Ibn Sīnā transmet par l'ouverture
    ibnkhaldoun: ['wuwei'],
  },
  4: { // L'asabiyya moderne est-elle possible
    kant:     ['ubuntu', 'asabiyya'], // Kant est mal à l'aise avec la cohésion communautaire
    arete:    ['resistance'],
  },
  5: { // L'épuisement comme outil de contrôle
    kant:     ['jitakyoei'],
    alkhwarizmi: ['ubuntu'],
  },
  6: { // Peut-on être libre dans une structure
    arete:    ['entropie'],
    ibnsina:  ['resistance'],
  },
  7: { // La mémoire fait-elle l'identité
    weil: ['asabiyya'],
    alkhwarizmi: ['ubuntu', 'jitakyoei'],
  },
};

const THEME_SUBTHEMES = {
  1: { text: '…mais pour qui, exactement ?',          sophiaLine: 'La question cachée surgit : la liberté de qui, la sécurité de qui ?' },
  2: { text: '…ou sommes-nous déjà remplacés ?',      sophiaLine: 'Le fragment tremble. L\'IA ne menace pas l\'avenir — elle a peut-être déjà effacé le présent.' },
  3: { text: '…ou imposer sans transmettre ?',        sophiaLine: 'Toute transmission porte une violence silencieuse. Le fragment doit en rendre compte.' },
  4: { text: '…dans un monde de l\'individu roi ?',   sophiaLine: 'L\'asabiyya ne peut naître que d\'une blessure partagée. Quelle est la vôtre ?' },
  5: { text: '…quand l\'épuisement devient désir ?',  sophiaLine: 'Le contrôle le plus redoutable est celui que l\'on ne reconnaît plus comme tel.' },
  6: { text: '…ou la structure est-elle la liberté ?',sophiaLine: 'Certains fragments ont cru que la règle était la cage. D\'autres qu\'elle était le sol.' },
  7: { text: '…ou la mémoire nous invente-t-elle ?',  sophiaLine: 'Ce fragment risque de disparaître précisément parce qu\'il a mal compris sa propre mémoire.' },
};

const THEME_BONUS_PUNCHLINES = {
  'kant_1':      { id:'kt_lib', texte:'La liberté sans loi morale n\'est que chaos — et le chaos profite toujours aux plus forts.', drain:38, cost:20, color:'#a5f3c3', gripAdd:-8 },
  'kant_2':      { id:'kt_ia',  texte:'L\'algorithme obéit à des maximes, jamais à l\'impératif catégorique. La différence est absolue.', drain:42, cost:22, color:'#a5f3c3', gripAdd:0 },
  'weil_1':  { id:'wv_lib', texte:'La liberté des uns n\'est réelle que si elle ne repose pas sur l\'oppression des autres.', drain:40, cost:18, color:'#a8a29e', gripAdd:-5 },
  'weil_5':  { id:'wv_ep',  texte:'L\'épuisement n\'est pas une fatalité — c\'est un dispositif politique. Nommer cela, c\'est déjà résister.', drain:44, cost:24, color:'#a8a29e', gripAdd:0 },
  'ibnsina_2':   { id:'ib_ia',  texte:'L\'intellect agent n\'est pas un algorithme. Il illumine — il ne calcule pas.', drain:45, cost:26, color:'#c084fc', gripAdd:-10 },
  'ibnsina_7':   { id:'ib_mem', texte:'La mémoire n\'est pas un stockage — c\'est une lumière qui se retourne sur elle-même.', drain:38, cost:20, color:'#c084fc', gripAdd:-12 },
  'arete_5':     { id:'ar_ep',  texte:'L\'épuisement cultivé comme vertu est la forme la plus raffinée de la servitude volontaire.', drain:36, cost:16, color:'#facc15', gripAdd:-8 },
  'ibnkhaldoun_4': { id:'ik_as', texte:'L\'asabiyya moderne existe — elle s\'appelle ressentiment collectif. Et elle porte les mêmes cycles.', drain:48, cost:28, color:'#fb7185', gripAdd:10 },
  'platon_7':    { id:'pl_mem', texte:'La mémoire n\'est que réminiscence — nous nous souvenons de ce que l\'âme a déjà contemplé.', drain:50, cost:30, color:'#60a5fa', gripAdd:-5 },
};

const THEME_BONUS_WHEEL = {

  // ── Kant ─────────────────────────────────────────────────────────────────
  'kant_1': {
    id: 'kt_lib', themeBonus: true, contextLabel: 'THÈME · Liberté',
    texte: 'Si chacun suit ses propres règles sans jamais se demander si elles valent pour tous, le plus fort gagne à tous les coups.',
    drain: 38, cost: 20, color: '#a5f3c3', gripAdd: -8,
    comboBonus: 45,
  },
  'kant_2': {
    id: 'kt_ia', themeBonus: true, contextLabel: 'THÈME · IA',
    texte: 'Une machine peut appliquer des règles, elle ne peut pas les choisir. C\'est toute la différence entre obéir et décider.',
    drain: 42, cost: 22, color: '#a5f3c3', gripAdd: 0,
    comboBonus: 45,
  },
  'kant_3': {
    id: 'kt_trans', themeBonus: true, contextLabel: 'THÈME · Transmission',
    texte: 'Apprendre par cœur sans jamais poser la question "pourquoi ?", c\'est dresser des perroquets, pas former des humains.',
    drain: 40, cost: 21, color: '#a5f3c3', gripAdd: -6,
    comboBonus: 45,
  },
  'kant_5': {
    id: 'kt_ep', themeBonus: true, contextLabel: 'THÈME · Épuisement',
    texte: 'Quand on te pousse à t\'épuiser sans jamais te demander ton avis, on te traite comme un outil, pas comme une personne.',
    drain: 44, cost: 24, color: '#a5f3c3', gripAdd: -4,
    comboBonus: 45,
  },
  'kant_6': {
    id: 'kt_str', themeBonus: true, contextLabel: 'THÈME · Structure',
    texte: 'Les règles du jeu ne sont pas une prison — sans elles, il n\'y a tout simplement pas de jeu du tout.',
    drain: 36, cost: 18, color: '#a5f3c3', gripAdd: -6,
    comboBonus: 45,
  },
  'kant_7': {
    id: 'kt_mem', themeBonus: true, contextLabel: 'THÈME · Mémoire',
    texte: 'Sans mémoire, chaque instant serait le premier. Tu ne pourrais même pas te souvenir que tu existes.',
    drain: 40, cost: 21, color: '#a5f3c3', gripAdd: -4,
    comboBonus: 45,
  },
  'kant_nb': {
    id: 'kt_nb', themeBonus: false, contextLabel: 'KANT',
    texte: 'Agis comme si chacune de tes décisions devait être imprimée en une du journal de demain.',
    drain: 35, cost: 18, color: '#86efac', gripAdd: -7,
  },

  // ── Weil ─────────────────────────────────────────────────────────────
  'weil_1': {
    id: 'wv_lib', themeBonus: true, contextLabel: 'THÈME · Liberté',
    texte: 'La liberté sans enracinement n\'est qu\'une errance. Où sont vos racines pour que votre liberté ait un sol ?',
    drain: 40, cost: 18, color: '#fb923c', gripAdd: -5,
    comboBonus: 45,
  },
  'weil_2': {
    id: 'wv_ia', themeBonus: true, contextLabel: 'THÈME · IA',
    texte: 'Une IA ne fait pas attention. L\'attention est la forme la plus rare de la générosité — une machine en est incapable.',
    drain: 44, cost: 24, color: '#fb923c', gripAdd: -3,
    comboBonus: 45,
  },
  'weil_3': {
    id: 'wv_trans', themeBonus: true, contextLabel: 'THÈME · Transmission',
    texte: 'Transmettre sans enraciner, c\'est jeter des graines sur du béton. La transmission véritable a besoin de terre.',
    drain: 38, cost: 19, color: '#fb923c', gripAdd: -6,
    comboBonus: 45,
  },
  'weil_5': {
    id: 'wv_ep', themeBonus: true, contextLabel: 'THÈME · Épuisement',
    texte: 'L\'épuisement n\'est pas une fatalité — c\'est le signe qu\'on a coupé l\'âme de ses racines. Réenracinez-vous.',
    drain: 44, cost: 24, color: '#fb923c', gripAdd: 0,
    comboBonus: 45,
  },
  'weil_6': {
    id: 'wv_str', themeBonus: true, contextLabel: 'THÈME · Structure',
    texte: 'Une structure sans enracinement est une prison dorée. Une structure enracinée est une maison commune.',
    drain: 38, cost: 19, color: '#fb923c', gripAdd: -7,
    comboBonus: 45,
  },
  'weil_7': {
    id: 'wv_mem', themeBonus: true, contextLabel: 'THÈME · Mémoire',
    texte: 'La mémoire sans enracinement est une fuite. Retournez à la terre de votre être, et vous vous souviendrez.',
    drain: 42, cost: 22, color: '#fb923c', gripAdd: -5,
    comboBonus: 45,
  },
  'weil_nb': {
    id: 'wv_nb', themeBonus: false, contextLabel: 'WEIL',
    texte: 'L\'attention est la seule réponse. Je vous regarde, et votre argument se vide de sa substance.',
    drain: 38, cost: 20, color: '#fdba74', gripAdd: -8,
  },

  // ── Ibn Sīnā ──────────────────────────────────────────────────────────────
  'ibnsina_1': {
    id: 'ib_lib', themeBonus: true, contextLabel: 'THÈME · Liberté',
    texte: 'La vraie liberté est intérieure. Si tu ne te gouvernes pas toi-même, un autre le fera à ta place.',
    drain: 42, cost: 24, color: '#c084fc', gripAdd: -9,
    comboBonus: 45,
  },
  'ibnsina_2': {
    id: 'ib_ia', themeBonus: true, contextLabel: 'THÈME · IA',
    texte: 'Un programme ne s\'émerveille jamais. Il ne se réveille pas la nuit en se demandant pourquoi il existe.',
    drain: 45, cost: 26, color: '#c084fc', gripAdd: -10,
    comboBonus: 45,
  },
  'ibnsina_3': {
    id: 'ib_trans', themeBonus: true, contextLabel: 'THÈME · Transmission',
    texte: 'On ne transmet vraiment que ce qui passe par l\'expérience vécue. Le reste, c\'est du bruit qu\'on échange.',
    drain: 38, cost: 20, color: '#c084fc', gripAdd: -7,
    comboBonus: 45,
  },
  'ibnsina_5': {
    id: 'ib_ep', themeBonus: true, contextLabel: 'THÈME · Épuisement',
    texte: 'Quand l\'esprit ne se nourrit plus de sens, il s\'effondre. L\'épuisement, c\'est l\'esprit qui meurt de faim.',
    drain: 40, cost: 20, color: '#c084fc', gripAdd: -8,
    comboBonus: 45,
  },
  'ibnsina_6': {
    id: 'ib_str', themeBonus: true, contextLabel: 'THÈME · Structure',
    texte: 'L\'univers a un ordre, mais tu as une place dedans. Comprendre l\'ordre, c\'est trouver où tu peux agir.',
    drain: 44, cost: 22, color: '#c084fc', gripAdd: -6,
    comboBonus: 45,
  },
  'ibnsina_7': {
    id: 'ib_mem', themeBonus: true, contextLabel: 'THÈME · Mémoire',
    texte: 'La mémoire n\'est pas un disque dur. C\'est une lanterne que tu promènes dans les pièces de ta vie.',
    drain: 38, cost: 20, color: '#c084fc', gripAdd: -12,
    comboBonus: 45,
  },
  'ibnsina_nb': {
    id: 'ib_nb', themeBonus: false, contextLabel: 'IBN SĪNĀ',
    texte: 'Ce qui fait de toi un humain, ce n\'est pas ton corps — c\'est que tu peux penser l\'univers entier.',
    drain: 42, cost: 22, color: '#a78bfa', gripAdd: -11,
  },

  // ── Arété ─────────────────────────────────────────────────────────────────
  'arete_1': {
    id: 'ar_lib', themeBonus: true, contextLabel: 'THÈME · Liberté',
    texte: 'Être libre, c\'est pas faire tout ce qui te passe par la tête. C\'est choisir parmi tes envies celles qui te grandissent.',
    drain: 34, cost: 15, color: '#facc15', gripAdd: -6,
    comboBonus: 45,
  },
  'arete_2': {
    id: 'ar_ia', themeBonus: true, contextLabel: 'THÈME · IA',
    texte: 'L\'IA peut résoudre des équations, mais elle ne sait pas ce qui est beau. Elle peut simuler la sagesse, pas la vivre.',
    drain: 38, cost: 20, color: '#facc15', gripAdd: -4,
    comboBonus: 45,
  },
  'arete_3': {
    id: 'ar_trans', themeBonus: true, contextLabel: 'THÈME · Transmission',
    texte: 'Si apprendre fait mal, on arrête vite. Si apprendre donne du plaisir, on n\'arrête jamais.',
    drain: 32, cost: 14, color: '#facc15', gripAdd: -5,
    comboBonus: 45,
  },
  'arete_5': {
    id: 'ar_ep', themeBonus: true, contextLabel: 'THÈME · Épuisement',
    texte: 'S\'épuiser pour montrer qu\'on est sérieux, c\'est confondre le martyre et le travail bien fait.',
    drain: 36, cost: 16, color: '#facc15', gripAdd: -8,
    comboBonus: 45,
  },
  'arete_6': {
    id: 'ar_str', themeBonus: true, contextLabel: 'THÈME · Structure',
    texte: 'Un cadre trop rigide étouffe. Un cadre trop lâche noie. La bonne structure, c\'est celle qui tient sans étouffer.',
    drain: 38, cost: 17, color: '#facc15', gripAdd: -5,
    comboBonus: 45,
  },
  'arete_7': {
    id: 'ar_mem', themeBonus: true, contextLabel: 'THÈME · Mémoire',
    texte: 'Ce dont on se souvient le mieux, ce sont les moments où on s\'est senti vivant ensemble.',
    drain: 30, cost: 12, color: '#facc15', gripAdd: -7,
    comboBonus: 45,
  },
  'arete_nb': {
    id: 'ar_nb', themeBonus: false, contextLabel: 'ARÉTÉ',
    texte: 'Le plaisir n\'est pas l\'ennemi de la raison. C\'est un signal. À toi de savoir ce qu\'il indique.',
    drain: 28, cost: 12, color: '#fde047', gripAdd: -6,
  },

  // ── Ibn Khaldoun ──────────────────────────────────────────────────────────
  'ibnkhaldoun_1': {
    id: 'ik_lib', themeBonus: true, contextLabel: 'THÈME · Liberté',
    texte: 'La liberté sans solidarité, c\'est la porte ouverte au premier chef de bande venu.',
    drain: 44, cost: 24, color: '#fb7185', gripAdd: 5,
    comboBonus: 45,
  },
  'ibnkhaldoun_2': {
    id: 'ik_ia', themeBonus: true, contextLabel: 'THÈME · IA',
    texte: 'Un réseau social n\'est pas une tribu. Les likes ne créent pas de loyauté, ils créent de la solitude collective.',
    drain: 46, cost: 26, color: '#fb7185', gripAdd: 6,
    comboBonus: 45,
  },
  'ibnkhaldoun_3': {
    id: 'ik_trans', themeBonus: true, contextLabel: 'THÈME · Transmission',
    texte: 'Transmettre sans créer de lien, c\'est jeter des graines sur du béton. Elles ne germeront jamais.',
    drain: 42, cost: 22, color: '#fb7185', gripAdd: 4,
    comboBonus: 45,
  },
  'ibnkhaldoun_4': {
    id: 'ik_as', themeBonus: true, contextLabel: 'THÈME · Asabiyya',
    texte: 'Le vrai pouvoir aujourd\'hui, c\'est ceux qui savent fédérer la colère sans jamais l\'éteindre. Une asabiyya 2.0.',
    drain: 48, cost: 28, color: '#fb7185', gripAdd: 10,
    comboBonus: 45,
  },
  'ibnkhaldoun_5': {
    id: 'ik_ep', themeBonus: true, contextLabel: 'THÈME · Épuisement',
    texte: 'Une société qui s\'épuise dans le confort ne tombe pas sous les coups de l\'ennemi : elle s\'effondre de l\'intérieur.',
    drain: 50, cost: 28, color: '#fb7185', gripAdd: 8,
    comboBonus: 45,
  },
  'ibnkhaldoun_6': {
    id: 'ik_str', themeBonus: true, contextLabel: 'THÈME · Structure',
    texte: 'Tout système meurt un jour. Les malins ne le défendent pas, ils préparent le suivant.',
    drain: 44, cost: 22, color: '#fb7185', gripAdd: 7,
    comboBonus: 45,
  },
  'ibnkhaldoun_nb': {
    id: 'ik_nb', themeBonus: false, contextLabel: 'IBN KHALDOUN',
    texte: 'L\'histoire ne se répète pas bêtement — mais ceux qui ignorent ses cycles sont condamnés à les revivre.',
    drain: 45, cost: 25, color: '#fda4af', gripAdd: 6,
  },

  // ── Platon ────────────────────────────────────────────────────────────────
  'platon_1': {
    id: 'pl_lib', themeBonus: true, contextLabel: 'THÈME · Liberté',
    texte: 'La liberté sans éducation, c\'est confier un volant à quelqu\'un qui n\'a jamais vu de voiture.',
    drain: 48, cost: 26, color: '#60a5fa', gripAdd: -4,
    comboBonus: 45,
  },
  'platon_2': {
    id: 'pl_ia', themeBonus: true, contextLabel: 'THÈME · IA',
    texte: 'L\'IA te montre des ombres d\'ombres. Elle ne te ment pas, mais elle te tient loin de ce qui est vrai.',
    drain: 50, cost: 28, color: '#60a5fa', gripAdd: -3,
    comboBonus: 45,
  },
  'platon_3': {
    id: 'pl_trans', themeBonus: true, contextLabel: 'THÈME · Transmission',
    texte: 'Enseigner, ce n\'est pas remplir un seau, c\'est allumer un feu. Si tu ne vois pas de flammes, tu n\'as rien transmis.',
    drain: 46, cost: 24, color: '#60a5fa', gripAdd: -5,
    comboBonus: 45,
  },
  'platon_5': {
    id: 'pl_ep', themeBonus: true, contextLabel: 'THÈME · Épuisement',
    texte: 'Quand tu es trop fatigué pour penser, tu te contentes de réagir. C\'est là que les manipulateurs t\'attendent.',
    drain: 48, cost: 26, color: '#60a5fa', gripAdd: -6,
    comboBonus: 45,
  },
  'platon_6': {
    id: 'pl_str', themeBonus: true, contextLabel: 'THÈME · Structure',
    texte: 'Une société bien foutue, c\'est une société où chacun fait ce qu\'il fait de mieux, pas ce qu\'on l\'oblige à faire.',
    drain: 44, cost: 22, color: '#60a5fa', gripAdd: -5,
    comboBonus: 45,
  },
  'platon_7': {
    id: 'pl_mem', themeBonus: true, contextLabel: 'THÈME · Mémoire',
    texte: 'Apprendre, c\'est se souvenir de choses qu\'on savait déjà sans le savoir. La mémoire, c\'est le sol de toute pensée.',
    drain: 50, cost: 30, color: '#60a5fa', gripAdd: -5,
    comboBonus: 45,
  },
  'platon_nb': {
    id: 'pl_nb', themeBonus: false, contextLabel: 'PLATON',
    texte: 'Celui qui a vu la lumière n\'a pas le droit de rester dehors. Il doit redescendre et parler, même si on se moque de lui.',
    drain: 52, cost: 30, color: '#93c5fd', gripAdd: -4,
  },
};

const GENERIC_WHEEL_LINES = {
  attaque: [
    { texte: 'Ce que vous appelez argument n\'est qu\'une affirmation déguisée en preuve.', drain: 20, gripAdd: 0 },
    { texte: 'La force d\'un raisonnement se mesure à ce qu\'il laisse hors de lui.', drain: 18, gripAdd: 0 },
    { texte: 'Vous démontrez ce que vous postuleriez de toute façon.', drain: 22, gripAdd: 0 },
    { texte: 'L\'évidence que vous invoquez n\'est évidente que pour vous.', drain: 16, gripAdd: 0 },
  ],
  defense: [
    { texte: 'Critiquer sans proposer, c\'est détruire sans reconstruire.', drain: 0, gripAdd: -10 },
    { texte: 'L\'objection que vous soulevez est réelle — et j\'en faisais déjà le fond de mon argument.', drain: 0, gripAdd: -12 },
    { texte: 'Votre pression révèle l\'exacte limite de votre pensée.', drain: 0, gripAdd: -8 },
    { texte: 'Ce silence que vous interprétez comme faiblesse est le signe que j\'ai déjà répondu.', drain: 0, gripAdd: -14 },
  ],
};

const SOPHIA_INTERVENTIONS = {
  dague_3: [ // 3 éclats perdus
    'Le fragment vacille. Il ne lui reste que des mots.',
    'L\'oubli avance. Chaque silence non comblé efface une trace.',
    'Trois éclats disparus. La Bibliothèque s\'assombrit.',
  ],
  dague_4: [ // 4 éclats perdus
    'Un éclat encore — et le fragment s\'efface pour toujours.',
    'La mémoire est en danger critique. Un seul acte peut encore tout changer.',
  ],
  round_end_no_punchline: [ // Round terminé sans punchline jouée
    'Le fragment n\'a pas pu s\'exprimer. L\'oubli progresse.',
    'Un round sans témoignage. La trace s\'amenuise.',
    'Sophia murmure : ce fragment avait quelque chose à dire. Il n\'a pas osé.',
  ],
  repetition: [ // Même concept joué deux fois de suite
    'La répétition sans renouveau est la première forme de l\'oubli.',
    'Un concept répété s\'use. Le fragment se fragmente lui-même.',
  ],
  synergie: [ // Synergie déclenchée — Dague regagne un éclat
    'Les idées se reconnectent. Un éclat de mémoire se ressoud.',
    'La friction entre les pensées ravive la flamme. La Dague se répare.',
  ],
  combo: [ // Combo concept+punchline parfait
    'Voilà. C\'est ainsi que la pensée traverse le temps.',
    'Le fragment brille. Un instant de pure transmission.',
  ],
  subtheme_reveal: [], // Rempli par THEME_SUBTHEMES
};

const PRECEPTEUR_REACTIONS = {
 
  // ── Joueur joue MA (silence, dégripe) ─────────────────────────────────────
  ma: {
    seiryoku:   ["Le silence que vous invoquez, je le retourne en élan. Votre vide m'appartient maintenant."],
    resistance: ["Vous vous taisez — moi je tiens. Le silence ne dissout pas la roche."],
    entropie:   ["Beau silence. Mais le désordre n'attend pas le moment propice. Il avance."],
    wuwei:      ["Deux silences face à face. Le premier qui parle perd peut-être. Ou peut-être pas."],
    asabiyya:   ["Votre silence me laisse le temps d'invoquer ce qui nous unit tous — contre vous."],
    hegel:      ["Le vide appelle la synthèse. J'absorbe votre silence et le dépasse."],
    philosophe: ["Le philosophe-roi n'attend pas dans le silence. Il contemple, puis il frappe."],
    _default:   ["Votre silence me donne le temps de construire ce que vous ne dites pas."],
  },
 
  // ── Joueur joue SEIRYOKU (retourne la force) ──────────────────────────────
  seiryoku: {
    resistance: ["Retourner ma force ? Le conatus ne plie pas. Je résiste à votre propre méthode."],
    entropie:   ["Vous redirigez l'élan — moi j'instille le désordre. Voyons ce qui survit."],
    wuwei:      ["Seiryoku contre Wu Wei. Vous retournez ce qui n'offre aucune prise. Intéressant."],
    ma:         ["Vous frappez avec ma propre énergie. Je réponds par le vide. Trouvez-y prise."],
    asabiyya:   ["Votre technique est belle. Ma cohésion collective est plus lourde que votre levier."],
    hegel:      ["Vous retournez. Je synthétise. Votre élan devient le socle de ma thèse."],
    kintsugi:   ["Vous retournez mon élan — je répare avec l'or de votre propre frappe."],
    _default:   ["Habile. Mais retourner une force n'est pas la même chose que l'annuler."],
  },
 
  // ── Joueur joue RÉSISTANCE ────────────────────────────────────────────────
  resistance: {
    seiryoku:   ["Vous ancrez. Je ne force pas — j'accompagne votre rigidité jusqu'à sa rupture."],
    entropie:   ["La résistance est solide. Le désordre est patient. Il attend votre premier grain de fatigue."],
    wuwei:      ["Vous résistez à ce qui ne pousse pas. Mon non-agir se glisse entre vos certitudes."],
    hypertexte: ["Chaque résistance crée un nœud. Mon réseau s'en nourrit."],
    asabiyya:   ["Un homme qui résiste seul. Moi j'ai la cohésion de tout un peuple derrière moi."],
    hegel:      ["La résistance est une thèse. J'en suis l'antithèse. La synthèse vous appartient — si vous la trouvez."],
    kintsugi:   ["Vous résistez. Je répare. Ce qui cède chez vous devient or chez moi."],
    _default:   ["Vous tenez votre position. C'est admirable. Et prévisible."],
  },
 
  // ── Joueur joue WUWEI (bloque, dégripe) ──────────────────────────────────
  wuwei: {
    seiryoku:   ["Vous ne faites rien. Moi je retourne ce rien en élan. Paradoxe bienvenu."],
    entropie:   ["Le non-agir contre le chaos. L'un des deux est une posture. L'autre est réel."],
    resistance: ["Vous flottez. Je tiens. Votre fluidité ne trouve pas de prise sur mon ancrage."],
    asabiyya:   ["Le sage n'agit pas — la foule, si. Devinez qui gagne en durée."],
    philosophe: ["Le non-agir du sage rencontre la puissance accumulée du philosophe-roi. Curieux face-à-face."],
    ma:         ["Deux formes de vide se regardent. L'une est tactique. L'autre est philosophie."],
    _default:   ["Vous choisissez l'absence. Je choisis l'action. Nous verrons ce qui laisse une trace."],
  },
 
  // ── Joueur joue ASABIYYA ──────────────────────────────────────────────────
  asabiyya: {
    ubuntu:     ["Cohésion contre cohésion. Vous invoquez l'histoire d'un peuple — moi l'humanité entière."],
    jitakyoei:  ["La force collective rencontre la prospérité mutuelle. Ces deux-là auraient dû s'entendre."],
    entropie:   ["La cohésion appelle le désordre comme une civilisation appelle sa chute."],
    resistance: ["Un peuple derrière vous. Un roc devant vous. Le roc ne bouge pas."],
    wuwei:      ["Votre cohésion est puissante. Mon non-agir la laisse s'épuiser seule."],
    hegel:      ["L'asabiyya est une thèse historique. J'en suis la synthèse — après le déclin."],
    _default:   ["Vous parlez au nom de tous. Je réponds en mon propre nom. C'est suffisant."],
  },
 
  // ── Joueur joue UBUNTU ────────────────────────────────────────────────────
  ubuntu: {
    asabiyya:   ["L'humanité universelle contre la cohésion tribale. Ibn Khaldoun vous répondrait : la tribu gagne d'abord."],
    jitakyoei:  ["Vous êtes parce que nous sommes. Nous prospérons ensemble. Ces deux vérités ne se contredisent pas — elles m'unissent."],
    resistance: ["L'humanité partagée contre l'ancrage individuel. Le roc ignore qu'il fait partie de la montagne."],
    entropie:   ["Le lien humain contre le désordre. L'entropie a toujours raison — à long terme."],
    _default:   ["Je suis parce que nous sommes. Et dans ce 'nous', vous êtes inclus — même si vous vous y opposez."],
  },
 
  // ── Joueur joue KINTSUGI ──────────────────────────────────────────────────
  kintsugi: {
    entropie:   ["Vous réparez à l'or — je brise à nouveau. Le désordre est plus patient que la résilience."],
    resistance: ["La cicatrice dorée contre le roc. L'un est vivant, l'autre immobile. Je choisis d'être immobile."],
    seiryoku:   ["Vous transformez la blessure en force. Je retourne cette force en élan. Beau cycle."],
    asabiyya:   ["Les fractures d'un peuple réparées à l'or — c'est exactement ce qu'est l'asabiyya. Étrange miroir."],
    _default:   ["Vous faites de vos failles une force. J'en prends note — et je cherche la faille suivante."],
  },
 
  // ── Joueur joue ENTROPIE ──────────────────────────────────────────────────
  entropie: {
    hegel:      ["Le chaos que vous invoquez — je l'absorbe et en fais une synthèse. Baudrillard rencontre Hegel."],
    kintsugi:   ["Vous saturez le sens — je répare avec l'or du sens lui-même. Paradoxe productif."],
    wuwei:      ["Votre désordre cherche une prise. Mon non-agir ne lui en offre aucune."],
    resistance: ["L'entropie gagne toujours. Sauf face à ce qui refuse de bouger. Pour l'instant."],
    asabiyya:   ["Le désordre symbolique contre la cohésion collective. L'histoire montre qui gagne."],
    _default:   ["Vous semez le désordre. Je me demande si vous savez ce que vous récoltez."],
  },
 
  // ── Joueur joue HEGEL ────────────────────────────────────────────────────
  hegel: {
    entropie:   ["Vous renvoyez le chaos transformé. Je réponds : le désordre n'a pas de synthèse — il a un successeur."],
    seiryoku:   ["L'Aufhebung renvoie mon élan transformé. Fascinant. Je le retourne une fois de plus."],
    resistance: ["Vous dépassez ma résistance en la conservant. Mais je tiens toujours — à un niveau que vous n'avez pas encore atteint."],
    asabiyya:   ["La dialectique hégélienne contre la cohésion sociale. Ces deux-là construisent les civilisations — ou les détruisent."],
    _default:   ["Thèse absorbée. Antithèse en cours. Votre synthèse m'appartient autant qu'à vous."],
  },
 
  // ── Joueur joue PHILOSOPHE ROI ────────────────────────────────────────────
  philosophe: {
    asabiyya:   ["Le roi-philosophe gouverne seul — mais sans peuple, il n'y a pas de cité à gouverner."],
    entropie:   ["La sagesse accumulée contre le désordre symbolique. Platon vous dirait que c'est le combat fondateur."],
    wuwei:      ["Le roi qui contemple rencontre le sage qui n'agit pas. Qui gouverne, au bout du compte ?"],
    resistance: ["La puissance intellectuelle ne suffit pas face à l'immovabilité. Le philosophe doit redescendre dans la caverne."],
    _default:   ["Chaque concept que vous jouez nourrit votre puissance. Chaque concept que je joue nourrit la mienne."],
  },
 
  // ── Joueur joue JITA KYOEI ────────────────────────────────────────────────
  jitakyoei: {
    ubuntu:     ["Prospérité mutuelle, humanité partagée. Ces deux-là me mettent dans une position inconfortable."],
    asabiyya:   ["Vous élevez les deux parties — moi j'invoque la force de ma cohésion. Étrange guerre."],
    resistance: ["Vous offrez la prospérité mutuelle. Je résiste. Ce n'est pas de la mauvaise foi — c'est de la prudence."],
    entropie:   ["Jita Kyoei contre l'entropie. L'un construit, l'autre dissout. Devinez qui dure."],
    _default:   ["Vous cherchez à élever les deux parties. Je cherche à gagner. Nos fins divergent."],
  },
 
  // ── Joueur joue HYPERTEXTE ────────────────────────────────────────────────
  hypertexte: {
    hegel:      ["Votre réseau s'épaissit — ma synthèse l'absorbe comme nœud supplémentaire."],
    entropie:   ["Un réseau de connexions contre un désordre de signes. Les deux se ressemblent plus qu'il n'y paraît."],
    philosophe: ["Chaque connexion renforce votre puissance. Je regarde grossir le réseau avec intérêt."],
    _default:   ["Votre réseau se densifie. Mais un réseau sans centre finit par se noyer dans ses propres connexions."],
  },
 
  // ── Joueur joue un ULTIME ────────────────────────────────────────────────
  imperatif: {
    _default:   ["La loi morale universelle. Implacable. J'y oppose non pas son contraire — mais sa limite : le réel."],
  },
  hedone: {
    _default:   ["Le plaisir éclairé restaure vos forces. J'admire la cohérence. Mais le plaisir n'est pas un argument."],
  },
  enracinement: {
    _default:   ["L'acte fondateur. La liberté totale en frappe. Il me faut quelque chose d'aussi absolu pour répondre."],
  },
  qanun: {
    _default:   ["L'intellect illuminé ouvre les voies cross-époque. Je referme ce que je peux refermer."],
  },
  allegorie: {
    _default:   ["Vous me montrez la lumière. Je préfère rester dans la caverne — j'y connais les ombres."],
  },
  algorithme_absolu: {
    _default:   ["La procédure parfaite. Mais toute méthode a une limite : ce qu'elle ne peut pas nommer."],
  },
  cycle_eternel: {
    _default:   ["Le cycle s'impose. Je note que votre propre puissance porte les germes de votre chute. Comme la mienne."],
  },
};
 
const PRECEPTEUR_FALLBACK_BY_NATURE = {
  // Joueur attaque → Précepteur attaque
  'attaque_attaque': [
    "Vous frappez. Je frappe. La question est de savoir qui tiendra le plus longtemps.",
    "Force contre force. C'est là que la philosophie révèle sa vraie nature.",
    "Vous n'avez pas tort de frapper. Moi non plus.",
    "Un choc frontal. Le métal de nos idées va produire des étincelles.",
    "Votre agressivité est un hommage. Vous me prenez au sérieux.",
    "En garde. L'attaque est la meilleure des défenses, disait-on."
  ],
  // Joueur attaque → Précepteur défend
  'attaque_defense': [
    "Votre frappe est réelle. Mais elle ne trouve pas de prise ici.",
    "Je ne résiste pas à votre argument — je l'absorbe. Nuance fondamentale.",
    "Frapper n'est pas convaincre. Je laisse votre force passer.",
    "Un bon roc ne bouge pas. Votre coup était pourtant bien senti.",
    "J'encaisse. Et je souris. C'est tout l'art de la défense.",
    "Votre attaque m'informe plus qu'elle ne me blesse."
  ],
  // Joueur défend → Précepteur attaque
  'defense_attaque': [
    "Vous vous protégez. C'est le moment que je choisissais pour frapper.",
    "La défense révèle ce qu'on tient à préserver. J'y vise maintenant.",
    "Votre ancrage me montre où vous êtes fragile.",
    "Ah, vous baissez la garde du débat pour vous protéger. Erreur.",
    "Un repli tactique. Le mien est stratégique. La différence va vous coûter cher.",
    "Toute défense est une fenêtre sur la faiblesse. La vôtre est ouverte."
  ],
  // Joueur défend → Précepteur défend
  'defense_defense': [
    "Deux positions défensives. Le débat cherche qui bougera en premier.",
    "Nous nous observons. Le premier qui s'expose perd peut-être l'avantage.",
    "La prudence de votre côté, la mienne du mien. Quelque chose doit finir par céder.",
    "Un round d'observation. Votre patience est votre seule vertu.",
    "Le temps est un allié pour celui qui tient. Nous tenons tous les deux.",
    "Un duel de miroirs. Aucun de nous ne veut briser la glace."
  ],
  precepteur_backfoot: [
    "Touché... Votre dernier argument m'a ébranlé. Mais je tiens.",
    "Je concède ce point. Il en reste beaucoup d'autres.",
    "Vous percez ma défense. C'est mérité. Continuons.",
  ],
};
 
// Réactions liées au THÈME — le Précepteur relie son coup au thème du duel
const PRECEPTEUR_THEME_AMPLIFICATIONS = {
  // ── KANT ──────────────────────────────────────────────────────────────
  'adv_kant_1':  "La liberté sans loi morale n'est qu'arbitraire. Votre liberté, dans ce duel, a-t-elle une maxime universalisable ?",
  'adv_kant_2':  "L'IA obéit à des règles — elle ne les choisit pas. C'est la différence entre l'outil et l'agent moral.",
  'adv_kant_3':  "Transmettre sans la règle, c'est livrer l'autre à ses penchants. La pédagogie est une discipline de la raison.",
  'adv_kant_5':  "L'épuisement que vous ressentez n'est pas un accident. C'est la pression de l'impératif sur l'inclination sensible.",
  'adv_kant_6':  "Être libre dans une structure, c'est précisément ce que la raison pratique exige. La contrainte que vous vous donnez.",
  'adv_kant_7':  "La mémoire sans concept est aveugle. Structurons vos souvenirs à la lumière de l'entendement.",
  'adv_kant_default': "La maxime de votre coup — pourriez-vous la vouloir universelle ?",

  // ── ARÉTÉ ─────────────────────────────────────────────────────────────
  'adv_arete_1': "Choisir parmi vos désirs ceux qui vous grandissent — c'est exactement ce que ce duel exige de vous.",
  'adv_arete_2': "L'IA peut simuler la sagesse, pas la vivre. La vôtre est une expérience, pas un calcul.",
  'adv_arete_4': "L'asabiyya moderne est une quête de plaisir collectif. Mais quel plaisir ? Vertueux ou servile ?",
  'adv_arete_5': "L'épuisement cultivé comme performance n'a rien de vertueux. Le plaisir éclairé sait quand s'arrêter.",
  'adv_arete_6': "Une structure qui donne du plaisir n'est pas une prison. Celle-là l'est peut-être. À vous de voir.",
  'adv_arete_7': "Le souvenir du bonheur est un bonheur en soi. Votre mémoire est-elle hantée ou habitée ?",
  'adv_arete_default': "Le souverain bien n'est pas la victoire. C'est la qualité de l'échange.",

  // ── WEIL ──────────────────────────────────────────────────────────
  'adv_weil_1': "La liberté sans enracinement n'est qu'une errance. Où sont vos racines ?",
  'adv_weil_2': "L'IA peut-elle faire attention ? Non. L'attention est une forme de générosité.",
  'adv_weil_3': "On ne transmet pas sans se dépouiller. L'attention est un don total.",
  'adv_weil_4': "L'enracinement n'est pas une idéologie collective. C'est un besoin vital de l'âme.",
  'adv_weil_5': "L'épuisement est le signe d'une attention qui ne se repose pas dans le réel.",
  'adv_weil_6': "Une structure qui ignore le besoin d'enracinement écrase l'âme.",
  'adv_weil_7': "La mémoire sans enracinement est une fuite. Retournez à la terre de votre être.",
  'adv_weil_default': "L'attention est la seule réponse. Je vous regarde, et votre argument se vide.",

  // ── IBN SĪNĀ ──────────────────────────────────────────────────────────
  'adv_ibnsina_1': "La liberté de l'intellect est la seule qui ne soit pas une illusion.",
  'adv_ibnsina_2': "Un programme ne s'émerveille pas. Moi si — face à la qualité de votre argumentation.",
  'adv_ibnsina_3': "Transmettre sans aimer la sagesse, c'est distribuer des coquilles vides.",
  'adv_ibnsina_4': "L'asabiyya est un corps social. L'intellect en est l'âme. L'un sans l'autre dépérit.",
  'adv_ibnsina_5': "L'épuisement est le signe d'un déséquilibre de l'âme rationnelle. Soignez-vous.",
  'adv_ibnsina_6': "L'ordre cosmique est la structure suprême. S'y insérer, c'est être libre.",
  'adv_ibnsina_7': "La mémoire n'est pas un stockage. Chaque concept que vous jouez éclaire ce qui précède.",
  'adv_ibnsina_default': "L'intellect agent illumine — mais seulement si vous tendez le miroir."
};

const RIPOSTE_PHILOSOPHIQUE = {
  // Joueur joue MA → le Précepteur peut répondre avec...
  ma:         ['seiryoku', 'asabiyya', 'philosophe', 'hegel', 'wuwei'],
  seiryoku:   ['resistance', 'wuwei', 'hegel', 'kintsugi', 'jitakyoei'],
  resistance: ['seiryoku', 'entropie', 'wuwei', 'hegel', 'hypertexte'],
  wuwei:      ['entropie', 'seiryoku', 'asabiyya', 'philosophe', 'hegel'],
  asabiyya:   ['ubuntu', 'jitakyoei', 'entropie', 'hegel', 'resistance'],
  ubuntu:     ['asabiyya', 'jitakyoei', 'resistance', 'entropie', 'philosophe'],
  kintsugi:   ['entropie', 'resistance', 'seiryoku', 'hypertexte', 'hegel'],
  entropie:   ['hegel', 'kintsugi', 'wuwei', 'resistance', 'philosophe'],
  hegel:      ['entropie', 'seiryoku', 'resistance', 'asabiyya', 'philosophe'],
  philosophe: ['asabiyya', 'entropie', 'wuwei', 'hegel', 'resistance'],
  jitakyoei:  ['ubuntu', 'asabiyya', 'resistance', 'entropie', 'philosophe'],
  hypertexte: ['hegel', 'entropie', 'philosophe', 'resistance', 'wuwei'],
  // Ultimes joueur → le Précepteur recule et choisit défense ou riposte forte
  imperatif:  ['resistance', 'entropie', 'hegel', 'wuwei', 'asabiyya'],
  hedone:     ['entropie', 'resistance', 'asabiyya', 'philosophe', 'hegel'],
  enracinement: ['attention', 'pesanteur', 'hegel', 'asabiyya', 'wuwei'],
  qanun:      ['philosophe', 'hegel', 'entropie', 'resistance', 'wuwei'],
};

const CONCEPTS_SOCRATIQUES = ['ma', 'philosophe', 'wuwei', 'hypertexte', 'ubuntu'];
const CONCEPTS_DIALECTIQUES = ['seiryoku', 'resistance', 'hegel', 'asabiyya', 'kintsugi'];
const CONCEPTS_REVELATEURS = ['entropie', 'jitakyoei', 'hegel', 'asabiyya', 'seiryoku'];
 
const PRECEPTEUR_THEME_PREFERENCE = {
  adv_kant: {
    1: ['resistance', 'hegel', 'wuwei'],       // Liberté → structure et universalité
    2: ['philosophe', 'hypertexte', 'hegel'],   // IA → raison et réseau
    3: ['wuwei', 'kintsugi', 'philosophe'],     // Transmission → patience
    5: ['resistance', 'kintsugi', 'wuwei'],     // Épuisement → endurance
    6: ['resistance', 'hegel', 'philosophe'],   // Structure → ordre
    7: ['hegel', 'philosophe', 'hypertexte'],   // Mémoire → dialectique
  },
  adv_arete: {
    1: ['ubuntu', 'jitakyoei', 'philosophe'],  // Liberté → bien commun
    5: ['kintsugi', 'ubuntu', 'ma'],            // Épuisement → guérison
    6: ['jitakyoei', 'ubuntu', 'kintsugi'],     // Structure → harmonie
    2: ['philosophe', 'ma', 'wuwei'],           // IA → sagesse tranquille
    7: ['ubuntu', 'kintsugi', 'jitakyoei'],     // Mémoire → lien
  },
  adv_weil: {
    1: ['attention', 'enracinement_propre', 'pesanteur'],
    3: ['ubuntu', 'jitakyoei', 'wuwei'],        // Transmission → lien
    5: ['entropie', 'resistance', 'ubuntu'],    // Épuisement → dénonciation
    6: ['hypertexte', 'entropie', 'hegel'],     // Structure → contestation
    7: ['entropie', 'hypertexte', 'resistance'],// Mémoire → déconstruction
  },
  adv_ibnsina: {
    2: ['asabiyya', 'hegel', 'philosophe'],     // IA → intellect
    7: ['philosophe', 'hegel', 'hypertexte'],   // Mémoire → connaissance
    3: ['hypertexte', 'philosophe', 'ma'],      // Transmission → lumière
    6: ['hegel', 'philosophe', 'asabiyya'],     // Structure → synthèse
    1: ['philosophe', 'hegel', 'wuwei'],        // Liberté → contemplation
  },
};

const CONCEPT_THEME_PROXIMITY = {
  resistance:  [1, 6, 5],     // Liberté, Structure, Épuisement
  ma:          [3, 7, 6],     // Transmission, Mémoire, Structure
  seiryoku:    [1, 5, 2],     // Liberté, Épuisement, IA
  wuwei:       [3, 6, 7],     // Transmission, Structure, Mémoire
  asabiyya:    [4, 1, 5],     // Asabiyya, Liberté, Épuisement
  ubuntu:      [4, 3, 1],     // Asabiyya, Transmission, Liberté
  jitakyoei:   [4, 3, 6],     // Asabiyya, Transmission, Structure
  entropie:    [2, 5, 1],     // IA, Épuisement, Liberté
  hegel:       [2, 7, 6],     // IA, Mémoire, Structure
  hypertexte:  [2, 3, 7],     // IA, Transmission, Mémoire
  philosophe:  [6, 1, 3],     // Structure, Liberté, Transmission
  kintsugi:    [5, 7, 3],     // Épuisement, Mémoire, Transmission
  // Ultimes
  imperatif:   [1, 6, 5],
  hedone:      [5, 6, 1],
  enracinement:[5, 7, 3],
  qanun:       [2, 7, 3],
  allegorie:   [7, 2, 3],
  algorithme_absolu: [2, 3, 6],
  cycle_eternel:     [4, 5, 1],
  sophia_ultimate:   [1, 2, 3, 4, 5, 6, 7],
};

function useIsMobile() {
  const [mobile, setMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 769 : false
  );
  useEffect(() => {
    const fn = () => setMobile(window.innerWidth < 769);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return mobile;
}

function DagueMemoireHUD({ eclats, prevEclats, charColor }) {
  const total = 5;
  const critical = eclats <= 2;
  const justLost = prevEclats !== undefined && eclats < prevEclats;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
      
      {/* Petite icône dague */}
      <div style={{ position: 'relative', width: 22, height: 38 }}>
        <svg viewBox="0 0 24 46" style={{ width: '100%', height: '100%' }}>
          <path
            d="M12 4 L16 28 L12 38 L8 28 Z"
            fill={critical ? "#ef4444" : (charColor || "#e9d5ff")}
            stroke="#e9d5ff88"
            strokeWidth="1.5"
          />
          <rect x="9" y="29" width="6" height="4" rx="1" fill="#1f1728" />
        </svg>
      </div>

      {/* Éclats horizontaux */}
      <div style={{ display: 'flex', gap: '3px' }}>
        {Array.from({ length: total }).map((_, i) => (
          <motion.div
            key={i}
            animate={{
              opacity: i < eclats ? 1 : 0.25,
              scale: i < eclats ? 1 : 0.75,
            }}
            style={{
              width: '7px',
              height: '7px',
              borderRadius: '50%',
              background: i < eclats 
                ? (critical ? '#ef4444' : charColor || '#e9d5ff') 
                : 'rgba(255,255,255,0.12)',
              boxShadow: i < eclats 
                ? `0 0 5px ${critical ? '#ef4444' : charColor || '#e9d5ff'}` 
                : 'none',
            }}
          />
        ))}
      </div>

      {/* Label discret */}
      <div style={{
        fontFamily: 'monospace',
        fontSize: '0.38rem',
        color: critical ? '#ef4444' : 'rgba(233,213,255,0.45)',
        letterSpacing: '0.08em',
        whiteSpace: 'nowrap',
      }}>
        {critical ? 'FRAGILE' : 'MÉMOIRE'}
      </div>
    </div>
  );
}

function ComboIndicator({ comboState, charColor }) {
  if (!comboState) return null;

  return (
    <AnimatePresence>
      <motion.div
        key={comboState.conceptId}
        initial={{ opacity: 0, y: -8, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -5 }}
        transition={{ duration: 0.35 }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.3rem 0.8rem',
          background: `${comboState.conceptColor}12`,
          border: `1px solid ${comboState.conceptColor}44`,
          borderLeft: `3px solid ${comboState.conceptColor}`,
          marginBottom: '0.4rem',
        }}
      >
        {/* Pulse */}
        <motion.div
          animate={{ opacity: [0.4, 1, 0.4], scale: [1, 1.15, 1] }}
          transition={{ duration: 1.2, repeat: Infinity }}
          style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            background: comboState.conceptColor,
            boxShadow: `0 0 8px ${comboState.conceptColor}`,
            flexShrink: 0,
          }}
        />

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontFamily: 'monospace',
            fontSize: '0.38rem',
            color: 'rgba(255,255,255,0.35)',
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
          }}>
            OUVERTURE ACTIVE — ENCHAÎNEZ UNE PUNCHLINE
          </div>
          <div style={{
            fontFamily: 'monospace',
            fontSize: '0.52rem',
            color: comboState.conceptColor,
            fontWeight: 700,
            letterSpacing: '0.08em',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {comboState.conceptLabel}
          </div>
        </div>

        {/* Flèche animée vers les punchlines */}
        <motion.div
          animate={{ x: [0, 4, 0] }}
          transition={{ duration: 0.8, repeat: Infinity }}
          style={{
            color: comboState.conceptColor,
            fontSize: '0.7rem',
            opacity: 0.7,
          }}
        >
          →
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function SophiaWhisper({ interruption, onDone }) {
  useEffect(() => {
    if (!interruption) return;
    const timer = setTimeout(onDone, interruption.urgency === 'high' ? 3500 : 2800);
    return () => clearTimeout(timer);
  }, [interruption, onDone]);

  return (
    <AnimatePresence>
      {interruption && (
        <motion.div
          key={interruption.text}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 30,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(2,6,23,0.75)',
            backdropFilter: 'blur(3px)',
            pointerEvents: 'none',
          }}
        >
          {/* Fond */}
          <div style={{
            background: interruption.urgency === 'high'
              ? 'rgba(127,29,29,0.85)'
              : 'rgba(10,5,30,0.88)',
            border: `1px solid ${interruption.urgency === 'high' ? '#ef444466' : 'rgba(233,213,255,0.2)'}`,
            padding: '0.6rem 1rem',
            backdropFilter: 'blur(8px)',
          }}>
            <div style={{
              fontFamily: 'monospace',
              fontSize: '0.32rem',
              letterSpacing: '0.28em',
              color: interruption.urgency === 'high' ? '#ef4444aa' : 'rgba(233,213,255,0.4)',
              textTransform: 'uppercase',
              marginBottom: '0.25rem',
            }}>
              SOPHIA PERENNIS
            </div>
            <p style={{
              fontFamily: 'serif',
              fontStyle: 'italic',
              fontSize: 'clamp(0.55rem, 2vw, 0.72rem)',
              color: interruption.urgency === 'high'
                ? 'rgba(254,202,202,0.85)'
                : 'rgba(233,213,255,0.75)',
              lineHeight: 1.6,
              margin: 0,
            }}>
              {interruption.text}
            </p>
          </div>

          {/* Ligne décorative */}
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            style={{
              height: '1px',
              background: interruption.urgency === 'high'
                ? 'linear-gradient(90deg, transparent, #ef4444, transparent)'
                : 'linear-gradient(90deg, transparent, rgba(233,213,255,0.4), transparent)',
              transformOrigin: 'center',
            }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function SubThemeReveal({ subTheme, onDone }) {
  useEffect(() => {
    if (!subTheme) return;
    const timer = setTimeout(onDone, 6000); // 6s max, le joueur peut skip
    return () => clearTimeout(timer);
  }, [subTheme, onDone]);

  return (
    <AnimatePresence>
      {subTheme && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          onClick={onDone}  // ← clic n'importe où pour skip
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 30,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(2,6,23,0.78)',
            backdropFilter: 'blur(3px)',
            cursor: 'pointer',
            pointerEvents: 'all', // ← permet le clic
          }}
        >
          <div style={{ textAlign: 'center', maxWidth: '420px', padding: '0 1.5rem' }}>
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              <div style={{
                fontFamily: 'monospace',
                fontSize: '0.35rem',
                letterSpacing: '0.3em',
                color: 'rgba(233,213,255,0.4)',
                textTransform: 'uppercase',
                marginBottom: '0.5rem',
              }}>
                SOPHIA RÉVÈLE
              </div>
              <div style={{
                fontFamily: "'Cinzel', serif",
                fontSize: 'clamp(0.8rem, 3.5vw, 1.1rem)',
                color: '#e9d5ff',
                letterSpacing: '0.08em',
                textShadow: '0 0 30px rgba(233,213,255,0.4)',
                marginBottom: '0.8rem',
                lineHeight: 1.3,
              }}>
                {subTheme.text}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 0.8 }}
            >
              <div style={{
                height: '1px',
                background: 'linear-gradient(90deg, transparent, rgba(233,213,255,0.3), transparent)',
                margin: '0.5rem 0',
              }}/>
              <p style={{
                fontFamily: 'serif',
                fontStyle: 'italic',
                fontSize: 'clamp(0.55rem, 2vw, 0.7rem)',
                color: 'rgba(233,213,255,0.55)',
                lineHeight: 1.7,
                margin: '0 0 0.75rem',
              }}>
                {subTheme.sophiaLine}
              </p>

              {/* Bouton skip visible */}
              <motion.div
                animate={{ opacity: [0.35, 0.7, 0.35] }}
                transition={{ duration: 1.8, repeat: Infinity }}
                style={{
                  fontFamily: 'monospace',
                  fontSize: '0.38rem',
                  letterSpacing: '0.22em',
                  color: 'rgba(233,213,255,0.35)',
                  textTransform: 'uppercase',
                }}
              >
                TOUCHER POUR CONTINUER
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function getRandomWisdomQuote(charId) {
  const quotes = WISDOM_QUOTES[charId] || [];
  return quotes[Math.floor(Math.random() * quotes.length)] || '';
}

function getRang(niveau) { return RANGS.find(r => niveau >= r.min && niveau <= r.max) || RANGS[0]; }

function getXPForLevel(n) { return Math.floor(100 * Math.pow(1.35, n - 1)); }

function addXP(prog, gained) {
  let { xp, niveau } = prog;
  xp += gained;
  while (xp >= getXPForLevel(niveau + 1)) { xp -= getXPForLevel(niveau + 1); niveau++; }
  return { ...prog, xp, niveau, slotsCombo: Math.min(3, Math.floor(niveau / 5)) };
}
 
function saveFragment(fragmentId) {
  try {
    const frags = JSON.parse(localStorage.getItem('judoverbal_fragments') || '[]');
    if (!frags.includes(fragmentId)) {
      frags.push(fragmentId);
      localStorage.setItem('judoverbal_fragments', JSON.stringify(frags));
    }
  } catch {}
}

function loadFragments() {
  try { return JSON.parse(localStorage.getItem('judoverbal_fragments') || '[]'); } catch { return []; }
}

function WisdomFlash({ fragment, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 5000); return () => clearTimeout(t); }, [onDone]);
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.85 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      style={{
        position: 'absolute', inset: 0, zIndex: 35,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(2,6,23,0.88)', backdropFilter: 'blur(6px)',
        pointerEvents: 'none',
      }}
    >
      <motion.div
        animate={{ boxShadow: ['0 0 30px #f59e0b44', '0 0 80px #f59e0b88', '0 0 30px #f59e0b44'] }}
        transition={{ repeat: Infinity, duration: 1.5 }}
        style={{
          maxWidth: 480, textAlign: 'center',
          padding: '2rem 2.5rem',
          background: 'rgba(2,6,23,0.97)',
          border: '1px solid #f59e0b66',
        }}
      >
        <div style={{ fontFamily: 'monospace', fontSize: '0.42rem', letterSpacing: '0.4em', color: '#f59e0b', textTransform: 'uppercase', marginBottom: '1rem' }}>
          ✦ FRAGMENT DE SAGESSE DÉBLOQUÉ ✦
        </div>
        <p style={{ fontFamily: 'serif', fontStyle: 'italic', fontSize: 'clamp(0.8rem,3.5vw,1.1rem)', color: 'rgba(255,255,255,0.85)', lineHeight: 1.7, marginBottom: '0.8rem' }}>
          {fragment.text}
        </p>
        <div style={{ fontFamily: 'monospace', fontSize: '0.48rem', letterSpacing: '0.15em', color: 'rgba(255,255,255,0.3)' }}>
          — {fragment.source}
        </div>
        <div style={{ marginTop: '1rem', fontFamily: 'monospace', fontSize: '0.42rem', color: 'rgba(245,158,11,0.4)', letterSpacing: '0.2em' }}>
          Consultable dans le Livret → Fragments
        </div>
      </motion.div>
    </motion.div>
  );
}

function resolveAIChar(aiChar) {
  const source = TOUS_PERSONNAGES.find(p => p.id === aiChar.sourceCharId);
  return {
    ...aiChar,
    image: source ? source.image : null,
    avatarColor: source ? source.avatarColor : aiChar.color,
    nom: aiChar.nom,
  };
}

function buildDeck(char) {
  const shared = char.deckIds.map(id => ({ ...CONCEPTS_SHARED[id], ownTradition: true })).filter(Boolean);
  const cross = (char.crossIds || []).map(id => ({ ...CONCEPTS_SHARED[id], ownTradition: false })).filter(Boolean);
  const propres = char.propres.map(c => ({ ...c, ownTradition: true }));
  const ultime = ULTIMES[char.id] ? { ...ULTIMES[char.id], ownTradition: true } : null;
  return [...propres, ...shared, ...cross, ...(ultime ? [ultime] : [])];
}

function buildAIDeck(aiChar) {
  const special = (aiChar.specialDeck || [])
    .map(id => CONCEPTS_SHARED[id])
    .filter(Boolean);
  return [...DECK_ADVERSAIRE_BASE, ...special]
    .filter((c, i, arr) => arr.findIndex(x => x.id === c.id) === i);
}



function FlowModal({ streak, charId, onDone }) {
  const isMobile = useIsMobile();
  const quotes = FLOW_QUOTES[charId] || FLOW_QUOTES.kant;
  const milestone = [...FLOW_MILESTONES].reverse().find(m => streak >= m) || 3;
  const quoteObj = quotes.find(q => q.streak === milestone) || quotes[0];
  const intensityColors = { 3: '#22d3ee', 5: '#a78bfa', 7: '#f59e0b', 10: '#ef4444' };
  const color = intensityColors[milestone] || '#22d3ee';
  const intensityLabel = { 3: 'FLUX', 5: 'ÉLAN', 7: 'MUSHIN', 10: 'TRANSCENDANCE' }[milestone] || 'FLUX';

  useEffect(() => {
    const t = setTimeout(onDone, 1200);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
      style={{
        position: 'absolute',
        bottom: isMobile ? '80px' : '40px',
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'center',
        zIndex: 25,
        pointerEvents: 'none',
      }}
    >
      <div style={{
        maxWidth: isMobile ? '92vw' : '420px',
        width: 'fit-content',
        background: 'rgba(2, 6, 23, 0.97)',
        border: `1px solid ${color}55`,
        boxShadow: `0 0 40px ${color}33, inset 0 0 30px rgba(0,0,0,0.6)`,
        padding: '0.9rem 1.25rem',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Barre de progression */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 3.5, ease: 'linear' }}
          style={{
            position: 'absolute', top: 0, left: 0, right: 0, height: '2px',
            background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
            transformOrigin: 'left',
          }}
        />
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
          {/* Indicateur pulsant */}
          <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.35rem', paddingTop: '0.1rem' }}>
            <motion.div
              animate={{ scale: [1, 1.3, 1], opacity: [0.7, 1, 0.7] }}
              transition={{ repeat: Infinity, duration: 1.2 }}
              style={{ width: 10, height: 10, borderRadius: '50%', background: color, boxShadow: `0 0 12px ${color}` }}
            />
            <span style={{ fontFamily: 'monospace', fontSize: '0.38rem', letterSpacing: '0.2em', color, textTransform: 'uppercase', writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
              ×{streak}
            </span>
          </div>
          {/* Contenu */}
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
              <span style={{ fontFamily: 'monospace', fontSize: '0.42rem', letterSpacing: '0.3em', color, textTransform: 'uppercase', fontWeight: 700 }}>
                {intensityLabel}
              </span>
              <motion.div
                animate={{ opacity: [0.4, 1, 0.4] }}
                transition={{ repeat: Infinity, duration: 0.8 }}
                style={{ flex: 1, height: '1px', background: `linear-gradient(90deg, ${color}44, transparent)` }}
              />
            </div>
            <p style={{ fontFamily: 'serif', fontStyle: 'italic', fontSize: 'clamp(0.65rem, 2.8vw, 0.82rem)', color: 'rgba(255,255,255,0.8)', lineHeight: 1.6, margin: '0 0 0.3rem' }}>
              « {quoteObj.quote} »
            </p>
            <div style={{ fontFamily: 'monospace', fontSize: '0.42rem', letterSpacing: '0.15em', color: 'rgba(255,255,255,0.25)' }}>
              — {quoteObj.source}
            </div>
            <div style={{ marginTop: '0.45rem', fontFamily: 'monospace', fontSize: '0.48rem', color: color + 'cc', letterSpacing: '0.1em' }}>
              Votre pensée est créative. Vous gagnez en puissance.
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function FlowHalo({ streak, color }) {
  if (streak < 3) return null;
  const intensity = Math.min(1, (streak - 3) / 7);
  const size = 60 + intensity * 30;
  return (
    <motion.div
      style={{
        position: 'absolute', inset: 0, borderRadius: '8px',
        background: `radial-gradient(ellipse at 50% 70%, ${color}${Math.floor(intensity * 40 + 10).toString(16).padStart(2,'0')} 0%, transparent 70%)`,
        pointerEvents: 'none', zIndex: 4,
      }}
      animate={{ opacity: [0.5, 0.9, 0.5] }}
      transition={{ repeat: Infinity, duration: 2 - intensity * 0.8 }}
    />
  );
}

function ConceptCard({
  hasSeen, unreadKnowledge, onOpenKnowledge,
  concept, onClick, disabled, playerEnergy,
  maCount, activePlayer, selectedChar, playerNiveau, lastPlayerConceptId
}) {
  const [hov, setHov] = useState(false);
  const [justPlayed, setJustPlayed] = useState(false);
  const isMobile = useIsMobile();

  const isOwnEpoque   = !concept.epoque || concept.epoque === 'universel' || concept.isUltimate || concept.epoque === selectedChar.tradition;
  const isUltimate    = concept.isUltimate;
  const crossCost     = isOwnEpoque ? 0 : 10;
  const passifMod     = selectedChar?.passif ? selectedChar.passif() : {};
  const effectiveCross = crossCost - (passifMod.crossEpochDiscount || 0);
  const totalCost     = concept.cost + Math.max(0, effectiveCross);

  const canUse           = playerEnergy >= totalCost && !(concept.id === 'ma' && maCount >= 3);
  const ultimateUnlocked = !isUltimate || (playerNiveau >= 5 && concept.charId === selectedChar?.id);
  const isActiveTurn     = activePlayer === 'player';
  const usable           = !disabled && canUse && isActiveTurn && ultimateUnlocked;
  const synergyActive    = lastPlayerConceptId && checkSynergy(lastPlayerConceptId, concept.id);

  const nature = concept.drain > 0 && concept.gripAdd >= 0 ? 'attaque'
    : concept.drain === 0 || concept.gripAdd < 0 ? 'defense' : 'neutre';
  const nm = {
    attaque: { label: '⚔', color: '#ef4444' },
    defense: { label: '🛡', color: '#22d3ee' },
    neutre:  { label: '◈', color: '#94a3b8' },
  }[nature];

  const handleClick = () => {
  if (!usable) return;
  setJustPlayed(true);
  setTimeout(() => setJustPlayed(false), 650);
  onClick(concept);   // onClick devient onSelectConcept — voir parent
};

  // Couleur de bordure selon état
  const borderColor = synergyActive   ? synergyActive.color
    : isUltimate                      ? concept.color + '88'
    : usable && hov                   ? concept.color + '66'
    : 'rgba(120,130,160,0.2)';

  const hasKnowledge = hasSeen(concept.id) && KNOWLEDGE_BASE[concept.id];
  const isUnread     = unreadKnowledge?.includes(concept.id);

  return (
    <motion.div
      layout
      onHoverStart={() => setHov(true)}
      onHoverEnd={() => setHov(false)}
      animate={{
        scale: justPlayed ? [1, 1.05, 0.97, 1] : 1,
        y: hov && usable && !isMobile ? -4 : 0,
      }}
      style={{ position: 'relative', width: '100%', height: '100%' }}
    >
      <motion.button
        whileTap={usable ? { scale: 0.95 } : {}}
        onClick={handleClick}
        style={{
          width: '100%',
          height: '100%',
          background: synergyActive
            ? 'rgba(30,25,40,0.92)'
            : justPlayed ? 'rgba(20,20,30,0.95)'
            : 'rgba(10,16,32,0.88)',
          border: `1.5px solid ${borderColor}`,
          boxShadow: hov && usable
            ? `0 6px 20px -6px rgba(0,0,0,0.8), 0 0 28px ${concept.color}30`
            : '0 4px 14px -6px rgba(0,0,0,0.7)',
          borderRadius: '6px',
          // ── Padding adaptatif ──
          padding: isMobile ? '0.5rem 0.55rem' : '0.8rem 0.9rem',
          display: 'flex',
          flexDirection: 'column',
          gap: isMobile ? '0.25rem' : '0.4rem',
          textAlign: 'left',
          position: 'relative',
          overflow: 'hidden',
          transition: 'border-color 0.2s, box-shadow 0.2s',
          cursor: usable ? 'pointer' : 'default',
        }}
      >
        {/* Barre top (synergie / ultime) */}
        {(synergyActive || isUltimate) && (
          <div style={{
            position: 'absolute', top: -1, left: -1, right: -1, height: '2px',
            background: synergyActive
              ? synergyActive.color
              : `linear-gradient(90deg, transparent, ${concept.color}, transparent)`,
          }} />
        )}

        {/* ── LIGNE 1 : label + kanji ── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.3rem' }}>
          <span style={{
            fontFamily: 'monospace',
            fontSize: isMobile ? '0.6rem' : '0.73rem',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: usable ? '#f1f5f9' : 'rgba(241,245,249,0.45)',
            fontWeight: 700,
            lineHeight: 1.2,
            flex: 1,
          }}>
            {concept.label}
          </span>
          {/* Kanji — masqué sur très petit mobile pour gagner de la place */}
          {!isMobile && (
            <span style={{
              fontFamily: 'serif', fontSize: '1.15rem',
              color: concept.color + '60', flexShrink: 0, marginTop: '-2px',
            }}>
              {concept.kanji}
            </span>
          )}
        </div>

        {/* ── LIGNE 2 : description — 2 lignes max sur mobile, 3 sur desktop ── */}
        <p style={{
          fontSize: isMobile ? '0.58rem' : '0.65rem',
          color: hov && usable ? '#e2e8f0' : 'rgba(163,180,208,0.75)',
          lineHeight: 1.45,
          margin: 0,
          flex: 1,
          display: '-webkit-box',
          WebkitLineClamp: isMobile ? 2 : 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}>
          {concept.desc}
        </p>

        {/* ── LIGNE 3 : badges coût / drain / nature ── */}
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: '0.25rem',
          alignItems: 'center', marginTop: 'auto',
        }}>
          {/* Nature (icône seule sur mobile) */}
          <span style={{
            fontFamily: 'monospace',
            fontSize: isMobile ? '0.5rem' : '0.46rem',
            padding: isMobile ? '1px 4px' : '1px 5px',
            border: `1px solid ${nm.color}60`,
            color: nm.color, borderRadius: '3px',
          }}>
            {isMobile ? nm.label : `${nm.label} ${nature.toUpperCase()}`}
          </span>

          {/* Coût */}
          <span style={{
            fontFamily: 'monospace',
            fontSize: isMobile ? '0.52rem' : '0.52rem',
            color: totalCost === 0 ? '#4ade80' : usable ? '#cbd5e1' : 'rgba(203,213,225,0.35)',
            fontWeight: totalCost === 0 ? 700 : 400,
          }}>
            {totalCost === 0 ? '◆ FREE' : `▼${totalCost}`}
          </span>

          {/* Drain */}
          {concept.drain > 0 && (
            <span style={{
              fontFamily: 'monospace', fontSize: isMobile ? '0.52rem' : '0.52rem',
              color: usable ? '#f87171' : 'rgba(248,113,113,0.35)',
            }}>
              -{concept.drain}
            </span>
          )}

          {/* Dégripe */}
          {concept.gripAdd < 0 && (
            <span style={{ fontFamily: 'monospace', fontSize: '0.48rem', color: '#4ade80' }}>
              ✦
            </span>
          )}

          {/* Tags ultime / synergie */}
          {isUltimate && (
            <span style={{ fontFamily: 'monospace', fontSize: '0.46rem', color: concept.color }}>⚡U</span>
          )}
          {synergyActive && (
            <span style={{ fontFamily: 'monospace', fontSize: '0.46rem', color: synergyActive.color, fontWeight: 700 }}>⚡S</span>
          )}
        </div>
      </motion.button>

      {/* Overlay inutilisable */}
      {!usable && !disabled && (
        <div style={{
          position: 'absolute', inset: 0,
          background: 'rgba(10,18,40,0.82)',
          borderRadius: '6px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 3,
        }}>
          <span style={{
            fontSize: isMobile ? '0.52rem' : '0.6rem',
            color: '#fda4af', textAlign: 'center',
            fontFamily: 'monospace',
            padding: '0.3rem 0.6rem',
            background: 'rgba(0,0,0,0.55)',
            borderRadius: '3px',
          }}>
            {!ultimateUnlocked ? '🧠 Niv.5'
              : playerEnergy < totalCost ? `▼${totalCost}`
              : 'MA ×3'}
          </span>
        </div>
      )}

      {/* Bouton connaissance */}
      {hasKnowledge && (
        <button
          onClick={e => { e.stopPropagation(); onOpenKnowledge(concept.id, 'concept'); }}
          style={{
            position: 'absolute',
            top: isMobile ? '4px' : '6px',
            right: isMobile ? '4px' : '6px',
            background: isUnread ? 'rgba(245,158,11,0.25)' : 'rgba(167,139,250,0.12)',
            border: `1px solid ${isUnread ? 'rgba(245,158,11,0.6)' : 'rgba(167,139,250,0.25)'}`,
            borderRadius: '3px',
            padding: '1px 4px',
            fontSize: '0.55rem',
            cursor: 'pointer',
            color: isUnread ? '#fbbf24' : 'rgba(167,139,250,0.7)',
            zIndex: 4,
            animation: isUnread ? 'glow-pulse 1.2s ease-in-out infinite' : 'none',
            boxShadow: isUnread ? '0 0 10px rgba(245,158,11,0.45)' : 'none',
          }}
        >
          📖
        </button>
      )}
    </motion.div>
  );
}

function PunchlineCard({ hasSeen, unreadKnowledge, onOpenKnowledge, item, onPlay, energy, selectedChar }) {
  const [hov, setHov] = useState(false);
  const [justPlayed, setJustPlayed] = useState(false);
  const isMobile = useIsMobile();

  const cost    = item.cost  || 12;
  const drain   = item.drain || 0;
  const canPlay = energy >= cost;
  const isAtk   = item.category === 'attaque';
  const isDef   = item.category === 'defense';

  const nmColor = isAtk ? '#ef4444' : isDef ? '#22d3ee' : '#94a3b8';
  const nmLabel = isAtk ? '⚔' : isDef ? '🛡' : '◈';

  const handleClick = () => {
    if (!canPlay) return;
    setJustPlayed(true);
    setTimeout(() => setJustPlayed(false), 500);
    onPlay({
      id: item.id,
      label: item.texte?.slice(0, 50) + (item.texte?.length > 50 ? '…' : ''),
      cost, drain,
      gripAdd:  item.gripAdd  || 0,
      color:    item.color    || selectedChar.avatarColor,
      desc:     item.texte,
      texte:    item.texte,
      ownTradition: true,
      epoque:   selectedChar.tradition,
    });
  };

  const hasKnowledge = hasSeen(item.id) && KNOWLEDGE_PUNCHLINES[item.id];
  const isUnread     = unreadKnowledge?.includes(item.id);

  return (
    <motion.button
      whileHover={canPlay && !isMobile ? { y: -2 } : {}}
      whileTap={canPlay ? { scale: 0.97 } : {}}
      onHoverStart={() => setHov(true)}
      onHoverEnd={() => setHov(false)}
      onClick={handleClick}
      style={{
        width: '100%', textAlign: 'left',
        // ── Padding adaptatif ──
        padding: isMobile ? '0.55rem 0.65rem 0.55rem 0.75rem' : '0.75rem 1rem 0.75rem 0.9rem',
        background: justPlayed ? `${item.color}22`
          : hov && canPlay ? `${item.color}0d`
          : 'rgba(255,255,255,0.02)',
        border: `1px solid ${justPlayed ? item.color
          : hov && canPlay ? item.color + '44'
          : 'rgba(255,255,255,0.06)'}`,
        cursor: canPlay ? 'pointer' : 'not-allowed',
        opacity: canPlay ? 1 : 0.3,
        transition: 'all 0.18s',
        position: 'relative',
        borderRadius: '4px',
      }}
    >
      {/* Barre latérale de nature */}
      <div style={{
        position: 'absolute', left: 0, top: 0, bottom: 0, width: '2px',
        background: isAtk ? '#ef444466' : isDef ? '#22d3ee66' : '#94a3b844',
        borderRadius: '4px 0 0 4px',
      }} />

      {/* ── Ligne badges ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: isMobile ? '0.3rem' : '0.4rem', flexWrap: 'wrap' }}>
        <span style={{
          fontFamily: 'monospace',
          fontSize: isMobile ? '0.48rem' : '0.42rem',
          color: nmColor,
          border: `1px solid ${nmColor}33`,
          padding: '0.05rem 0.3rem',
          textTransform: 'uppercase', letterSpacing: '0.08em',
        }}>
          {/* Sur mobile : icône seule */}
          {isMobile ? nmLabel : `${nmLabel} ${isAtk ? 'ATTAQUE' : isDef ? 'DÉFENSE' : 'NEUTRE'}`}
        </span>

        <span style={{
          fontFamily: 'monospace',
          fontSize: '0.44rem',
          color: cost === 0 ? '#10b981' : 'rgba(255,255,255,0.28)',
        }}>▼{cost}E</span>

        {drain > 0 && (
          <span style={{ fontFamily: 'monospace', fontSize: '0.44rem', color: 'rgba(239,68,68,0.5)' }}>
            -{drain}
          </span>
        )}
        {item.gripAdd < 0 && (
          <span style={{ fontFamily: 'monospace', fontSize: '0.44rem', color: '#10b981' }}>✦</span>
        )}
      </div>

      {/* ── Texte de la punchline ── */}
      <p style={{
        fontFamily: 'serif', fontStyle: 'italic',
        fontSize: isMobile ? 'clamp(0.58rem, 2.8vw, 0.72rem)' : 'clamp(0.62rem, 2.5vw, 0.78rem)',
        color: hov && canPlay ? 'rgba(255,255,255,0.82)' : 'rgba(255,255,255,0.5)',
        lineHeight: 1.5, margin: 0,
        // Tronquer à 3 lignes sur mobile
        display: '-webkit-box',
        WebkitLineClamp: isMobile ? 3 : 99,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        transition: 'color 0.18s',
      }}>
        « {item.texte} »
      </p>

      {/* Bouton connaissance */}
      {hasKnowledge && (
        <span
            role="button"
            tabIndex={0}
            onClick={e => { e.stopPropagation(); onOpenKnowledge(item.id, 'punchline'); }}
            onKeyDown={e => { if (e.key === 'Enter') onOpenKnowledge(item.id, 'punchline'); }}
            style={{
            position: 'absolute',
            top: isMobile ? '4px' : '6px',
            right: isMobile ? '4px' : '6px',
            background: isUnread ? 'rgba(245,158,11,0.25)' : 'rgba(167,139,250,0.12)',
            border: `1px solid ${isUnread ? 'rgba(245,158,11,0.6)' : 'rgba(167,139,250,0.25)'}`,
            borderRadius: '3px',
            padding: '1px 4px',
            fontSize: '0.52rem',
            cursor: 'pointer',
            color: isUnread ? '#fbbf24' : 'rgba(167,139,250,0.7)',
            zIndex: 4,
            animation: isUnread ? 'glow-pulse 1.2s ease-in-out infinite' : 'none',
            boxShadow: isUnread ? '0 0 10px rgba(245,158,11,0.45)' : 'none',
            lineHeight: 1,
            }}
        >
            📖
        </span>
      )}
    </motion.button>
  );
}

function PunchlineWheel({
  options,
  conceptColor,
  conceptLabel,
  onSelect,
  onSkip,
  playerGrip,
  opponentGrip,
  playerEnergy,
  themeId,
  characterId,
  roundPhase,
  opponentName,
}) {
  const [selected, setSelected] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const isMobile = useIsMobile();

  const charTheme = CHAR_THEME_MAP[characterId] || null;
  const isResonance = charTheme && themeId && charTheme.resonance.includes(themeId);
  const isFriction = charTheme && themeId && charTheme.friction.includes(themeId);

  const handleSelect = (punchline) => {
    if (selected !== null) return;
    setSelected(punchline.id);
    setTimeout(() => onSelect(punchline), 420);
  };

  useEffect(() => {
    setSelected(null);
    setIsCollapsed(false);
  }, [options]);

  const getSubtitle = () => {
    if (playerGrip <= 25) return `⚠ Votre emprise vacille. Chaque mot compte.`;
    if (opponentGrip >= 75) return `${opponentName} domine. Une vérité peut tout renverser.`;
    if (roundPhase === 'final') return `Dernière frappe. L'Histoire retiendra ce choix.`;
    if (isResonance) return `${conceptLabel} résonne avec votre nature. L'écho sera puissant.`;
    if (isFriction) return `Ce thème n'est pas le vôtre. Mais une vérité reste une vérité.`;
    return `${opponentName} attend. Quelle vérité allez-vous opposer ?`;
  };

  // ── ÉTAT RÉDUIT (miniature flottante en BAS À DROITE) ───────────────
  if (isCollapsed) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0 }}
        onClick={() => setIsCollapsed(false)}
        style={{
          position: 'fixed',
          bottom: isMobile ? '1rem' : '1.5rem',
          right: isMobile ? '1rem' : '1.5rem',  // ← BAS À DROITE
          zIndex: 55,
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',               // ← aligné à droite
          gap: '0.3rem',
          pointerEvents: 'auto',
        }}
      >
        <div style={{
          background: 'rgba(8, 14, 28, 0.92)',
          border: `1.5px solid ${conceptColor}66`,
          borderRadius: '10px',
          padding: isMobile ? '0.5rem 0.9rem' : '0.6rem 1.1rem',
          boxShadow: `0 6px 24px rgba(0,0,0,0.6), 0 0 18px ${conceptColor}18`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '0.2rem',
        }}>
          <span style={{
            fontFamily: "'Cinzel', serif",
            fontSize: isMobile ? '0.65rem' : '0.75rem',
            color: conceptColor,
            fontWeight: 600,
            letterSpacing: '0.06em',
          }}>
            🎯 {conceptLabel}
          </span>
          <span style={{
            fontFamily: 'monospace',
            fontSize: '0.45rem',
            color: 'rgba(255,255,255,0.35)',
            letterSpacing: '0.06em',
          }}>
            TOUCHER POUR OUVRIR
          </span>
        </div>

        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: [0, 1, 1, 0], y: [0, -6, -6, -10] }}
          transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
          style={{
            background: 'rgba(0,0,0,0.75)',
            borderRadius: '4px',
            padding: '3px 8px',
            maxWidth: '200px',
            textAlign: 'center',
          }}
        >
          <span style={{
            fontFamily: 'monospace',
            fontSize: '0.48rem',
            color: 'rgba(255,255,255,0.5)',
            letterSpacing: '0.04em',
          }}>
            Cliquez en dehors pour changer de concept
          </span>
        </motion.div>
      </motion.div>
    );
  }

  // ── ÉTAT NORMAL ──────────────────────────────────────────────────────
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        display: 'flex',
        alignItems: isMobile ? 'flex-end' : 'center',
        justifyContent: 'center',
      }}
    >
      {/* Overlay SANS FLOU — fond sombre uni pour rester dans le jeu */}
      <div
        onClick={(e) => {
          e.stopPropagation();
          setIsCollapsed(true);
        }}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0,0,0,0.35)',    // ← pas de flou, juste assombrissement léger
          cursor: 'pointer',
        }}
      />

      <motion.div
        initial={isMobile 
          ? { opacity: 0, y: '100%' } 
          : { opacity: 0, scale: 0.95, y: 10 }
        }
        animate={isMobile 
          ? { opacity: 1, y: 0 } 
          : { opacity: 1, scale: 1, y: 0 }
        }
        exit={isMobile 
          ? { opacity: 0, y: '100%' } 
          : { opacity: 0, scale: 0.95, y: 10 }
        }
        transition={{ type: 'spring', stiffness: 340, damping: 28 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          ...(isMobile ? {
            width: '100%',
            borderRadius: '16px 16px 0 0',
            borderTop: `2px solid ${conceptColor}88`,
            maxHeight: '75vh',
          } : {
            width: 'min(88vw, 560px)',
            borderRadius: '10px',
            border: `1px solid ${conceptColor}44`,
            maxHeight: '85vh',
          }),
          background: 'rgba(8, 14, 28, 0.98)',
          padding: isMobile 
            ? '1rem 1rem calc(1.2rem + env(safe-area-inset-bottom, 0px))' 
            : '1.4rem 1.4rem 1.2rem',
          boxShadow: isMobile
            ? `0 -8px 40px rgba(0,0,0,0.75), 0 0 32px ${conceptColor}18`
            : `0 24px 64px rgba(0,0,0,0.85), 0 0 48px ${conceptColor}1a`,
          display: 'flex',
          flexDirection: 'column',
          gap: isMobile ? '0.6rem' : '0.75rem',
          zIndex: 1,
        }}
      >
        {/* ── Poignée drawer (mobile) ── */}
        {isMobile && (
          <div style={{
            width: '42px',
            height: '4px',
            borderRadius: '2px',
            background: `${conceptColor}55`,
            margin: '0 auto',
            flexShrink: 0,
          }} />
        )}

        {/* ── En-tête ── */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '0.3rem',
          flexShrink: 0,
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '0.6rem',
          }}>
            <span style={{
              fontFamily: "'Cinzel', serif",
              fontSize: isMobile ? '0.8rem' : '0.95rem',
              fontWeight: 500,
              color: isFriction ? '#fbbf24' : conceptColor,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
            }}>
              {isResonance && <span style={{ fontSize: '0.75rem' }}>✦</span>}
              {isFriction && <span style={{ color: '#fbbf24', fontSize: '0.75rem' }}>⚡</span>}
              {conceptLabel}
              {isResonance && <span style={{ fontSize: '0.75rem' }}>✦</span>}
            </span>

            <motion.button
              whileHover={{ opacity: 1 }}
              whileTap={{ scale: 0.94 }}
              onClick={(e) => {
                e.stopPropagation();
                if (selected !== null) return;
                onSkip();
              }}
              disabled={selected !== null}
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: '4px',
                padding: isMobile ? '5px 14px' : '4px 12px',
                fontFamily: 'monospace',
                fontSize: isMobile ? '0.55rem' : '0.55rem',
                color: 'rgba(255,255,255,0.35)',
                cursor: 'pointer',
                letterSpacing: '0.1em',
                opacity: 0.7,
                transition: 'opacity 0.15s',
                flexShrink: 0,
                touchAction: 'manipulation',
              }}
            >
              PASSER →
            </motion.button>

            {!isMobile && (
              <motion.button
                whileHover={{ opacity: 1 }}
                whileTap={{ scale: 0.94 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsCollapsed(true);
                }}
                style={{
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  fontFamily: 'monospace',
                  fontSize: '0.55rem',
                  color: 'rgba(255,255,255,0.25)',
                  cursor: 'pointer',
                  letterSpacing: '0.1em',
                  opacity: 0.5,
                  transition: 'opacity 0.15s',
                  flexShrink: 0,
                  touchAction: 'manipulation',
                }}
                title="Réduire pour voir le duel"
              >
                _
              </motion.button>
            )}
          </div>

          <p style={{
            fontFamily: 'serif',
            fontStyle: 'italic',
            fontSize: isMobile ? '0.58rem' : '0.7rem',
            lineHeight: 1.4,
            color: playerGrip <= 25
              ? 'rgba(248,113,113,0.6)'
              : isResonance
                ? `${conceptColor}bb`
                : 'rgba(255,255,255,0.3)',
            margin: 0,
            transition: 'color 0.3s',
          }}>
            {getSubtitle()}
          </p>
        </div>

        {/* ── Séparateur ── */}
        <div style={{
          height: '1px',
          background: `linear-gradient(90deg, transparent, ${isFriction ? '#fbbf24' : conceptColor}44, transparent)`,
          flexShrink: 0,
        }} />

        {/* ── Options ── */}
        <div
          className="hide-scrollbar"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: isMobile ? '0.5rem' : '0.55rem',
            overflowY: 'auto',
            flex: '1 1 auto',
            minHeight: 0,
          }}
        >
          {options.map((p, i) => {
            const isSelected = selected === p.id;
            const isDisabled = selected !== null && !isSelected;
            
            const newPlayerGrip = playerGrip + (p.gripAdd || 0);
            const newOpponentGrip = Math.max(0, opponentGrip - (p.drain || 0));
            const energyDelta = p.cost ? `−${p.cost} NRJ` : null;
            const isOnTheme = p.themeBonus;
            const isOffTheme = themeId && !isOnTheme && p.themeBonus === false;

            return (
              <motion.button
                key={p.id}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.06 }}
                whileHover={!isDisabled && selected === null && !isMobile ? { x: 4 } : {}}
                whileTap={selected === null ? { scale: 0.985 } : {}}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelect(p);
                }}
                disabled={selected !== null}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: isMobile
                    ? '0.75rem 0.85rem 0.75rem 1rem'
                    : '0.8rem 0.95rem 0.8rem 1.15rem',
                  background: isSelected 
                    ? `${p.color}12` 
                    : 'rgba(255,255,255,0.018)',
                  border: `1.5px solid ${
                    isSelected
                      ? p.color
                      : isOffTheme && !isFriction
                        ? 'rgba(248,113,113,0.22)'
                        : isFriction && isOffTheme
                          ? 'rgba(251,191,36,0.22)'
                          : 'rgba(255,255,255,0.07)'
                  }`,
                  borderRadius: '6px',
                  cursor: selected !== null ? 'default' : 'pointer',
                  opacity: isDisabled ? 0.35 : 1,
                  transition: 'all 0.18s',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: isMobile ? '0.4rem' : '0.4rem',
                  position: 'relative',
                  overflow: 'hidden',
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: `${p.color}22`,
                  flexShrink: 0,
                }}
              >
                {/* Barre latérale */}
                <motion.div
                  animate={{
                    scaleY: isSelected ? 1 : 0.6,
                    opacity: isSelected ? 1 : isOnTheme ? 0.55 : 0.3
                  }}
                  style={{
                    position: 'absolute',
                    left: 0, top: 0, bottom: 0,
                    width: '3px',
                    background: `linear-gradient(180deg, ${p.color}, ${p.color}33)`,
                    borderRadius: '6px 0 0 6px',
                    transformOrigin: 'center',
                  }}
                />

                {/* Badges */}
                <div style={{
                  display: 'flex',
                  gap: '0.4rem',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}>
                  <span style={{
                    fontFamily: 'monospace',
                    fontSize: isMobile ? '0.48rem' : '0.55rem',
                    fontWeight: 600,
                    padding: isMobile ? '2px 7px' : '2px 7px',
                    border: `1.5px solid ${p.color}44`,
                    color: isFriction && isOffTheme ? '#fbbf24' : p.color,
                    borderRadius: '3px',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                  }}>
                    {p.contextLabel || 'ATTAQUE'}
                  </span>

                  {isOnTheme && (
                    <span style={{
                      fontFamily: 'monospace',
                      fontSize: isMobile ? '0.46rem' : '0.52rem',
                      fontWeight: 600,
                      color: isResonance ? '#86efac' : '#fde68a',
                      letterSpacing: '0.06em',
                    }}>
                      {isResonance ? '✦ RÉSONANCE' : '★ THÈME'}
                    </span>
                  )}

                  {isFriction && isOffTheme && (
                    <span style={{
                      fontFamily: 'monospace',
                      fontSize: isMobile ? '0.46rem' : '0.52rem',
                      fontWeight: 600,
                      color: '#fbbf24',
                      letterSpacing: '0.06em',
                    }}>
                      ⚡ HORS-THÈME
                    </span>
                  )}

                  {/* ── BADGE DE SYNERGIE (NOUVEAU) ── */}
                  {p.synergyBadge && (
                    <span style={{
                      fontFamily: 'monospace',
                      fontSize: isMobile ? '0.44rem' : '0.5rem',
                      fontWeight: 600,
                      color: p.synergyBadgeColor || '#c084fc',
                      background: `${p.synergyBadgeColor || '#c084fc'}12`,
                      padding: '2px 7px',
                      borderRadius: '3px',
                      letterSpacing: '0.05em',
                    }}>
                      {p.synergyBadge}
                    </span>
                  )}
                </div>

                {/* Conséquences */}
                <div style={{
                  display: 'flex',
                  gap: '0.8rem',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  fontSize: isMobile ? '0.5rem' : '0.56rem',
                  fontFamily: 'monospace',
                }}>
                  {p.drain > 0 && (
                    <span style={{
                      color: 'rgba(248,113,113,0.8)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      background: 'rgba(248,113,113,0.05)',
                      padding: '1px 7px',
                      borderRadius: '3px',
                    }}>
                      <span style={{ opacity: 0.55, fontSize: '0.9em' }}>Adv</span>
                      <span style={{ fontWeight: 600 }}>{opponentGrip}%</span>
                      <span style={{ opacity: 0.45 }}>→</span>
                      <span style={{ color: '#fca5a5', fontWeight: 700 }}>{newOpponentGrip}%</span>
                    </span>
                  )}
                  {p.gripAdd !== 0 && (
                    <span style={{
                      color: p.gripAdd < 0 ? 'rgba(52,211,153,0.8)' : 'rgba(248,113,113,0.8)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      background: p.gripAdd < 0 
                        ? 'rgba(52,211,153,0.05)' 
                        : 'rgba(248,113,113,0.05)',
                      padding: '1px 7px',
                      borderRadius: '3px',
                    }}>
                      <span style={{ opacity: 0.55, fontSize: '0.9em' }}>Vous</span>
                      <span style={{ fontWeight: 600 }}>{playerGrip}%</span>
                      <span style={{ opacity: 0.45 }}>→</span>
                      <span style={{
                        color: newPlayerGrip < playerGrip ? '#6ee7b7' : '#fca5a5',
                        fontWeight: 700
                      }}>
                        {newPlayerGrip}%
                      </span>
                    </span>
                  )}
                  {energyDelta && (
                    <span style={{
                      color: 'rgba(251,191,36,0.6)',
                      marginLeft: 'auto',
                      fontWeight: 600,
                    }}>
                      {energyDelta}
                    </span>
                  )}
                  <span style={{
                    color: 'rgba(52,211,153,0.6)',
                    marginLeft: p.cost ? '0' : 'auto',
                    fontWeight: 600,
                  }}>
                    +{(isResonance && isOnTheme) ? (p.comboBonus || 35) + 10 : p.comboBonus || 35}% combo
                  </span>
                </div>

                {/* Texte */}
                <p style={{
                  fontFamily: 'Georgia, serif',
                  fontStyle: 'italic',
                  fontSize: isMobile
                    ? 'clamp(0.68rem, 3.2vw, 0.82rem)'
                    : 'clamp(0.75rem, 2vw, 0.85rem)',
                  lineHeight: 1.55,
                  color: isSelected ? 'rgba(255,255,255,0.93)' : 'rgba(255,255,255,0.68)',
                  margin: 0,
                  transition: 'color 0.18s',
                  letterSpacing: '0.01em',
                }}>
                  « {p.texte} »
                </p>
              </motion.button>
            );
          })}
        </div>

        {/* ── Pied ── */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.2rem',
          flexShrink: 0,
        }}>
          {isFriction && !options.some(o => o.themeBonus) && (
            <p style={{
              fontFamily: 'monospace',
              fontSize: isMobile ? '0.44rem' : '0.52rem',
              color: 'rgba(251,191,36,0.45)',
              textAlign: 'center',
              margin: 0,
              fontStyle: 'italic',
            }}>
              ⚡ Ce thème n'est pas le vôtre. L'impact sera réduit.
            </p>
          )}
          {!isFriction && !options.some(o => o.themeBonus) && (
            <p style={{
              fontFamily: 'monospace',
              fontSize: isMobile ? '0.44rem' : '0.52rem',
              color: 'rgba(255,255,255,0.16)',
              textAlign: 'center',
              margin: 0,
              fontStyle: 'italic',
            }}>
              Aucune punchline ne correspond au thème — choisissez la plus percutante.
            </p>
          )}
          
          <p style={{
            fontFamily: 'monospace',
            fontSize: isMobile ? '0.4rem' : '0.48rem',
            color: 'rgba(255,255,255,0.12)',
            textAlign: 'center',
            margin: 0,
            letterSpacing: '0.04em',
          }}>
            💡 Cliquez en dehors pour voir votre main et changer de concept
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}

function useDuelNarrative() {
  const [queueLeft, setQueueLeft] = useState([]);
  const [queueRight, setQueueRight] = useState([]);
  const [queueCenter, setQueueCenter] = useState([]);
  const [currentLeft, setCurrentLeft] = useState(null);
  const [currentRight, setCurrentRight] = useState(null);
  const [currentCenter, setCurrentCenter] = useState(null);
  const processingLeft = useRef(false);
  const processingRight = useRef(false);
  const processingCenter = useRef(false);

  const push = useCallback((msg) => {
    const side = msg.side ?? 'center';
    if (side === 'left') setQueueLeft(q => [...q, { ...msg, id: Date.now() + Math.random() }]);
    else if (side === 'right') setQueueRight(q => [...q, { ...msg, id: Date.now() + Math.random() }]);
    else setQueueCenter(q => [...q, { ...msg, id: Date.now() + Math.random() }]);
  }, []);

  const dismiss = useCallback((side) => {
    if (side === 'left') { setCurrentLeft(null); processingLeft.current = false; }
    else if (side === 'right') { setCurrentRight(null); processingRight.current = false; }
    else { setCurrentCenter(null); processingCenter.current = false; }
  }, []);

  const clearAll = useCallback(() => {
    setQueueLeft([]);   setQueueRight([]);  setQueueCenter([]);
    setCurrentLeft(null); setCurrentRight(null); setCurrentCenter(null);
    processingLeft.current = false;
    processingRight.current = false;
    processingCenter.current = false;
  }, []);

  // Défilement gauche
  useEffect(() => {
    if (!processingLeft.current && queueLeft.length > 0) {
      processingLeft.current = true;
      const [next, ...rest] = queueLeft;
      setQueueLeft(rest);
      setCurrentLeft(next);
    }
  }, [queueLeft, currentLeft]);

  // Défilement droite
  useEffect(() => {
    if (!processingRight.current && queueRight.length > 0) {
      processingRight.current = true;
      const [next, ...rest] = queueRight;
      setQueueRight(rest);
      setCurrentRight(next);
    }
  }, [queueRight, currentRight]);

  // Défilement centre
  useEffect(() => {
    if (!processingCenter.current && queueCenter.length > 0) {
      processingCenter.current = true;
      const [next, ...rest] = queueCenter;
      setQueueCenter(rest);
      setCurrentCenter(next);
    }
  }, [queueCenter, currentCenter]);

  return {
    messages: {
      left: currentLeft,
      right: currentRight,
      center: currentCenter,
    },
    push,
    dismiss,
    clearAll,
  };
}

function DuelNarrative({ messages, onDone }) {
  const isMobile = useIsMobile();
 
  if (!messages) return null;
 
  const side = messages.side ?? 'center';
 
  const getPositionStyle = () => {
    const base = { position: 'absolute', zIndex: 40 };
 
    if (isMobile) {
      return {
        ...base,
        top: side === 'left' ? '18%' : '42%',
        left: 0, right: 0,
        display: 'flex',
        justifyContent: 'center',
        // Cliquable sur mobile : zone de tap généreuse
        padding: '0 0.5rem',
      };
    }
 
    if (side === 'left') {
      return { ...base, top: '56%', left: '10%', width: 'min(38vw, 380px)', textAlign: 'left' };
    }
    if (side === 'right') {
      return { ...base, top: '56%', right: '5%', width: 'min(38vw, 380px)', textAlign: 'right' };
    }
    return {
      ...base,
      top: '32%', left: '50%', transform: 'translateX(-50%)',
      width: 'min(72vw, 480px)', textAlign: 'center',
    };
  };
 
  const handleClose = () => {
    // Passe l'id au parent pour que dismiss vérifie qu'on ferme le bon message
    onDone(messages.id);
  };
 
  return (
    <AnimatePresence mode="wait">
      {messages && (
        <motion.div
          key={messages.id}
          initial={{ opacity: 0, y: 12, scale: 0.96 }}
          animate={{ opacity: 1, y: 0,  scale: 1    }}
          exit={{    opacity: 0, y: -8,  scale: 0.98 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          onClick={handleClose}
          style={{
            ...getPositionStyle(),
            cursor: 'pointer',         // signal visuel : cliquable
            pointerEvents: 'all',      // capte les clics
          }}
        >
          <div style={{
            background: 'rgba(4, 8, 20, 0.92)',
            border: `1px solid ${messages.color ?? 'rgba(255,255,255,0.12)'}44`,
            borderRadius: '6px',
            padding: isMobile ? '0.7rem 0.9rem' : '0.85rem 1.1rem',
            backdropFilter: 'blur(16px)',
            boxShadow: `0 16px 48px rgba(0,0,0,0.7), 0 0 32px ${messages.color ?? 'rgba(255,255,255,0.1)'}18`,
            display: 'flex',
            flexDirection: 'column',
            gap: '0.35rem',
            position: 'relative',
            overflow: 'hidden',
            maxWidth: '85vw',
            width: 'fit-content',
          }}>
 
            {/* Ligne déco top colorée */}
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
              style={{
                position: 'absolute', top: 0, left: 0, right: 0, height: '2px',
                background: `linear-gradient(90deg, transparent, ${messages.color ?? '#c4b5fd'}, transparent)`,
                transformOrigin: 'left',
              }}
            />
 
            {/* Actor label */}
            {messages.actor && (
              <div style={{
                fontFamily: 'monospace',
                fontSize: isMobile ? '0.42rem' : '0.44rem',
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: messages.color ?? 'rgba(255,255,255,0.35)',
                opacity: 0.75,
                textAlign: side === 'center' ? 'center' : side === 'right' ? 'right' : 'left',
              }}>
                {messages.actor === 'sophia'     && '✦ SOPHIA'}
                {messages.actor === 'precepteur' && `◀ ${messages.actorLabel ?? 'PRÉCEPTEUR'}`}
                {messages.actor === 'joueur'     && `${messages.actorLabel ?? 'VOUS'} ▶`}
                {messages.actor === 'system'     && '◈ SYSTÈME'}
                {messages.actor === 'synergie'   && '⚡ SYNERGIE'}
                {messages.actor === 'combo'      && '⚔ COMBO'}
              </div>
            )}
 
            {/* Texte principal */}
            <p style={{
              fontFamily: 'serif',
              fontStyle: 'italic',
              fontSize: isMobile
                ? 'clamp(0.68rem, 3.2vw, 0.82rem)'
                : 'clamp(0.72rem, 2.2vw, 0.88rem)',
              color: 'rgba(255,255,255,0.88)',
              lineHeight: 1.6,
              margin: 0,
              // Pas de clamp sur les lignes : le joueur doit pouvoir lire tout le texte
              textAlign: side === 'center' ? 'center' : side === 'right' ? 'right' : 'left',
            }}>
              {messages.text}
            </p>
 
            {/* Indicateur "cliquer pour continuer" — remplace la barre de progression */}
            <motion.div
              animate={{ opacity: [0.3, 0.7, 0.3] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
              style={{
                fontFamily: 'monospace',
                fontSize: '0.36rem',
                letterSpacing: '0.2em',
                color: `${messages.color ?? '#c4b5fd'}88`,
                textAlign: side === 'right' ? 'right' : side === 'left' ? 'left' : 'center',
                marginTop: '0.1rem',
              }}
            >
              {isMobile ? '▸ TOUCHER POUR CONTINUER' : '▸ CLIQUER POUR CONTINUER'}
            </motion.div>
 
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function getPunchlineNature(p) {
  if (p.drain > 0) return 'attaque';
  if (p.gripAdd < 0) return 'defense';
  return 'neutre';
}

function getPrecepteurReaction(conceptJoueurId, conceptAdvId, advCharId, themeId, allConcepts, state = {}) {
  
  // ════════════════════════════════════════════════════════════════════════
  // NIVEAU 1 : PAIRE EXACTE (PRECEPTEUR_REACTIONS)
  // Le joueur a joué X, l'IA répond avec Y → réplique spécifique
  // ════════════════════════════════════════════════════════════════════════
  
  const joueurEntry = PRECEPTEUR_REACTIONS[conceptJoueurId];
  
  if (joueurEntry) {
    // 1a. Paire exacte : conceptJoueurId + conceptAdvId
    const paire = joueurEntry[conceptAdvId];
    if (paire && Array.isArray(paire) && paire.length > 0) {
      return paire[Math.floor(Math.random() * paire.length)];
    }
    
    // 1b. Fallback _default pour ce concept joueur
    if (joueurEntry._default) {
      const d = joueurEntry._default;
      return Array.isArray(d) ? d[Math.floor(Math.random() * d.length)] : d;
    }
  }

  // ════════════════════════════════════════════════════════════════════════
  // NIVEAU 2 : CATÉGORIE STRATÉGIQUE DU PRÉCEPTEUR
  // Analyse si le Précepteur attaque, défend ou contre-attaque
  // pour choisir une tonalité de réponse adaptée
  // ════════════════════════════════════════════════════════════════════════

  // 2a. Déterminer la nature du concept joué par le Précepteur
  const concepts = allConcepts || [];
  const ca = concepts.find(c => c.id === conceptAdvId);
  const advNature = ca ? (ca.drain > 0 ? 'attaque' : 'defense') : 'attaque';
  
  // 2b. Déterminer la nature du concept joué par le Joueur
  const cj = concepts.find(c => c.id === conceptJoueurId);
  const joueurNature = cj ? (cj.drain > 0 ? 'attaque' : 'defense') : 'attaque';

  // 2c. Affiner la catégorie stratégique du Précepteur en fonction de l'état du duel
  let categorieStrategique = advNature; // Par défaut : attaque ou defense selon le concept

  if (state.playerComboStreak >= 2) {
    // Le joueur est en combo → le Précepteur contre-attaque
    categorieStrategique = 'contre';
  } else if (state.opponentGrip > 60) {
    // Le Précepteur est sous pression → il défend
    categorieStrategique = 'defense';
  } else if (state.playerEnergy < 25) {
    // Le joueur est faible → le Précepteur attaque
    categorieStrategique = 'attaque';
  }

  // 2d. Tenter de piocher dans les RIPOSTES (système refondu) si le conceptAdvId y existe
  if (RIPOSTES[conceptAdvId] && RIPOSTES[conceptAdvId][categorieStrategique]) {
    const pool = RIPOSTES[conceptAdvId][categorieStrategique];
    if (pool.length > 0) {
      // 30% de chance d'ajouter une riposte conditionnelle
      let poolEtendu = [...pool];
      if (Math.random() < 0.3) {
        if (state.playerGrip > 60 && RIPOSTES_CONDITIONNELLES.high_grip) {
          poolEtendu = poolEtendu.concat(RIPOSTES_CONDITIONNELLES.high_grip);
        }
        if (state.playerEnergy < 30 && RIPOSTES_CONDITIONNELLES.low_energy) {
          poolEtendu = poolEtendu.concat(RIPOSTES_CONDITIONNELLES.low_energy);
        }
        if (state.playerComboStreak > 0 && RIPOSTES_CONDITIONNELLES.after_combo) {
          poolEtendu = poolEtendu.concat(RIPOSTES_CONDITIONNELLES.after_combo);
        }
        if (state.conceptRepeat && RIPOSTES_CONDITIONNELLES.repetition) {
          poolEtendu = poolEtendu.concat(RIPOSTES_CONDITIONNELLES.repetition);
        }
      }
      return poolEtendu[Math.floor(Math.random() * poolEtendu.length)];
    }
  }

  // ════════════════════════════════════════════════════════════════════════
  // NIVEAU 3 : AMPLIFICATION THÉMATIQUE
  // Le Précepteur est "chez lui" sur ce thème → réplique d'autorité
  // ════════════════════════════════════════════════════════════════════════

  const themeKey = `${advCharId}_${themeId}`;
  const themeAmp = PRECEPTEUR_THEME_AMPLIFICATIONS[themeKey];
  if (themeAmp) return themeAmp;

  // Fallback thématique par défaut
  const themeDefaultKey = `${advCharId}_default`;
  const themeDefault = PRECEPTEUR_THEME_AMPLIFICATIONS[themeDefaultKey];
  if (themeDefault) return themeDefault;

  // ════════════════════════════════════════════════════════════════════════
  // NIVEAU 4 : FALLBACK PAR NATURE
  // Ni paire exacte, ni thème → logique binaire attaque/défense
  // ════════════════════════════════════════════════════════════════════════

  // 4a. Précepteur en difficulté
  if (state.opponentEnergy < 25 && PRECEPTEUR_FALLBACK_BY_NATURE.precepteur_backfoot) {
    const backfoot = PRECEPTEUR_FALLBACK_BY_NATURE.precepteur_backfoot;
    if (Math.random() < 0.5) {
      return backfoot[Math.floor(Math.random() * backfoot.length)];
    }
  }

  // 4b. Paire d'action classique
  const naturePair = `${joueurNature}_${advNature}`;
  const fallbacks = PRECEPTEUR_FALLBACK_BY_NATURE[naturePair];
  if (fallbacks && fallbacks.length > 0) {
    return fallbacks[Math.floor(Math.random() * fallbacks.length)];
  }

  // ════════════════════════════════════════════════════════════════════════
  // NIVEAU 5 : GÉNÉRIQUE ABSOLU (dernier recours)
  // ════════════════════════════════════════════════════════════════════════

  const ultimes = [
    "Votre mouvement est noté. Ma réponse est calculée.",
    "Le débat continue. Chaque mot nous rapproche de la vérité.",
    "Intéressant. Poursuivons.",
    "Je prends acte de votre position. Voici la mienne.",
    "Le dialogue est la seule méthode. Continuons."
  ];
  return ultimes[Math.floor(Math.random() * ultimes.length)];
}

function KimonoAvatar({
  side = 'left',
  isHit = false,
  isAttacking = false,
  grip = 0,
  isBlocking = false,
  char = { avatarColor: '#a78bfa' },
  isAnimating = false,
  inFlow = false,
  flowStreak = 0,
}) {
  const isRight = side === 'right';
  const isHighGrip = grip > 50;

  return (
    <motion.div
      animate={{
        x: isHit
          ? isRight ? [0, 12, -6, 2, 0] : [0, -12, 6, -2, 0]
          : isAttacking
          ? isRight ? [0, -22, 0] : [0, 22, 0]
          : 0,
        scaleX: isRight ? -1 : 1,
      }}
      transition={{ duration: 0.35, type: 'spring', stiffness: 280, damping: 18 }}
      style={{
            width: '100%', height: '100%',
            objectFit: 'cover', objectPosition: 'center 20%',
            filter: 'drop-shadow(0 15px 25px rgba(0,0,0,0.65))',
            aspectRatio: '3/4',
          }}
    >
      {char.image ? (
        <motion.img
          key={char.image}
          src={char.image}
          alt={char.nom || 'Personnage'}
          style={{
            width: '100%', height: '100%',
            objectFit: 'cover', objectPosition: 'center 20%',
            filter: 'drop-shadow(0 15px 25px rgba(0,0,0,0.65))',
            
          }}
          animate={{
            y: [0, -7, 0],
            filter: isHighGrip
              ? ['drop-shadow(0 15px 25px rgba(0,0,0,0.65)) drop-shadow(0 0 18px #ef4444)', 'drop-shadow(0 15px 25px rgba(0,0,0,0.65)) drop-shadow(0 0 30px #ef4444)', 'drop-shadow(0 15px 25px rgba(0,0,0,0.65)) drop-shadow(0 0 18px #ef4444)']
              : 'drop-shadow(0 15px 25px rgba(0,0,0,0.65))',
          }}
          transition={{
            y: { repeat: Infinity, duration: 3.2, ease: 'easeInOut' },
            filter: { repeat: Infinity, duration: 0.7 },
          }}
        />
      ) : (
        <div style={{ width: '100%', height: '100%', background: '#1e2937', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', fontSize: '0.8rem' }}>
          Pas d'image
        </div>
      )}
      {isBlocking && (
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: [1, 1.15, 0.92, 1], opacity: [0.8, 1, 0.7, 0.9], rotate: [0, 10, -10, 0] }}
          transition={{ repeat: Infinity, duration: 0.85 }}
          style={{ position: 'absolute', top: '12%', left: '18%', width: '64%', height: '78%', borderRadius: '50%', filter: `drop-shadow(0 0 25px ${char.avatarColor})`, zIndex: 10, pointerEvents: 'none' }}>
          <div style={{ fontSize: '3.8rem', textAlign: 'center', marginTop: '22%' }}>🛡️</div>
        </motion.div>
      )}
      {isHighGrip && (
        <motion.div
          style={{ position: 'absolute', inset: 0, background: `radial-gradient(circle at 50% 42%, #ef4444 5%, transparent 65%)`, opacity: 0.35, pointerEvents: 'none', zIndex: 5 }}
          animate={{ opacity: [0.25, 0.55, 0.25] }}
          transition={{ repeat: Infinity, duration: 0.6 }}
        />
      )}
      
            {/* ==================== EFFET SLASH - VERSION CORRIGÉE ==================== */}
      {isAttacking && (
        <motion.div
          initial={{ 
            opacity: 0, 
            scaleX: 0.15,
            x: isRight ? -45 : 25 
          }}
          animate={{ 
            opacity: [0, 1, 0.8, 0],
            scaleX: [0.15, 2.15, 2.45],
            x: isRight ? [-45, 15] : [25, -10]
          }}
          transition={{ 
            duration: 0.30, 
            ease: "easeOut" 
          }}
          style={{
            position: 'absolute',
            top: '33%',
            left: isRight ? '37%' : '24%',
            width: '160%',
            height: '4.5px',
            background: `linear-gradient(90deg, transparent, ${char.avatarColor}aa, #ffffff, ${char.avatarColor}ee, transparent)`,
            boxShadow: `0 0 30px ${char.avatarColor}, 0 0 60px ${char.avatarColor}`,
            borderRadius: '999px',
            transformOrigin: isRight ? 'left center' : 'right center',
            zIndex: 12,
          }}
        />
      )}

      {inFlow && <FlowHalo streak={flowStreak} color={char.avatarColor} />}
    </motion.div>
  );
}

const ARENA_IMAGES = ['/images/arena-1.jpg', '/images/arena-2.jpg', '/images/arena-3.jpg'];
const DAMAGE_LABELS = ['EFFLEURE', 'TOUCHE', 'PERCUTE', 'DÉSTABILISE', 'DÉVASTE', 'TRANSCENDE'];

function Arena({
  playerColor = '#22d3ee',
  opponentColor = '#f59e0b',
  arenaIndex = 0,
  // Nouvelles props (optionnelles, rétrocompatibles)
  intensity = 0,      // 0..1, tension globale
  playerHit = false,
  oppHit = false,
}) {
  return (
    <>
      {/* ── Photos d'arènes en rotation ── */}
      {ARENA_IMAGES.map((src, i) => (
        <motion.div
          key={src}
          initial={{ opacity: 0 }}
          animate={{
            opacity: arenaIndex === i ? [0.55, 0.68, 0.55] : 0,
            // Parallaxe douce (effet Ken Burns horizontal)
            backgroundPosition:
              arenaIndex === i ? ['40% 50%', '60% 50%', '40% 50%'] : '50% 50%',
          }}
          transition={{
            opacity: {
              duration: arenaIndex === i ? 9 : 2.5,
              repeat: arenaIndex === i ? Infinity : 0,
              ease: 'easeInOut',
            },
            backgroundPosition: {
              duration: 18,
              repeat: Infinity,
              ease: 'linear',
            },
          }}
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${src})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(2px) saturate(0.6) brightness(0.55)',
            zIndex: 0,
            willChange: 'opacity, background-position',
          }}
        />
      ))}

      {/* ── SVG overlay ── */}
      <svg
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
        }}
        viewBox="0 0 900 420"
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          {/* Vignette globale */}
          <linearGradient id="arenaVignette" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="rgba(3,6,18,0.72)" />
            <stop offset="38%" stopColor="rgba(3,6,18,0.28)" />
            <stop offset="62%" stopColor="rgba(3,6,18,0.18)" />
            <stop offset="100%" stopColor="rgba(3,6,18,0.82)" />
          </linearGradient>

          {/* Vignettes latérales */}
          <linearGradient id="arenaEdgeL" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgba(0,0,0,0.78)" />
            <stop offset="22%" stopColor="rgba(0,0,0,0.22)" />
            <stop offset="100%" stopColor="transparent" />
          </linearGradient>
          <linearGradient id="arenaEdgeR" x1="100%" y1="0%" x2="0%" y2="0%">
            <stop offset="0%" stopColor="rgba(0,0,0,0.78)" />
            <stop offset="22%" stopColor="rgba(0,0,0,0.22)" />
            <stop offset="100%" stopColor="transparent" />
          </linearGradient>

          {/* Halos */}
          <radialGradient id="haloL" cx="14%" cy="72%" r="38%">
            <stop offset="0%" stopColor={`${playerColor}30`} />
            <stop offset="55%" stopColor={`${playerColor}0a`} />
            <stop offset="100%" stopColor="transparent" />
          </radialGradient>
          <radialGradient id="haloR" cx="86%" cy="72%" r="38%">
            <stop offset="0%" stopColor={`${opponentColor}28`} />
            <stop offset="55%" stopColor={`${opponentColor}08`} />
            <stop offset="100%" stopColor="transparent" />
          </radialGradient>

          {/* Sol réfléchissant */}
          <linearGradient id="floorReflect" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="rgba(0,0,0,0)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0.88)" />
          </linearGradient>
          <linearGradient id="floorSheen" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="rgba(200,180,255,0.06)" />
            <stop offset="60%" stopColor="rgba(200,180,255,0.02)" />
            <stop offset="100%" stopColor="transparent" />
          </linearGradient>

          {/* Séparateur central – jauge de tension */}
          <linearGradient id="divLineBase" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="transparent" />
            <stop offset="15%" stopColor="rgba(233,213,255,0.35)" />
            <stop offset="50%" stopColor="rgba(255,255,255,0.2)" />
            <stop offset="85%" stopColor="rgba(233,213,255,0.3)" />
            <stop offset="100%" stopColor="transparent" />
          </linearGradient>

          {/* Jauge dynamique (hauteur = intensity * 230) */}
          <linearGradient id="divLineDynamic" x1="0%" y1="100%" x2="0%" y2="0%">
            <stop offset="0%" stopColor={`${playerColor}dd`} />
            <stop offset="45%" stopColor="#e9d5ff" />
            <stop offset="55%" stopColor="#e9d5ff" />
            <stop offset="100%" stopColor={`${opponentColor}dd`} />
          </linearGradient>

          {/* Onde de choc */}
          <linearGradient id="shockwave" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="rgba(255,255,255,0)" />
            <stop offset="50%" stopColor="rgba(255,255,255,0.9)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
          </linearGradient>

          {/* Lignes de perspective au sol */}
          <linearGradient id="perspLine" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.05)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
          </linearGradient>

          {/* Rail HUD */}
          <linearGradient id="hudRailTop" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="rgba(3,6,18,0.95)" />
            <stop offset="100%" stopColor="transparent" />
          </linearGradient>
          <linearGradient id="hudRailBot" x1="0%" y1="100%" x2="0%" y2="0%">
            <stop offset="0%" stopColor="rgba(3,6,18,0.95)" />
            <stop offset="100%" stopColor="transparent" />
          </linearGradient>

          {/* Barre de temps HUD */}
          <linearGradient id="timeBar" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={`${playerColor}aa`} />
            <stop offset="50%" stopColor="#e9d5ff" />
            <stop offset="100%" stopColor={`${opponentColor}aa`} />
          </linearGradient>

          {/* Filtres */}
          <filter id="softBlur"><feGaussianBlur stdDeviation="4" /></filter>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3.5" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="heavyBlur"><feGaussianBlur stdDeviation="16" /></filter>
        </defs>

        {/* Styles globaux & keyframes */}
        <style>{`
          @keyframes divPulse     { 0%,100%{opacity:0.55} 50%{opacity:1} }
          @keyframes nodeGlow     { 0%,100%{opacity:0.55} 50%{opacity:0.95} }
          @keyframes floorPulseL  { 0%,100%{opacity:0.55} 50%{opacity:0.9} }
          @keyframes floorPulseR  { 0%,100%{opacity:0.5}  50%{opacity:0.85} }
          @keyframes timeProgress { 0%{transform: scaleX(0)} 100%{transform: scaleX(1)} }
          @keyframes shockwave    { 0%{transform: scaleY(0.3); opacity:1} 100%{transform: scaleY(2.5); opacity:0} }
          @keyframes heartbeat    { 0%,100%{transform: scale(1); opacity:0.8} 15%{transform: scale(1.3); opacity:1} 30%{transform: scale(1)} }
          @keyframes floatParticle { 0%{transform: translateY(0) translateX(0); opacity:0} 10%{opacity:1} 90%{opacity:1} 100%{transform: translateY(-40px) translateX(10px); opacity:0} }
          .div-line      { animation: divPulse 2.4s ease-in-out infinite; }
          .div-node      { animation: nodeGlow 2.4s ease-in-out infinite; }
          .floor-l       { animation: floorPulseL 3.1s ease-in-out infinite; }
          .floor-r       { animation: floorPulseR 3.4s ease-in-out infinite 0.6s; }
          .shockwave-l,
          .shockwave-r   { animation: shockwave 0.6s ease-out forwards; transform-origin: center; }
          .central-heart { animation: heartbeat 1.4s ease-in-out infinite; transform-origin: center; }
          .time-bar-fill { animation: timeProgress 90s linear infinite; transform-origin: left; }
          .float-p1, .float-p2, .float-p3 { animation: floatParticle 6s ease-in-out infinite; }
          .float-p2 { animation-delay: 2s; left: 65%; }
          .float-p3 { animation-delay: 4s; left: 30%; }
        `}</style>

        {/* Overlay global */}
        <rect width="900" height="420" fill="url(#arenaVignette)" />
        <rect width="900" height="420" fill="url(#arenaEdgeL)" />
        <rect width="900" height="420" fill="url(#arenaEdgeR)" />

        {/* Halos au sol */}
        <rect width="900" height="420" fill="url(#haloL)" />
        <rect width="900" height="420" fill="url(#haloR)" />

        {/* Sol réfléchissant */}
        <rect x="0" y="294" width="900" height="126" fill="url(#floorReflect)" />
        <rect x="0" y="294" width="900" height="80" fill="url(#floorSheen)" />

        {/* Lignes de perspective (profondeur) */}
        <line x1="0" y1="420" x2="450" y2="300" stroke="url(#perspLine)" strokeWidth="1" />
        <line x1="900" y1="420" x2="450" y2="300" stroke="url(#perspLine)" strokeWidth="1" />
        <line x1="180" y1="420" x2="450" y2="310" stroke="url(#perspLine)" strokeWidth="0.5" />
        <line x1="720" y1="420" x2="450" y2="310" stroke="url(#perspLine)" strokeWidth="0.5" />

        {/* Reflets colorés au sol */}
        <ellipse cx="158" cy="310" rx="90" ry="10"
          fill={`${playerColor}22`} filter="url(#softBlur)" className="floor-l" />
        <ellipse cx="742" cy="310" rx="90" ry="10"
          fill={`${opponentColor}1e`} filter="url(#softBlur)" className="floor-r" />

        {/* Ligne de sol */}
        <line x1="0" y1="296" x2="900" y2="296"
          stroke="rgba(200,180,255,0.18)" strokeWidth="1.5" />

        {/* ── Colonne centrale interactive (jauge + repère temporel) ── */}
        {/* Fond de la colonne */}
        <rect x="448" y="68" width="4" height="230" fill="url(#divLineBase)" className="div-line" />

        {/* Jauge de tension (hauteur dynamique) */}
        <rect
          x="448"
          y={296 - intensity * 230}        // bas fixe en 296, hauteur vers le haut
          width="4"
          height={Math.max(2, intensity * 230)}
          fill="url(#divLineDynamic)"
          style={{ transition: 'y 0.2s ease, height 0.2s ease' }}
          filter="url(#glow)"
        />

        {/* Onde de choc lors d'un impact */}
        {(playerHit || oppHit) && (
          <rect
            x="448" y="68" width="4" height="230"
            fill="url(#shockwave)"
            className={playerHit ? 'shockwave-l' : 'shockwave-r'}
          />
        )}

        {/* Nœud central (battement temporel) */}
        <g className="central-heart">
          <circle cx="450" cy="186" r="6" fill="#e9d5ff" opacity="0.85" filter="url(#glow)" />
          <circle cx="450" cy="186" r="14" fill="rgba(233,213,255,0.1)" />
        </g>

        {/* Nœud supérieur */}
        <g filter="url(#glow)" className="div-node">
          <circle cx="450" cy="72" r="5" fill="#e9d5ff" opacity="0.85" />
          <circle cx="450" cy="72" r="10" fill="rgba(233,213,255,0.15)" />
        </g>

        {/* Nœud inférieur (au sol) */}
        <g filter="url(#glow)" className="div-node">
          <ellipse cx="450" cy="296" rx="22" ry="5"
            fill="rgba(233,213,255,0.28)" filter="url(#softBlur)" />
          <circle cx="450" cy="296" r="4" fill="#e9d5ff" opacity="0.7" />
        </g>

        {/* ── Brouillard volumétrique (profondeur) ── */}
        <ellipse cx="450" cy="240" rx="350" ry="100"
          fill="rgba(30,20,50,0.22)" filter="url(#heavyBlur)"
          style={{ mixBlendMode: 'multiply' }} />

        {/* ── Rails HUD ── */}
        <rect x="0" y="0" width="900" height="72" fill="url(#hudRailTop)" />
        <rect x="0" y="358" width="900" height="62" fill="url(#hudRailBot)" />

        {/* Barre de temps (HUD haut) */}
        <rect x="320" y="18" width="260" height="4" rx="2"
          fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.15)" strokeWidth="0.5" />
        <rect x="320" y="18" width="260" height="4" rx="2"
          fill="url(#timeBar)" className="time-bar-fill"
          style={{ transformOrigin: 'left', opacity: 0.8 }} />

        {/* Filets HUD */}
        <line x1="0" y1="68" x2="900" y2="68" stroke="rgba(233,213,255,0.12)" strokeWidth="1" />
        <line x1="0" y1="358" x2="900" y2="358" stroke="rgba(233,213,255,0.12)" strokeWidth="1" />

        {/* Coins HUD réactifs */}
        <g style={{ transition: 'opacity 0.2s', opacity: playerHit ? 1 : 0.6 }}>
          <path d="M8 8 L8 26 M8 8 L28 8"
            stroke={`${playerColor}88`} strokeWidth="1.5" fill="none" />
          <path d="M8 412 L8 394 M8 412 L28 412"
            stroke={`${playerColor}55`} strokeWidth="1.5" fill="none" />
        </g>
        <g style={{ transition: 'opacity 0.2s', opacity: oppHit ? 1 : 0.6 }}>
          <path d="M892 8 L892 26 M892 8 L872 8"
            stroke={`${opponentColor}88`} strokeWidth="1.5" fill="none" />
          <path d="M892 412 L892 394 M892 412 L872 412"
            stroke={`${opponentColor}55`} strokeWidth="1.5" fill="none" />
        </g>

        {/* Particules d'ambiance (poussière lumineuse) */}
        <circle cx="180" cy="300" r="1.5" fill="#e9d5ff" opacity="0.4" className="float-p1" />
        <circle cx="680" cy="250" r="1"   fill="#e9d5ff" opacity="0.3" className="float-p2" />
        <circle cx="400" cy="330" r="1.2" fill="#e9d5ff" opacity="0.35" className="float-p3" />
      </svg>
    </>
  );
}

function ParticleArena({
  playerColor,
  opponentColor,
  intensity = 0,
  playerHit = false,
  oppHit = false,
}) {
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
      {/* Auras permanentes */}
      <div style={{
        position: 'absolute', left: 0, bottom: 0, width: '32%', height: '72%',
        background: `radial-gradient(ellipse at 35% 90%, ${playerColor}24 0%, ${playerColor}0d 48%, transparent 72%)`,
        mixBlendMode: 'screen',
        animation: 'aura-breathe-l 3.2s ease-in-out infinite',
      }} />
      <div style={{
        position: 'absolute', right: 0, bottom: 0, width: '32%', height: '72%',
        background: `radial-gradient(ellipse at 65% 90%, ${opponentColor}20 0%, ${opponentColor}0b 50%, transparent 74%)`,
        mixBlendMode: 'screen',
        animation: 'aura-breathe-r 3.5s ease-in-out infinite 0.8s',
      }} />

      {/* Tension centrale */}
      {intensity > 0.1 && (
        <div style={{
          position: 'absolute', left: '36%', top: '12%', width: '28%', height: '68%',
          background: `radial-gradient(ellipse at 50% 50%,
            rgba(255,255,255,${(intensity * 0.18).toFixed(2)}) 0%,
            ${playerColor}${Math.min(255, Math.floor(intensity * 32)).toString(16).padStart(2, '0')} 30%,
            ${opponentColor}${Math.min(255, Math.floor(intensity * 26)).toString(16).padStart(2, '0')} 62%,
            transparent 85%)`,
          mixBlendMode: 'screen',
          filter: intensity > 0.6 ? 'blur(2px)' : 'none',
          transition: 'opacity 0.3s',
        }} />
      )}

      {/* Flashs d'attaque */}
      {playerHit && (
        <>
          <div style={{
            position: 'absolute', left: '-2%', top: '14%', width: '60%', height: '62%',
            background: `linear-gradient(94deg, transparent 10%, ${playerColor}18 28%, ${playerColor}bb 46%, ${playerColor}44 56%, transparent 70%)`,
            transform: 'skewX(-14deg)',
            animation: 'slash-l 0.45s cubic-bezier(0.22,1,0.36,1) forwards',
            mixBlendMode: 'screen',
          }} />
          <div style={{
            position: 'absolute', left: '8%', top: '22%', width: '44%', height: '48%',
            background: `radial-gradient(ellipse at 22% 48%, rgba(255,255,255,0.9) 0%, ${playerColor} 35%, transparent 68%)`,
            animation: 'burst-l 0.4s ease-out forwards',
            mixBlendMode: 'screen',
          }} />
          <div style={{
            position: 'absolute', inset: 0,
            background: `radial-gradient(ellipse at 48% 46%, rgba(255,240,220,0.55) 0%, transparent 52%)`,
            animation: 'hit-flash 0.38s ease-out forwards',
            mixBlendMode: 'screen',
          }} />
        </>
      )}

      {oppHit && (
        <>
          <div style={{
            position: 'absolute', right: '-2%', top: '14%', width: '60%', height: '62%',
            background: `linear-gradient(86deg, transparent 18%, ${opponentColor}44 36%, ${opponentColor}bb 52%, ${opponentColor}18 62%, transparent 76%)`,
            transform: 'skewX(14deg)',
            animation: 'slash-r 0.45s cubic-bezier(0.22,1,0.36,1) forwards',
            mixBlendMode: 'screen',
          }} />
          <div style={{
            position: 'absolute', right: '8%', top: '22%', width: '44%', height: '48%',
            background: `radial-gradient(ellipse at 78% 48%, rgba(255,255,255,0.9) 0%, ${opponentColor} 35%, transparent 68%)`,
            animation: 'burst-r 0.4s ease-out forwards',
            mixBlendMode: 'screen',
          }} />
          <div style={{
            position: 'absolute', inset: 0,
            background: `radial-gradient(ellipse at 52% 46%, rgba(255,240,220,0.5) 0%, transparent 52%)`,
            animation: 'hit-flash 0.38s ease-out forwards',
            mixBlendMode: 'screen',
          }} />
        </>
      )}

      {/* Reflet dynamique au sol */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: '22%',
        background: `linear-gradient(90deg, ${playerColor}18 0%, transparent 42%, transparent 58%, ${opponentColor}14 100%)`,
        mixBlendMode: 'screen',
        opacity: 0.6 + intensity * 0.4,
        transition: 'opacity 0.4s',
      }} />

      <style>{`
        /* keyframes identiques à la version d'origine */
        @keyframes aura-breathe-l {
          0%,100% { opacity: 0.65; transform: scale(1); }
          50%      { opacity: 1;    transform: scale(1.04); }
        }
        @keyframes aura-breathe-r {
          0%,100% { opacity: 0.6;  transform: scale(1); }
          50%      { opacity: 0.95; transform: scale(1.05); }
        }
        @keyframes slash-l {
          0%   { opacity: 0; transform: skewX(-14deg) translateX(-12%); }
          30%  { opacity: 1; }
          100% { opacity: 0; transform: skewX(-14deg) translateX(8%); }
        }
        @keyframes slash-r {
          0%   { opacity: 0; transform: skewX(14deg) translateX(12%); }
          30%  { opacity: 1; }
          100% { opacity: 0; transform: skewX(14deg) translateX(-8%); }
        }
        @keyframes burst-l {
          0%   { opacity: 0; transform: scale(0.6); }
          25%  { opacity: 1; transform: scale(1.1); }
          100% { opacity: 0; transform: scale(1.4); }
        }
        @keyframes burst-r {
          0%   { opacity: 0; transform: scale(0.6); }
          25%  { opacity: 1; transform: scale(1.1); }
          100% { opacity: 0; transform: scale(1.4); }
        }
        @keyframes hit-flash {
          0%   { opacity: 0.9; }
          100% { opacity: 0;   }
        }
      `}</style>
    </div>
  );
}

const createThreeBase = (container, camCfg, ambiCfg, saved) => {
  const w = container.clientWidth, h = container.clientHeight;
  const scene = new THREE.Scene();
  scene.background = null; // fond transparent, l’image fera le boulot
  if (ambiCfg?.fog?.enabled) scene.fog = new THREE.Fog(ambiCfg.fog.color || '#050507', ambiCfg.fog.near || 10, ambiCfg.fog.far || 50);

  const cam = camCfg || {};
  const camera = new THREE.PerspectiveCamera(50, w / h, 0.1, 200);
  if (saved?.position) camera.position.copy(saved.position);
  else camera.position.set(cam.initialPosition?.x ?? 0, cam.initialPosition?.y ?? 2, cam.initialPosition?.z ?? 8);

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(w, h);
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 0.85;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  container.appendChild(renderer.domElement);

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true; controls.dampingFactor = 0.08;
  controls.minDistance = 5; controls.maxDistance = 25; // restreint pour ne pas trop s’éloigner
  if (saved?.target) controls.target.copy(saved.target);
  else controls.target.set(cam.lookAt?.x ?? 0, cam.lookAt?.y ?? 0, cam.lookAt?.z ?? 0);
  controls.update();

  // Éclairage minimal, juste pour le sol
  scene.add(new THREE.AmbientLight(0x334466, 0.4));
  const sun = new THREE.DirectionalLight(0xffffff, 0.8); sun.position.set(5, 10, 7); scene.add(sun);
  const fill = new THREE.DirectionalLight(0x4488ff, 0.3); fill.position.set(-5, -3, -5); scene.add(fill);

  const handleResize = () => {
    const nw = container.clientWidth, nh = container.clientHeight;
    camera.aspect = nw / nh; camera.updateProjectionMatrix();
    renderer.setSize(nw, nh);
  };
  window.addEventListener('resize', handleResize);

  return {
    scene, camera, renderer, controls,
    getCameraState: () => ({ position: camera.position.clone(), target: controls.target.clone() }),
    dispose: () => { window.removeEventListener('resize', handleResize); controls.dispose(); renderer.dispose(); if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement); },
  };
};

export const ArenaScene = ({
  playerColor = '#22d3ee',
  opponentColor = '#f59e0b',
  intensity = 0,
  playerHit = false,
  oppHit = false,
  arenaImages = [],
  arenaIndex = 0,
  playerStreak = 0,
  opponentStreak = 0,
  roundsWon = 0,
}) => {
  const containerRef = useRef(null);
  const threeRef = useRef(null);
  const rafRef = useRef(null);
  const clockRef = useRef(new THREE.Clock());

  // Références pour les éléments à animer
  const bgPlaneRef = useRef(null);
  const playerHaloRef = useRef(null);
  const oppHaloRef = useRef(null);
  const progressLineRef = useRef(null);

  // Préchargement des textures pour éviter les reconstructions
  const texturesRef = useRef([]);
  useEffect(() => {
    if (!arenaImages.length) return;
    const loader = new THREE.TextureLoader();
    const promises = arenaImages.map(src =>
      new Promise((resolve) => {
        loader.load(src, (tex) => {
          tex.colorSpace = THREE.SRGBColorSpace;
          resolve(tex);
        });
      })
    );
    Promise.all(promises).then((all) => {
      texturesRef.current = all;
      // Si le plan de fond existe déjà, on met à jour sa texture
      if (bgPlaneRef.current) {
        bgPlaneRef.current.material.map = all[arenaIndex % all.length] || null;
        bgPlaneRef.current.material.needsUpdate = true;
      }
    });
  }, [arenaImages]);

  // Effet principal : construction de la scène (une seule fois)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    if (threeRef.current) threeRef.current.dispose();

    const base = createThreeBase(container,
      { initialPosition: { x: 0, y: 1.6, z: 12 }, lookAt: { x: 0, y: 0.3, z: 0 } }, // caméra abaissée
      { fog: { enabled: true, color: '#050507', near: 10, far: 30 } }
    );
    threeRef.current = base;
    const { scene, camera, renderer, controls } = base;

    // ── Fond photographique ──────────────────────────
    const bgPlane = new THREE.Mesh(
      new THREE.PlaneGeometry(2, 2), // sera redimensionné dynamiquement
      new THREE.MeshBasicMaterial({
        map: texturesRef.current[0] || null,
        depthWrite: false,
        depthTest: false,
      })
    );
    bgPlane.frustumCulled = false; // toujours visible
    scene.add(bgPlane);
    bgPlaneRef.current = bgPlane;

    // Appliquer la première texture disponible
    if (texturesRef.current.length > 0) {
      bgPlane.material.map = texturesRef.current[0];
      bgPlane.material.needsUpdate = true;
    }

    

    // ── Ligne centrale ──────────────────────────────
    const divider = new THREE.Mesh(
      new THREE.BoxGeometry(0.2, 0.12, 16),
      new THREE.MeshBasicMaterial({ color: 0xc4b5fd, transparent: true, opacity: 0.5, depthWrite: false })
    );
    divider.position.set(0, 0.08, 0);
    scene.add(divider);

    // ── Indicateur de progression (ligne d’énergie) ──
    const progressGeo = new THREE.PlaneGeometry(0.1, 0.04);
    const progressMat = new THREE.MeshBasicMaterial({
      color: playerColor,
      transparent: true,
      opacity: 0.6,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const progressLine = new THREE.Mesh(progressGeo, progressMat);
    progressLine.rotation.x = -Math.PI / 2;
    // Position initiale : légèrement décalée sur le côté joueur
    progressLine.position.set(-7.5 + 1.5, 0.03, 1.2);
    scene.add(progressLine);
    progressLineRef.current = progressLine;

    // ── Particules d’ambiance ───────────────────────
    const pCount = 220;
    const pGeo = new THREE.BufferGeometry();
    const pPos = new Float32Array(pCount * 3);
    for (let i = 0; i < pCount * 3; i += 3) {
      pPos[i]     = (Math.random() - 0.5) * 30;
      pPos[i + 1] = Math.random() * 2;            // rester près du sol (0-2)
      pPos[i + 2] = (Math.random() - 0.5) * 18;
    }
    pGeo.setAttribute('position', new THREE.BufferAttribute(pPos, 3));
    const particles = new THREE.Points(pGeo, new THREE.PointsMaterial({
      color: 0xdddddd,
      size: 0.055,
      transparent: true,
      opacity: 0.45,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    }));
    scene.add(particles);

    // ── Boucle d’animation ─────────────────────────
    const animate = () => {
      rafRef.current = requestAnimationFrame(animate);
      const t = clockRef.current.getElapsedTime();
      controls.update();

      
      // Indicateur de progression
      if (progressLineRef.current) {
        const line = progressLineRef.current;
        // Largeur : elle augmente avec les rounds gagnés
        const targetWidth = 0.15 + (roundsWon / 3) * 6; // max 3 rounds → ~6.15 unités
        line.scale.x = targetWidth;
        // Pulsation légère si streak
        line.material.opacity = 0.4 + (playerStreak > 0 ? 0.3 : 0);
        // On peut décaler la position X pour qu'elle parte du coin gauche
        line.position.x = -7.5 + 1.5 + targetWidth / 2; // centrer la ligne sur son axe
      }

      const distance = 0.1; // juste devant la caméra
      const vFov = camera.fov * Math.PI / 180;
      const height = 2 * Math.tan(vFov / 2) * distance;
      const width = height * camera.aspect;

      bgPlane.position.copy(camera.position).add(
        camera.getWorldDirection(new THREE.Vector3()).multiplyScalar(distance)
      );
      bgPlane.quaternion.copy(camera.quaternion);
      bgPlane.scale.set(width, height, 1);

      renderer.render(scene, camera);
    };
    animate();

    return () => {
      cancelAnimationFrame(rafRef.current);
      if (threeRef.current) threeRef.current.dispose();
    };
  }, []); // Ne dépend plus d’arenaIndex ni arenaImages

  // Mise à jour de la texture de fond quand arenaIndex change
  useEffect(() => {
    if (bgPlaneRef.current && texturesRef.current.length > 0) {
      const tex = texturesRef.current[arenaIndex % texturesRef.current.length];
      if (tex) {
        bgPlaneRef.current.material.map = tex;
        bgPlaneRef.current.material.needsUpdate = true;
      }
    }
  }, [arenaIndex]);

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%', background: '#050507' }} />
  );
};

function AIThinkingIndicator({ aiChar, visible }) {
  if (!visible) return null;
  return (
    <motion.div initial={{ opacity:0, x:20 }} animate={{ opacity:1, x:0 }} exit={{ opacity:0, x:20 }}
      style={{ position:'absolute', top:'14%', right:'1rem', zIndex:15, background:'rgba(2,6,23,0.9)', border:`1px solid ${aiChar.color}44`, padding:'0.3rem 0.65rem', display:'flex', alignItems:'center', gap:'0.4rem' }}>
      <motion.div animate={{ opacity:[0.3,1,0.3] }} transition={{ repeat:Infinity, duration:0.5 }}
        style={{ width:5, height:5, borderRadius:'50%', background:aiChar.color }}/>
      <motion.div animate={{ opacity:[0.3,1,0.3] }} transition={{ repeat:Infinity, duration:0.5, delay:0.15 }}
        style={{ width:5, height:5, borderRadius:'50%', background:aiChar.color }}/>
      <motion.div animate={{ opacity:[0.3,1,0.3] }} transition={{ repeat:Infinity, duration:0.5, delay:0.3 }}
        style={{ width:5, height:5, borderRadius:'50%', background:aiChar.color }}/>
      <span style={{ fontFamily:'monospace', fontSize:'0.45rem', color:aiChar.color, letterSpacing:'0.15em', textTransform:'uppercase' }}>{aiChar.nom} réfléchit…</span>
    </motion.div>
  );
}

function AICharSwitchBanner({ oldChar, newChar, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 1000); return () => clearTimeout(t); }, [onDone]);
  return (
    <motion.div initial={{ opacity:0, scale:0.85 }} animate={{ opacity:1, scale:1 }} exit={{ opacity:0, scale:0.9 }}
      style={{ position:'absolute', top:'28%', left:'50%', transform:'translate(-50%,-50%)', zIndex:40, textAlign:'center', pointerEvents:'none' }}>
      <div style={{ background:'rgba(2,6,23,0.97)', border:`1px solid ${newChar.color}`, padding:'1rem 2rem', boxShadow:`0 0 50px ${newChar.color}44` }}>
        <div style={{ fontFamily:'monospace', fontSize:'0.48rem', letterSpacing:'0.35em', color:'rgba(255,255,255,0.3)', textTransform:'uppercase', marginBottom:'0.5rem' }}>⚡ CHANGEMENT DE STRATÉGIE</div>
        <div style={{ fontFamily:'serif', fontSize:'1.1rem', color:'rgba(255,255,255,0.5)', fontStyle:'italic', marginBottom:'0.4rem' }}>{oldChar.nom}</div>
        <div style={{ fontFamily:'monospace', fontSize:'0.7rem', color:'rgba(255,255,255,0.3)' }}>↓</div>
        <div style={{ fontFamily:'serif', fontSize:'1.5rem', color:newChar.color, fontStyle:'italic', fontWeight:700, marginTop:'0.4rem' }}>{newChar.nom}</div>
        <div style={{ fontFamily:'monospace', fontSize:'0.45rem', color:newChar.color+'88', letterSpacing:'0.2em', marginTop:'0.3rem', textTransform:'uppercase' }}>NOUVELLE STRATÉGIE DÉPLOYÉE</div>
      </div>
    </motion.div>
  );
}



function getDamageLabel(drain) {
  if (drain === 0) return 'SILENCE';
  if (drain < 15) return DAMAGE_LABELS[0];
  if (drain < 25) return DAMAGE_LABELS[1];
  if (drain < 35) return DAMAGE_LABELS[2];
  if (drain < 45) return DAMAGE_LABELS[3];
  if (drain < 55) return DAMAGE_LABELS[4];
  return DAMAGE_LABELS[5];
}

function checkSynergy(prevConceptId, currConceptId) {
  return SYNERGIES.find(s =>
    (s.ids[0] === prevConceptId && s.ids[1] === currConceptId) ||
    (s.ids[1] === prevConceptId && s.ids[0] === currConceptId)
  );
}

function AnalysisTimer({ duration, active, color }) {
  const [timeLeft, setTimeLeft] = useState(duration);

  useEffect(() => {
    if (!active) { setTimeLeft(duration); return; }
    setTimeLeft(duration);
    const interval = setInterval(() => {
      setTimeLeft(t => { if (t <= 1) { clearInterval(interval); return 0; } sounds.countdown(); return t-1; });
    }, 1000);
    return () => clearInterval(interval);
  }, [active, duration]);
  if (!active) return null;
  return (
    <motion.div initial={{ opacity:0, y:-10 }} animate={{ opacity:1, y:0 }}
      style={{ position:'absolute', top:'6%', left:'50%', transform:'translateX(-50%)', zIndex:15, display:'flex', flexDirection:'column', alignItems:'center', gap:'0.2rem' }}>
      <div style={{ fontFamily:'monospace', fontSize:'0.42rem', letterSpacing:'0.25em', color:'rgba(255,255,255,0.2)', textTransform:'uppercase' }}>TEMPS D'ANALYSE</div>
      <motion.div
        animate={{ color: timeLeft <= 2 ? ['#ef4444','#fff','#ef4444'] : color }}
        transition={{ repeat: timeLeft <= 2 ? Infinity : 0, duration: 0.4 }}
        style={{ fontFamily:'monospace', fontSize:'1.5rem', fontWeight:700, letterSpacing:'0.1em' }}>
        {timeLeft}s
      </motion.div>
      <div style={{ width:80, height:3, background:'rgba(255,255,255,0.1)', overflow:'hidden' }}>
        <motion.div animate={{ width:`${(timeLeft/duration)*100}%` }} transition={{ duration:0.9 }} style={{ height:'100%', background:color }}/>
      </div>
    </motion.div>
  );
}

async function saveGuilde(entry) {
  try { const key = `guilde:${Date.now()}`; await window.storage.set(key, JSON.stringify(entry), true); } catch(e) {}
}

async function loadGuilde() {
  try {
    const res = await window.storage.list('guilde:', true);
    if (!res?.keys) return [];
    const entries = await Promise.all(res.keys.slice(-20).map(async k => {
      try { const r = await window.storage.get(k, true); return r ? JSON.parse(r.value) : null; } catch { return null; }
    }));
    return entries.filter(Boolean).reverse();
  } catch { return []; }
}

function GuildePanel({ onClose, playerNom }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  useEffect(() => { loadGuilde().then(e => { setEntries(e); setLoading(false); }); }, []);
  const post = async () => {
    if (!msg.trim()) return;
    const entry = { nom: playerNom || 'Anonyme', msg: msg.trim(), ts: Date.now() };
    await saveGuilde(entry);
    setEntries(prev => [entry, ...prev]);
    setMsg('');
  };
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.9)', backdropFilter: 'blur(12px)', padding: '1rem' }}
      onClick={onClose}>
      <motion.div initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }}
        style={{ maxWidth: '560px', width: '100%', maxHeight: '80vh', background: '#070d1f', border: '1px solid rgba(245,158,11,0.25)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ height: '2px', background: 'linear-gradient(90deg, transparent, #f59e0b, transparent)' }}/>
        <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontFamily: 'serif', fontSize: '1.2rem', color: '#f59e0b', fontStyle: 'italic' }}>道場 · La Guilde</div>
            <div style={{ fontFamily: 'monospace', fontSize: '0.42rem', letterSpacing: '0.25em', color: 'rgba(255,255,255,0.25)', textTransform: 'uppercase' }}>ESPACE COMMUNAUTAIRE · PARTAGE DE SAVOIRS</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.4)', width: 30, height: 30, cursor: 'pointer', fontSize: '0.85rem' }}>✕</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }} className="hide-scrollbar">
          {loading && <div style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'monospace', fontSize: '0.6rem', textAlign: 'center', padding: '2rem' }}>Chargement…</div>}
          {!loading && entries.length === 0 && <div style={{ color: 'rgba(255,255,255,0.2)', fontFamily: 'monospace', fontSize: '0.62rem', textAlign: 'center', padding: '2rem' }}>La guilde est silencieuse. Soyez le premier à partager.</div>}
          {entries.map((e, i) => (
            <motion.div key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
              style={{ padding: '0.6rem 0.8rem', background: 'rgba(245,158,11,0.05)', border: '1px solid rgba(245,158,11,0.12)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                <span style={{ fontFamily: 'monospace', fontSize: '0.52rem', color: '#f59e0b', letterSpacing: '0.1em' }}>{e.nom}</span>
                <span style={{ fontFamily: 'monospace', fontSize: '0.42rem', color: 'rgba(255,255,255,0.2)' }}>{new Date(e.ts).toLocaleDateString('fr-FR')}</span>
              </div>
              <p style={{ fontFamily: 'monospace', fontSize: '0.62rem', color: 'rgba(255,255,255,0.6)', margin: 0, lineHeight: 1.5 }}>{e.msg}</p>
            </motion.div>
          ))}
        </div>
        <div style={{ padding: '0.75rem 1.25rem', borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', gap: '0.5rem' }}>
          <input value={msg} onChange={e => setMsg(e.target.value)} onKeyDown={e => e.key === 'Enter' && post()}
            placeholder="Partagez un combo, une stratégie, une réplique…"
            style={{ flex: 1, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)', fontFamily: 'monospace', fontSize: '0.62rem', padding: '0.4rem 0.6rem', outline: 'none' }}/>
          <button onClick={post} style={{ padding: '0.4rem 0.9rem', background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.4)', color: '#f59e0b', fontFamily: 'monospace', fontSize: '0.55rem', letterSpacing: '0.1em', cursor: 'pointer' }}>ENVOYER</button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function MusicControl({ track }) {
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(30);
  const [expanded, setExpanded] = useState(false);
  const trackLabels = { menu: 'MÉDITATION', battle: 'RÉSONANCE', victory: 'RÉSOLUTION', '': '—' };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', position: 'relative' }}>
      <AnimatePresence>
        {expanded && (
          <motion.div key="vol-panel" initial={{ opacity: 0, scaleX: 0 }} animate={{ opacity: 1, scaleX: 1 }} exit={{ opacity: 0, scaleX: 0 }}
            style={{ position: 'absolute', right: '100%', top: '50%', transform: 'translateY(-50%)', marginRight: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.6rem', background: 'rgba(2,6,23,0.97)', border: '1px solid rgba(167,139,250,0.2)', padding: '0.3rem 0.75rem', whiteSpace: 'nowrap', transformOrigin: 'right center' }}>
            <span style={{ fontFamily: 'monospace', fontSize: '0.4rem', letterSpacing: '0.18em', color: 'rgba(167,139,250,0.6)', textTransform: 'uppercase' }}>♫ {trackLabels[track] || '—'} · PESACORE</span>
            <input type="range" min="0" max="100" value={volume} onChange={e => { const v = parseInt(e.target.value); setVolume(v); music.setVolume(v / 100); }} style={{ width: '65px', accentColor: '#a78bfa', cursor: 'pointer' }}/>
            <span style={{ fontFamily: 'monospace', fontSize: '0.4rem', color: 'rgba(255,255,255,0.25)', minWidth: '2.5ch' }}>{volume}</span>
          </motion.div>
        )}
      </AnimatePresence>
      <motion.button whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.94 }}
        onClick={() => setExpanded(e => !e)} onDoubleClick={() => { const m = music.toggleMute(); setMuted(m); }}
        title="Clic : volume · Double-clic : mute"
        style={{ background: expanded ? 'rgba(167,139,250,0.1)' : 'rgba(255,255,255,0.04)', border: `1px solid ${expanded ? 'rgba(167,139,250,0.4)' : 'rgba(255,255,255,0.1)'}`, color: muted ? 'rgba(255,255,255,0.2)' : expanded ? '#a78bfa' : 'rgba(255,255,255,0.45)', fontFamily: 'monospace', fontSize: '0.55rem', letterSpacing: '0.08em', padding: '0.28rem 0.55rem', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
        <span>{muted ? '🔇' : '♫'}</span>
        <span style={{ fontSize: '0.38rem', letterSpacing: '0.14em', textTransform: 'uppercase' }}>{muted ? 'OFF' : 'BGM'}</span>
      </motion.button>
    </div>
  );
}

function Section({ color, title, children }) {
  return (
    <div style={{ padding: '0.85rem', background: `${color}07`, border: `1px solid ${color}1a` }}>
      <div style={{ color, fontSize: '0.55rem', letterSpacing: '0.22em', textTransform: 'uppercase', marginBottom: '0.6rem', fontWeight: 700 }}>▸ {title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>{children}</div>
    </div>
  );
}

function Livret({ onClose }) {
  const [tab, setTab] = useState('intro');
  const tabs = [
    { id:'intro',       label:'序 Intro' },
    { id:'regles',      label:'⚔️ Règles' },
    { id:'concepts',    label: '🧠 Concepts' },
    { id:'adversaire',  label:'🤖 Précepteur' },
    { id:'personnages', label:'👘 Pers' },
    { id:'progression', label:'⭐ Progrès' },
    { id:'philosophie', label:'🌊 Phi' },
    { id: 'fragments', label: '✦ Sagesse' },
    { id: 'flow',      label: '🌀 Flow' },
  ];
  
  return (
  <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.92)',
        backdropFilter: 'blur(12px)',
        padding: '1rem'
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 30 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 30 }}
        style={{
          maxWidth: '850px',
          width: '100%',
          maxHeight: '92vh',
          background: 'linear-gradient(180deg, #0a1325 0%, #05080f 100%)',
          border: '2px solid rgba(167,139,250,0.45)',
          borderRadius: '12px',
          boxShadow: '0 0 70px rgba(167,139,250,0.3), inset 0 0 40px rgba(0,0,0,0.7)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
        onClick={e => e.stopPropagation()}
        //className="hide-scrollbar"
      >
        {/* Top Bar */}
        <div style={{ height: '4px', background: 'linear-gradient(90deg, #a78bfa, #f59e0b, #22d3ee)' }} />

        <div style={{ padding: '1.25rem 1.5rem 0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ 
              fontFamily: 'serif', 
              fontSize: '1.6rem', 
              color: '#fff', 
              fontStyle: 'bold', 
              textShadow: '0 0 20px #a78bfa' 
            }}>
              泰冥零 — THAI MEZRA
            </div>
            <div style={{ 
              fontFamily: 'monospace', 
              fontSize: '0.52rem', 
              letterSpacing: '0.4em', 
              color: '#a78bfa', 
              textTransform: 'uppercase', 
              marginTop: '0.15rem' 
            }}>
              PESACORE MODE • v1 • ARCADE-001
            </div>
          </div>

          <motion.button
            whileHover={{ scale: 1.15, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
            onClick={onClose}
            style={{
              background: 'none',
              border: '2px solid rgba(167,139,250,0.4)',
              color: '#a78bfa',
              width: 40,
              height: 40,
              cursor: 'pointer',
              fontSize: '1.2rem',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ✕
          </motion.button>
        </div>

        {/* Tabs */}
         <div className="livret-tabs"   // nouvelle classe pour le style de la scrollbar
            style={{
              display: 'flex',
              borderBottom: '1px solid rgba(167,139,250,0.2)',
              padding: '0 1rem',
              overflowX: 'auto',
              flexShrink: 0,
              gap: '4px',
              // Suppression de scrollbarWidth, msOverflowStyle et className hide-scrollbar
            }}
          >
            {tabs.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                style={{
                  background: tab === t.id ? 'rgba(167,139,250,0.15)' : 'transparent',
                  border: 'none',
                  borderBottom: tab === t.id ? '3px solid #a78bfa' : '3px solid transparent',
                  color: tab === t.id ? '#e0e7ff' : 'rgba(165,243,252,0.65)',
                  fontFamily: 'monospace',
                  fontSize: 'clamp(0.53rem, 2.4vw, 0.67rem)',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  padding: '0.7rem 0.85rem',   // légèrement réduit
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap',
                  borderRadius: '6px 6px 0 0',
                  flexShrink: 0,
                  minWidth: 'fit-content'
                }}
              >
                {t.label}
              </button>
            ))}
          </div>

        {/* Content Area - Scroll caché */}
        <div 
          style={{ 
            flex: 1, 
            overflowY: 'auto', 
            padding: '1.5rem', 
            fontFamily: 'monospace', 
            fontSize: 'clamp(0.63rem, 2.5vw, 0.78rem)', 
            color: 'rgba(203,213,225,0.88)', 
            lineHeight: 1.82, 
            letterSpacing: '0.03em' 
          }} 
          className="hide-scrollbar"
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={tab}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 0.25 }}
            >

              {tab === 'intro' && (
                <div>
                  <p style={{ fontFamily: 'serif', fontStyle: 'italic', fontSize: 'clamp(0.85rem, 3.2vw, 1.12rem)', color: '#c4d0ff', marginBottom: '1.5rem', lineHeight: 1.55 }}>
                    « Ne pas vaincre par la force — mais guider l'énergie adverse jusqu'à sa propre chute. »
                  </p>
                  <p>Le <strong style={{ color: '#a78bfa' }}>THAI MEZRA</strong> est un duel rhétorique. Vous incarnez un philosophe face au <strong style={{ color: '#f59e0b' }}>Précepteur</strong>.</p>
                  <p style={{ marginTop: '0.75rem' }}>Chaque concept philosophique <strong style={{ color: '#22d3ee' }}>coûte de l'Énergie Vitale</strong> et inflige des dégâts.</p>
                  <p style={{ marginTop: '0.75rem' }}>Match en <strong style={{ color: '#f59e0b' }}>3 rounds</strong>. Premier à 2 rounds remporte le match.</p>

                  <div style={{ marginTop: '1.75rem', padding: '1.1rem', background: 'rgba(167,139,250,0.08)', border: '1px solid rgba(167,139,250,0.35)', borderRadius: '10px' }}>
                    <div style={{ color: '#facc15', fontSize: '0.62rem', letterSpacing: '0.25em', textTransform: 'uppercase', marginBottom: '0.8rem' }}>✦ NOUVEAUTÉS v2 — ARCADE EDITION</div>
                    <p>• <strong style={{ color: '#f59e0b' }}>4 personnages</strong> avec kimono animé et ultime unique.</p>
                    <p>• <strong style={{ color: '#22d3ee' }}>Concepts cross-époque</strong> : accès à d'autres ères avec surcoût.</p>
                    <p>• <strong style={{ color: '#a78bfa' }}>Progression</strong> : rangs Apprenti → Transcendant.</p>
                    <p>• <strong style={{ color: '#10b981' }}>Guilde</strong> : espace partagé persistant.</p>
                  </div>
                </div>
              )}

              {tab === 'regles' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                  <Section color="#f59e0b" title="Structure du match" icon="🎮">
                    <p>• Chaque round commence à <strong style={{ color: '#22d3ee' }}>100 Énergie Vitale</strong>.</p>
                    <p>• Alternance : votre tour → le Précepteur.</p>
                    <p>• Premier à 0 perd le round. 2 rounds = victoire.</p>
                    <p>• <strong style={{ color: '#facc15' }}>Double KO</strong> : round rejoué.</p>
                  </Section>

                  <Section color="#c084fc" title="Concepts cross-époque" icon="⏳">
                    <p>Chaque personnage appartient à une <strong style={{ color: '#c084fc' }}>époque philosophique</strong>.</p>
                    <p>Jouer un concept d'une <strong style={{ color: '#ef4444' }}>autre époque</strong> coûte <strong style={{ color: '#ef4444' }}>+10 Énergie</strong> supplémentaire.</p>
                    <p>Ibn Sīnā réduit cette pénalité à <strong style={{ color: '#c084fc' }}>+5</strong> grâce à son passif.</p>
                    <p>Stratégie hybride = puissance accrue, coût augmenté.</p>
                  </Section>

                  <Section color="#f59e0b" title="Ultimes" icon="🌟">
                    <p>Chaque personnage possède une <strong style={{ color: '#f59e0b' }}>technique ultime</strong> débloquée au <strong style={{ color: '#f59e0b' }}>niveau 5</strong>.</p>
                    <p>L'ultime n'apparaît dans la main que si vous jouez le personnage correspondant.</p>
                  </Section>

                  <Section color="#ef4444" title="État Grippal" icon="☣️">
                    <p>• 0–50% : aucune pénalité.</p>
                    <p>• {'>'}50% : perte d'énergie à chaque tour : <code style={{ background: 'rgba(255,255,255,0.06)', padding: '0.1rem 0.4rem' }}>(grip-50)×0.1</code></p>
                    <p>• 70%+ : halo rouge + surcharge critique.</p>
                  </Section>
                </div>
              )}

              {tab === 'concepts' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {[...CONCEPTS_BASE, ...CONCEPTS_ULTIMES].map(c => (
                    <div key={c.id} style={{
                      display: 'flex',
                      gap: '0.9rem',
                      padding: '0.85rem',
                      background: `${c.color}0d`,
                      border: `1px solid ${c.isUltimate ? c.color + '66' : c.color + '33'}`,
                      borderRadius: '8px',
                      alignItems: 'flex-start'
                    }}>
                      <div style={{ flexShrink: 0, width: 46, textAlign: 'center' }}>
                        <div style={{ fontFamily: 'serif', fontSize: '1.45rem', color: c.color + '95' }}>{c.kanji}</div>
                        {c.isUltimate && <div style={{ fontSize: '0.4rem', color: '#fbbf24', letterSpacing: '0.15em' }}>ULTIME</div>}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: c.color, fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.1em', marginBottom: '0.25rem' }}>{c.label}</div>
                        <div style={{ color: 'rgba(203,213,225,0.7)', fontSize: '0.64rem', lineHeight: 1.6 }}>{c.desc}</div>
                      </div>
                      <div style={{ flexShrink: 0, textAlign: 'right', fontSize: '0.5rem', lineHeight: 1.4 }}>
                        <span style={{ color: c.cost === 0 ? '#10b981' : 'rgba(165,243,252,0.75)' }}>
                          {c.cost === 0 ? '◆ FREE' : `▼ ${c.cost}E`}
                        </span>
                        {c.drain > 0 && <div style={{ color: '#f87171' }}>◇ -{c.drain}</div>}
                        <div style={{ fontSize: '0.43rem', color: 'rgba(255,255,255,0.3)', marginTop: '4px' }}>{c.epoque}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {tab === 'adversaire' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                  <p style={{ fontFamily: 'serif', fontStyle: 'italic', color: 'rgba(255,255,255,0.55)', lineHeight: 1.7 }}>
                    « ADV-7 n'est pas un simple bot. C'est un Précepteur... »
                  </p>

                  <Section color="#f59e0b" title="Changement de Précepteur" icon="🔄">
                    <p>Le Précepteur peut incarner <strong style={{ color: '#f59e0b' }}>4 philosophes</strong> différents selon le contexte :</p>
                    {AI_CHARS.map(ac => (
                      <div key={ac.id} style={{ padding: '0.5rem 0.7rem', background: `${ac.color}08`, border: `1px solid ${ac.color}22`, marginBottom: '0.4rem', borderRadius: '6px' }}>
                        <span style={{ color: ac.color, fontWeight: 700, fontSize: '0.68rem' }}>{ac.nom}</span>
                        <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.58rem', marginLeft: '0.6rem' }}>— {ac.tradition}</span>
                      </div>
                    ))}
                  </Section>

                  <Section color="#a78bfa" title="Conditions de changement" icon="⚡">
                    <p>• <strong style={{ color: '#a5f3c3' }}>Précepteur-KANT</strong> : si énergie Précepteur {'<'} 35 au round 1.</p>
                    <p>• <strong style={{ color: '#facc15' }}>Précepteur-ARÉTÉ</strong> : si grip Précepteur {'>'}55%.</p>
                    <p>• <strong style={{ color: '#fb923c' }}>Précepteur-WEIL</strong> : si vous êtes en flow (streak ≥3).</p>
                    <p>• <strong style={{ color: '#c084fc' }}>Précepteur-AVIC.</strong> : si votre énergie {'<'}30.</p>
                  </Section>

                  <Section color="#22d3ee" title="Indicateurs visuels" icon="👁️">
                    <p>• <strong>Trois points clignotants</strong> : Le Précepteur réfléchit (délai 700-1400ms).</p>
                    <p>• <strong>Bannière de changement</strong> : nom du nouveau philosophe révélé.</p>
                    <p>• <strong>Mémoire du Précepteur</strong> : les 3 derniers coups adverses sont visibles.</p>
                  </Section>
                </div>
              )}

              {tab === 'personnages' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                  {PERSONNAGES.map(p => (
                    <div key={p.id} style={{
                      padding: '1rem',
                      background: `${p.avatarColor}08`,
                      border: `1px solid ${p.avatarColor}33`,
                      borderRadius: '10px',
                      display: 'flex',
                      gap: '1.25rem',
                      alignItems: 'flex-start'
                    }}>
                      <div style={{ width: 68, height: 88, flexShrink: 0 }}>
                        <KimonoAvatar char={p} side="left" isAnimating={false} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: p.avatarColor, fontWeight: 700, fontSize: '0.78rem', letterSpacing: '0.1em' }}>{p.fullNom}</div>
                        <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.52rem', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: '0.5rem' }}>{p.epoque}</div>
                        <p style={{ color: 'rgba(203,213,225,0.7)', fontSize: '0.65rem', lineHeight: 1.6, marginBottom: '0.6rem' }}>{p.description}</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                          <span style={{ fontSize: '0.58rem', color: '#10b981' }}>✦ PASSIF : {p.passifDesc}</span>
                          <span style={{ fontSize: '0.58rem', color: p.avatarColor }}>
                            ⚡ ULTIME : {p.ultimateLabel} — {CONCEPTS.find(c => c.id === p.ultimateId)?.desc}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {tab === 'progression' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                  <Section color="#f59e0b" title="Rangs" icon="🏆">
                    {RANGS.map(r => (
                      <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginBottom: '0.6rem' }}>
                        <span style={{ fontFamily: 'serif', fontSize: '1.35rem', color: r.color + '88' }}>{r.kanji}</span>
                        <span style={{ color: r.color, fontWeight: 700, fontSize: '0.7rem', letterSpacing: '0.1em' }}>{r.label}</span>
                        <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.6rem' }}>NIV. {r.min}–{r.max === 99 ? '∞' : r.max}</span>
                      </div>
                    ))}
                  </Section>

                  <Section color="#a78bfa" title="Déblocages par niveau" icon="🔓">
                    <p>• NIV. 5 : <strong style={{ color: '#f59e0b' }}>Ultime personnage</strong> débloqué</p>
                    <p>• NIV. 5 : <strong style={{ color: '#a78bfa' }}>1er slot combo</strong></p>
                    <p>• NIV. 10 : <strong style={{ color: '#a78bfa' }}>2e slot combo</strong></p>
                    <p>• NIV. 15 : <strong style={{ color: '#a78bfa' }}>3e slot combo</strong></p>
                  </Section>

                  <Section color="#22d3ee" title="Gain XP" icon="📈">
                    <p>• Victoire round : <strong style={{ color: '#22d3ee' }}>+25 XP</strong></p>
                    <p>• Victoire match : <strong style={{ color: '#f59e0b' }}>+75 XP bonus</strong></p>
                    <p>• Défaite match : <strong style={{ color: '#94a3b8' }}>+15 XP</strong></p>
                  </Section>
                </div>
              )}

              {tab === 'philosophie' && (
                <div>
                  <p style={{ fontFamily: 'serif', fontStyle: 'italic', fontSize: '0.98rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.7, marginBottom: '1.5rem' }}>
                    « Le THAI MEZRA n'est pas l'art de convaincre par la force — c'est l'art de lire le mouvement adverse et d'y trouver l'ouverture. »
                  </p>
                  {[
                    ['#a78bfa', 'Seiryoku Zenyo (精力善用)', "Jigoro Kano. Usage maximal de l'énergie minimale."],
                    ['#22d3ee', 'Ma (間)', "Le vide porteur de sens. Une pause crée plus d'impact qu'une réplique précipitée."],
                    ['#a78bfa', 'Asabiyya (عصبية)', "Ibn Khaldoun, XIVe s. La cohésion sociale comme force politique."],
                    ['#10b981', 'Jita Kyoei (自他共栄)', "Kano. Prospérité mutuelle — élever les deux positions."],
                    ['#f7d44a', 'Kintsugi (金継ぎ)', "Réparer à l'or. Les fissures deviennent la partie la plus précieuse."],
                    ['#94a3b8', 'Synthèse Hégélienne (揚棄)', "Thèse, antithèse, synthèse. Aufhebung — le dépassement conservateur."],
                    ['#fb923c', 'Ubuntu (烏班圖)', "« Je suis parce que nous sommes. » L'identité naît de la relation."],
                    ['#c084fc', 'Ibn Sīnā — Intellect Agent', "L'intellect universel illumine la raison particulière. Le savoir transcende les cultures."],
                  ].map(([color, title, desc]) => (
                    <p key={title} style={{ marginTop: '0.85rem' }}>
                      <strong style={{ color }}>{title}</strong> — {desc}
                    </p>
                  ))}
                </div>
              )}

              {tab === 'flow' && (
                <div>
                  <p style={{ fontFamily:'serif', fontStyle:'italic', fontSize:'clamp(0.85rem,3.2vw,1.05rem)', color:'rgba(255,255,255,0.6)', lineHeight:1.7, marginBottom:'1.5rem' }}>
                    « Mushin — 無心 — l'esprit sans esprit. L'état où la pensée cesse d'être un obstacle à l'action. »
                  </p>
                  <p>Le <strong style={{color:'#22d3ee'}}>Flow</strong> est cet état décrit par Mihály Csíkszentmihályi : une absorption totale dans l'activité, où temps et effort s'effacent. Les meistres japonais l'appellent <strong style={{color:'#f59e0b'}}>Mushin</strong> — littéralement "sans esprit" — l'absence d'hésitation.</p>
                  <p style={{marginTop:'0.75rem'}}>Dans l'Arène Rhétorique, vous l'atteindrez sans chercher à le provoquer. Il viendra de la continuité de votre engagement.</p>
                  <p style={{marginTop:'0.75rem'}}>Les paliers :</p>
                  <div style={{marginTop:'0.6rem',display:'flex',flexDirection:'column',gap:'0.4rem'}}>
                    {[['×3','FLUX','#22d3ee','La pensée commence à couler.'],['×5','ÉLAN','#a78bfa','Le momentum s\'installe.'],['×7','MUSHIN','#f59e0b','L\'esprit sans obstacle.'],['×10','TRANSCENDANCE','#ef4444','Fragment de sagesse révélé.']].map(([x,l,c,d])=>(
                      <div key={l} style={{display:'flex',gap:'0.75rem',alignItems:'center',padding:'0.45rem 0.6rem',background:`${c}08`,border:`1px solid ${c}22`}}>
                        <span style={{fontFamily:'monospace',fontSize:'0.72rem',fontWeight:700,color:c,minWidth:'2.5ch'}}>{x}</span>
                        <span style={{fontFamily:'monospace',fontSize:'0.6rem',color:c,letterSpacing:'0.1em',minWidth:'9ch'}}>{l}</span>
                        <span style={{fontFamily:'monospace',fontSize:'0.58rem',color:'rgba(255,255,255,0.4)'}}>{d}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {tab === 'fragments' && (
                <div>
                  <div style={{fontFamily:'monospace',fontSize:'0.45rem',letterSpacing:'0.25em',color:'rgba(245,158,11,0.5)',textTransform:'uppercase',marginBottom:'1rem'}}>
                    FRAGMENTS COLLECTÉS EN ATTEIGNANT ×10 SANS ÊTRE TOUCHÉ
                  </div>
                  {(() => {
                    const collected = loadFragments();
                    const unlocked = WISDOM_FRAGMENTS.filter(f => collected.includes(f.id));
                    const locked = WISDOM_FRAGMENTS.length - unlocked.length;
                    return (
                      <>
                        {unlocked.length === 0 && (
                          <div style={{fontFamily:'serif',fontStyle:'italic',fontSize:'0.75rem',color:'rgba(255,255,255,0.2)',textAlign:'center',padding:'2rem 0'}}>
                            Aucun fragment collecté. Atteignez ×10 sans être touché pour révéler la sagesse cachée.
                          </div>
                        )}
                        {unlocked.map(f => (
                          <div key={f.id} style={{marginBottom:'1rem',padding:'0.85rem',background:'rgba(245,158,11,0.05)',border:'1px solid rgba(245,158,11,0.2)'}}>
                            <p style={{fontFamily:'serif',fontStyle:'italic',fontSize:'0.78rem',color:'rgba(255,255,255,0.75)',lineHeight:1.65,marginBottom:'0.4rem'}}>{f.text}</p>
                            <div style={{fontFamily:'monospace',fontSize:'0.45rem',color:'rgba(245,158,11,0.5)',letterSpacing:'0.12em'}}>— {f.source}</div>
                          </div>
                        ))}
                        {locked > 0 && (
                          <div style={{fontFamily:'monospace',fontSize:'0.52rem',color:'rgba(255,255,255,0.15)',textAlign:'center',marginTop:'1rem'}}>
                            {locked} fragment{locked > 1 ? 's' : ''} encore caché{locked > 1 ? 's' : ''}…
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}

            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div style={{ padding: '0.9rem 1.5rem', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <span style={{ fontFamily: 'monospace', fontSize: '0.45rem', letterSpacing: '0.25em', color: 'rgba(255,255,255,0.15)', textTransform: 'uppercase' }}>
            PESACORE • PESA-002 • v2
          </span>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.96 }}
            onClick={onClose}
            style={{
              padding: '0.5rem 1.4rem',
              border: '1px solid #a78bfa',
              background: 'rgba(167,139,250,0.08)',
              color: '#a78bfa',
              fontFamily: 'monospace',
              fontSize: '0.58rem',
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              borderRadius: '6px'
            }}
          >
            FERMER LE LIVRET
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function GripMeter({ value, side }) {
  const pct = Math.min(100, value);
  const color = pct < 30 ? '#10b981' : pct < 60 ? '#f59e0b' : '#ef4444';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', justifyContent: side === 'left' ? 'flex-start' : 'flex-end', alignItems: 'center', gap: '0.4rem' }}>
        <span style={{ fontFamily: 'monospace', fontSize: 'clamp(0.38rem, 2.5vw, 0.58rem)', letterSpacing: '0.2em', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase' }}>GRIP</span>
        <span style={{ fontFamily: 'monospace', fontSize: 'clamp(0.55rem, 3.5vw, 0.8rem)', color, fontWeight: 700 }}>{Math.round(pct)}%</span>
      </div>
      <div style={{ height: '3px', background: 'rgba(255,255,255,0.07)', overflow: 'hidden', direction: side === 'right' ? 'rtl' : 'ltr' }}>
        <motion.div animate={{ width: `${pct}%` }} transition={{ duration: 0.5 }} style={{ height: '100%', background: color }}/>
      </div>
    </div>
  );
}

function EnergyBar({ value, side, color = '#22d3ee', label }) {
  const pct = Math.max(0, Math.min(100, value));
  const segments = 10;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
      <div style={{ display: 'flex', justifyContent: side === 'left' ? 'flex-start' : 'flex-end', alignItems: 'baseline', gap: '0.5rem' }}>
        <span style={{ fontFamily: 'monospace', fontSize: 'clamp(0.4rem, 3vw, 0.6rem)', letterSpacing: '0.2em', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase' }}>{label}</span>
        <span style={{ fontFamily: 'monospace', fontSize: 'clamp(0.75rem, 5vw, 1.05rem)', fontWeight: 700, color, lineHeight: 1 }}>{Math.round(value)}</span>
      </div>
      <div style={{ display: 'flex', gap: '2px', flexDirection: side === 'right' ? 'row-reverse' : 'row' }}>
        {Array.from({ length: segments }).map((_, i) => {
          const segPct = ((i + 1) / segments) * 100;
          const filled = pct >= segPct;
          return (
            <div key={i} style={{ flex: 1, height: 'clamp(5px, 1.2vw, 9px)', background: 'rgba(255,255,255,0.05)', overflow: 'hidden' }}>
              <motion.div animate={{ width: filled ? '100%' : '0%' }} transition={{ duration: 0.4 }}
                style={{ height: '100%', background: color, boxShadow: filled ? `0 0 5px ${color}55` : 'none' }}/>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SynergyBanner({ synergy, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 2500);
    return () => clearTimeout(t);
  }, [onDone]);

  const isLeft = synergy.side === 'left';
  const isRight = synergy.side === 'right';

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.8 }}
      style={{
        position: 'absolute',
        top: '60%',
        left: isLeft ? '20%' : isRight ? undefined : '50%',
        right: isRight ? '20%' : undefined,
        transform: isRight ? 'translate(50%, -50%)' : 'translate(-50%, -50%)',
        zIndex: 30,
        textAlign: 'center',
        pointerEvents: 'none',
      }}
    >
      <motion.div
        animate={{
          boxShadow: [
            `0 0 20px ${synergy.color}55`,
            `0 0 60px ${synergy.color}88`,
            `0 0 20px ${synergy.color}55`,
          ],
        }}
        transition={{ repeat: Infinity, duration: 0.8 }}
        style={{
          background: `linear-gradient(135deg, rgba(2,6,23,0.97), rgba(2,6,23,0.9))`,
          border: `1px solid ${synergy.color}`,
          padding: '0.8rem 1.5rem',
        }}
      >
        <div
          style={{
            fontFamily: 'monospace',
            fontSize: '0.5rem',
            letterSpacing: '0.35em',
            color: synergy.color,
            textTransform: 'uppercase',
          }}
        >
          ✦ SYNERGIE ACTIVÉE ✦
        </div>
        <div
          style={{
            fontFamily: 'serif',
            fontSize: '1.3rem',
            color: '#fff',
            fontStyle: 'italic',
            margin: '0.3rem 0',
          }}
        >
          {synergy.name}
        </div>
        <div style={{ fontFamily: 'serif', fontSize: '1.8rem', color: synergy.color + '88' }}>
          {synergy.kanji}
        </div>
        <div
          style={{
            fontFamily: 'monospace',
            fontSize: '0.58rem',
            color: 'rgba(255,255,255,0.6)',
            marginTop: '0.3rem',
            lineHeight: 1.5,
          }}
        >
          {synergy.bonus.msg}
        </div>
      </motion.div>
    </motion.div>
  );
}

function DisorderMeter({ value, side }) {
  if (!value || value === 0) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: side === 'left' ? 'flex-start' : 'flex-end', gap: '0.3rem', marginTop: '0.2rem' }}>
      <span style={{ fontFamily: 'monospace', fontSize: 'clamp(0.38rem, 2vw, 0.5rem)', color: 'rgba(255,255,255,0.3)' }}>🌫️ DÉSORDRE</span>
      <span style={{ fontFamily: 'monospace', fontSize: 'clamp(0.48rem, 3vw, 0.65rem)', color: '#c084fc', fontWeight: 600 }}>{Math.round(value)}%</span>
    </div>
  );
}

function DamagePopup({ label, color, side }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 0, scale: 0.5 }}
      animate={{ opacity: [0, 1, 1, 0], y: [-20, -55, -90, -120], scale: [0.5, 1.3, 1.1, 0.9] }}
      transition={{ duration: 1.5, times: [0, 0.2, 0.65, 1] }}
      style={{ position: 'absolute', top: '28%', [side === 'left' ? 'right' : 'left']: '4%', fontFamily: 'monospace', fontSize: 'clamp(0.9rem, 7vw, 1.8rem)', fontWeight: 900, color, letterSpacing: '0.06em', textTransform: 'uppercase', textShadow: `0 0 25px ${color}`, pointerEvents: 'none', zIndex: 20, WebkitTextStroke: '1px rgba(0,0,0,0.5)' }}
    >{label}</motion.div>
  );
}

function loadGlobalStats() {
  try { return JSON.parse(localStorage.getItem('judoverbal_stats') || '{"totalDrainDealt":0,"matchesWon":0,"synergiesActivated":0,"unlockedChars":[]}'); }
  catch { return { totalDrainDealt: 0, matchesWon: 0, synergiesActivated: 0, unlockedChars: [] }; }
}

function saveGlobalStats(stats) {
  try { localStorage.setItem('judoverbal_stats', JSON.stringify(stats)); } catch {}
}

function checkUnlocks(stats) {
  const newUnlocks = [];
  for (const char of [...PERSONNAGES_CACHES, SOPHIA]) {
    if (stats.unlockedChars.includes(char.id)) continue;
    const cond = char.unlockCondition;
    let met = false;
    if (cond.type === 'totalDrainDealt' && stats.totalDrainDealt >= cond.value) met = true;
    if (cond.type === 'matchesWon' && stats.matchesWon >= cond.value) met = true;
    if (cond.type === 'synergiesActivated' && stats.synergiesActivated >= cond.value) met = true;
    if (cond.type === 'allCharsUnlocked') {
      const allOthers = PERSONNAGES_CACHES.every(c => stats.unlockedChars.includes(c.id));
      if (allOthers) met = true;
    }
    if (met) newUnlocks.push(char.id);
  }
  return newUnlocks;
}

function RoundIntro({ round, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 2200); return () => clearTimeout(t); }, [onDone]);
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'absolute', inset: 0, zIndex: 50, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'rgba(2,6,23,0.93)', backdropFilter: 'blur(8px)' }}>
      <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
        style={{ fontFamily: 'monospace', fontSize: 'clamp(0.5rem, 4vw, 0.8rem)', letterSpacing: '0.4em', color: 'rgba(255,255,255,0.2)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>ROUND {round}</motion.p>
      <motion.h2 initial={{ opacity: 0, scale: 1.4 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5, duration: 0.5 }}
        style={{ fontFamily: 'serif', fontSize: 'clamp(2.5rem, 12vw, 5rem)', fontWeight: 400, color: '#fff', letterSpacing: '0.05em', margin: 0, fontStyle: 'italic' }}>Combat</motion.h2>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: [0, 1, 0] }} transition={{ delay: 1.2, duration: 0.8 }}
        style={{ fontFamily: 'monospace', fontSize: 'clamp(0.5rem, 3.5vw, 0.8rem)', letterSpacing: '0.5em', color: '#f59e0b', textTransform: 'uppercase', marginTop: '0.75rem' }}>始め — HAJIME</motion.div>
    </motion.div>
  );
}

function VictoryScreen({ winner, energyLeft, roundsWon, onRematch, onExit, selectedChar, narrativeDefeat }) {
  const isPlayerWin = winner === 'player';
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: 'absolute', inset: 0, zIndex: 50,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        background: isPlayerWin 
          ? 'radial-gradient(ellipse at 50% 50%, #0a1a0a 0%, #020617 80%)'
          : 'radial-gradient(ellipse at 50% 50%, #1a0a0a 0%, #020617 80%)',
        gap: '1.5rem',
        padding: '2rem',
      }}
    >
      {/* ─── Titre ──────────────────────────────────────── */}
      <motion.div
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2, duration: 1 }}
        className="cinzel"
        style={{
          fontSize: 'clamp(1.2rem, 5vw, 2rem)',
          color: isPlayerWin ? '#e9d5ff' : '#ef4444',
          textShadow: isPlayerWin 
            ? '0 0 40px rgba(233,213,255,0.5)' 
            : '0 0 40px rgba(239,68,68,0.4)',
          textAlign: 'center',
          letterSpacing: '0.15em',
        }}
      >
        {isPlayerWin 
          ? 'FRAGMENT RESTAURÉ' 
          : narrativeDefeat 
            ? 'LE FRAGMENT S\'EST DISSOUS'
            : 'LE FRAGMENT S\'ESTOMPE'
        }
      </motion.div>

      {/* ─── messages narratif ───────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 1 }}
        style={{
          maxWidth: '450px',
          textAlign: 'center',
          padding: '1.5rem',
          background: isPlayerWin ? 'rgba(233,213,255,0.05)' : 'rgba(239,68,68,0.05)',
          border: `1px solid ${isPlayerWin ? 'rgba(233,213,255,0.15)' : 'rgba(239,68,68,0.15)'}`,
        }}
      >
        <div className="mono" style={{
          fontSize: '0.4rem',
          letterSpacing: '0.3em',
          color: 'rgba(233,213,255,0.5)',
          textTransform: 'uppercase',
          marginBottom: '0.6rem',
        }}>
          SOPHIA PERENNIS
        </div>
        <p style={{
          fontFamily: 'serif',
          fontStyle: 'italic',
          fontSize: 'clamp(0.6rem, 2vw, 0.8rem)',
          color: 'rgba(255,255,255,0.65)',
          lineHeight: 1.7,
          margin: 0,
        }}>
          {isPlayerWin 
            ? `« La trace de ${selectedChar.nom} brille à nouveau dans la Bibliothèque. Sa pensée a traversé l'oubli. Un fragment de plus dans la mémoire du monde. »`
            : `« Le fragment vacille, mais il n'est pas perdu. Chaque défaite est une ombre qui appelle une lumière plus grande. Retournez dans l'arène. »`
          }
        </p>
      </motion.div>

      {/* ─── Stats du combat ────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.8 }}
        style={{
          display: 'flex',
          gap: '1.5rem',
          fontFamily: 'monospace',
          fontSize: '0.55rem',
          color: 'rgba(255,255,255,0.4)',
        }}
      >
        <span>Rounds : {roundsWon[0]} - {roundsWon[1]}</span>
        <span>Énergie restante : {energyLeft}%</span>
      </motion.div>

      {/* ─── Actions ────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.3, duration: 0.8 }}
        style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}
      >
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={onRematch}
          style={{
            padding: '0.8rem 2rem',
            background: 'rgba(233,213,255,0.08)',
            border: '1px solid rgba(233,213,255,0.25)',
            color: '#e9d5ff',
            fontFamily: 'monospace',
            fontSize: '0.6rem',
            letterSpacing: '0.15em',
            cursor: 'pointer',
            textTransform: 'uppercase',
          }}
        >
          ⚔ Nouveau rituel
        </motion.button>
        
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={onExit}
          style={{
            padding: '0.8rem 2rem',
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.12)',
            color: 'rgba(255,255,255,0.5)',
            fontFamily: 'monospace',
            fontSize: '0.6rem',
            letterSpacing: '0.15em',
            cursor: 'pointer',
            textTransform: 'uppercase',
          }}
        >
          ← Bibliothèque
        </motion.button>
      </motion.div>

      {/* ─── Citation du personnage ─────────────────────── */}
      {isPlayerWin && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.4 }}
          transition={{ delay: 2, duration: 2 }}
          style={{
            position: 'absolute',
            bottom: '10%',
            left: '70%',
            textAlign: 'center',
            maxWidth: '400px',
            padding: '0 1rem',
          }}
        >
          <p style={{
            fontFamily: 'serif',
            fontStyle: 'italic',
            fontSize: '0.75rem',
            color: selectedChar.avatarColor,
            opacity: 1,
            lineHeight: 1.6,
          }}>
            {getRandomWisdomQuote(selectedChar.id)}
          </p>
        </motion.div>
      )}
    </motion.div>
  );
}

function UnlockRevealAnim({ char, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 5000); return () => clearTimeout(t); }, [onDone]);
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: 'fixed', inset: 0, zIndex: 400,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(2,6,23,0.97)', backdropFilter: 'blur(16px)',
      }}
    >
      <motion.div
        initial={{ scale: 0.7, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 200, damping: 18 }}
        style={{ textAlign: 'center', maxWidth: 420, padding: '2rem' }}
      >
        {/* Particules */}
        {Array.from({ length: 12 }).map((_, i) => (
          <motion.div key={i}
            style={{ position:'absolute', width:6, height:6, borderRadius:'50%', background:char.avatarColor, left:'50%', top:'40%' }}
            animate={{ x: Math.cos(i/12*Math.PI*2)*120, y: Math.sin(i/12*Math.PI*2)*120, opacity:[1,0], scale:[1,0.3] }}
            transition={{ duration:1.2, delay:0.3+i*0.05 }}
          />
        ))}
        <motion.div
          animate={{ boxShadow: [`0 0 40px ${char.avatarColor}44`, `0 0 120px ${char.avatarColor}88`, `0 0 40px ${char.avatarColor}44`] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
          style={{ width: 100, height: 140, margin: '0 auto 1.5rem', position: 'relative' }}
        >
          <KimonoAvatar char={char} side="left" isAnimating />
        </motion.div>
        <div style={{ fontFamily:'monospace', fontSize:'0.42rem', letterSpacing:'0.4em', color:'rgba(255,255,255,0.3)', textTransform:'uppercase', marginBottom:'0.5rem' }}>✦ PERSONNAGE DÉBLOQUÉ ✦</div>
        <div style={{ fontFamily:'serif', fontSize:'2rem', color:char.avatarColor, fontStyle:'italic', marginBottom:'0.4rem', textShadow:`0 0 40px ${char.avatarColor}` }}>{char.fullNom}</div>
        <div style={{ fontFamily:'monospace', fontSize:'0.52rem', color:'rgba(255,255,255,0.4)', letterSpacing:'0.15em', marginBottom:'1rem' }}>{char.epoque}</div>
        <div style={{ fontFamily:'monospace', fontSize:'0.62rem', color:'rgba(255,255,255,0.6)', lineHeight:1.6, marginBottom:'0.75rem' }}>{char.description}</div>
        <div style={{ fontFamily:'monospace', fontSize:'0.52rem', color:char.avatarColor }}>✦ {char.passifDesc}</div>
        <div style={{ fontFamily:'monospace', fontSize:'0.42rem', color:'rgba(255,255,255,0.15)', marginTop:'1.5rem', letterSpacing:'0.2em' }}>DISPONIBLE DANS LE MENU DE SÉLECTION</div>
      </motion.div>
    </motion.div>
  );
}
const ARENAS = ['/images/arena-1.jpg', '/images/arena-2.jpg', '/images/arena-3.jpg'];

function CinematicOpeningScreen({ onComplete, characters = [] }) {
  const [lineIndex, setLineIndex]           = useState(0);
  const [typed, setTyped]                   = useState('');
  const [completedLines, setCompletedLines] = useState([]);
  const [showSkip, setShowSkip]             = useState(false);
  const [flickering, setFlickering]         = useState(false);
  const [currentCharIndex, setCurrentCharIndex] = useState(0);
  const [isComplete, setIsComplete]         = useState(false);
  const [arenaIndex, setArenaIndex]         = useState(0);

  const typingRef  = useRef(null);
  const phaseRef   = useRef(null);
  const initRef    = useRef(null);
  const textBoxRef = useRef(null);

  // ─── Fenêtre glissante : on ne garde que les 6 dernières lignes visibles ───
  const WINDOW_SIZE = 6;

  

  const ARENA_SWITCH_POINTS = [
    { from: 0,  arena: 0 },
    { from: 14, arena: 1 },
    { from: 30, arena: 2 },
    { from: 55, arena: 0 },
  ];

  const LINES = [
    { text: 'Il fut un temps où les idées gouvernaient le monde.',         delay: 900,  speed: 38, style: 'normal'    },
    { text: 'Kant. Weil. Ibn Sīnā. Et les autres.',                   delay: 900,  speed: 55, style: 'names'     },
    { text: 'Leurs pensées ont traversé des siècles.',                    delay: 700,  speed: 38, style: 'normal'    },
    { text: 'Puis le silence a commencé.',                                delay: 1100, speed: 42, style: 'warning'   },
    { text: "Pas d'un coup. Lentement.",                                  delay: 900,  speed: 45, style: 'normal'    },
    { text: 'Une note de bas de page. Un nom dans un manuel. Un oubli.',  delay: 800,  speed: 32, style: 'normal'    },
    { text: "Jusqu'à ce qu'il ne reste plus rien.",                       delay: 1200, speed: 40, style: 'warning'   },
    { text: '— — —',                                                       delay: 1400, speed: 80, style: 'separator' },

    { text: "Pour l'instant, ils sont décontextualisés...",               delay: 1000, speed: 38, style: 'normal'    },
    { text: "Kant ne défend pas l'universalité de la raison.",            delay: 800,  speed: 40, style: 'normal'    },
    { text: "Simone Weil ne lutte pas contre l'oppression'.",    delay: 700,  speed: 40, style: 'normal'    },
    { text: "Ibn Sīnā ne préserve pas le savoir menacé.",                 delay: 700,  speed: 40, style: 'normal'    },
    { text: "Ils sont simplement là.",                                    delay: 800,  speed: 42, style: 'normal'    },
    { text: "Il est temps d'y remédier.",                                 delay: 900,  speed: 45, style: 'reveal'    },
    { text: '— — —',                                                       delay: 1200, speed: 80, style: 'separator' },

    { text: "Ces figures se sont estompées avec le temps.",               delay: 1000, speed: 38, style: 'normal'    },
    { text: "Des connaissances éparpillées.",                             delay: 700,  speed: 40, style: 'normal'    },
    { text: "Des souvenirs flous.",                                       delay: 600,  speed: 42, style: 'normal'    },
    { text: "Ou pire : une occultation profonde.",                        delay: 900,  speed: 38, style: 'warning'   },
    { text: "C'est cela, l'enjeu véritable.",                             delay: 1000, speed: 40, style: 'reveal'    },
    { text: '— — —',                                                       delay: 1200, speed: 80, style: 'separator' },

    { text: "Ils ne se battent pas entre eux.",                           delay: 1100, speed: 38, style: 'reveal'    },
    { text: "Ils se battent contre l'oubli.",                             delay: 700,  speed: 38, style: 'reveal'    },
    { text: "L'arène n'est pas un tournoi.",                              delay: 900,  speed: 40, style: 'reveal'    },
    { text: "C'est un rituel de remémoration.",                           delay: 800,  speed: 38, style: 'reveal'    },
    { text: '— — —',                                                       delay: 1000, speed: 80, style: 'separator' },

    { text: "Ces personnages ne sont pas vraiment présents.",             delay: 1000, speed: 38, style: 'normal'    },
    { text: "Ce sont des échos.",                                         delay: 600,  speed: 45, style: 'normal'    },
    { text: "Des fragments de mémoire collective.",                       delay: 700,  speed: 40, style: 'normal'    },
    { text: "Tentant de se reconstituer dans un monde qui les a effacés.",delay: 900,  speed: 35, style: 'warning'   },
    { text: '— — —',                                                       delay: 1200, speed: 80, style: 'separator' },

    { text: "Le véritable adversaire n'est ni Kant ni Weil.",         delay: 1200, speed: 38, style: 'warning'   },
    { text: "C'est le silence qui recouvre la pensée humaine.",           delay: 900,  speed: 38, style: 'warning'   },
    { text: "C'est l'algorithme qui remplace la réflexion.",              delay: 800,  speed: 38, style: 'warning'   },
    { text: "C'est la doxa qui étouffe la nuance.",                       delay: 800,  speed: 38, style: 'warning'   },
    { text: '— — —',                                                       delay: 1200, speed: 80, style: 'separator' },

    { text: "Quelque chose les a convoqués dans cette arène.",            delay: 1100, speed: 38, style: 'sophia'    },
    { text: "Il ne s'agit pas d'une lutte à mort.",                              delay: 800,  speed: 42, style: 'sophia'    },
    { text: "Le but est qu'ils se souviennent les uns des autres.",        delay: 800,  speed: 38, style: 'sophia'    },
    { text: "Chaque duel est une tentative de réactiver une trace.",      delay: 900,  speed: 35, style: 'sophia'    },
    { text: "Une étincelle qu'on ravive avant qu'elle ne s'éteigne.",     delay: 900,  speed: 36, style: 'sophia'    },
    { text: '— — —',                                                       delay: 1200, speed: 80, style: 'separator' },

    { text: "La Sophia Perennis elle-même.",                              delay: 1100, speed: 42, style: 'sophia'    },
    { text: "La Sagesse éternelle.",                                      delay: 800,  speed: 45, style: 'sophia'    },
    { text: "Gardienne de la mémoire métaphysique de l'humanité.",        delay: 900,  speed: 36, style: 'sophia'    },
    { text: "Elle veille.",                                               delay: 1000, speed: 55, style: 'sophia'    },
    { text: '— — —',                                                       delay: 1000, speed: 80, style: 'separator' },

    { text: "Quand une pensée menace de disparaître pour de bon,",        delay: 1100, speed: 38, style: 'sophia'    },
    { text: "elle active le Mémorium.",                                   delay: 900,  speed: 42, style: 'sophia'    },
    { text: "Elle appelle les échos dans l'arène.",                       delay: 800,  speed: 40, style: 'sophia'    },
    { text: "Non par cruauté.",                                           delay: 800,  speed: 48, style: 'sophia'    },
    { text: "Mais parce que la friction est ce qui ravive la flamme.",    delay: 1000, speed: 35, style: 'sophia'    },
    { text: '— — —',                                                       delay: 1200, speed: 80, style: 'separator' },

    { text: "Chaque drain infligé n'est pas une blessure.",               delay: 1100, speed: 38, style: 'reveal'    },
    { text: "C'est une idée qui s'imprime dans la conscience.",           delay: 900,  speed: 36, style: 'reveal'    },
    { text: "Chaque point d'énergie restauré n'est pas un soin.",         delay: 900,  speed: 36, style: 'reveal'    },
    { text: "C'est une mémoire qui se réactive.",                         delay: 800,  speed: 40, style: 'reveal'    },
    { text: '— — —',                                                       delay: 1200, speed: 80, style: 'separator' },

    { text: "Tu n'incarnes pas un personnage qui se bat contre un autre.",delay: 1200, speed: 35, style: 'player'    },
    { text: "Tu es le dernier dépositaire d'une tradition.",              delay: 1000, speed: 38, style: 'player'    },
    { text: "Par toi, ils peuvent encore exister.",                       delay: 800,  speed: 38, style: 'player'    },
    { text: "Chaque duel est une cérémonie de résurrection symbolique.",  delay: 1000, speed: 35, style: 'player'    },
    { text: '— — —',                                                       delay: 1200, speed: 80, style: 'separator' },

    { text: "Quand tu invoques l'Impératif Catégorique,",                 delay: 1100, speed: 38, style: 'player'    },
    { text: "tu ne fais pas que baisser une jauge.",                      delay: 800,  speed: 40, style: 'player'    },
    { text: "Tu réinscris la loi morale universelle dans la conscience.", delay: 1000, speed: 32, style: 'player'    },
    { text: '— — —',                                                       delay: 1400, speed: 80, style: 'separator' },

    { text: "Si tu perds,",                                               delay: 1200, speed: 55, style: 'warning'   },
    { text: "ce n'est pas une simple défaite technique.",                 delay: 900,  speed: 38, style: 'warning'   },
    { text: "C'est un fragment de sagesse qui s'éteint.",                 delay: 1000, speed: 35, style: 'warning'   },
    { text: "Un peu plus.",                                               delay: 1200, speed: 60, style: 'warning'   },
    { text: '— — —',                                                       delay: 1500, speed: 80, style: 'separator' },

    { text: "Les pensées oubliées ne sont pas des bonus à débloquer.",    delay: 1200, speed: 36, style: 'reveal'    },
    { text: "Ce sont des sagesses en danger critique d'extinction.",      delay: 1000, speed: 34, style: 'reveal'    },
    { text: "Et toi,",                                                    delay: 1400, speed: 55, style: 'player'    },
    { text: "tu es leur dernier refuge.",                                 delay: 1200, speed: 42, style: 'player'    },
  ];

  // ─── Carousel personnages ───────────────────────────────────────────────────
  useEffect(() => {
    if (characters.length === 0) return;
    const interval = setInterval(() => {
      setCurrentCharIndex(prev => (prev + 1) % characters.length);
    }, 3200);
    return () => clearInterval(interval);
  }, [characters.length]);

  // ─── Typewriter ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (lineIndex >= LINES.length) {
      phaseRef.current = setTimeout(() => setIsComplete(true), 1800);
      return;
    }

    // Arène selon progression
    const newArena = ARENA_SWITCH_POINTS.reduce((acc, { from, arena }) => {
      return lineIndex >= from ? arena : acc;
    }, 0);
    setArenaIndex(newArena);

    const line = LINES[lineIndex];
    let charPos = 0;
    setTyped('');

    const startTyping = () => {
      typingRef.current = setInterval(() => {
        charPos++;
        setTyped(line.text.slice(0, charPos));

        if (charPos >= line.text.length) {
          clearInterval(typingRef.current);

          phaseRef.current = setTimeout(() => {
            // On pousse la ligne complétée dans la fenêtre glissante
            setCompletedLines(prev => {
              const next = [...prev, { ...line, id: lineIndex }];
              // On garde seulement les WINDOW_SIZE dernières
              return next.length > WINDOW_SIZE ? next.slice(next.length - WINDOW_SIZE) : next;
            });
            setTyped('');
            setLineIndex(l => l + 1);
          }, line.delay);
        }
      }, line.speed);
    };

    initRef.current = setTimeout(startTyping, lineIndex === 0 ? 1600 : 0);

    return () => {
      clearInterval(typingRef.current);
      clearTimeout(phaseRef.current);
      clearTimeout(initRef.current);
    };
  }, [lineIndex]);

  // ─── Skip / Flicker ─────────────────────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => setShowSkip(true), 2000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const flicker = () => {
      if (Math.random() < 0.3) {
        setFlickering(true);
        setTimeout(() => setFlickering(false), 80 + Math.random() * 120);
      }
      setTimeout(flicker, 3000 + Math.random() * 5000);
    };
    const t = setTimeout(flicker, 2000);
    return () => clearTimeout(t);
  }, []);

  const handleContinue = () => { if (isComplete) onComplete(); };
  const handleSkip = () => {
    clearInterval(typingRef.current);
    clearTimeout(phaseRef.current);
    clearTimeout(initRef.current);
    onComplete();
  };

  // ─── Styles par type ────────────────────────────────────────────────────────
  const getLineStyle = (style, dimmed = false) => {
    const base = {
      fontFamily: "'Share Tech Mono', monospace",
      lineHeight: 1.8,
      margin: 0,
      transition: 'opacity 0.4s',
    };
    const opacity = dimmed ? 0.22 : 1;
    switch (style) {
      case 'names':     return { ...base, fontSize: 'clamp(0.9rem, 3vw, 1.2rem)',    color: `rgba(233,213,255,${opacity})`,   letterSpacing: '0.2em',  fontWeight: 700 };
      case 'warning':   return { ...base, fontSize: 'clamp(0.7rem, 2.2vw, 0.9rem)',  color: `rgba(239,68,68,${opacity})`,     letterSpacing: '0.08em' };
      case 'sophia':    return { ...base, fontSize: 'clamp(0.65rem, 2vw, 0.82rem)',  color: `rgba(192,132,252,${opacity})`,   letterSpacing: '0.1em',  fontStyle: 'italic' };
      case 'reveal':    return { ...base, fontSize: 'clamp(0.75rem, 2.5vw, 1rem)',   color: `rgba(233,213,255,${opacity})`,   letterSpacing: '0.12em', fontWeight: 600 };
      case 'player':    return { ...base, fontSize: 'clamp(0.8rem, 2.8vw, 1.05rem)', color: `rgba(250,204,21,${opacity})`,    letterSpacing: '0.14em', fontWeight: 700 };
      case 'separator': return { ...base, fontSize: '0.7rem',                         color: `rgba(233,213,255,${dimmed ? 0.08 : 0.2})`, letterSpacing: '0.5em', textAlign: 'center' };
      default:          return { ...base, fontSize: 'clamp(0.62rem, 1.9vw, 0.78rem)', color: `rgba(255,255,255,${dimmed ? 0.18 : 0.55})`, letterSpacing: '0.05em' };
    }
  };

  const currentLine = lineIndex < LINES.length ? LINES[lineIndex] : null;

  // Progression arène pour les filtres
  const arenaPhase = lineIndex <= 7 ? 'early' : lineIndex <= 21 ? 'mid' : 'late';
  const arenaFilter = {
    early: 'blur(1px) saturate(0.8) brightness(0.55)',
    mid:   'blur(2px) saturate(0.7) brightness(0.45)',
    late:  'blur(3px) saturate(0.6) brightness(0.35)',
  }[arenaPhase];
  const arenaOpacity = { early: [0.45, 0.55, 0.45], mid: [0.35, 0.42, 0.35], late: [0.22, 0.28, 0.22] }[arenaPhase];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{
        position: 'fixed', inset: 0, background: '#000',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        zIndex: 100, overflow: 'hidden',
        cursor: isComplete ? 'pointer' : 'default',
      }}
      onClick={handleContinue}
    >
      {/* ── Arènes ── */}
      {ARENAS.map((arena, index) => (
        <motion.div
          key={arena}
          initial={{ opacity: 0 }}
          animate={{ opacity: arenaIndex === index ? arenaOpacity : 0 }}
          transition={{
            opacity: {
              duration: arenaIndex === index ? 6 : 2.5,
              repeat: arenaIndex === index ? Infinity : 0,
              ease: 'easeInOut',
            },
          }}
          style={{
            position: 'absolute', inset: 0, zIndex: 0,
            backgroundImage: `url(${arena})`,
            backgroundSize: 'cover', backgroundPosition: 'center',
            filter: arenaFilter,
          }}
        />
      ))}

      {/* Vignette radiale */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none',
        background: arenaPhase === 'early'
          ? 'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.65) 100%)'
          : arenaPhase === 'mid'
            ? 'radial-gradient(ellipse at center, transparent 45%, rgba(0,0,0,0.78) 100%)'
            : 'radial-gradient(ellipse at center, transparent 35%, rgba(0,0,0,0.92) 100%)',
        transition: 'background 2s ease-in-out',
      }}/>

      {/* Carousel personnages */}
      <div style={{ position: 'absolute', inset: 0, zIndex: 2, overflow: 'hidden' }}>
        {characters.map((char, index) => (
          <motion.div
            key={char.id || index}
            initial={{ opacity: 0, scale: 1.08 }}
            animate={{
              opacity: currentCharIndex === index ? 0.18 : 0,
              scale:   currentCharIndex === index ? 1.02  : 1.08,
            }}
            transition={{ opacity: { duration: 2 }, scale: { duration: 3.5 } }}
            style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              filter: 'blur(1.5px) brightness(0.6)',
            }}
          >
            <KimonoAvatar
              char={char}
              side={index % 2 === 0 ? 'left' : 'right'}
              isAnimating={currentCharIndex === index}
              inFlow={currentCharIndex === index && index % 3 === 0}
              flowStreak={3}
              style={{ width: '82vh', height: '82vh', maxWidth: '620px' }}
            />
          </motion.div>
        ))}
      </div>

      {/* Grain + CRT */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 3, pointerEvents: 'none',
        opacity: flickering ? 0.12 : 0.05, transition: 'opacity 0.05s',
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E")`,
      }}/>
      <div style={{
        position: 'absolute', inset: 0, zIndex: 5, pointerEvents: 'none',
        backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.12) 2px, rgba(0,0,0,0.12) 4px)',
        opacity: flickering ? 0.5 : 0.2,
      }}/>

      {/* Lueur Sophia */}
      <motion.div
        animate={{ opacity: [0.02, 0.06, 0.02], scale: [1, 1.05, 1] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        style={{
          position: 'absolute', zIndex: 2,
          width: '60vw', height: '60vw', borderRadius: '50%',
          background: 'radial-gradient(circle, #c084fc10 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      {/* Bandes cinéma */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '8vh', background: '#000', zIndex: 10 }}/>
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '8vh', background: '#000', zIndex: 10 }}/>

      {/* Titre */}
      <motion.div
        animate={{
          opacity: lineIndex > 4 ? [0.55, 0.75, 0.55] : 0,
          y:       lineIndex > 4 ? [-4, 2, -4]         : -20,
        }}
        transition={{ duration: 4.2, repeat: Infinity, ease: 'easeInOut' }}
        style={{
          position: 'absolute', top: '11vh', left: '80%',
          transform: 'translateX(-50%)',
          zIndex: 6, textAlign: 'center', pointerEvents: 'none',
        }}
      >
        <div style={{
          fontSize: 'clamp(1.1rem, 4.5vw, 1.65rem)',
          letterSpacing: '0.22em', color: '#d1c4ff', fontWeight: 700, lineHeight: 1,
          textShadow: '0 0 25px rgba(209,196,255,0.5), 0 0 55px rgba(167,139,250,0.3)',
        }}>泰冥零</div>
        <motion.div
          animate={{ opacity: [0.35, 0.6, 0.35] }}
          transition={{ duration: 3.8, repeat: Infinity }}
          style={{
            fontFamily: "'Cinzel', serif",
            fontSize: 'clamp(0.48rem, 1.8vw, 0.62rem)',
            color: 'rgba(233,213,255,1)', letterSpacing: '0.45em',
            marginTop: '0.35rem', textTransform: 'uppercase',
          }}
        >THAI MEZRA</motion.div>
      </motion.div>

      {/* ── Zone texte : fenêtre glissante ── */}
      <div
        ref={textBoxRef}
        style={{
          position: 'relative', zIndex: 6,
          width: '100%', maxWidth: '680px',
          padding: '0 2.5rem',
          display: 'flex', flexDirection: 'column',
          gap: '0.5rem',
          // Hauteur fixe : jamais de layout shift
          height: `${(WINDOW_SIZE + 1) * 2.4}rem`,
          justifyContent: 'flex-end',
          overflow: 'hidden',
        }}
      >
        {/* Lignes complétées (fenêtre glissante, les plus anciennes sont grisées) */}
        <AnimatePresence initial={false}>
          {completedLines.map((line, i) => {
            const isOld = i < completedLines.length - 2;
            return (
              <motion.p
                key={line.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6, transition: { duration: 0.35 } }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                style={getLineStyle(line.style, isOld)}
              >
                {line.text}
              </motion.p>
            );
          })}
        </AnimatePresence>

        {/* Ligne en cours de frappe */}
        {currentLine && (
          <motion.p
            key={`typing-${lineIndex}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.15 }}
            style={getLineStyle(currentLine.style, false)}
          >
            {typed}
            <motion.span
              animate={{ opacity: [1, 0, 1] }}
              transition={{ duration: 0.7, repeat: Infinity }}
              style={{ color: '#e9d5ff77', marginLeft: '1px' }}
            >▋</motion.span>
          </motion.p>
        )}
      </div>

      {/* THAI MEZRA bas */}
      <motion.div
        animate={{ opacity: lineIndex > 6 ? 0.12 : 0 }}
        transition={{ duration: 2 }}
        style={{
          position: 'absolute', bottom: '10vh',
          left: '50%', transform: 'translateX(-50%)',
          zIndex: 50, textAlign: 'center',
        }}
      >
        <div style={{
          fontFamily: "'Cinzel', serif",
          fontSize: 'clamp(0.5rem, 1.5vw, 0.7rem)',
          color: 'rgba(233,213,255,0.50)', letterSpacing: '0.5em', textTransform: 'uppercase',
        }}>THAI MEZRA</div>
      </motion.div>

      {/* Continuer */}
      <AnimatePresence>
        {isComplete && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
            style={{
              position: 'absolute', bottom: '13vh', left: '78%',
              transform: 'translateX(-50%)', zIndex: 20,
            }}
          >
            <motion.button
              onClick={handleContinue}
              whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}
              animate={{ opacity: [0.75, 1, 0.75] }}
              transition={{ duration: 2.2, repeat: Infinity }}
              style={{
                background: 'rgba(0,0,0,0.6)',
                border: '1px solid rgba(233,213,255,0.4)',
                color: '#e9d5ff',
                fontFamily: 'monospace',
                fontSize: 'clamp(0.52rem, 1.6vw, 0.68rem)',
                letterSpacing: '0.18em', padding: '0.55rem 1.6rem',
                borderRadius: '4px', cursor: 'pointer',
                textTransform: 'uppercase', whiteSpace: 'nowrap',
                boxShadow: '0 0 20px rgba(233,213,255,0.15)',
              }}
            >APPUYEZ POUR CONTINUER →</motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Skip */}
      <AnimatePresence>
        {showSkip && !isComplete && (
          <motion.button
            initial={{ opacity: 0 }} animate={{ opacity: 0.3 }} exit={{ opacity: 0 }}
            whileHover={{ opacity: 0.8 }}
            onClick={e => { e.stopPropagation(); handleSkip(); }}
            style={{
              position: 'absolute', bottom: '11vh', right: '2rem',
              background: 'none', border: '1px solid rgba(255,255,255,0.12)',
              color: 'rgba(255,255,255,10)', fontFamily: 'monospace',
              fontSize: '0.5rem', letterSpacing: '0.2em',
              padding: '0.3rem 0.8rem', cursor: 'pointer',
              textTransform: 'uppercase', zIndex: 20,
            }}
          >PASSER →</motion.button>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function TitleScreen({ onReplayOpening, selectedChar, onSelect, onStart, onContinue, hasSavedMatch, globalStats, user, onShowLivret, onShowGuilde }) {
  const fragmentsSauves = globalStats?.unlockedChars?.length || 0;
  const totalFragments  = 7;
  const unlockedHidden  = PERSONNAGES_CACHES.filter(c => globalStats?.unlockedChars?.includes(c.id));
  const sophiaUnlocked  = globalStats?.unlockedChars?.includes('sophia');
  const isMobile        = useIsMobile();

  // Particules dérivantes (canvas-free, pur DOM)
  const particles = Array.from({ length: isMobile ? 12 : 22 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 1.5 + 0.5,
    dur: Math.random() * 12 + 8,
    delay: Math.random() * -15,
  }));

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      style={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        overflowY: 'auto',
        overflowX: 'hidden',
        background: '#03040e',
        position: 'relative',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >

      {/* ── FOND : grille hex fantôme ──────────────────────────────────── */}
      <div style={{
        position: 'fixed',
        inset: 0,
        backgroundImage: `
          radial-gradient(ellipse 80% 60% at 50% 0%, #1a0a3a 0%, transparent 65%),
          
          url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='52'%3E%3Cpath d='M30 2 L58 17 L58 47 L30 62 L2 47 L2 17 Z' fill='none' stroke='%23ffffff04' stroke-width='0.5'/%3E%3C/svg%3E")
        `,
        backgroundSize: 'auto, auto, 60px 52px',
        pointerEvents: 'none',
        zIndex: 0,
      }} />

      {/* ── Scan-line globale très subtile ────────────────────────────── */}
      <div style={{
        position: 'fixed',
        inset: 0,
        background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.08) 2px, rgba(0,0,0,0.08) 4px)',
        pointerEvents: 'none',
        zIndex: 0,
      }} />

      {/* ── Particules flottantes ─────────────────────────────────────── */}
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0, overflow: 'hidden' }}>
        {particles.map(p => (
          <motion.div
            key={p.id}
            animate={{
              y: [0, -60, 0],
              x: [0, (Math.random() - 0.5) * 30, 0],
              opacity: [0, 0.35, 0],
            }}
            transition={{ duration: p.dur, delay: p.delay, repeat: Infinity, ease: 'easeInOut' }}
            style={{
              position: 'absolute',
              left: `${p.x}%`,
              top: `${p.y}%`,
              width: `${p.size}px`,
              height: `${p.size}px`,
              borderRadius: '50%',
              background: '#c4b5fd',
              boxShadow: '0 0 4px #c4b5fd',
            }}
          />
        ))}
      </div>

      {/* ────────────────────────────────────────────────────────────────
          CONTENU PRINCIPAL
      ────────────────────────────────────────────────────────────────── */}
      <div style={{
        position: 'relative',
        zIndex: 1,
        width: '100%',
        maxWidth: '680px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: isMobile ? '1.4rem 1rem 2rem' : '2.5rem 1.5rem 3rem',
        gap: isMobile ? '1.4rem' : '2rem',
      }}>

        {/* ── TITRE ──────────────────────────────────────────────────── */}
        <div style={{ textAlign: 'center', width: '100%' }}>

          {/* Ligne déco supérieure */}
          <motion.div
            initial={{ scaleX: 0, opacity: 0 }}
            animate={{ scaleX: 1, opacity: 1 }}
            transition={{ delay: 0.2, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            style={{
              height: '1px',
              background: 'linear-gradient(90deg, transparent, #c4b5fd55, transparent)',
              marginBottom: isMobile ? '0.9rem' : '1.2rem',
            }}
          />

          {/* Kanji */}
          <motion.div
            initial={{ opacity: 0, letterSpacing: '0.6em' }}
            animate={{ opacity: 1, letterSpacing: isMobile ? '0.32em' : '0.38em' }}
            transition={{ delay: 0.3, duration: 1.4 }}
            className="cinzel"
            style={{
              fontSize: isMobile ? '1.1rem' : '1.5rem',
              color: '#c4b5fd',
              textShadow: '0 0 40px rgba(196,181,253,0.6), 0 0 80px rgba(167,139,250,0.2)',
              marginBottom: '0.3rem',
              fontWeight: 700,
            }}
          >
            泰冥 零
          </motion.div>

          {/* Sous-titre */}
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.9 }}
            style={{
              fontFamily: 'monospace',
              fontSize: isMobile ? '0.42rem' : '0.48rem',
              letterSpacing: '0.38em',
              color: 'rgba(196,181,253,0.45)',
              textTransform: 'uppercase',
              marginBottom: isMobile ? '0.5rem' : '0.65rem',
            }}
          >
            Bienvenue dans
          </motion.div>

          {/* Titre principal */}
          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 1.2 }}
            className="cinzel"
            style={{
              fontSize: isMobile ? 'clamp(2rem, 10vw, 2.8rem)' : 'clamp(2.4rem, 5vw, 3.4rem)',
              color: '#f0e6ff',
              textShadow: '0 0 60px rgba(233,213,255,0.25), 0 2px 0 rgba(0,0,0,0.8)',
              margin: '0 0 0.2rem',
              letterSpacing: isMobile ? '0.18em' : '0.22em',
              lineHeight: 1,
              fontWeight: 700,
            }}
          >
            THAI MEZRA
          </motion.h1>

          {/* Tag line */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 1 }}
            style={{
              fontFamily: 'monospace',
              fontSize: isMobile ? '0.5rem' : '0.58rem',
              color: 'rgba(233,213,255,0.5)',
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              marginBottom: isMobile ? '0.6rem' : '0.8rem',
            }}
          >
            La bibliothèque des échos
          </motion.div>

          {/* Ligne déco inférieure */}
          <motion.div
            initial={{ scaleX: 0, opacity: 0 }}
            animate={{ scaleX: 1, opacity: 1 }}
            transition={{ delay: 1.1, duration: 1 }}
            style={{
              height: '1px',
              background: 'linear-gradient(90deg, transparent, #c4b5fd33, transparent)',
            }}
          />
        </div>

        {/* ── SÉLECTION DE FRAGMENT ─────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          style={{ width: '100%' }}
        >
          {/* Label section */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.6rem',
            marginBottom: isMobile ? '0.6rem' : '0.9rem',
          }}>
            <div style={{ flex: 1, height: '1px', background: 'rgba(196,181,253,0.1)' }} />
            <span style={{
              fontFamily: 'monospace',
              fontSize: isMobile ? '0.5rem' : '0.54rem',
              letterSpacing: '0.28em',
              color: 'rgba(196,181,253,0.65)',
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            }}>
              Choisissez un personnage
            </span>
            <div style={{ flex: 1, height: '1px', background: 'rgba(196,181,253,0.1)' }} />
          </div>

          {/* Grille personnages */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
            gap: isMobile ? '0.5rem' : '0.65rem',
            marginBottom: unlockedHidden.length > 0 || sophiaUnlocked ? '0.9rem' : 0,
          }}>
            {PERSONNAGES.map((char, idx) => (
              <CharCard
                key={char.id}
                char={char}
                isSelected={selectedChar?.id === char.id}
                onSelect={onSelect}
                delay={1.3 + idx * 0.07}
                isMobile={isMobile}
              />
            ))}
          </div>

          {/* Fragments restaurés */}
          {unlockedHidden.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              transition={{ delay: 1.6, duration: 0.5 }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                margin: isMobile ? '0.5rem 0' : '0.6rem 0',
              }}>
                <div style={{ flex: 1, height: '1px', background: 'rgba(245,158,11,0.1)' }} />
                <span style={{
                  fontFamily: 'monospace',
                  fontSize: '0.38rem',
                  color: 'rgba(245,158,11,0.4)',
                  letterSpacing: '0.25em',
                  textTransform: 'uppercase',
                }}>
                  Fragments restaurés
                </span>
                <div style={{ flex: 1, height: '1px', background: 'rgba(245,158,11,0.1)' }} />
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${Math.min(unlockedHidden.length, isMobile ? 2 : 4)}, 1fr)`,
                gap: isMobile ? '0.5rem' : '0.6rem',
                maxWidth: unlockedHidden.length <= 2 && !isMobile ? '320px' : '100%',
                margin: '0 auto',
              }}>
                {unlockedHidden.map((char, idx) => (
                  <CharCard
                    key={char.id}
                    char={char}
                    isSelected={selectedChar?.id === char.id}
                    onSelect={onSelect}
                    delay={1.7 + idx * 0.07}
                    isMobile={isMobile}
                    variant="hidden"
                  />
                ))}
              </div>
            </motion.div>
          )}

          {/* Sophia */}
          {sophiaUnlocked && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.9, duration: 0.7 }}
              style={{ marginTop: '0.8rem', textAlign: 'center' }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '0.45rem',
              }}>
                <div style={{ flex: 1, height: '1px', background: 'rgba(233,213,255,0.08)' }} />
                <span style={{
                  fontFamily: 'monospace',
                  fontSize: '0.44rem',
                  color: 'rgba(233,213,255,0.3)',
                  letterSpacing: '0.25em',
                  textTransform: 'uppercase',
                }}>
                  La Gardienne
                </span>
                <div style={{ flex: 1, height: '1px', background: 'rgba(233,213,255,0.08)' }} />
              </div>
              <SophiaButton
                char={SOPHIA}
                isSelected={selectedChar?.id === 'sophia'}
                onSelect={onSelect}
                isMobile={isMobile}
              />
            </motion.div>
          )}
        </motion.div>

        {/* ── MÉMOIRE RESTAURÉE ─────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 0.8 }}
          style={{
            width: '100%',
            background: 'rgba(196,181,253,0.025)',
            border: '1px solid rgba(196,181,253,0.08)',
            padding: isMobile ? '0.6rem 0.9rem' : '0.7rem 1.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
          }}
        >
          <div>
            <div style={{
              fontFamily: 'monospace',
              fontSize: isMobile ? '0.5rem' : '0.54rem',
              color: 'rgba(196,181,253,0.8)',
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              marginBottom: '0.35rem',
            }}>
              Mémoire restaurée
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.22rem' }}>
              {Array.from({ length: totalFragments }).map((_, i) => (
                <motion.div
                  key={i}
                  animate={{ opacity: i < fragmentsSauves ? [0.5, 1, 0.5] : 0.1 }}
                  transition={{ duration: 2.5, repeat: Infinity, delay: i * 0.25 }}
                  style={{
                    width: isMobile ? '7px' : '9px',
                    height: isMobile ? '7px' : '9px',
                    borderRadius: '50%',
                    background: i < fragmentsSauves ? '#c4b5fd' : 'rgba(255,255,255,0.06)',
                    boxShadow: i < fragmentsSauves ? '0 0 8px #c4b5fd88' : 'none',
                    transition: 'all 0.3s',
                  }}
                />
              ))}
            </div>
          </div>
          <div style={{
            fontFamily: 'monospace',
            fontSize: isMobile ? '1rem' : '1.2rem',
            color: 'rgba(196,181,253,0.2)',
            letterSpacing: '0.05em',
            fontWeight: 300,
          }}>
            {fragmentsSauves}<span style={{ fontSize: '0.5em', verticalAlign: 'middle', color: 'rgba(196,181,253,0.1)' }}>/{totalFragments}</span>
          </div>
        </motion.div>

        {/* ── ACTIONS ───────────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.2, duration: 0.7 }}
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.6rem',
          }}
        >
          {/* CTA principal */}
          <motion.button
            whileHover={{ scale: 1.02, boxShadow: `0 0 40px ${selectedChar?.avatarColor || '#c4b5fd'}33` }}
            whileTap={{ scale: 0.97 }}
            onClick={onStart}
            style={{
              width: '100%',
              maxWidth: isMobile ? '100%' : '340px',
              padding: isMobile ? '0.85rem 1.5rem' : '0.9rem 2.5rem',
              background: `linear-gradient(135deg, ${selectedChar?.avatarColor || '#c4b5fd'}22 0%, ${selectedChar?.avatarColor || '#c4b5fd'}08 100%)`,
              border: `1px solid ${selectedChar?.avatarColor || '#c4b5fd'}44`,
              color: '#f0e6ff',
              fontFamily: 'monospace',
              fontSize: isMobile ? '0.62rem' : '0.66rem',
              letterSpacing: '0.22em',
              cursor: 'pointer',
              textTransform: 'uppercase',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.6rem',
              transition: 'all 0.25s',
              position: 'relative',
              overflow: 'hidden',
              touchAction: 'manipulation',
            }}
          >
            {/* Shimmer interne */}
            <motion.div
              animate={{ x: ['-100%', '200%'] }}
              transition={{ duration: 3.5, repeat: Infinity, ease: 'linear', repeatDelay: 1.5 }}
              style={{
                position: 'absolute',
                inset: 0,
                background: `linear-gradient(90deg, transparent, ${selectedChar?.avatarColor || '#c4b5fd'}18, transparent)`,
                pointerEvents: 'none',
              }}
            />
            <span style={{ fontSize: '0.9em' }}>⚔</span>
            <span>{hasSavedMatch ? 'Continuer le randori' : 'Entrer dans l\'arène'}</span>
          </motion.button>

          {/* Actions secondaires */}
          <div style={{
            display: 'flex',
            gap: isMobile ? '0.4rem' : '0.7rem',
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}>
            {[
              { label: 'Codex Memoriae', icon: '📖', color: 'rgba(167,139,250,1.5)', onClick: onShowLivret },
              { label: 'Les Archives',   icon: '🏛',  color: 'rgba(245,158,11,1.5)',  onClick: onShowGuilde },
              { label: 'Cinématique',    icon: '🎥',  color: 'rgba(167,139,250,1.4)', onClick: onReplayOpening },
            ].map(({ label, icon, color, onClick }) => (
              <motion.button
                key={label}
                whileHover={{ opacity: 1, y: -1 }}
                whileTap={{ scale: 0.95 }}
                onClick={onClick}
                style={{
                  background: 'rgba(255,255,255,0.02)',
                  border: `1px solid rgba(255,255,255,0.07)`,
                  color,
                  fontFamily: 'monospace',
                  fontSize: isMobile ? '0.44rem' : '0.48rem',
                  letterSpacing: '0.1em',
                  padding: isMobile ? '0.45rem 0.7rem' : '0.45rem 0.9rem',
                  cursor: 'pointer',
                  textTransform: 'uppercase',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.28rem',
                  transition: 'all 0.18s',
                  opacity: 0.75,
                  touchAction: 'manipulation',
                }}
              >
                <span>{icon}</span>
                {/* Sur mobile très étroit, masquer le label texte pour garder les boutons lisibles */}
                <span style={{ display: isMobile ? 'none' : 'inline' }}>{label}</span>
                <span style={{ display: isMobile ? 'inline' : 'none' }}>
                  {label.split(' ')[0]}
                </span>
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* ── CITATION SOPHIA ──────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.6, duration: 2 }}
          style={{ textAlign: 'center', padding: '0 1rem' }}
        >
          <p style={{
            fontFamily: 'Georgia, serif',
            fontStyle: 'italic',
            fontSize: isMobile ? 'clamp(0.48rem, 2.2vw, 0.6rem)' : 'clamp(0.5rem, 1.3vw, 0.64rem)',
            color: 'rgba(233,213,255,0.5)',
            lineHeight: 1.7,
            margin: '0 0 0.3rem',
          }}>
            « Les idées ne meurent jamais vraiment. Elles attendent, dans l'ombre de la mémoire,
            que quelqu'un les nomme à nouveau. »
          </p>
          <div style={{
            fontFamily: 'monospace',
            fontSize: '0.36rem',
            color: 'rgba(233,213,255,0.4)',
            letterSpacing: '0.42em',
          }}>
            — SOPHIA PERENNIS
          </div>
        </motion.div>

      </div>
    </motion.div>
  );
}

/* ─── Sous-composant : carte personnage ──────────────────────────────────── */
function CharCard({ char, isSelected, onSelect, delay, isMobile, variant = 'default' }) {
  const [hovered, setHovered] = useState(false);

  const accentColor = char.avatarColor || '#c4b5fd';
  const isHidden    = variant === 'hidden';

  return (
    <motion.button
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5 }}
      whileTap={{ scale: 0.96 }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      onClick={() => onSelect(char)}
      style={{
        background: isSelected
          ? `${accentColor}12`
          : hovered ? `${accentColor}08` : 'rgba(8,12,26,0.85)',
        border: isSelected
          ? `1.5px solid ${accentColor}66`
          : isHidden
          ? '1px solid rgba(245,158,11,0.18)'
          : '1px solid rgba(255,255,255,0.05)',
        borderRadius: '6px',
        padding: isMobile ? '0.65rem 0.4rem 0.5rem' : '0.75rem 0.45rem 0.55rem',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '0.35rem',
        position: 'relative',
        overflow: 'hidden',
        transition: 'background 0.2s, border-color 0.2s',
        boxShadow: isSelected
          ? `0 0 28px ${accentColor}20, 0 8px 24px rgba(0,0,0,0.5)`
          : '0 4px 16px rgba(0,0,0,0.35)',
        touchAction: 'manipulation',
        WebkitTapHighlightColor: `${accentColor}22`,
      }}
    >
      {/* Scan-line au hover (desktop) */}
      {hovered && !isMobile && (
        <motion.div
          initial={{ y: '-100%' }}
          animate={{ y: '200%' }}
          transition={{ duration: 0.6, ease: 'linear' }}
          style={{
            position: 'absolute',
            left: 0, right: 0,
            height: '30%',
            background: `linear-gradient(180deg, transparent, ${accentColor}18, transparent)`,
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Coin déco sélectionné */}
      {isSelected && (
        <>
          <div style={{
            position: 'absolute', top: 0, left: 0,
            width: '14px', height: '14px',
            borderTop: `2px solid ${accentColor}`,
            borderLeft: `2px solid ${accentColor}`,
            borderRadius: '6px 0 0 0',
          }} />
          <div style={{
            position: 'absolute', bottom: 0, right: 0,
            width: '14px', height: '14px',
            borderBottom: `2px solid ${accentColor}`,
            borderRight: `2px solid ${accentColor}`,
            borderRadius: '0 0 6px 0',
          }} />
        </>
      )}

      {/* Avatar */}
      <div style={{
        width: isMobile ? '52px' : '60px',
        height: isMobile ? '52px' : '60px',
        borderRadius: '50%',
        overflow: 'hidden',
        border: isSelected ? `2px solid ${accentColor}66` : '1.5px solid rgba(255,255,255,0.08)',
        boxShadow: isSelected ? `0 0 18px ${accentColor}44` : 'none',
        background: '#0a0d1a',
        flexShrink: 0,
        transition: 'box-shadow 0.25s, border-color 0.25s',
      }}>
        <img
          src={char.image}
          alt={char.nom}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            filter: isSelected
              ? 'grayscale(0%) brightness(1.05)'
              : hovered
              ? 'grayscale(20%) brightness(0.85)'
              : 'grayscale(50%) brightness(0.65)',
            transition: 'filter 0.3s',
          }}
          onError={e => {
            e.target.style.display = 'none';
            const emojis = { '#a5f3c3': '📜', '#facc15': '🏺', '#fb923c': '✊', '#60a5fa': '🔷', '#c084fc': '🔮' };
            e.target.parentElement.innerHTML = `<span style="font-size:1.4rem;display:flex;align-items:center;justify-content:center;width:100%;height:100%;">${emojis[char.avatarColor] || '📚'}</span>`;
          }}
        />
      </div>

      {/* Nom */}
      <span style={{
        fontFamily: 'monospace',
        fontSize: isMobile ? '0.5rem' : '0.56rem',
        color: isSelected ? '#fff' : 'rgba(255,255,255,0.45)',
        letterSpacing: '0.06em',
        fontWeight: isSelected ? 700 : 400,
        textAlign: 'center',
        lineHeight: 1.2,
        transition: 'color 0.2s',
      }}>
        {char.nom}
      </span>

      {/* Tradition — masquée sur mobile non sélectionné */}
      {(!isMobile || isSelected) && (
        <span style={{
          fontFamily: 'monospace',
          fontSize: '0.48rem',
          color: isSelected ? accentColor : 'rgba(255,255,255,0.18)',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          transition: 'color 0.2s',
        }}>
          {char.tradition}
        </span>
      )}
    </motion.button>
  );
}

/* ─── Sous-composant : bouton Sophia ────────────────────────────────────── */
function SophiaButton({ char, isSelected, onSelect, isMobile }) {
  return (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.96 }}
      onClick={() => onSelect(char)}
      style={{
        background: isSelected ? 'rgba(233,213,255,0.1)' : 'rgba(233,213,255,0.03)',
        border: isSelected ? '1.5px solid rgba(233,213,255,0.45)' : '1px solid rgba(233,213,255,0.12)',
        borderRadius: '6px',
        padding: isMobile ? '0.6rem 1.2rem' : '0.6rem 2rem',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.8rem',
        transition: 'all 0.25s',
        boxShadow: isSelected ? '0 0 30px rgba(233,213,255,0.15)' : 'none',
        touchAction: 'manipulation',
      }}
    >
      <div style={{
        width: '42px',
        height: '42px',
        borderRadius: '50%',
        overflow: 'hidden',
        border: isSelected ? '2px solid rgba(233,213,255,0.5)' : '1.5px solid rgba(233,213,255,0.2)',
        boxShadow: isSelected ? '0 0 16px rgba(233,213,255,0.4)' : 'none',
        background: '#090b18',
      }}>
        <img
          src={char.image}
          alt="Sophia"
          style={{
            width: '100%', height: '100%', objectFit: 'cover',
            filter: isSelected ? 'none' : 'grayscale(30%) brightness(0.7)',
            transition: 'filter 0.3s',
          }}
          onError={e => {
            e.target.style.display = 'none';
            e.target.parentElement.innerHTML = '<span style="font-size:1.2rem;display:flex;align-items:center;justify-content:center;width:100%;height:100%;">✨</span>';
          }}
        />
      </div>
      <div style={{ textAlign: 'left' }}>
        <div style={{
          fontFamily: 'monospace',
          fontSize: isMobile ? '0.56rem' : '0.6rem',
          color: isSelected ? '#f0e6ff' : 'rgba(233,213,255,0.65)',
          letterSpacing: '0.15em',
          fontWeight: isSelected ? 700 : 400,
        }}>
          SOPHIA
        </div>
        <div style={{
          fontFamily: 'monospace',
          fontSize: '0.34rem',
          color: 'rgba(233,213,255,0.25)',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          marginTop: '0.1rem',
        }}>
          Philosophia Perennis
        </div>
      </div>
    </motion.button>
  );
}

function IntroductionScreen({ theme, selectedChar, onBegin, currentAIChar, arenaIndex = 0 }) {
  const [phase, setPhase] = useState(0);
  const [readyToProceed, setReadyToProceed] = useState(false);
  const resolvedOpponent = resolveAIChar(currentAIChar);

  const ARENAS = ['/images/arena-1.jpg', '/images/arena-2.jpg', '/images/arena-3.jpg'];

  // Texte narratif
  const narrativeText = useMemo(() => [
    {
      speaker: 'SOPHIA PERENNIS',
      text: `Un fragment s'estompe. ${selectedChar.nom} — ${selectedChar.epoque.split('·')[0].trim()} — sa trace dans la mémoire collective s'affaiblit.`,
      delay: 0,
    },
    {
      speaker: 'SOPHIA PERENNIS',
      text: `Pour le restaurer, il doit affronter un autre écho. ${resolvedOpponent.nom} se dresse dans l'arène.`,
      delay: 2800,
    },
    {
      speaker: 'SOPHIA PERENNIS',
      text: `Le thème : « ${theme.theme} ». C'est la question que ${selectedChar.nom} n'a pas achevée en son temps.`,
      delay: 5000,
    },
    {
      speaker: selectedChar.nom.toUpperCase(),
      text: theme.citation || `Je suis prêt. La mémoire ne s'éteindra pas aujourd'hui.`,
      delay: 7200,
      color: selectedChar.avatarColor,
    },
  ], [selectedChar, resolvedOpponent, theme]);

  // Progression des phases
  useEffect(() => {
    if (phase < narrativeText.length) {
      const timer = setTimeout(
        () => setPhase(p => p + 1),
        narrativeText[phase]?.delay || 1500
      );
      return () => clearTimeout(timer);
    } else {
      const t = setTimeout(() => setReadyToProceed(true), 400);
      return () => clearTimeout(t);
    }
  }, [phase, narrativeText]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={() => readyToProceed && onBegin()}
      style={{
        position: 'fixed',
        inset: 0,
        background: '#000',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        zIndex: 50,
        cursor: readyToProceed ? 'pointer' : 'default',
      }}
    >
      {/* ── FOND ARÈNES ── */}
      {ARENAS.map((arena, index) => (
        <motion.div
          key={arena}
          initial={{ opacity: 0 }}
          animate={{ opacity: arenaIndex === index ? [0.45, 0.55, 0.45] : 0 }}
          transition={{
            opacity: {
              duration: arenaIndex === index ? 8 : 2.5,
              repeat: arenaIndex === index ? Infinity : 0,
              ease: 'easeInOut',
            },
          }}
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${arena})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(3px) saturate(0.5) brightness(0.4)',
            zIndex: 0,
          }}
        />
      ))}

      {/* Vignette radiale */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none',
        background: 'radial-gradient(ellipse 80% 70% at 50% 50%, transparent 10%, rgba(0,0,0,0.92) 100%)',
      }} />

      {/* Grain */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 2, pointerEvents: 'none', opacity: 0.04,
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.05'/%3E%3C/svg%3E")`,
      }} />

      {/* Scanlines CRT */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 3, pointerEvents: 'none', opacity: 0.15,
        backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.15) 2px, rgba(0,0,0,0.15) 4px)',
      }} />

      {/* Bandes cinéma */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '8vh', background: '#000', zIndex: 10 }} />
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '8vh', background: '#000', zIndex: 10 }} />

      {/* ── TITRE ── */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.5 }}
        transition={{ duration: 1.5 }}
        style={{
          position: 'absolute',
          top: '9vh',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 11,
          textAlign: 'center',
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        <div style={{
          fontSize: 'clamp(1rem, 4vw, 1.4rem)',
          letterSpacing: '0.25em',
          color: '#d1c4ff',
          textShadow: '0 0 20px rgba(209,196,255,0.3)',
          fontWeight: 700,
        }}>泰冥零</div>
        <div style={{
          fontFamily: "'Cinzel', serif",
          fontSize: 'clamp(0.38rem, 1.4vw, 0.52rem)',
          color: 'rgba(233,213,255,1)',
          letterSpacing: '0.55em',
          marginTop: '0.2rem',
        }}>THAI MEZRA</div>
      </motion.div>

      {/* ── DUEL ZONE ── */}
      <div style={{
        position: 'relative',
        zIndex: 6,
        width: '100%',
        maxWidth: '820px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0,
        padding: '0 1rem',
        marginBottom: '2vh',
      }}>

        {/* === CAPSULE JOUEUR (gauche) === */}
        <motion.div
          initial={{ opacity: 0, x: -80 }}
          animate={{ opacity: phase >= 1 ? 1 : 0.2, x: 0 }}
          transition={{ duration: 1, ease: 'easeOut' }}
          style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            flex: '1 1 0',
          }}
        >
          {/* Capsule frame gauche */}
          <div style={{
            position: 'relative',
            width: '100%',
            maxWidth: '220px',
            aspectRatio: '2/3',
            borderRadius: '12px 4px 4px 12px',
            overflow: 'hidden',
            border: `1.5px solid ${selectedChar.avatarColor}44`,
            boxShadow: phase >= 1
              ? `0 0 40px ${selectedChar.avatarColor}22, inset 0 0 30px rgba(0,0,0,0.5)`
              : 'none',
            transition: 'box-shadow 1s ease',
            background: 'rgba(0,0,0,0.6)',
          }}>
            {/* Fond coloré subtil */}
            <div style={{
              position: 'absolute', inset: 0,
              background: `radial-gradient(ellipse at 50% 80%, ${selectedChar.avatarColor}18 0%, transparent 70%)`,
              zIndex: 0,
            }} />

            {/* Avatar */}
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
              zIndex: 1,
            }}>
              <KimonoAvatar
                char={selectedChar}
                side="left"
                isAnimating={phase >= 1}
                inFlow={false}
                flowStreak={0}
                style={{ width: '75%', height: 'auto', opacity: phase >= 1 ? 1 : 0.3, transition: 'opacity 1s' }}
              />
            </div>

            {/* Scan line lumineux */}
            {phase >= 1 && (
              <motion.div
                animate={{ top: ['-5%', '105%'] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'linear', repeatDelay: 2 }}
                style={{
                  position: 'absolute', left: 0, right: 0, height: '2px', zIndex: 3,
                  background: `linear-gradient(90deg, transparent, ${selectedChar.avatarColor}88, transparent)`,
                  pointerEvents: 'none',
                }}
              />
            )}

            {/* Badge coin */}
            <div style={{
              position: 'absolute', top: '0.5rem', left: '0.5rem', zIndex: 4,
              fontFamily: "'Share Tech Mono', monospace",
              fontSize: '0.42rem', letterSpacing: '0.3em',
              color: 'rgba(255,255,255,0.25)', textTransform: 'uppercase',
            }}>
              P1
            </div>

            {/* Indicateur actif */}
            {phase >= 1 && (
              <motion.div
                animate={{ opacity: [0.4, 1, 0.4] }}
                transition={{ duration: 1.8, repeat: Infinity }}
                style={{
                  position: 'absolute', top: '0.5rem', right: '0.5rem', zIndex: 4,
                  width: '6px', height: '6px', borderRadius: '50%',
                  background: selectedChar.avatarColor,
                  boxShadow: `0 0 6px ${selectedChar.avatarColor}`,
                }}
              />
            )}
          </div>

          {/* Nom + étiquette sous la capsule */}
          <div style={{ marginTop: '0.7rem', textAlign: 'center' }}>
            <motion.div
              animate={phase >= 1 ? { opacity: [0.7, 1, 0.7] } : { opacity: 0.3 }}
              transition={{ duration: 3, repeat: Infinity }}
              style={{
                fontFamily: "'Share Tech Mono', monospace",
                fontSize: 'clamp(0.6rem, 1.6vw, 0.78rem)',
                color: selectedChar.avatarColor,
                letterSpacing: '0.2em',
                fontWeight: 700,
                textTransform: 'uppercase',
                textShadow: `0 0 14px ${selectedChar.avatarColor}55`,
              }}
            >{selectedChar.nom}</motion.div>
            <div style={{
              fontFamily: "'Share Tech Mono', monospace",
              fontSize: '0.42rem', color: 'rgba(255,255,255,0.28)',
              letterSpacing: '0.35em', textTransform: 'uppercase', marginTop: '0.25rem',
            }}>Dépositaire</div>
          </div>
        </motion.div>

        {/* === VS CENTRAL === */}
        <motion.div
          initial={{ opacity: 0, scale: 0.4 }}
          animate={{
            opacity: phase >= 1 ? 1 : 0,
            scale: phase >= 1 ? 1 : 0.4,
          }}
          transition={{ duration: 0.6, ease: 'backOut', delay: 0.1 }}
          style={{
            position: 'relative',
            zIndex: 7,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            width: 'clamp(52px, 8vw, 72px)',
            userSelect: 'none',
          }}
        >
          {/* Ligne verticale haut */}
          <motion.div
            initial={{ scaleY: 0 }}
            animate={{ scaleY: phase >= 1 ? 1 : 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            style={{
              width: '1px',
              height: 'clamp(30px, 5vh, 50px)',
              background: 'linear-gradient(to bottom, transparent, rgba(233,213,255,0.25))',
              transformOrigin: 'top',
              marginBottom: '0.5rem',
            }}
          />

          {/* Cercle VS */}
          <div style={{
            position: 'relative',
            width: 'clamp(44px, 6.5vw, 60px)',
            height: 'clamp(44px, 6.5vw, 60px)',
          }}>
            {/* Halo pulsant */}
            {phase >= 1 && (
              <motion.div
                animate={{ scale: [1, 1.35, 1], opacity: [0.3, 0, 0.3] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                  position: 'absolute', inset: '-8px',
                  borderRadius: '50%',
                  border: '1px solid rgba(233,213,255,0.2)',
                }}
              />
            )}

            {/* Cercle principal */}
            <div style={{
              position: 'absolute', inset: 0,
              borderRadius: '50%',
              border: '1.5px solid rgba(233,213,255,0.35)',
              background: 'radial-gradient(circle, rgba(60,10,80,0.9) 0%, rgba(0,0,0,0.95) 100%)',
              backdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <span style={{
                fontFamily: "'Share Tech Mono', monospace",
                fontSize: 'clamp(0.65rem, 1.8vw, 0.85rem)',
                color: '#e9d5ff',
                letterSpacing: '0.12em',
                fontWeight: 700,
                textShadow: '0 0 12px rgba(233,213,255,0.6)',
              }}>VS</span>
            </div>
          </div>

          {/* Ligne verticale bas */}
          <motion.div
            initial={{ scaleY: 0 }}
            animate={{ scaleY: phase >= 1 ? 1 : 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            style={{
              width: '1px',
              height: 'clamp(30px, 5vh, 50px)',
              background: 'linear-gradient(to top, transparent, rgba(233,213,255,0.25))',
              transformOrigin: 'bottom',
              marginTop: '0.5rem',
            }}
          />
        </motion.div>

        {/* === CAPSULE ADVERSAIRE (droite) === */}
        <motion.div
          initial={{ opacity: 0, x: 80 }}
          animate={{ opacity: phase >= 2 ? 1 : 0.2, x: 0 }}
          transition={{ duration: 1, ease: 'easeOut', delay: 0.15 }}
          style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            flex: '1 1 0',
          }}
        >
          {/* Capsule frame droite */}
          <div style={{
            position: 'relative',
            width: '100%',
            maxWidth: '220px',
            aspectRatio: '2/3',
            borderRadius: '4px 12px 12px 4px',
            overflow: 'hidden',
            border: `1.5px solid ${resolvedOpponent.avatarColor || '#ef4444'}44`,
            boxShadow: phase >= 2
              ? `0 0 40px ${resolvedOpponent.avatarColor || '#ef4444'}22, inset 0 0 30px rgba(0,0,0,0.5)`
              : 'none',
            transition: 'box-shadow 1s ease',
            background: 'rgba(0,0,0,0.6)',
          }}>
            {/* Fond coloré subtil */}
            <div style={{
              position: 'absolute', inset: 0,
              background: `radial-gradient(ellipse at 50% 80%, ${resolvedOpponent.avatarColor || '#ef4444'}18 0%, transparent 70%)`,
              zIndex: 0,
            }} />

            {/* Avatar */}
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
              zIndex: 1,
            }}>
              <KimonoAvatar
                char={resolvedOpponent}
                side="right"
                isAnimating={phase >= 2}
                inFlow={false}
                flowStreak={0}
                style={{ width: '75%', height: 'auto', opacity: phase >= 2 ? 1 : 0.3, transition: 'opacity 1s' }}
              />
            </div>

            {/* Scan line lumineux */}
            {phase >= 2 && (
              <motion.div
                animate={{ top: ['-5%', '105%'] }}
                transition={{ duration: 3.2, repeat: Infinity, ease: 'linear', repeatDelay: 2.5 }}
                style={{
                  position: 'absolute', left: 0, right: 0, height: '2px', zIndex: 3,
                  background: `linear-gradient(90deg, transparent, ${resolvedOpponent.avatarColor || '#ef4444'}88, transparent)`,
                  pointerEvents: 'none',
                }}
              />
            )}

            {/* Badge coin */}
            <div style={{
              position: 'absolute', top: '0.5rem', right: '0.5rem', zIndex: 4,
              fontFamily: "'Share Tech Mono', monospace",
              fontSize: '0.42rem', letterSpacing: '0.3em',
              color: 'rgba(255,255,255,0.25)', textTransform: 'uppercase',
            }}>
              AI
            </div>

            {/* Indicateur actif */}
            {phase >= 2 && (
              <motion.div
                animate={{ opacity: [0.4, 1, 0.4] }}
                transition={{ duration: 2.1, repeat: Infinity }}
                style={{
                  position: 'absolute', top: '0.5rem', left: '0.5rem', zIndex: 4,
                  width: '6px', height: '6px', borderRadius: '50%',
                  background: resolvedOpponent.avatarColor || '#ef4444',
                  boxShadow: `0 0 6px ${resolvedOpponent.avatarColor || '#ef4444'}`,
                }}
              />
            )}
          </div>

          {/* Nom + étiquette */}
          <div style={{ marginTop: '0.7rem', textAlign: 'center' }}>
            <motion.div
              animate={phase >= 2 ? { opacity: [0.7, 1, 0.7] } : { opacity: 0.3 }}
              transition={{ duration: 3.2, repeat: Infinity }}
              style={{
                fontFamily: "'Share Tech Mono', monospace",
                fontSize: 'clamp(0.6rem, 1.6vw, 0.78rem)',
                color: resolvedOpponent.avatarColor || '#ef4444',
                letterSpacing: '0.2em',
                fontWeight: 700,
                textTransform: 'uppercase',
                textShadow: `0 0 14px ${resolvedOpponent.avatarColor || '#ef4444'}55`,
              }}
            >{resolvedOpponent.nom}</motion.div>
            <div style={{
              fontFamily: "'Share Tech Mono', monospace",
              fontSize: '0.42rem', color: 'rgba(255,255,255,0.28)',
              letterSpacing: '0.35em', textTransform: 'uppercase', marginTop: '0.25rem',
            }}>Écho</div>
          </div>
        </motion.div>
      </div>

      {/* ── THÈME (entre les capsules et la narration) ── */}
      {phase >= 2 && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          style={{
            position: 'relative',
            zIndex: 11,
            textAlign: 'center',
            padding: '0.4rem 1.4rem',
            marginBottom: '0.8rem',
            borderTop: '1px solid rgba(233,213,255,0.1)',
            borderBottom: '1px solid rgba(233,213,255,0.1)',
          }}
        >
          <p style={{
            fontFamily: "'Cormorant Garamond', 'Georgia', serif",
            fontStyle: 'italic',
            fontSize: 'clamp(0.65rem, 1.9vw, 0.82rem)',
            color: 'rgba(192,132,252,0.65)',
            lineHeight: 1.6,
            margin: 0,
            letterSpacing: '0.04em',
          }}>
            « {theme.theme} »
          </p>
        </motion.div>
      )}

      {/* ── NARRATION ── */}
      <div style={{
        position: 'relative',
        zIndex: 11,
        width: '100%',
        maxWidth: '520px',
        padding: '0 1.5rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.65rem',
        alignItems: 'center',
        minHeight: '80px',
      }}>
        <AnimatePresence>
          {narrativeText.slice(0, phase + 1).map((fragment, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: i === phase ? 1 : 0.45, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.55, ease: 'easeOut' }}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}
            >
              <div style={{
                fontFamily: "'Share Tech Mono', monospace",
                fontSize: 'clamp(0.42rem, 1.2vw, 0.5rem)',
                letterSpacing: '0.35em',
                color: fragment.color || 'rgba(233,213,255,0.45)',
                textTransform: 'uppercase', fontWeight: 600, textAlign: 'center',
              }}>
                {fragment.speaker}
              </div>
              <p style={{
                fontFamily: fragment.speaker === 'SOPHIA PERENNIS'
                  ? "'Cormorant Garamond', 'Georgia', serif"
                  : "'Share Tech Mono', monospace",
                fontStyle: fragment.speaker === 'SOPHIA PERENNIS' ? 'italic' : 'normal',
                fontSize: fragment.speaker === 'SOPHIA PERENNIS'
                  ? 'clamp(0.72rem, 2.2vw, 0.9rem)'
                  : 'clamp(0.75rem, 2.2vw, 0.92rem)',
                color: fragment.speaker === 'SOPHIA PERENNIS'
                  ? 'rgba(233,213,255,0.8)'
                  : fragment.color || 'rgba(255,255,255,0.85)',
                lineHeight: 1.7, margin: 0, letterSpacing: '0.02em',
                textAlign: 'center',
                textShadow: fragment.speaker === 'SOPHIA PERENNIS' ? '0 0 15px rgba(233,213,255,0.06)' : 'none',
                maxWidth: '100%',
              }}>
                {fragment.text}
              </p>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* ── BOUTON ENTRER ── */}
      <AnimatePresence>
        {readyToProceed && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.7 }}
            style={{
              position: 'relative',
              zIndex: 15,
              marginTop: '1.5vh',
            }}
          >
            <motion.div
              animate={{ opacity: [0.6, 1, 0.6] }}
              transition={{ duration: 2, repeat: Infinity }}
              style={{
                fontFamily: "'Share Tech Mono', monospace",
                fontSize: 'clamp(0.58rem, 1.8vw, 0.76rem)',
                color: '#e9d5ff',
                letterSpacing: '0.32em',
                textTransform: 'uppercase',
                border: '1px solid rgba(233,213,255,0.3)',
                padding: '0.6rem 2.4rem',
                borderRadius: '4px',
                background: 'rgba(0,0,0,0.6)',
                backdropFilter: 'blur(8px)',
                cursor: 'pointer',
                textShadow: '0 0 12px rgba(233,213,255,0.3)',
                boxShadow: '0 0 20px rgba(233,213,255,0.06)',
              }}
            >
              Entrer dans l'arène
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── FOOTER ── */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.1 }}
        transition={{ duration: 1.5 }}
        style={{
          position: 'absolute',
          bottom: '5vh',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 11,
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        <div style={{
          fontFamily: "'Cinzel', serif",
          fontSize: 'clamp(0.42rem, 1.2vw, 0.56rem)',
          color: 'rgba(233,213,255,0.5)',
          letterSpacing: '0.55em',
          textTransform: 'uppercase',
        }}>THAI MEZRA</div>
      </motion.div>
    </motion.div>
  );
}

const TABS = [
  { id: 'corpus',   label: 'CORPUS',   icon: '⚔' },
  { id: 'atouts',   label: 'ATOUTS',   icon: '✦' },
  { id: 'journal',  label: 'JOURNAL',  icon: '📜' },
  { id: 'synergies',label: 'SYNERGIES',icon: '⚡' },
];

function useKnowledgeTracker() {
  const [seen, setSeen] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem('jvb_knowledge_seen') || '[]'));
    } catch { return new Set(); }
  });
 
  const markSeen = useCallback((id) => {
    setSeen(prev => {
      const next = new Set(prev);
      next.add(id);
      try {
        localStorage.setItem('jvb_knowledge_seen', JSON.stringify([...next]));
      } catch {}
      return next;
    });
  }, []);
 
  const hasSeen = useCallback((id) => seen.has(id), [seen]);
 
  return { hasSeen, markSeen };
}

function BottomPanel({ onSelectConcept, hasSeen, unreadKnowledge, onOpenKnowledge, state, playerDeck, selectedChar, progression, playerInFlow, logRef, playMove, lastPlayerConceptId, comboState }) {
  const [tab, setTab] = useState('corpus');
  const isMobile = useIsMobile();

  const synergyList = state.lastPlayerConceptId
    ? SYNERGIES.filter(s => s.ids.includes(state.lastPlayerConceptId))
    : [];

  const [unread, setUnread] = useState(0);
  useEffect(() => { if (tab !== 'journal') setUnread(n => n + 1); }, [state.log.length]);
  useEffect(() => { if (tab === 'journal') setUnread(0); }, [tab]);

  const punchlines = ATOUTS[selectedChar.id] || [];

  const TabBtn = ({ t }) => {
    const isActive  = tab === t.id;
    const hasBadge  = t.id === 'journal' && unread > 0 && tab !== 'journal';
    const accentColor = t.id === 'corpus' ? selectedChar.avatarColor
      : t.id === 'journal'   ? '#22d3ee'
      : t.id === 'atouts'    ? '#f59e0b'
      : '#a78bfa';
    return (
      <motion.button
        whileTap={{ scale: 0.93 }}
        onClick={() => setTab(t.id)}
        style={{
          position: 'relative',
          background: isActive ? 'rgba(255,255,255,0.055)' : 'transparent',
          border: 'none',
          borderBottom: isActive ? `2px solid ${accentColor}` : '2px solid transparent',
          color: isActive ? '#fff' : 'rgba(255,255,255,0.25)',
          fontFamily: 'monospace',
          // Sur mobile : label encore plus court
          fontSize: isMobile ? 'clamp(0.44rem, 2.5vw, 0.58rem)' : 'clamp(0.5rem, 2.8vw, 0.62rem)',
          letterSpacing: '0.1em',
          padding: isMobile ? '0.35rem 0.4rem' : '0.4rem 0.6rem',
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: '0.25rem',
          transition: 'all 0.18s',
          whiteSpace: 'nowrap', flex: 1,
          touchAction: 'manipulation',
        }}
      >
        <span style={{ fontSize: isMobile ? '0.8rem' : '0.9rem' }}>{t.icon}</span>
        {/* Sur mobile très étroit : icône seule pour les onglets non actifs */}
        <span style={{ textTransform: 'uppercase' }}>
          {isMobile && !isActive ? t.icon ? t.shortLabel ?? t.label : t.label : t.label}
        </span>
        {hasBadge && (
          <motion.span
            initial={{ scale: 0 }} animate={{ scale: 1 }}
            style={{ position: 'absolute', top: '4px', right: '4px', width: '7px', height: '7px', borderRadius: '50%', background: '#22d3ee' }}
          />
        )}
      </motion.button>
    );
  };

  return (
    <div style={{
      flex: '1 1 0',
      // ── Hauteur adaptative : plus courte sur mobile ──
      minHeight: isMobile ? 200 : 220,
      maxHeight: isMobile ? '42vh' : '480px',
      display: 'flex', flexDirection: 'column',
      background: '#020617',
      borderTop: '1px solid rgba(255,255,255,0.06)',
      overflow: 'hidden',
    }}>
      {/* Onglets */}
      <div style={{
        display: 'flex', overflowX: 'auto',
        background: 'rgba(2,6,23,0.98)',
        borderBottom: '1px solid rgba(255,255,255,0.07)',
        // Éviter le scroll horizontal parasite sur mobile
        scrollbarWidth: 'none',
      }}>
        {TABS.map(t => <TabBtn key={t.id} t={t} />)}
      </div>

      <div className="hide-scrollbar" style={{ flex: 1, overflow: 'auto', position: 'relative' }}>
        <AnimatePresence mode="wait">

          {/* ── CORPUS ── */}
          {tab === 'corpus' && (
            <motion.div key="corpus" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {/* En-tête */}
              <div style={{
                padding: isMobile ? '0.25rem 0.6rem' : '0.3rem 0.75rem',
                borderBottom: '1px solid rgba(255,255,255,0.04)',
                flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span style={{ fontFamily: 'monospace', fontSize: isMobile ? '0.48rem' : '0.52rem', color: selectedChar.avatarColor }}>
                  {selectedChar.nom} · {selectedChar.tradition?.toUpperCase()}
                </span>
                <span style={{ fontFamily: 'monospace', fontSize: isMobile ? '0.45rem' : '0.48rem', color: '#facc15' }}>
                  MA {state.maUsedCount}/3
                </span>
              </div>

              {/* Grid concepts */}
              <div className="concepts-grid hide-scrollbar" style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '0.35rem' : '0.5rem' }}>
                {playerDeck.map(c => (
                  <ConceptCard
                    key={c.id} concept={c} onClick={onSelectConcept}
                    hasSeen={hasSeen} unreadKnowledge={unreadKnowledge} onOpenKnowledge={onOpenKnowledge}
                    disabled={state.phase !== 'battle' || state.activePlayer !== 'player' || state.analysisTimer}
                    playerEnergy={state.playerEnergy} maCount={state.maUsedCount}
                    activePlayer={state.activePlayer} selectedChar={selectedChar}
                    playerNiveau={progression.niveau}
                    lastPlayerConceptId={state.lastPlayerConceptId}
                  />
                ))}
              </div>
            </motion.div>
          )}

          {/* ── ATOUTS ── */}
          {tab === 'atouts' && (
            <motion.div key="atouts" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <div style={{
                padding: isMobile ? '0.3rem 0.6rem' : '0.4rem 0.75rem',
                borderBottom: '1px solid rgba(255,255,255,0.04)',
                flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span style={{ fontFamily: 'monospace', fontSize: isMobile ? '0.48rem' : '0.52rem', color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
                  Punchlines · {selectedChar.nom}
                </span>
                <span style={{ fontFamily: 'monospace', fontSize: '0.4rem', color: 'rgba(255,255,255,0.18)' }}>
                  {punchlines.length}
                </span>
              </div>

              {/* Légende compacte — masquée sur très petit mobile */}
              {!isMobile && (
                <div style={{ padding: '0.3rem 0.75rem', borderBottom: '1px solid rgba(255,255,255,0.04)', display: 'flex', gap: '0.75rem', flexShrink: 0 }}>
                  <span style={{ fontFamily: 'monospace', fontSize: '0.38rem', color: 'rgba(239,68,68,0.55)', letterSpacing: '0.08em' }}>⚔ Attaque — inflige un drain</span>
                  <span style={{ fontFamily: 'monospace', fontSize: '0.38rem', color: 'rgba(34,211,238,0.55)', letterSpacing: '0.08em' }}>🛡 Défense — restaure / bloque</span>
                </div>
              )}

              <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '0.4rem 0.5rem' : '0.6rem 0.75rem', display: 'flex', flexDirection: 'column', gap: isMobile ? '0.35rem' : '0.5rem' }}
                className="hide-scrollbar">
                {punchlines.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '2rem', fontFamily: 'monospace', fontSize: '0.58rem', color: 'rgba(255,255,255,0.18)', fontStyle: 'italic' }}>
                    Pas encore de punchlines.
                  </div>
                )}
                {punchlines.map(item => (
                  <PunchlineCard
                    key={item.id} item={item} 
                    onPlay={playable => {
                      if (state.phase === 'battle' && state.activePlayer === 'player' && !state.analysisTimer) playMove(playable);
                    }}
                    energy={state.playerEnergy} selectedChar={selectedChar}
                    hasSeen={hasSeen} unreadKnowledge={unreadKnowledge} onOpenKnowledge={onOpenKnowledge}
                  />
                ))}
              </div>
            </motion.div>
          )}

          {/* ── JOURNAL ── */}
          {tab === 'journal' && (
            <motion.div key="journal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <div style={{
                padding: isMobile ? '0.3rem 0.6rem' : '0.4rem 0.75rem',
                borderBottom: '1px solid rgba(255,255,255,0.04)',
                flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span style={{ fontFamily: 'monospace', fontSize: isMobile ? '0.48rem' : '0.52rem', color: 'rgba(255,255,255,0.22)', textTransform: 'uppercase', letterSpacing: '0.15em' }}>JOURNAL</span>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  {playerInFlow && <span style={{ fontFamily: 'monospace', fontSize: '0.42rem', color: '#22d3ee' }}>✨ ×{state.playerNoHitStreak}</span>}
                  <span style={{ fontFamily: 'monospace', fontSize: '0.4rem', color: 'rgba(255,255,255,0.15)' }}>T{state.turn}</span>
                </div>
              </div>

              <div ref={logRef} style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '0.4rem 0.5rem' : '0.6rem 0.75rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}
                className="hide-scrollbar">
                {state.log.length === 0 && (
                  <div style={{ padding: '0.4rem 0', fontFamily: 'monospace', fontSize: '0.6rem', color: 'rgba(255,255,255,0.14)', lineHeight: 1.6, fontStyle: 'italic' }}>
                    « Seiryoku Zenyo — l'économie d'énergie vitale. »
                  </div>
                )}
                <AnimatePresence initial={false}>
                  {state.log.map(entry => (
                    <motion.div key={entry.id}
                      initial={{ opacity: 0, x: entry.actor === 'player' ? -8 : entry.actor === 'opponent' ? 8 : 0 }}
                      animate={{ opacity: 1, x: 0 }}
                      style={{ display: 'flex', flexDirection: 'column', gap: '0.08rem', alignItems: entry.actor === 'player' ? 'flex-start' : entry.actor === 'opponent' ? 'flex-end' : 'center' }}>
                      {entry.actor !== 'system' && (
                        <span style={{ fontFamily: 'monospace', fontSize: '0.44rem', letterSpacing: '0.1em', color: entry.color + 'aa', textTransform: 'uppercase' }}>
                          {entry.actor === 'player' ? `▶ ${selectedChar.nom.toUpperCase()}` : '◀ Précepteur'}
                        </span>
                      )}
                      <div style={{
                        maxWidth: entry.actor === 'system' ? '100%' : '88%',
                        padding: entry.actor === 'system' ? '0.18rem 0.45rem' : '0.35rem 0.55rem',
                        background: entry.actor === 'system' ? 'rgba(239,68,68,0.06)' : `${entry.color}0e`,
                        border: `1px solid ${entry.actor === 'system' ? 'rgba(239,68,68,0.15)' : entry.color + '22'}`,
                        textAlign: entry.actor === 'system' ? 'center' : 'left',
                        borderRadius: '3px',
                      }}>
                        <span style={{ fontFamily: 'monospace', fontSize: isMobile ? 'clamp(0.48rem,2.6vw,0.62rem)' : 'clamp(0.5rem,2.8vw,0.66rem)', color: entry.actor === 'system' ? '#ef4444aa' : 'rgba(255,255,255,0.62)', lineHeight: 1.45 }}>
                          {entry.text}
                        </span>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>

              {state.playerGrip > 40 && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  style={{ padding: isMobile ? '0.28rem 0.6rem' : '0.35rem 0.75rem', borderTop: '1px solid rgba(239,68,68,0.1)', background: 'rgba(239,68,68,0.04)', flexShrink: 0 }}>
                  <span style={{ fontFamily: 'monospace', fontSize: isMobile ? '0.48rem' : '0.52rem', color: 'rgba(239,68,68,0.72)' }}>
                    ⚡ Grip {Math.round(state.playerGrip)}%{state.playerGrip > 65 ? ' — CRITIQUE' : ''}
                  </span>
                </motion.div>
              )}
            </motion.div>
          )}

          {/* ── SYNERGIES ── */}
          {tab === 'synergies' && (
            <motion.div key="synergies" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '0.6rem' : '1rem' }}
              className="hide-scrollbar">
              {!state.lastPlayerConceptId ? (
                <div style={{ fontFamily: 'monospace', fontSize: '0.58rem', color: 'rgba(255,255,255,0.14)', textAlign: 'center', padding: '1.5rem 0', fontStyle: 'italic' }}>
                  Jouez un concept pour révéler les synergies.
                </div>
              ) : synergyList.length === 0 ? (
                <div style={{ fontFamily: 'monospace', fontSize: '0.58rem', color: 'rgba(255,255,255,0.14)', textAlign: 'center', padding: '1.5rem 0' }}>
                  Aucune synergie depuis ce concept.
                </div>
              ) : (
                <>
                  <div style={{ fontFamily: 'monospace', fontSize: '0.42rem', color: 'rgba(167,139,250,0.38)', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
                    COMBINAISONS DISPONIBLES
                  </div>
                  {synergyList.map(s => (
                    <motion.div key={s.name} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }}
                      style={{ padding: isMobile ? '0.5rem 0.65rem' : '0.65rem 0.8rem', background: `${s.color}09`, border: `1px solid ${s.color}2a`, display: 'flex', gap: '0.6rem', alignItems: 'flex-start', marginBottom: '0.4rem', borderRadius: '4px' }}>
                      {!isMobile && <span style={{ fontFamily: 'serif', fontSize: '1.3rem', color: s.color + '55', flexShrink: 0, lineHeight: 1 }}>{s.kanji}</span>}
                      <div>
                        <div style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: isMobile ? '0.56rem' : '0.6rem', color: s.color, letterSpacing: '0.08em', marginBottom: '0.18rem' }}>{s.name}</div>
                        <div style={{ fontFamily: 'monospace', fontSize: isMobile ? '0.5rem' : '0.52rem', color: 'rgba(255,255,255,0.42)', lineHeight: 1.45 }}>{s.bonus.msg}</div>
                      </div>
                    </motion.div>
                  ))}
                </>
              )}
            </motion.div>
          )}

        </AnimatePresence>
      </div>

      <ComboIndicator comboState={state.comboState} charColor={selectedChar.avatarColor} />
    </div>
  );
}

function saveMatchState(stateData) {
  try { localStorage.setItem('judoverbal_match', JSON.stringify(stateData)); } catch {}
}

function loadMatchState() {
  try { const s = localStorage.getItem('judoverbal_match'); return s ? JSON.parse(s) : null; } catch { return null; }
}

function clearMatchState() { localStorage.removeItem('judoverbal_match'); }

function saveMatchHistory(result) {
  try {
    const history = JSON.parse(localStorage.getItem('judoverbal_history') || '[]');
    history.push({ ...result, date: Date.now() });
    localStorage.setItem('judoverbal_history', JSON.stringify(history.slice(-20)));
  } catch {}
}

function getAffordableConcepts(energy, maUsedCount, deck, isPlayer = false, char = null) {
  return deck.filter(c => {
    let totalCost = c.cost;
    if (isPlayer && char?.passif) {
      const passifMod = char.passif();
      totalCost = Math.max(0, c.cost - (passifMod.costReduction || 0));
      if (c.ownTradition === false) {
        totalCost += Math.max(0, 10 - (passifMod.crossEpochDiscount || 0));
      }
    } else {
      // adversaire : on considère tous les concepts comme ownTradition:true
      totalCost = c.cost;
    }
    if (energy < totalCost) return false;
    if (c.id === 'ma' && maUsedCount >= 3) return false;
    if (c.isUltimate) return false; // l'adversaire n'en a pas, le joueur est géré ailleurs
    return true;
  });
}

const ScreenShake = ({ active, children }) => {
  if (!active) return children;
  
  return (
    <motion.div
      animate={{ 
        x: [0, -4, 3, -2, 1, 0],
        y: [0, 2, -3, 1, -1, 0] 
      }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      style={{ width: '100%', height: '100%' }}
    >
      {children}
    </motion.div>
  );
};

const HitFlash = ({ flash }) => (
  <AnimatePresence>
    {flash && (
      <motion.div
        initial={{ opacity: 0.6 }}
        animate={{ opacity: 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.35 }}
        style={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(ellipse at ${flash.side === 'right' ? '65%' : '35%'} 50%, ${flash.color}44 0%, transparent 70%)`,
          pointerEvents: 'none',
          zIndex: 20,
        }}
      />
    )}
  </AnimatePresence>
);

const SlashEffect = ({ slash }) => (
  <AnimatePresence>
    {slash && (
      <motion.div
        initial={{ 
          opacity: 0, 
          scaleX: 0.3,
          x: slash.side === 'right' ? '30%' : '-30%',
        }}
        animate={{ 
          opacity: [0, 0.8, 0],
          scaleX: [0.3, 1.2, 1.5],
          x: slash.side === 'right' ? ['30%', '0%', '-15%'] : ['-30%', '0%', '15%'],
        }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        style={{
          position: 'absolute',
          top: '15%',
          height: '70%',
          width: '40%',
          [slash.side === 'right' ? 'left' : 'right']: '10%',
          background: `linear-gradient(${slash.side === 'right' ? '90deg' : '270deg'}, transparent 0%, ${slash.actor === 'player' ? '#22d3ee44' : '#ef444444'} 40%, ${slash.actor === 'player' ? '#22d3ee88' : '#ef444488'} 60%, transparent 100%)`,
          borderRadius: '8px',
          pointerEvents: 'none',
          zIndex: 15,
          transformOrigin: slash.side === 'right' ? 'left center' : 'right center',
        }}
      />
    )}
  </AnimatePresence>
);

const ConceptAnnounce = ({ announce }) => (
  <AnimatePresence>
    {announce && (
      <motion.div
        initial={{ opacity: 0, scale: 0.7, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 1.1, y: -10 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 25,
          pointerEvents: 'none',
          textAlign: 'center',
        }}
      >
        <span
          className="cinzel"
          style={{
            fontSize: 'clamp(1.5rem, 6vw, 3rem)',
            color: announce.color || '#facc15',
            textShadow: `0 0 30px ${announce.color || '#facc15'}, 0 0 60px ${announce.color || '#facc15'}44`,
            letterSpacing: '0.1em',
            fontWeight: 700,
            textTransform: 'uppercase',
          }}
        >
          {announce.text}
        </span>
      </motion.div>
    )}
  </AnimatePresence>
);

function determineProfile(state) {
  const { playerEnergy, opponentEnergy, round, playerNoHitStreak, turn } = state;
 
  // ── RÉVÉLATEUR : le Précepteur montre sa vraie nature ──
  // Round 3, ou joueur en danger grave au round 1 (on ne l'écrase pas, on l'oblige à réagir)
  if (round >= 3) return 'revelateur';
 
  // Si le joueur a un streak de 5+ (flow), le Précepteur monte d'un cran
  if (playerNoHitStreak >= 5 && round >= 2) return 'revelateur';
 
  // ── SOCRATIQUE : début de round ou joueur très en forme ──
  // Les 3 premiers tours d'un round → le Précepteur observe
  const turnsInRound = (turn - 1) % 20; // approximation
  if (playerEnergy >= 220 && opponentEnergy >= 220) return 'socratique';
  if (playerEnergy >= 250) return 'socratique'; // joueur très fort → on l'encourage
 
  // ── DIALECTIQUE : phase normale ──
  return 'dialectique';
}

function shouldShowMercy(state) {
  const { playerEnergy, round, turn } = state;
 
  // Round 1, joueur < 100 énergie sur 300 → on ne l'achève pas, on lui laisse une ouverture
  if (round === 1 && playerEnergy < 100) return true;
 
  // Tout début de duel (tours 1-2) → le Précepteur ne frappe pas fort d'entrée
  if (turn <= 2) return true;
 
  return false;
}

function computePrecepteurMove(currentState) {
  if (currentState.phase !== 'battle' || currentState.activePlayer !== 'opponent') return null;
  
  const {
    lastPlayerConceptId,
    opponentEnergy,
    playerEnergy,
    opponentGrip,
    playerGrip,
    maUsedCount,
    round,
    theme,
    currentAIChar,
    aiDeck,
    playerNoHitStreak,
  } = currentState;
  
  const deck = aiDeck || DECK_ADVERSAIRE_BASE;
  const affordable = (id) => {
    const c = deck.find(x => x.id === id);
    return c && c.cost <= opponentEnergy && !(c.id === 'ma' && maUsedCount >= 3);
  };
  const getC = (id) => deck.find(x => x.id === id);
  
  const profile    = determineProfile(currentState);
  const mercy      = shouldShowMercy(currentState);
  const themeId    = theme?.id;
  const advCharId  = currentAIChar?.id ?? 'adv_kant';
  
  let choice = null;
  
  if (themeId && !mercy) {
    const themePrefs = PRECEPTEUR_THEME_PREFERENCE[advCharId]?.[themeId] ?? [];
      const ripostes   = lastPlayerConceptId ? (RIPOSTE_PHILOSOPHIQUE[lastPlayerConceptId] ?? []) : [];
  
      // Intersection : concept qui est à la fois thématique ET riposte
      const intersection = themePrefs.filter(id => ripostes.includes(id) && affordable(id));
    if (intersection.length > 0) {
        choice = getC(intersection[0]);
    }
  
    // Sinon : concept thématique seul, selon le profil
    if (!choice) {
      const profileFilter = profile === 'socratique' ? CONCEPTS_SOCRATIQUES
                          : profile === 'dialectique' ? CONCEPTS_DIALECTIQUES
                          : CONCEPTS_REVELATEURS;
      const themeAndProfile = themePrefs.filter(id => profileFilter.includes(id) && affordable(id));
      if (themeAndProfile.length > 0) {
        choice = getC(themeAndProfile[0]);
      }
    }
  }
  
    
  if (!choice && lastPlayerConceptId) {
    const ripostes = RIPOSTE_PHILOSOPHIQUE[lastPlayerConceptId] ?? [];
  
    // Filtrer selon le profil pour ne pas toujours jouer le contre optimal
    const profilePool = profile === 'socratique'
      ? ripostes.filter(id => CONCEPTS_SOCRATIQUES.includes(id))
      : profile === 'dialectique'
      ? ripostes.filter(id => CONCEPTS_DIALECTIQUES.includes(id) || CONCEPTS_SOCRATIQUES.includes(id))
      : ripostes; // revelateur : tout est permis
  
    const candidates = profilePool.filter(id => affordable(id));
    if (candidates.length > 0) {
      choice = getC(candidates[0]);
    }
  
    // Fallback : n'importe quelle riposte affordable
    if (!choice) {
      const any = ripostes.find(id => affordable(id));
      if (any) choice = getC(any);
    }
  }
  
  if (!choice) {
      if (playerNoHitStreak >= 3) {
        const blockers = ['wuwei', 'kintsugi', 'hegel'];
        const blocker = blockers.find(id => affordable(id));
        if (blocker) choice = getC(blocker);
      }
  
      // Précepteur en danger → se soigne
      if (!choice && opponentEnergy < 80) {
        const healers = ['ma', 'kintsugi', 'ubuntu', 'jitakyoei'];
        const healer = healers.find(id => affordable(id));
        if (healer) choice = getC(healer);
      }
  
      // Joueur très haut en grip → exploite la vulnérabilité
      if (!choice && playerGrip > 55) {
        const exploiters = ['seiryoku', 'entropie', 'asabiyya'];
        const exploiter = exploiters.find(id => affordable(id));
      if (exploiter) choice = getC(exploiter);
    }
  
    // Profil SOCRATIQUE : préférer un concept léger qui invite à jouer
    if (!choice && profile === 'socratique') {
      const light = CONCEPTS_SOCRATIQUES.find(id => affordable(id));
      if (light) choice = getC(light);
    }
  
    // Profil RÉVÉLATEUR : préférer un concept fort avec intention
    if (!choice && profile === 'revelateur') {
      const strong = CONCEPTS_REVELATEURS.find(id => affordable(id));
      if (strong) choice = getC(strong);
    }
  
    // MERCY : si le joueur est en difficulté, le Précepteur joue léger
    if (!choice && mercy) {
      const gentle = ['ma', 'ubuntu', 'wuwei', 'kintsugi', 'philosophe'];
      const g = gentle.find(id => affordable(id));
      if (g) choice = getC(g);
    }
  }
  
  if (!choice) {
      // Trier le deck par pertinence : concepts de la tradition du Précepteur en premier
      const advTradition = currentAIChar?.tradition;
      const sortedDeck = [...deck]
        .filter(c => !c.isUltimate && c.cost <= opponentEnergy && !(c.id === 'ma' && maUsedCount >= 3))
        .sort((a, b) => {
          const aMatch = a.epoque === advTradition ? -1 : 0;
          const bMatch = b.epoque === advTradition ? -1 : 0;
          return aMatch - bMatch;
        });
  
      if (sortedDeck.length > 0) {
        // Légère variance pour éviter la répétition : parmi les 3 premiers
        const pool = sortedDeck.slice(0, Math.min(3, sortedDeck.length));
        choice = pool[Math.floor(Math.random() * pool.length)];
      }
  }
  
  if (!choice) return null;
  return choice;
}

function checkPrecepteurSwitch(st) {
  if (!st || st.phase !== 'battle') return st;
  
  // Protection : pas de switch trop fréquent (min 3 tours d'écart)
  if (st._lastSwitchTurn && st.turn - st._lastSwitchTurn < 3) return st;
  
  const playerConcepts = st.playerUsedConcepts ?? [];
  const currentAdvId   = st.currentAIChar?.id;
  
  // ── Compter les traditions jouées par le joueur ──────────────────────────
  const traditionCount = {};
  playerConcepts.forEach(id => {
    const c = CONCEPTS_BASE.find(x => x.id === id) || Object.values(CONCEPTS_SHARED).find(x => x.id === id);
    if (c?.epoque) traditionCount[c.epoque] = (traditionCount[c.epoque] || 0) + 1;
  });
  
  // Tradition dominante du joueur (au moins 3 concepts joués dans cette tradition)
  const dominantTradition = Object.entries(traditionCount).find(([, count]) => count >= 3)?.[0];

  if (!dominantTradition) return st;
  
  // ── Trouver le Précepteur dont la tradition RÉPOND à la dominante ─────────
  // Logique : si le joueur maîtrise la tradition 'lumiere' (Kant),
  // un Précepteur de tradition 'medievale' ou 'moderne' apporte un défi nouveau.
  const TRADITION_COUNTER = {
    lumiere:   ['medievale', 'moderne'],
    medievale: ['lumiere', 'antique'],
    moderne:   ['judo', 'medievale'],
    antique:   ['moderne', 'lumiere'],
    judo:      ['antique', 'medievale'],
    taoiste:   ['judo', 'lumiere'],
    africain:  ['medievale', 'antique'],
    };
  
  const counterTraditions = TRADITION_COUNTER[dominantTradition] ?? [];
  const candidate = AI_CHARS.find(ai =>
    ai.id !== currentAdvId &&
    counterTraditions.includes(ai.tradition)
  );
  
  if (!candidate) return st;
  
    // ── Appliquer le switch ───────────────────────────────────────────────────
  sounds.characterSwitch?.();
  
  return {
    ...st,
    currentAIChar:      candidate,
    aiDeck:             buildAIDeck(candidate),
    _lastSwitchTurn:    st.turn,
    _advKantHasSwitched: candidate.id === 'adv_kant' ? true : st._advKantHasSwitched,
  };
}

function EchoLibrary({ knowledgeId, type = 'concept', onClose }) {
  const data = type === 'punchline'
    ? KNOWLEDGE_PUNCHLINES[knowledgeId]
    : KNOWLEDGE_BASE[knowledgeId];

  // ── États ──
  const [phase, setPhase] = useState('awakening'); // awakening | dreaming | memory
  const [activeMemory, setActiveMemory] = useState(null);
  const [hoveredThread, setHoveredThread] = useState(null);
  const [floatingWords, setFloatingWords] = useState([]);
  const [ripple, setRipple] = useState(null); // { x, y }

  // ── Effet d'éveil ──
  useEffect(() => {
    const timer = setTimeout(() => setPhase('dreaming'), 2000);
    return () => clearTimeout(timer);
  }, []);

  // ── Génération des lucioles mémorielles ──
  const memoryFireflies = useMemo(() => 
    Array.from({ length: 22 }, (_, i) => ({
      id: i,
      x: 15 + Math.random() * 70,
      y: 10 + Math.random() * 80,
      size: 1.5 + Math.random() * 3.5,
      glow: 0.2 + Math.random() * 0.6,
      path: Math.random() * Math.PI * 2,
      speed: 8 + Math.random() * 18,
      hue: type === 'concept' 
        ? 35 + Math.random() * 25  // ambré
        : 260 + Math.random() * 30, // violet
    })), [type]
  );

  // ── Arbres-livres ──
  const bookTrees = useMemo(() => [
    { id: 'precepteur', x: 50, y: 38, label: 'Le Précepteur', icon: '🌳', root: true },
    { id: 'image', x: 18, y: 52, label: 'Le Portrait', icon: '🌿' },
    { id: 'fiche', x: 82, y: 52, label: 'La Fiche', icon: '🍂' },
  ], []);

  if (!data) return null;

  const handleTreeClick = (id) => {
    setActiveMemory(id);
    setPhase('memory');
    
    // Faire apparaître des mots flottants
    const words = data.precepteur?.split(' ').slice(0, 18) || [];
    setFloatingWords(words.map((w, i) => ({
      word: w,
      x: 20 + Math.random() * 60,
      y: 30 + Math.random() * 40,
      delay: i * 0.08,
    })));
  };

  const handleCanvasClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setRipple({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
    setTimeout(() => setRipple(null), 1200);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={handleCanvasClick}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: '#0a0a0f',
        overflow: 'hidden',
        cursor: phase === 'dreaming' ? 'crosshair' : 'default',
      }}
    >
      {/* ── Ciel étoilé profond ── */}
      <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
        <defs>
          <radialGradient id="nebula" cx="50%" cy="30%" r="60%">
            <stop offset="0%" stopColor={type === 'concept' ? '#2a1a08' : '#1a0a2a'} stopOpacity="0.8" />
            <stop offset="50%" stopColor="#080812" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#050510" stopOpacity="1" />
          </radialGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <rect width="100%" height="100%" fill="url(#nebula)" />
        
        {/* Étoiles */}
        {Array.from({ length: 60 }, (_, i) => (
          <circle
            key={i}
            cx={Math.random() * 100 + '%'}
            cy={Math.random() * 100 + '%'}
            r={0.5 + Math.random() * 1.3}
            fill={`rgba(255,${220 + Math.random() * 35},${140 + Math.random() * 80},${0.3 + Math.random() * 0.5})`}
          >
            <animate
              attributeName="opacity"
              values={`${0.2 + Math.random() * 0.3};${0.6 + Math.random() * 0.4};${0.2 + Math.random() * 0.3}`}
              dur={`${2 + Math.random() * 5}s`}
              repeatCount="indefinite"
            />
          </circle>
        ))}
      </svg>

      {/* ── Lucioles mémorielles ── */}
      {memoryFireflies.map(f => (
        <motion.div
          key={f.id}
          style={{
            position: 'absolute',
            left: `${f.x}%`, top: `${f.y}%`,
            width: f.size, height: f.size,
            borderRadius: '50%',
            background: `hsl(${f.hue}, 80%, 70%)`,
            boxShadow: `0 0 ${f.size * 4}px hsl(${f.hue}, 90%, 60%)`,
            filter: 'url(#glow)',
            pointerEvents: 'none',
            mixBlendMode: 'screen',
          }}
          animate={{
            x: [0, Math.cos(f.path) * 40, Math.cos(f.path + 1.5) * 25, Math.cos(f.path) * 50, 0],
            y: [0, Math.sin(f.path) * 35, Math.sin(f.path + 1.8) * 40, Math.sin(f.path) * 20, 0],
            opacity: [f.glow, f.glow * 1.6, f.glow * 0.5, f.glow * 1.3, f.glow],
          }}
          transition={{
            duration: f.speed,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: Math.random() * 3,
          }}
        />
      ))}

      {/* ── Surface de l'eau noire (sol) ── */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: phase !== 'awakening' ? 0.7 : 0 }}
        transition={{ duration: 3 }}
        style={{
          position: 'absolute',
          bottom: 0, left: 0, right: 0,
          height: '28%',
          background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(2,2,8,0.9) 40%, #020208 100%)',
        }}
      />

      {/* ── Onde de clic ── */}
      {ripple && (
        <motion.div
          initial={{ scale: 0, opacity: 0.8 }}
          animate={{ scale: 4, opacity: 0 }}
          transition={{ duration: 1.2, ease: 'easeOut' }}
          style={{
            position: 'absolute',
            left: `${ripple.x}%`, top: `${ripple.y}%`,
            width: '40px', height: '40px',
            marginLeft: '-20px', marginTop: '-20px',
            borderRadius: '50%',
            border: `1px solid ${type === 'concept' ? 'rgba(200,150,80,0.4)' : 'rgba(160,120,200,0.4)'}`,
            pointerEvents: 'none',
          }}
        />
      )}

      {/* ── Arbres-livres ── */}
      {phase === 'dreaming' && bookTrees.map(tree => (
        <motion.div
          key={tree.id}
          initial={{ opacity: 0, scale: 0, y: 60 }}
          animate={{ 
            opacity: 1, 
            scale: hoveredThread === tree.id ? 1.08 : 1,
            y: 0 
          }}
          transition={{ 
            delay: tree.root ? 0.8 : 1.2,
            type: 'spring',
            stiffness: 80,
            damping: 12,
          }}
          style={{
            position: 'absolute',
            left: `${tree.x}%`, top: `${tree.y}%`,
            transform: 'translate(-50%, -50%)',
            cursor: 'pointer',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', gap: '0.3rem',
            zIndex: 10,
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleTreeClick(tree.id);
          }}
          onMouseEnter={() => setHoveredThread(tree.id)}
          onMouseLeave={() => setHoveredThread(null)}
        >
          {/* Feuillage lumineux */}
          <motion.div
            animate={hoveredThread === tree.id ? {
              filter: ['drop-shadow(0 0 8px rgba(200,170,100,0.4))', 'drop-shadow(0 0 18px rgba(200,170,100,0.7))', 'drop-shadow(0 0 8px rgba(200,170,100,0.4))']
            } : {}}
            transition={{ duration: 2, repeat: Infinity }}
            style={{
              fontSize: tree.root ? '4rem' : '2.8rem',
              filter: 'drop-shadow(0 0 6px rgba(200,170,100,0.25))',
              lineHeight: 1,
            }}
          >
            {tree.icon}
          </motion.div>
          
          {/* Tronc filamenteux */}
          <div style={{
            width: tree.root ? '3px' : '2px',
            height: tree.root ? '60px' : '40px',
            background: `linear-gradient(180deg, 
              ${type === 'concept' ? 'rgba(180,140,80,0.6)' : 'rgba(140,100,180,0.6)'} 0%,
              transparent 100%)`,
          }} />
          
          {/* Étiquette */}
          <span style={{
            fontSize: '0.58rem',
            color: hoveredThread === tree.id 
              ? 'rgba(220,200,160,1.9)' 
              : 'rgba(160,140,110,0.9)',
            fontFamily: 'Georgia, serif',
            fontStyle: 'italic',
            letterSpacing: '0.12em',
            transition: 'color 0.5s',
            textShadow: hoveredThread === tree.id 
              ? '0 0 12px rgba(200,160,80,1.5)' 
              : 'none',
          }}>
            {tree.label}
          </span>
        </motion.div>
      ))}

      {/* ── Phase d'éveil : titre ── */}
      {phase === 'awakening' && (
        <motion.div
          initial={{ opacity: 0, letterSpacing: '1.2em' }}
          animate={{ opacity: [0, 1, 0.8], letterSpacing: '0.3em' }}
          transition={{ duration: 2.5, ease: 'easeOut' }}
          style={{
            position: 'absolute',
            top: '42%', left: '50%',
            transform: 'translate(-50%, -50%)',
            fontSize: 'clamp(1.2rem, 3.5vw, 1.8rem)',
            color: type === 'concept' ? '#c8a96e' : '#b49ac8',
            fontFamily: 'Georgia, serif',
            fontStyle: 'italic',
            textAlign: 'center',
            whiteSpace: 'nowrap',
          }}
        >
          La Bibliothèque des Échos
        </motion.div>
      )}

      {/* ── Pénétrez la mémoire (sous-titre) ── */}
      {phase === 'dreaming' && !activeMemory && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: [0.3, 0.6, 0.3], y: 0 }}
          transition={{ duration: 4, repeat: Infinity }}
          style={{
            position: 'absolute',
            bottom: '10%', left: '50%',
            transform: 'translateX(-50%)',
            fontSize: '0.7rem',
            color: 'rgba(180,160,130,1.9)',
            fontFamily: 'monospace',
            letterSpacing: '0.35em',
            textTransform: 'uppercase',
            pointerEvents: 'none',
          }}
        >
          Touchez un arbre pour faire revivre un souvenir
        </motion.div>
      )}

      {/* ── Phase Mémoire : Lecture ── */}
      {phase === 'memory' && activeMemory && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            pointerEvents: 'all',
          }}
        >
          {/* Fond de page ancienne */}
          <div style={{
            width: 'min(580px, 90vw)',
            maxHeight: '80vh',
            padding: '2.5rem 2rem',
            background: `
              radial-gradient(ellipse at 50% 30%, rgba(30,20,10,0.9) 0%, rgba(8,5,3,0.95) 100%)
            `,
            border: '1px solid rgba(180,140,80,0.15)',
            borderRadius: '6px',
            boxShadow: '0 40px 100px rgba(0,0,0,0.9), 0 0 0 1px rgba(60,40,15,0.3)',
            overflow: 'auto',
            position: 'relative',
          }}>
            {/* Marques de brûlure sur les bords */}
            <div style={{
              position: 'absolute', inset: 0, pointerEvents: 'none',
              background: `
                radial-gradient(circle at 5% 95%, rgba(80,30,5,0.3) 0%, transparent 30%),
                radial-gradient(circle at 92% 8%, rgba(80,30,5,0.2) 0%, transparent 25%),
                radial-gradient(circle at 85% 85%, rgba(40,15,2,0.3) 0%, transparent 20%)
              `,
              borderRadius: '6px',
            }} />

            {activeMemory === 'precepteur' && (
              <>
                <div style={{
                  fontFamily: 'monospace', fontSize: '0.55rem',
                  color: 'rgba(180,150,100,0.9)',
                  letterSpacing: '0.4em', textTransform: 'uppercase',
                  marginBottom: '1.5rem',
                }}>
                  ✦ La Voix du Précepteur ✦
                </div>
                <p style={{
                  fontFamily: "'Georgia', serif", fontStyle: 'italic',
                  fontSize: 'clamp(0.8rem, 2vw, 0.95rem)',
                  color: 'rgba(210,190,155,0.85)',
                  lineHeight: 2.1, margin: 0,
                }}>
                  {data.precepteur}
                </p>
              </>
            )}
            
            {activeMemory === 'image' && (
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  display: 'inline-block', padding: '8px',
                  background: 'linear-gradient(135deg, #5a3a10, #8a6020, #5a3a10)',
                  borderRadius: '3px',
                  boxShadow: '0 15px 40px rgba(0,0,0,0.8)',
                }}>
                  {data.image ? (
                    <img src={data.image} alt={data.titre}
                      style={{
                        maxWidth: '220px', maxHeight: '280px',
                        filter: 'sepia(0.6) brightness(0.7) contrast(1.2)',
                        display: 'block',
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '180px', height: '240px',
                      background: '#0a0501',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'rgba(150,120,80,0.2)', fontSize: '2rem',
                    }}>🖼</div>
                  )}
                </div>
                <div style={{
                  marginTop: '0.8rem', fontSize: '0.65rem',
                  color: 'rgba(180,150,110,0.9)',
                  fontFamily: 'monospace',
                }}>
                  {data.auteur} — {data.epoque}
                </div>
              </div>
            )}
            
            {activeMemory === 'fiche' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {(data.fiche?.concept || data.fiche?.auteurBio) && (
                  <p style={{
                    fontFamily: 'monospace', fontSize: '0.6rem',
                    color: 'rgba(170,150,120,0.77)',
                    lineHeight: 1.9, margin: 0,
                  }}>
                    {data.fiche?.concept || data.fiche?.auteurBio}
                  </p>
                )}
                {data.fiche?.citation && (
                  <div style={{
                    borderLeft: '2px solid rgba(180,140,80,0.3)',
                    paddingLeft: '1rem',
                  }}>
                    <p style={{
                      fontFamily: "'Georgia', serif", fontStyle: 'italic',
                      fontSize: '0.75rem',
                      color: 'rgba(180,155,120,0.9)',
                      lineHeight: 1.8, margin: 0,
                    }}>
                      "{data.fiche.citation}"
                    </p>
                  </div>
                )}
              </div>
            )}

            <button
              onClick={(e) => {
                e.stopPropagation();
                setActiveMemory(null);
                setPhase('dreaming');
                setFloatingWords([]);
              }}
              style={{
                marginTop: '1.5rem',
                background: 'none',
                border: '1px solid rgba(180,140,80,0.5)',
                color: 'rgba(180,150,100,0.9)',
                padding: '0.4rem 1rem',
                fontFamily: 'monospace', fontSize: '0.5rem',
                letterSpacing: '0.2em',
                cursor: 'pointer',
                borderRadius: '2px',
                transition: 'all 0.3s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = 'rgba(200,160,100,0.5)';
                e.currentTarget.style.color = 'rgba(200,170,120,0.8)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = 'rgba(180,140,80,0.2)';
                e.currentTarget.style.color = 'rgba(180,150,100,0.5)';
              }}
            >
              ← Retour aux arbres
            </button>
          </div>
        </motion.div>
      )}

      {/* ── Mots flottants (particules sémantiques) ── */}
      {floatingWords.map((w, i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0, x: 0, y: 0 }}
          animate={{
            opacity: [0, 0.7, 0],
            x: [0, (Math.random() - 0.5) * 120],
            y: [0, -40 - Math.random() * 60],
          }}
          transition={{
            duration: 4 + Math.random() * 3,
            delay: w.delay,
            ease: 'easeOut',
          }}
          style={{
            position: 'absolute',
            left: `${w.x}%`, top: `${w.y}%`,
            color: type === 'concept' 
              ? 'rgba(200,160,100,0.75)' 
              : 'rgba(160,130,200,0.75)',
            fontFamily: 'Georgia, serif',
            fontStyle: 'italic',
            fontSize: '0.55rem',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          {w.word}
        </motion.span>
      ))}

      {/* ── Bouton sortie ── */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.5 }}
        whileHover={{ opacity: 1 }}
        onClick={(e) => {
          e.stopPropagation();
          setPhase('awakening');
          setTimeout(onClose, 800);
        }}
        style={{
          position: 'absolute', top: '1.5rem', right: '1.5rem',
          background: 'none', border: 'none',
          color: 'rgba(150,120,80,0.9)',
          fontSize: '1.2rem', cursor: 'pointer',
          zIndex: 30,
        }}
      >
        ✕
      </motion.button>
    </motion.div>
  );
}




/* ─── COMPOSANT PRINCIPAL ─────────────────────────────────────────────────── */
export default function JudoVerbalBattle() {
  const { user, isAuthenticated } = useAuth();
  const { messages, push, dismiss, clearAll } = useDuelNarrative();
  //const { toasts, addToast, clearToasts } = usePhiloToast();
  const isMobile = useIsMobile();
  const [showLivret, setShowLivret] = useState(false);
  const [showGuilde, setShowGuilde] = useState(false);
  const [musicTrack, setMusicTrack] = useState('menu');
  const [showTutorial, setShowTutorial] = useState(false);
  const [arenaIndex, setArenaIndex] = useState(0);
  
  const [hasSavedMatch, setHasSavedMatch] = useState(false);
  const [aiCharSwitch, setAiCharSwitch] = useState(null);
  const [globalStats, setGlobalStats] = useState(loadGlobalStats);
  const [newUnlockAnim, setNewUnlockAnim] = useState(null);
  const { hasSeen, markSeen } = useKnowledgeTracker();
  const [knowledgeModal, setKnowledgeModal] = useState(null);
  const [unreadKnowledge, setUnreadKnowledge] = useState(() => {
    try {
      const stored = localStorage.getItem('jvb_unread_knowledge');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [pendingConcept, setPendingConcept] = useState(null);
  const [wheelOptions, setWheelOptions]     = useState([]);  
 
  const [screen, setScreen] = useState('title');
  const [activeSynergy, setActiveSynergy] = useState(null);
  const [selectedChar, setSelectedChar] = useState(() => {
    try { const s = localStorage.getItem('judoverbal_char'); return s ? (PERSONNAGES.find(c => c.id === s) || DEFAULT_CHAR) : DEFAULT_CHAR; } catch { return DEFAULT_CHAR; }
  });
  const [progression, setProgression] = useState(() => {
    try { const s = localStorage.getItem('judoverbal_prog'); return s ? JSON.parse(s) : { niveau: 1, xp: 0, slotsCombo: 0 }; } catch { return { niveau: 1, xp: 0, slotsCombo: 0 }; }
  });
  const getInitialAIChar = (selectedCharId) => {
    // On exclut les adv dont le sourceCharId correspond au perso joueur
    const candidates = AI_CHARS.filter(ai => ai.sourceCharId !== selectedCharId);
    if (candidates.length === 0) return AI_CHARS[0]; // fallback ultime
    return candidates[Math.floor(Math.random() * candidates.length)];
  };
  const makeInitState = useCallback(() => {
    const initialAIChar = getInitialAIChar(selectedChar.id);

    return {
        playerEnergy: 300,
        opponentEnergy: 300,
        playerGrip: 0,
        playerDisorder: 0,
        opponentGrip: 0,
        opponentDisorder: 0,
        round: 1,
        roundsWon: [0, 0],
        phase: 'intro',
        activePlayer: 'player',
        theme: THEMES[Math.floor(Math.random() * THEMES.length)],
        maUsedCount: 0,
        playerBlockNext: false,
        opponentBlockNext: false,
        playerUsedConcepts: [],
        lastOpponentConceptDrain: 0,
        lastPlayerConceptDrain: 0,
        hypertexteStack: 0,
        log: [],
        turn: 0,
        lastDamageLabel: null,
        lastDamageSide: null,
        playerHit: false,
        oppHit: false,
        playerAtk: false,
        oppAtk: false,
        opponentMemory: [],
        playerNoHitStreak: 0,
        opponentNoHitStreak: 0,
        lastPlayerConceptId: null,
        lastOpponentConceptId: null,
        currentAIChar: initialAIChar,
        aiDeck: buildAIDeck(initialAIChar),
        analysisTimer: false,
        showFlowModal: false,
        showWisdomFlash: null,
        dagueEclats: 5,
        comboState: null,
        punchlinePlayedThisRound: false,
        subThemeRevealed: false,
        themeBonusPunchlineUsed: false,
        _lastSwitchTurn: 0,
        _advKantHasSwitched: false,
        conceptRepeat: false,
        playerComboStreak: 0, 
        
    };
}, [selectedChar.id]);

  const [state, setState] = useState(makeInitState);
  const [screenShake, setScreenShake] = useState(false);
  const [hitFlash, setHitFlash] = useState(null); // { side: 'left'|'right', color: string }
  const [slashEffect, setSlashEffect] = useState(null); // { side: 'left'|'right', actor: 'player'|'opponent' }
  const [conceptAnnounce, setConceptAnnounce] = useState(null); // { text: string, color: string }
  const [opponentScanning, setOpponentScanning] = useState(false);

  const [showOpening, setShowOpening] = useState(() => {
    try { return !localStorage.getItem('jvb_opening_seen'); } catch { return true; }
    });
 

  const [sophiaInterruption, setSophiaInterruption] = useState(null);
  const [subThemeReveal, setSubThemeReveal] = useState(null);
  
  const [turnPhase, setTurnPhase] = useState('IDLE');
  const [queuedResult, setQueuedResult] = useState(null);
  const logRef = useRef(null);
  const arenaRef = useRef(null);
  const roundEndTimeoutRef = useRef(null);
  const analysisTimerRef = useRef(null);
  const aiThinkRef = useRef(null);
  const prevStreakRef = useRef(0);
  const prevDagueRef = useRef(5);
  
  const isSuperAdmin = Boolean(
    user?.role === 'superadmin' || user?.isSuperAdmin === true
  );
  
  const userPesas = typeof user?.pesas === 'string' 
    ? parseInt(user?.pesas, 10) 
    : (user?.pesas ?? 0);  

  const replayOpening = () => {
  setShowOpening(true);
};
  
  const hasEnoughPesas = userPesas >= 2200;     
  
  useEffect(() => {
      if (isSuperAdmin) {
        if (screen === "blocked") {
          setScreen("title");
        }
        return;
      }
  
      if (!user) return;

    if (!isAuthenticated || !hasEnoughPesas) {
      setScreen("blocked");
    } else if (screen === "blocked") {
      setScreen("title");
    }
    }, [isSuperAdmin, user, hasEnoughPesas, screen, isAuthenticated]);
    
  

  
  useEffect(() => {const done = localStorage.getItem('tutorialDone'); if (!done) { setShowTutorial(true); }}, []);
  useEffect(() => { setHasSavedMatch(!!loadMatchState());}, []);
  useEffect(() => { try { localStorage.setItem('judoverbal_char', selectedChar.id); } catch {} }, [selectedChar]);
  useEffect(() => { try { localStorage.setItem('judoverbal_prog', JSON.stringify(progression)); } catch {} }, [progression]);
  
  useEffect(() => {
  if (screen !== 'blocked') {
    music.playMenu();
    setMusicTrack('menu');
  }
  return () => music.dispose();
}, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, [state.log]);
  
  const getThemeConstraints = useCallback((charId, themeId) => {
    const constraints = THEME_DECK_CONSTRAINTS[themeId];
    if (!constraints) return [];
    return constraints[charId] || [];
  }, []);
  
  const getThemeBonusPunchline = useCallback((charId, themeId) => {
    const key = `${charId}_${themeId}`;
    return THEME_BONUS_PUNCHLINES[key] || null;
  }, []);
  
  const playerDeck = useMemo(() => {
    const base = buildDeck(selectedChar);
    const excluded = getThemeConstraints(selectedChar.id, state.theme?.id);
    const resonance = CHAR_THEME_MAP[selectedChar.id]?.resonance || [];
    const friction   = CHAR_THEME_MAP[selectedChar.id]?.friction   || [];
    const isResonance = resonance.includes(state.theme?.id);
    const isFriction  = friction.includes(state.theme?.id);

    return base
      .filter(c => !excluded.includes(c.id))
      .map(c => {
        const isPropre = selectedChar.propres?.some(p => p.id === c.id);
        if (!isPropre) return c;
        return {
          ...c,
          cost:  isResonance ? Math.max(0, c.cost - 5) : isFriction ? c.cost + 5 : c.cost,
          drain: isFriction  ? Math.floor(c.drain * 0.9) : c.drain,
        };
      });
  }, [selectedChar, state.theme?.id, getThemeConstraints]);

  const playerInFlow = state.playerNoHitStreak >= 3;
  const oppenentInFlow = state.opponentNoHitStreak >=3;
  const arenaIntensity = (state.playerGrip + state.opponentGrip) / 200;
  const applyGripPenalty = (energy, grip) => grip > 50 ? Math.max(0, energy - Math.floor((grip - 50) * 0.1)) : energy;
  const rang = getRang(progression.niveau);
  const winner = state.phase === 'victory' ? (state.roundsWon[0] >= 2 ? 'player' : 'opponent') : null;
  const narrativeDefeat = state.dagueEclats === 0 && winner === 'opponent';
  const xpNext = getXPForLevel(progression.niveau + 1);
  const xpPct = Math.min(100, (progression.xp / xpNext) * 100);
  const currentAIChar = state.currentAIChar || AI_CHARS[0];
  const resolvedAdversary = resolveAIChar(currentAIChar);
  
  const startAnalysisTimer = useCallback((duration=5) => {
    setState(prev => ({ ...prev, analysisTimer:true }));
    if (analysisTimerRef.current) clearTimeout(analysisTimerRef.current);
    analysisTimerRef.current = setTimeout(() => {
      setState(prev => ({ ...prev, analysisTimer:false }));
    }, duration*1000);
  }, []);

  const checkAndResolveDeadlock = useCallback((prevState) => {
  if (prevState.phase !== 'battle') return prevState;

  const playerHasMoves = getAffordableConcepts(
    prevState.playerEnergy,
    prevState.maUsedCount,
    playerDeck,
    true,
    selectedChar
  ).length > 0;

  const opponentHasMoves = getAffordableConcepts(
    prevState.opponentEnergy,
    prevState.maUsedCount,
    DECK_ADVERSAIRE_BASE,
    false,
    null
  ).length > 0;

  if (playerHasMoves || opponentHasMoves) return prevState;

  // === DEADLOCK ===
  let roundWinner = null;
  if (prevState.playerEnergy > prevState.opponentEnergy) roundWinner = 'player';
  else if (prevState.opponentEnergy > prevState.playerEnergy) roundWinner = 'opponent';
  else roundWinner = 'draw';

  const newLog = [
    ...prevState.log,
    {
      id: Date.now(),
      actor: 'system',
      text: '⚖️ Aucun coup possible — fin du round par deadlock.',
      color: '#facc15',
      ts: prevState.turn + 1,
    },
  ];

  let newRoundsWon = [...prevState.roundsWon];
  if (roundWinner === 'player') newRoundsWon[0]++;
  else if (roundWinner === 'opponent') newRoundsWon[1]++;

  const someoneWonMatch = newRoundsWon[0] >= 2 || newRoundsWon[1] >= 2;

  return {
    ...prevState,
    phase: someoneWonMatch ? 'victory' : 'roundEnd',
    roundsWon: newRoundsWon,
    log: newLog,
    activePlayer: null,
    turn: prevState.turn + 1,
    // Nettoyage propre
    playerBlockNext: false,
    opponentBlockNext: false,
  };
}, [playerDeck, selectedChar]);  

  const handleOpenKnowledge = useCallback((knowledgeId, type) => {
    // Enlever le clignotement
    setUnreadKnowledge(prev => {
      const updated = prev.filter(id => id !== knowledgeId);
      try { localStorage.setItem('jvb_unread_knowledge', JSON.stringify(updated)); } catch {}
      return updated;
    });
    // Ouvrir la modale
    setKnowledgeModal({ id: knowledgeId, type });
  }, []);
  
  const themeBonusPunchline = useMemo(() => {
    if (state.themeBonusPunchlineUsed) return null;
    return getThemeBonusPunchline(selectedChar.id, state.theme?.id);
  }, [selectedChar.id, state.theme?.id, state.themeBonusPunchlineUsed, getThemeBonusPunchline]);
  
  const resolveMove = useCallback((actor, concept, prevState) => {
    // Toutes les références à "state" deviennent "prevState"
    if (prevState.phase !== 'battle') return null;
    if (actor === 'player' && prevState.activePlayer !== 'player') return null;
    if (actor === 'opponent' && prevState.activePlayer !== 'opponent') return null;
    if (concept.id === 'ma' && prevState.maUsedCount >= 3) return null;

    const passifMod = actor === 'player' && selectedChar?.passif ? selectedChar.passif() : {};
    const isOwnTradition = actor !== 'player' || concept.ownTradition !== false;
    const crossCost = isOwnTradition ? 0 : Math.max(0, 10 - (passifMod.crossEpochDiscount || 0));
    const finalCost = Math.max(0, concept.cost - (passifMod.costReduction || 0)) + crossCost;

    const currentEnergy = actor === 'player' ? prevState.playerEnergy : prevState.opponentEnergy;
    if (currentEnergy < finalCost) return null;
    if (concept.isUltimate && actor === 'player' && progression.niveau < 5) return null;

    // === CLONE DE L'ÉTAT ===
    let pE = prevState.playerEnergy, oE = prevState.opponentEnergy;
    let pG = prevState.playerGrip, oG = prevState.opponentGrip;
    let pBlock = prevState.playerBlockNext, oBlock = prevState.opponentBlockNext;
    let pUsed = [...prevState.playerUsedConcepts];
    let oDis = prevState.opponentDisorder, pDis = prevState.playerDisorder || 0;
    let hyper = prevState.hypertexteStack;
    let lastODrain = prevState.lastOpponentConceptDrain;
    let lastPDrain = prevState.lastPlayerConceptDrain;
    let maCount = prevState.maUsedCount;
    let noHitStreak = prevState.playerNoHitStreak, oppNoHitStreak = prevState.opponentNoHitStreak;
    let opponentMemory = [...prevState.opponentMemory];
    let newLog = [...prevState.log];
    let synergyFound = null;   

    const addL = (actorLog, text, color) => {
      newLog.push({ id: Date.now() + Math.random(), actor: actorLog, text, color, ts: prevState.turn });
    };

    // Coût
    if (actor === 'player') pE -= finalCost; else oE -= finalCost;

    let effectiveDrain = concept.drain;

    // === BLOCAGE ===
    if (actor === 'player' && oBlock) { 
      effectiveDrain = 0; oBlock = false; 
      addL('system', "🛡️ Wu Wei — l'attaque est bloquée !", '#a5f3c3'); 
    } else if (actor === 'opponent' && pBlock) { 
      effectiveDrain = 0; pBlock = false; 
      addL('system', "🛡️ Votre Wu Wei absorbe l'attaque !", '#a5f3c3'); 
    }

    // Pénalité de désordre
    if (actor === 'player' && oDis > 0) effectiveDrain = Math.floor(effectiveDrain * (1 - Math.min(0.5, oDis / 100)));
    if (actor === 'opponent' && pDis > 0) effectiveDrain = Math.floor(effectiveDrain * (1 - Math.min(0.5, pDis / 100)));
    
    // Bonus passif & flow
    if (actor === 'player' && passifMod.bonusDrain && effectiveDrain > 0) effectiveDrain += passifMod.bonusDrain;
    if (actor === 'player' && noHitStreak >= 3 && effectiveDrain > 0) { 
      effectiveDrain = Math.floor(effectiveDrain * 1.15); 
      addL('system', '✨ FLOW — dégâts amplifiés !', '#22d3ee'); 
    }

    if (actor === 'opponent' && oppNoHitStreak >= 3 && effectiveDrain > 0) { 
      effectiveDrain = Math.floor(effectiveDrain * 1.15); 
      addL('system', '✨ FLOW — dégâts amplifiés !', '#41e8aa'); 
    }

    

    // === SYNERGIES ===
    if (actor === 'player' && prevState.lastPlayerConceptId) {
      const syn = checkSynergy(prevState.lastPlayerConceptId, concept.id);
      if (syn) {
        synergyFound = syn;
        if (syn.bonus.drain) effectiveDrain += syn.bonus.drain;
        if (syn.bonus.heal) pE = Math.min(300, pE + syn.bonus.heal);
        if (syn.bonus.gripReduce) pG = Math.max(0, pG - syn.bonus.gripReduce);
        if (syn.bonus.disorder) oDis = Math.min(100, oDis + syn.bonus.disorder);
        addL('system', `⚡ SYNERGIE : ${syn.name} — ${syn.bonus.msg}`, syn.color);
      }
    }
    if (actor === 'opponent' && prevState.lastOpponentConceptId) {
      const syn = checkSynergy(prevState.lastOpponentConceptId, concept.id);
      if (syn) {
        synergyFound = syn;
        if (syn.bonus.drain) effectiveDrain += syn.bonus.drain;
        if (syn.bonus.heal) pE = Math.min(300, pE + syn.bonus.heal);
        if (syn.bonus.gripReduce) pG = Math.max(0, pG - syn.bonus.gripReduce);
        if (syn.bonus.disorder) oDis = Math.min(100, oDis + syn.bonus.disorder);
        addL('system', `⚡ SYNERGIE : ${syn.name} — ${syn.bonus.msg}`, syn.color);
      }
    }

    // Appliquer le drain
    if (actor === 'player') { oE -= effectiveDrain; lastPDrain = effectiveDrain; }
    else { pE -= effectiveDrain; lastODrain = effectiveDrain; }

    // === EFFETS SPÉCIAUX ===
    if (concept.id === 'ma') { 
      if (actor === 'player') { 
        pE = Math.min(300, pE + 10 + (passifMod.bonusHeal || 0)); 
        pDis = Math.max(0, pDis - 15); maCount++; 
      } else { 
        oE = Math.min(300, oE + 10); oDis = Math.max(0, oDis - 15); maCount++; 
      } 
    }
    if (concept.id === 'jitakyoei' || concept.id === 'ubuntu') { 
      const b = actor === 'player' ? (passifMod.bonusHeal || 0) : 0; 
      pE = Math.min(300, pE + 10 + b); oE = Math.min(300, oE + 10); 
    }
    if (concept.id === 'attention' && actor === 'player') {
      pBlock = true;
      pE = Math.min(300, pE + 15);
    }
    if (concept.id === 'eudaimonia' && actor === 'player') { pE = Math.min(300, pE + 12 + (passifMod.bonusHeal || 0)); }
    if (concept.id === 'ataraxie') { if (actor === 'player') { pG = Math.max(0, pG - 30); oG = Math.min(300, oG + 10); } }
    if (concept.id === 'autonomie' && actor === 'player') { pE = Math.min(300, pE + 20); pG = Math.max(0, pG - 20); }
    if (concept.id === 'avicenne_med' && actor === 'player') { pE = Math.min(300, pE + 18); oG = Math.min(300, oG + 10); }
    if (concept.id === 'ambiguite') { if (actor === 'player') { pBlock = true; pG = Math.max(0, pG - 18); } }
    if (concept.id === 'mauvaise_foi' && actor === 'player' && prevState.opponentBlockNext) effectiveDrain *= 2;
    if (concept.id === 'kintsugi') { 
      if (actor === 'player') { const h = Math.floor(pG * 0.3); pE = Math.min(300, pE + h); } 
      else { const h = Math.floor(oG * 0.3); oE = Math.min(300, oE + h); } 
    }
    if (concept.id === 'philosophe' && actor === 'player') { 
      const bonus = pUsed.length * 5; pE = Math.min(300, pE + bonus); pUsed = [...pUsed, concept.id]; 
    }
    if (concept.id === 'hypertexte') { 
      const bonusDrain = 5 * hyper; 
      if (actor === 'player') oE -= bonusDrain; else pE -= bonusDrain; hyper++; 
    }
    if (concept.id === 'hegel') { if (actor === 'player') oE -= lastODrain; else pE -= lastPDrain; }
    if (concept.id === 'entropie') { if (actor === 'player') oDis = Math.min(100, oDis + 20); else pDis = Math.min(100, pDis + 20); }
    if (concept.id === 'wuwei') { if (actor === 'player') pBlock = true; else oBlock = true; }
    if (concept.id === 'intellect' && actor === 'player') { 
      const crossBonus = Array.from(pUsed).filter(id => CONCEPTS_SHARED[id] && selectedChar.crossIds?.includes(id)).length * 5; 
      oE -= crossBonus; 
    }
    if (concept.id === 'ishara' && actor === 'player') { oE -= Math.floor(lastODrain * 0.8); }
    if (concept.id === 'imperatif') { oE -= 15; }
    if (concept.id === 'hedone' && actor === 'player') { pE = Math.min(300, pE + 30 + (passifMod.bonusHeal || 0)); }
    if (concept.id === 'qanun' && actor === 'player') { }
    if (concept.id === 'enracinement' && actor === 'player') {
      pE = Math.min(300, pE + 20);
    }

    // Grip
    if (actor === 'player') pG = Math.max(0, Math.min(100, pG + concept.gripAdd));
    else oG = Math.max(0, Math.min(100, oG + concept.gripAdd));

    // Pénalité grip
    pE = applyGripPenalty(pE, pG);
    oE = applyGripPenalty(oE, oG);
    pE = Math.max(0, Math.min(300, pE));
    oE = Math.max(0, Math.min(300, oE));

    // Flow streak
    if (actor === 'opponent' && effectiveDrain > 0) oppNoHitStreak = 0;
    else if (actor === 'player') noHitStreak++;

    if (actor === 'opponent' && effectiveDrain > 0) {
      const passifJoueur = selectedChar?.passif ? selectedChar.passif() : {};
      if (passifJoueur.defensiveEnergyGain) {
        pE = Math.min(300, pE + passifJoueur.defensiveEnergyGain);
      }
    }

    // Mémoire adverse
    if (actor === 'opponent') opponentMemory = [concept.id, ...opponentMemory].slice(0, 3);
    
    // Log de réplique
    const riposteObj = RIPOSTES[concept.id];
      let logText;
      if (riposteObj) {
        const categories = Object.values(riposteObj).flat(); // toutes les phrases mélangées
        logText = categories[Math.floor(Math.random() * categories.length)];
      } else {
        logText = concept.desc || concept.label || concept.id;
      }
      addL(actor, logText, concept.color);
    newLog = newLog.slice(-30);

    // Damage label pour le popup
    const damLabel = effectiveDrain > 0 ? getDamageLabel(effectiveDrain) : null;
    const damSide = actor === 'player' ? 'right' : 'left';

    // Résolution du round
    let roundWinner = null;
    if (pE <= 0 || oE <= 0) {
      if (pE <= 0 && oE <= 0) { roundWinner = 'draw'; addL('system', '⚖️ DOUBLE KO — round rejoué', '#facc15'); }
      else if (oE <= 0) roundWinner = 'player';
      else roundWinner = 'opponent';
    }

    let newRoundsWon = [...prevState.roundsWon];
    if (roundWinner === 'player') newRoundsWon[0]++;
    if (roundWinner === 'opponent') newRoundsWon[1]++;

    let newPhase = prevState.phase;
    if (roundWinner !== null) {
      if (newRoundsWon[0] >= 2 || newRoundsWon[1] >= 2) newPhase = 'victory';
      else newPhase = 'roundEnd';
    }

    let dagueEclats = prevState.dagueEclats ?? 5;
    let dagueReason = null; // Pour le whisper Sophia
    let conceptRepeat = prevState.conceptRepeat || false;

    // Perte : même concept deux fois de suite
    if (actor === 'player' && prevState.lastPlayerConceptId === concept.id && !concept.texte) {
    dagueEclats = Math.max(0, dagueEclats - 1);
    dagueReason = 'repetition';
    conceptRepeat = true;
    } else if (actor === 'player') {
    conceptRepeat = false;
    }

    // Gain : synergie déclenchée
    if (synergyFound) {
      dagueEclats = Math.min(5, dagueEclats + 1);
      dagueReason = 'synergie';
    }

    // -- LOGIQUE COMBO --
    let comboState = prevState.comboState;
    let punchlinePlayedThisRound = prevState.punchlinePlayedThisRound || false;
    let comboBonus = 0;
    let playerComboStreak = prevState.playerComboStreak || 0;  // ★ déjà déclaré plus haut
          // ★ déjà déclaré plus haut

    if (actor === 'player') {
    const isPunchline = Boolean(concept.texte);

    if (isPunchline && comboState) {
        // COMBO RÉUSSI : punchline après concept
        comboBonus = Math.floor(effectiveDrain * 0.35);
        oE = Math.max(0, oE - comboBonus);
        dagueEclats = Math.min(5, dagueEclats + 1);
        dagueReason = 'combo';
        punchlinePlayedThisRound = true;
        playerComboStreak = (prevState.playerComboStreak || 0) + 1;  // ★ INCRÉMENTE
        comboState = null;
        addL('system', `⚔ COMBO — ${concept.texte?.substring(0, 40)}...`, concept.color || '#facc15');
        
    } else if (isPunchline && !comboState) {
        // Punchline sans concept ouvert : effet réduit
        effectiveDrain = Math.floor(effectiveDrain * 0.65);
        oE = Math.max(0, (oE + concept.drain || 0) - effectiveDrain);
        punchlinePlayedThisRound = true;
        playerComboStreak = 0;  // ★ RESET : punchline seule ne compte pas comme streak
        comboState = null;
        
    } else if (!isPunchline) {
        // Concept joué : ouvre le combo
        comboState = {
        conceptId: concept.id,
        conceptLabel: concept.label,
        conceptColor: concept.color,
        };
        playerComboStreak = 0;  // ★ RESET : jouer un concept casse le streak de punchlines
    }
    }
    
    return {
      newState: {
        ...prevState,
        playerEnergy: pE,
        opponentEnergy: oE,
        playerGrip: pG,
        opponentGrip: oG,
        playerDisorder: pDis,
        opponentDisorder: oDis,
        playerBlockNext: pBlock,
        opponentBlockNext: oBlock,
        playerUsedConcepts: pUsed,
        hypertexteStack: hyper,
        lastOpponentConceptDrain: lastODrain,
        lastPlayerConceptDrain: lastPDrain,
        maUsedCount: maCount,
        log: newLog,
        turn: prevState.turn + 1,
        phase: newPhase,
        roundsWon: newRoundsWon,
        activePlayer: newPhase === 'battle' && !roundWinner
          ? (actor === 'player' ? 'opponent' : 'player')
          : prevState.activePlayer,
        opponentMemory,
        playerNoHitStreak: noHitStreak,
        opponentNoHitStreak: oppNoHitStreak,
        lastPlayerConceptId: actor === 'player' ? concept.id : prevState.lastPlayerConceptId,
        lastOpponentConceptId: actor === 'opponent' ? concept.id : prevState.lastOpponentConceptId,
        analysisTimer: false,
        dagueEclats,
        comboState: actor === 'player' ? comboState : prevState.comboState,
        punchlinePlayedThisRound,
        subThemeRevealed: prevState.subThemeRevealed,
        themeBonusPunchlineUsed: prevState.themeBonusPunchlineUsed,
        conceptRepeat,
        playerComboStreak,
      },
      damageLabel: damLabel,
      damageSide: damSide,
      effectiveDrain,
      roundWinner,
      newPhase,
      synergyFound,
      actor,
      dagueReason,
    };
  }, [selectedChar, progression]);

  const playMove = useCallback((concept) => {
    if (turnPhase !== 'IDLE') return;
    if (state.phase !== 'battle' || state.activePlayer !== 'player') return;

    if (musicTrack !== 'battle' || !music.isPlaying) {
      music.playBattle();
      setMusicTrack('battle');
    }

    if (concept.id === themeBonusPunchline?.id) {
      setState(prev => ({ ...prev, themeBonusPunchlineUsed: true }));
    }  

    const result = resolveMove('player', concept, state);
    
    if (!result) {
      // Coup invalide — on passe le tour
      setState(prev => {
        const newLog = [...prev.log, {
          id: Date.now(), 
          actor: 'system',
          text: "⏭️ Pas assez d'énergie — tour passé.",
          color: '#ef4444', 
          ts: prev.turn + 1
        }];
        let nextState = {
          ...prev,
          activePlayer: 'opponent',
          turn: prev.turn + 1,
          log: newLog.slice(-30),
          analysisTimer: false
        };

        // Vérification : le joueur peut-il encore jouer ?
        const playerCanPlay = getAffordableConcepts(
          prev.playerEnergy,
          prev.maUsedCount,
          playerDeck,
          true,
          selectedChar
        ).length > 0;

        // ═══ FIN DE ROUND : JOUEUR BLOQUÉ ═══
        if (!playerCanPlay) {

          // Cas 1 : Énergie à 0 → KO technique classique
          if (prev.playerEnergy <= 0) {
            const koLog = [...newLog, {
              id: Date.now() + 1, 
              actor: 'system',
              text: '💀 Épuisement total — vous perdez le round.',
              color: '#ef4444', 
              ts: prev.turn + 2
            }];
            const newRoundsWon = [...prev.roundsWon];
            newRoundsWon[1]++;
            const matchWon = newRoundsWon[1] >= 2;
            if (matchWon) {
              setProgression(prevProg => addXP(prevProg, 15));
            }
            return {
              ...prev,
              phase: matchWon ? 'victory' : 'roundEnd',
              roundsWon: newRoundsWon,
              log: koLog.slice(-30),
              activePlayer: null,
              turn: prev.turn + 2,
              analysisTimer: false,
              playerBlockNext: false,
              opponentBlockNext: false,
            };
          }

          // Cas 2 : Énergie > 0 mais aucun coup disponible → forfait philosophique
          const opponentCanPlay = getAffordableConcepts(
            prev.opponentEnergy,
            prev.maUsedCount,
            prev.aiDeck || DECK_ADVERSAIRE_BASE,
            false,
            null
          ).length > 0;

          // L'IA peut jouer OU l'IA a plus d'énergie → elle gagne
          if (opponentCanPlay || prev.opponentEnergy >= prev.playerEnergy) {
            const forfeitLog = [...newLog, {
              id: Date.now() + 1,
              actor: 'system',
              text: prev.opponentEnergy > prev.playerEnergy 
                ? '⚖️ Plus aucun coup disponible — l\'adversaire a l\'avantage, round perdu.'
                : '⚖️ Impasse rhétorique — forfait. Votre élan s\'est éteint.',
              color: '#f59e0b',
              ts: prev.turn + 2
            }];
            const newRoundsWon = [...prev.roundsWon];
            newRoundsWon[1]++;
            const matchWon = newRoundsWon[1] >= 2;
            if (matchWon) {
              setProgression(prevProg => addXP(prevProg, 15));
            }
            return {
              ...prev,
              phase: matchWon ? 'victory' : 'roundEnd',
              roundsWon: newRoundsWon,
              log: forfeitLog.slice(-30),
              activePlayer: null,
              turn: prev.turn + 2,
              analysisTimer: false,
              playerBlockNext: false,
              opponentBlockNext: false,
            };
          }

          // Cas 3 : Le joueur est bloqué mais a plus d'énergie que l'IA
          // → on laisse checkAndResolveDeadlock trancher
        }

        nextState = checkPrecepteurSwitch(nextState);
        return checkAndResolveDeadlock(nextState);
      });
      
      setTimeout(() => { setTurnPhase('IDLE'); }, 300);
      return;
    }

    // Appliquer le résultat
    const ns = {
      ...result,
      concept,
      newState: checkPrecepteurSwitch(result.newState)
    };

    setQueuedResult(ns);
    setTurnPhase('PLAYER_QUEUED');

    // ─── KNOWLEDGE : déblocage de connaissance ─────────────────────
    const knowledgeId = concept.id;
    //const kType = concept.texte ? 'punchline' : 'concept';

    if (!hasSeen(knowledgeId) && (KNOWLEDGE_BASE[knowledgeId] || KNOWLEDGE_PUNCHLINES[knowledgeId])) {
      // Marquer comme "non lu" → fera clignoter le bouton 📖
      setUnreadKnowledge(prev => {
        if (prev.includes(knowledgeId)) return prev;
        const updated = [...prev, knowledgeId];
        try { localStorage.setItem('jvb_unread_knowledge', JSON.stringify(updated)); } catch {}
        return updated;
      });
      // Marquer comme vu dans le tracker (le bouton apparaîtra)
      markSeen(knowledgeId);
    }
  }, [turnPhase, state, musicTrack, themeBonusPunchline?.id, resolveMove, checkPrecepteurSwitch, hasSeen, playerDeck, selectedChar, checkAndResolveDeadlock, markSeen]);

  const generatePunchlineOptions = (concept, theme, char, atouts) => {
  const options = [];
  const usedIds = new Set();
  const conceptNature = concept.drain > 0 ? 'attaque' : 'defense';
  const charId  = char.id;
  const themeId = theme?.id;
 
  const add = (p) => {
    if (!p || usedIds.has(p.id)) return false;
    usedIds.add(p.id);
    options.push(p);
    return true;
  };
 
  // ══════════════════════════════════════════════════════════════════════════
  // SLOT 1 — Punchline de thème exacte (themeBonus: true)
  // Clé : charId_themeId — une seule, la plus précieuse
  // ══════════════════════════════════════════════════════════════════════════
  if (themeId) {
    const themeKey = `${charId}_${themeId}`;
    const exact = THEME_BONUS_WHEEL[themeKey];
    if (exact) {
      add({ ...exact, themeBonus: true });
    }
  }
 
  // ══════════════════════════════════════════════════════════════════════════
  // SLOTS 2-3 — Punchlines Atouts du personnage (nature correspondante)
  // Source : ATOUTS[charId], filtrées par category === conceptNature
  // Mélangées pour éviter la répétition entre deux tours
  // ══════════════════════════════════════════════════════════════════════════
  const charAtouts = atouts[charId] || [];
  const matchingAtouts = charAtouts
    .filter(p => p.category === conceptNature)
    .map(p => ({
      id:           p.id,
      texte:        p.texte,
      drain:        p.drain   ?? 0,
      color:        p.color   ?? char.avatarColor,
      gripAdd:      p.gripAdd ?? 0,
      contextLabel: conceptNature === 'attaque' ? 'ATTAQUE' : 'DÉFENSE',
      themeBonus:   false,
      comboBonus:   35,
    }))
    .sort(() => Math.random() - 0.5); // mélange à chaque appel
 
  // Prendre jusqu'à 2 atouts matchants
  for (const p of matchingAtouts) {
    if (options.length >= 3) break;
    add(p);
  }
 
  // ══════════════════════════════════════════════════════════════════════════
  // SLOTS 4-5 — THEME_BONUS_WHEEL hors-thème du même personnage
  //
  // Logique : piocher dans les punchlines des autres thèmes du personnage,
  // triées par proximité philosophique avec le concept joué.
  // Labelisées avec le nom du personnage (pas 'THÈME') pour être honnêtes.
  // Filtrées par nature pour rester cohérentes avec le concept.
  // ══════════════════════════════════════════════════════════════════════════
  const proximityOrder = CONCEPT_THEME_PROXIMITY[concept.id] ?? [1, 2, 3, 4, 5, 6, 7];
 
  // Construire la liste complète des punchlines wheel du personnage, triées
  const wheelEntriesForChar = [];
 
  for (const tid of proximityOrder) {
    if (tid === themeId) continue; // on a déjà le thème exact en slot 1
    const key = `${charId}_${tid}`;
    const entry = THEME_BONUS_WHEEL[key];
    if (entry) wheelEntriesForChar.push(entry);
  }
 
  // Ajouter aussi la punchline non-thématique du perso (_nb) en priorité basse
  const nbKey = `${charId}_nb`;
  const nbEntry = THEME_BONUS_WHEEL[nbKey];
  if (nbEntry) wheelEntriesForChar.push(nbEntry);
 
  for (const raw of wheelEntriesForChar) {
    if (options.length >= 5) break;
    const nature = getPunchlineNature(raw);
    // Accepter : même nature que le concept OU neutre
    if (nature !== conceptNature && nature !== 'neutre') continue;
 
    add({
      ...raw,
      themeBonus:   false,                      // ce n'est plus le thème exact
      contextLabel: char.nom?.toUpperCase() ?? 'FRAPPE', // nom du perso comme label
      comboBonus:   30,
    });
  }
 
  // ══════════════════════════════════════════════════════════════════════════
  // SLOT 6 (et complément si < 5) — Atouts de l'autre nature + génériques
  //
  // Si le concept est 'attaque' et qu'on n'a pas encore 5 options,
  // on pioche dans les atouts 'defense' du personnage (riposte inattendue)
  // puis dans les génériques de qualité.
  // ══════════════════════════════════════════════════════════════════════════
  if (options.length < 5) {
    // Atouts de l'autre nature (riposte philosophique inattendue)
    const otherNature = conceptNature === 'attaque' ? 'defense' : 'attaque';
    const otherAtouts = charAtouts
      .filter(p => p.category === otherNature)
      .sort(() => Math.random() - 0.5)
      .map(p => ({
        id:           p.id,
        texte:        p.texte,
        drain:        p.drain   ?? 0,
        color:        p.color   ?? char.avatarColor,
        gripAdd:      p.gripAdd ?? 0,
        contextLabel: 'RIPOSTE',
        themeBonus:   false,
        comboBonus:   28,
      }));
 
    for (const p of otherAtouts) {
      if (options.length >= 6) break;
      add(p);
    }
  }
 
  // Génériques en dernier recours
  if (options.length < 5) {
    const generics = GENERIC_WHEEL_LINES[conceptNature] || GENERIC_WHEEL_LINES.attaque;
    const shuffledGenerics = [...generics].sort(() => Math.random() - 0.5);
 
    for (const g of shuffledGenerics) {
      if (options.length >= 6) break;
      const id = `generic_${concept.id}_${options.length}`;
      if (usedIds.has(id)) continue;
      usedIds.add(id);
      options.push({
        id,
        texte:        g.texte,
        drain:        Math.max(0, Math.floor((concept.drain || 0) * 0.5) + g.drain),
        color:        concept.color ?? '#94a3b8',
        gripAdd:      g.gripAdd,
        contextLabel: 'RIPOSTE',
        themeBonus:   false,
        comboBonus:   22,
      });
    }
  }
 
  // Retourner 5-6 options (jamais slice à 3)
  return options.slice(0, 6);
}; 

  const handleConceptSelect = (concept, theme) => {
    if (state.phase !== 'battle' || state.activePlayer !== 'player') return;
    
    const options = generatePunchlineOptions(
      concept,
      theme,       // le thème courant du duel
      selectedChar,
      ATOUTS,
      THEME_BONUS_WHEEL,      
    );
    
    setPendingConcept(concept);
    setWheelOptions(options);
  };

  const handlePunchlineChosen = (punchline) => {
    if (!pendingConcept) return;
    if (state.phase !== 'battle' || state.activePlayer !== 'player') return;
    
    const move = {
      ...pendingConcept,
      drain:   pendingConcept.drain + (punchline.drain || 0),
      gripAdd: pendingConcept.gripAdd + (punchline.gripAdd || 0),
      comboMultiplier: punchline.themeBonus ? 1.4 : 1.0,
      punchlineTexte:  punchline.texte,
      punchlineColor:  punchline.color,
    };
    
    setPendingConcept(null);
    setWheelOptions([]);
    playMove(move);

    
    push({
      text:       `« ${punchline.texte} »`,
      actor:      'joueur',
      actorLabel: selectedChar.nom,
      color:      selectedChar.avatarColor,
      side:       'left',
      duration:   12000,
    });
  
};   

  const handleWheelSkip = () => {
    if (!pendingConcept) return;
    if (state.phase !== 'battle' || state.activePlayer !== 'player') return;
    const concept = pendingConcept;
    setPendingConcept(null);
    setWheelOptions([]);
    playMove(concept);   // coup nu, pas de bonus punchline
  };  

  const handleTitleStart = useCallback(() => {
    if (isSuperAdmin) {
      clearMatchState();
      setState(makeInitState());
      setTurnPhase('IDLE');
      setQueuedResult(null);
      setScreen('introduction');
      music.playCinematicOpening(); // ← cinématique pendant l'intro
      setMusicTrack('cinematicOpening');
      return;
    }
  
    if (!isAuthenticated || !hasEnoughPesas) {
      setScreen('blocked');
      return;
    }
  
    clearMatchState();
    setState(makeInitState());
    setTurnPhase('IDLE');
    setQueuedResult(null);
    setScreen('introduction');
    music.playCinematicOpening(); // ← cinématique pendant l'intro
    setMusicTrack('cinematicOpening');
  }, [hasEnoughPesas, isAuthenticated, isSuperAdmin, makeInitState]);

  const handleIntroBegin = useCallback(() => {
    setScreen('battle');
    music.playBattle();      // ← seulement ici qu'on lance battle
    setMusicTrack('battle');
  }, []);

  const continueMatch = useCallback(() => {
    const saved = loadMatchState();
    if (!saved) return;
    setState(saved);
    setScreen('battle');
    // On reprend la musique de combat (pas la cinématique)
    music.playBattle();
    setMusicTrack('battle');
  }, []);  
  
  const executeOpponentTurn = useCallback((currentState) => {
    // Sécurité : ne pas exécuter si le jeu n'est pas en phase battle
    if (!currentState || currentState.phase !== 'battle') return;
    if (currentState.activePlayer !== 'opponent') return;

    // Vérifier si l'IA peut jouer
    const affordableOpponent = getAffordableConcepts(
      currentState.opponentEnergy,
      currentState.maUsedCount,
      currentState.aiDeck || DECK_ADVERSAIRE_BASE,
      false,
      null
    );

    // Si l'IA n'a aucun coup abordable
    if (affordableOpponent.length === 0) {
      setState(prev => {
        // Sécurité : ne pas appliquer deux fois
        if (prev.phase !== 'battle' || prev.activePlayer !== 'opponent') return prev;

        const newLog = [...prev.log, {
          id: Date.now(),
          actor: 'system',
          text: currentState.opponentEnergy <= 0
            ? '💀 Le Précepteur est à court d\'énergie — vous gagnez le round !'
            : '⏭️ Le Précepteur passe son tour (plus de coups disponibles).',
          color: currentState.opponentEnergy <= 0 ? '#22d3ee' : '#94a3b8',
          ts: prev.turn + 1
        }];

        // Si l'IA a 0 d'énergie, le joueur gagne le round
        if (currentState.opponentEnergy <= 0) {
          const newRoundsWon = [...prev.roundsWon];
          newRoundsWon[0]++;
          const matchWon = newRoundsWon[0] >= 2;

          if (matchWon) {
            setProgression(prevProg => addXP(prevProg, 100));
          } else {
            setProgression(prevProg => addXP(prevProg, 25));
          }

          return {
            ...prev,
            phase: matchWon ? 'victory' : 'roundEnd',
            roundsWon: newRoundsWon,
            log: newLog.slice(-30),
            activePlayer: null,
            turn: prev.turn + 1,
            analysisTimer: false,
            opponentBlockNext: false,
            playerBlockNext: false,
          };
        }

        // Sinon, juste passer le tour au joueur (deadlock ou plus de coups)
        const hasPlayerMoves = getAffordableConcepts(
          currentState.playerEnergy,
          currentState.maUsedCount,
          playerDeck,
          true,
          selectedChar
        ).length > 0;

        if (!hasPlayerMoves && currentState.playerEnergy <= 0) {
          const newRoundsWon = [...prev.roundsWon];
          if (currentState.opponentEnergy > currentState.playerEnergy) {
            newRoundsWon[1]++;
          } else if (currentState.playerEnergy > currentState.opponentEnergy) {
            newRoundsWon[0]++;
          }
          const matchWon = newRoundsWon[0] >= 2 || newRoundsWon[1] >= 2;
          const finalLog = [...newLog, {
            id: Date.now() + 1,
            actor: 'system',
            text: currentState.opponentEnergy > currentState.playerEnergy
              ? '⚖️ L\'adversaire survit à l\'épuisement — il gagne le round.'
              : '⚖️ Double épuisement — round terminé.',
            color: '#facc15',
            ts: prev.turn + 2
          }];

          if (newRoundsWon[0] > prev.roundsWon[0]) {
            setProgression(prevProg => addXP(prevProg, matchWon ? 100 : 25));
          } else if (matchWon) {
            setProgression(prevProg => addXP(prevProg, 15));
          }

          return {
            ...prev,
            phase: matchWon ? 'victory' : 'roundEnd',
            roundsWon: newRoundsWon,
            log: finalLog.slice(-30),
            activePlayer: null,
            turn: prev.turn + 2,
            analysisTimer: false,
            opponentBlockNext: false,
            playerBlockNext: false,
          };
        }

        // Cas 2 : Aucun des deux ne peut jouer (deadlock par épuisement de coups)
        if (!hasPlayerMoves && currentState.playerEnergy > 0) {
          const newRoundsWon = [...prev.roundsWon];
          if (currentState.playerEnergy > currentState.opponentEnergy) {
            newRoundsWon[0]++;
          } else if (currentState.opponentEnergy > currentState.playerEnergy) {
            newRoundsWon[1]++;
          }
          // Si égalité → draw, pas de changement
          const hasWinner = newRoundsWon[0] > prev.roundsWon[0] || newRoundsWon[1] > prev.roundsWon[1];
          const matchWon = newRoundsWon[0] >= 2 || newRoundsWon[1] >= 2;
          const finalLog = [...newLog, {
            id: Date.now() + 1,
            actor: 'system',
            text: hasWinner
              ? '⚖️ Impasse rhétorique — celui qui a le plus d\'énergie l\'emporte.'
              : '⚖️ Équilibre parfait — round nul, à refaire.',
            color: '#facc15',
            ts: prev.turn + 2
          }];

          if (newRoundsWon[0] > prev.roundsWon[0]) {
            setProgression(prevProg => addXP(prevProg, matchWon ? 100 : 25));
          } else if (newRoundsWon[1] > prev.roundsWon[1] && matchWon) {
            setProgression(prevProg => addXP(prevProg, 15));
          }

          return {
            ...prev,
            phase: matchWon ? 'victory' : 'roundEnd',
            roundsWon: newRoundsWon,
            log: finalLog.slice(-30),
            activePlayer: null,
            turn: prev.turn + 2,
            analysisTimer: false,
            opponentBlockNext: false,
            playerBlockNext: false,
          };
        }

        // Cas 3 : Le joueur peut encore jouer → on lui passe le tour
        return {
          ...prev,
          activePlayer: 'player',
          turn: prev.turn + 1,
          log: newLog.slice(-30),
          analysisTimer: false,
        };
      });

      setOpponentScanning(false);
      startAnalysisTimer(5);
      return; // ← CRITICAL : on sort, pas de suite
    }

    // Si l'IA peut jouer, on continue normalement
    setOpponentScanning(true);
    sounds.thinking?.();

    const thinkDelay = 800 + Math.floor(Math.random() * 400);

    const thinkTimer = setTimeout(() => {
      setOpponentScanning(false);

      // Re-vérifier que la phase est toujours valide avant de jouer
      setState(prev => {
        if (prev.phase !== 'battle' || prev.activePlayer !== 'opponent') return prev;
        return prev;
      });

      const chosenConcept = computePrecepteurMove(currentState);

      if (!chosenConcept) {
        setState(prev => {
          if (prev.phase !== 'battle' || prev.activePlayer !== 'opponent') return prev;
          const newLog = [...prev.log, {
            id: Date.now(), actor: 'system',
            text: '⏭️ Le Précepteur passe son tour.',
            color: '#ef4444', ts: prev.turn + 1
          }];
          return {
            ...prev,
            activePlayer: 'player',
            turn: prev.turn + 1,
            log: newLog.slice(-30),
            analysisTimer: false,
          };
        });
        startAnalysisTimer(5);
        return;
      }

      const oppResult = resolveMove('opponent', { ...chosenConcept, ownTradition: true }, currentState);

      // Annonce du concept adverse
      const oppConcept = DECK_ADVERSAIRE_BASE.find(c => c.id === chosenConcept.id);
      if (oppConcept) {
          setConceptAnnounce({
              text: oppConcept.label || oppConcept.id,
              color: oppConcept.color || '#ef4444',
          });
      }
      sounds.conceptCall?.();

      setTimeout(() => {
        setConceptAnnounce(null);
        setState(prev => {
          if (prev.phase !== 'battle') return prev;
          return { ...prev, oppAtk: true };
        });
        setSlashEffect({ side: 'left', actor: 'opponent' });
        sounds.slash?.();

        setTimeout(() => {
          setSlashEffect(null);
          sounds.hit?.();

          setState(prev => {
          if (prev.phase !== 'battle') return prev;
            const newState = {
                ...prev,
                ...oppResult.newState,
                lastDamageLabel: oppResult.damageLabel,
                lastDamageSide: oppResult.damageSide,
                playerHit: oppResult.effectiveDrain > 0,
                oppAtk: true,
            };

              return checkPrecepteurSwitch(newState);
          });

          const opponentConceptId = oppResult.newState.lastOpponentConceptId;

          if (opponentConceptId && currentState.lastPlayerConceptId) {
            const reactionText = getPrecepteurReaction(
              currentState.lastPlayerConceptId,
              opponentConceptId,
              currentState.currentAIChar?.id,
              currentState.theme?.id,
              CONCEPTS,
              currentState,
            );
            push({
              text:       reactionText,
              actor:      'precepteur',
              actorLabel: currentState.currentAIChar?.nom ?? 'PRÉCEPTEUR',
              color:      currentState.currentAIChar?.color ?? '#94a3b8',
              duration:   30000,
              side:       'right',
            });
          }

          if (oppResult.effectiveDrain > 0) {
            setScreenShake(true);
            setHitFlash({ side: 'right', color: '#ef4444' });
          }

          if (oppResult.synergyFound) {
            setActiveSynergy({ ...oppResult.synergyFound, side: 'right' });
            sounds.synergy?.();
            const synLines = SOPHIA_INTERVENTIONS.synergie;
            push({
              text:     synLines[Math.floor(Math.random() * synLines.length)]
                        + ` [${oppResult.synergyFound.name}]`,
              actor:    'synergie',
              color:    oppResult.synergyFound.color,
              duration: 2500,
              side: 'center',
            });
          }

          if (oppResult.roundWinner) {
            if (oppResult.roundWinner === 'player') {
              setProgression(prevProg => addXP(prevProg, oppResult.newPhase === 'victory' ? 100 : 25));
            } else if (oppResult.roundWinner === 'opponent' && oppResult.newPhase === 'victory') {
              setProgression(prevProg => addXP(prevProg, 15));
            }

            if (oppResult.newPhase !== 'victory') {
              if (roundEndTimeoutRef.current) clearTimeout(roundEndTimeoutRef.current);
              roundEndTimeoutRef.current = setTimeout(() => {
                setState(prev => {
                  if (prev.phase === 'roundEnd') {
                    const nextRound = oppResult.roundWinner === 'draw' ? prev.round : prev.round + 1;
                    return {
                      ...prev,
                      phase: 'intro',
                      playerEnergy: 300, opponentEnergy: 300,
                      playerGrip: 0, opponentGrip: 0,
                      playerDisorder: 0, opponentDisorder: 0,
                      playerBlockNext: false, opponentBlockNext: false,
                      playerUsedConcepts: [], hypertexteStack: 0, maUsedCount: 0,
                      lastPlayerConceptDrain: 0, lastOpponentConceptDrain: 0,
                      round: Math.min(nextRound, 3), activePlayer: 'player',
                      opponentMemory: [], playerNoHitStreak: 0,
                      lastPlayerConceptId: null, lastOpponentConceptId: null,
                      analysisTimer: false,
                    };
                  }
                  return prev;
                });
              }, 1600);
            }
          }

          setTimeout(() => {
            setState(prev => ({
              ...prev,
              playerHit: false, oppHit: false,
              playerAtk: false, oppAtk: false,
              lastDamageLabel: null,
            }));
            setScreenShake(false);
            setHitFlash(null);
            setSlashEffect(null);
            setConceptAnnounce(null);
            startAnalysisTimer(5);
          }, 400);

        }, 700);
      }, 400);
    }, thinkDelay);

    // Nettoyer le timer si le composant est démonté
    return () => clearTimeout(thinkTimer);
  }, [startAnalysisTimer, playerDeck, selectedChar, computePrecepteurMove, checkPrecepteurSwitch, push]);

  const resetGame = useCallback(() => {
    clearAll();
    setSophiaInterruption(null);
    setSubThemeReveal(null);
    setActiveSynergy(null);
    setAiCharSwitch(null);
    
    clearMatchState();
    if (roundEndTimeoutRef.current) clearTimeout(roundEndTimeoutRef.current);
    if (aiThinkRef.current) clearTimeout(aiThinkRef.current);
    if (analysisTimerRef.current) clearTimeout(analysisTimerRef.current);
    setTurnPhase('IDLE');
    setQueuedResult(null);
    setScreenShake(false);
    setHitFlash(null);
    setSlashEffect(null);
    setConceptAnnounce(null);
    setOpponentScanning(false);
    setSophiaInterruption(null);
    setSubThemeReveal(null);
    setActiveSynergy(null);
    setPendingConcept(null);
    setWheelOptions(null);
    setState(makeInitState());

    setScreen('battle');
    music.playMenu();
    setMusicTrack('battle'); 
    
  }, [makeInitState, clearAll]);

  useEffect(() => {
    if (screen === 'battle' && !['victory', 'intro'].includes(state.phase)) {
      saveMatchState(state);
    } else if (state.phase === 'victory') {
      clearMatchState();
    }
  }, [state, screen]); 

  useEffect(() => {
    if (state.playerNoHitStreak === 10 && state.phase === 'battle') {
      const collected = loadFragments();
      const available = WISDOM_FRAGMENTS.filter(f =>
        f.charId === selectedChar.id && !collected.includes(f.id)
      );
      if (available.length > 0) {
        const fragment = available[Math.floor(Math.random() * available.length)];
        saveFragment(fragment.id);
        setState(p => ({ ...p, showWisdomFlash: fragment }));
      }
    }
  }, [state.playerNoHitStreak]);

  useEffect(() => {
    const prev = prevStreakRef.current;
    const cur = state.playerNoHitStreak;
    if (FLOW_MILESTONES.includes(cur) && cur > prev) {
      sounds.synergy();
      setState(p => ({ ...p, showFlowModal: true }));
    }
    prevStreakRef.current = cur;
  }, [state.playerNoHitStreak]);
  
  useEffect(() => {
    if (state.phase !== 'victory' || !winner) return;

    clearAll();
    setSophiaInterruption(null);
    setSubThemeReveal(null);
    setActiveSynergy(null);

    // 1. Mise à jour des statistiques globales + déblocages
    setGlobalStats(prev => {
      const synCount = state.log.filter(l => l.text?.includes('SYNERGIE')).length;

      const updated = {
        ...prev,
        totalDrainDealt: prev.totalDrainDealt + (300 - state.opponentEnergy) * 3,
        matchesWon: winner === 'player' ? prev.matchesWon + 1 : prev.matchesWon,
        synergiesActivated: prev.synergiesActivated + synCount,
      };

      const newUnlocks = checkUnlocks(updated);

      if (newUnlocks.length > 0) {
        updated.unlockedChars = [...(prev.unlockedChars || []), ...newUnlocks];
        
        const firstNew = TOUS_PERSONNAGES.find(c => c.id === newUnlocks[0]);
        if (firstNew) {
          setTimeout(() => setNewUnlockAnim(firstNew), 2500);
        }
      }

      saveGlobalStats(updated);
      return updated;
    });

    // 2. Sauvegarde de l'historique du match
    saveMatchHistory({
      winner,
      player: selectedChar.nom,
      opponent: 'Précepteur',
      roundsWon: state.roundsWon,
      niveau: progression.niveau,
      date: Date.now(),
    });

    clearMatchState();
    setHasSavedMatch(false);

  }, [state.phase, winner, state.opponentEnergy, state.log, state.roundsWon, selectedChar.nom, progression.niveau, clearAll]);
  
  useEffect(() => {
  if (state.phase === 'roundEnd') {
    clearAll();
    setSophiaInterruption(null);
    setSubThemeReveal(null);
    setActiveSynergy(null);

    if (roundEndTimeoutRef.current) clearTimeout(roundEndTimeoutRef.current);
    roundEndTimeoutRef.current = setTimeout(() => {

      // ── VIDER TOUTES LES MODALES AVANT LE PROCHAIN ROUND ──
      setSophiaInterruption(null);
      setSubThemeReveal(null);
      setActiveSynergy(null);
      setAiCharSwitch(null);
      setConceptAnnounce(null);
      setPendingConcept(null);
      setWheelOptions(null);
      clearAll();  
      

      setState(prev => {
        if (prev.phase !== 'roundEnd') return prev;
        const nextRound = prev.round + 1;
        if (nextRound <= 3) {
          setArenaIndex(prevIdx => (prevIdx + 1) % ARENAS.length);
        }
        return {
          ...prev,
          phase: 'intro',
          playerEnergy: 300, opponentEnergy: 300,
          playerGrip: 0, opponentGrip: 0,
          playerDisorder: 0, opponentDisorder: 0,
          playerBlockNext: false, opponentBlockNext: false,
          playerUsedConcepts: [], hypertexteStack: 0, maUsedCount: 0,
          lastPlayerConceptDrain: 0, lastOpponentConceptDrain: 0,
          round: Math.min(nextRound, 3), activePlayer: 'player',
          opponentMemory: [], playerNoHitStreak: 0,
          lastPlayerConceptId: null, analysisTimer: false,
          punchlinePlayedThisRound: false, comboState: null,
        };
      });
    }, 1800);
  }
}, [clearAll, state.phase, state.round]);

  useEffect(()=>()=>{ if(roundEndTimeoutRef.current)clearTimeout(roundEndTimeoutRef.current); if(aiThinkRef.current)clearTimeout(aiThinkRef.current); if(analysisTimerRef.current)clearTimeout(analysisTimerRef.current); },[]);
  
  useEffect(() => {
    if (turnPhase === 'IDLE') return;
    if (!['PLAYER_QUEUED', 'PLAYER_ANIMATING', 'PLAYER_IMPACT'].includes(turnPhase)) return;

    let timer1, timer2, timer3;

    const applyPlayerImpact = (result) => {
      if (!result) return;

      setState(prev => ({
        ...result.newState,
        lastDamageLabel: result.damageLabel,
        lastDamageSide: result.damageSide,
        oppHit: result.effectiveDrain > 0,
        playerAtk: true,
      }));

      if (result.effectiveDrain > 0) {
        setScreenShake(true);
        setHitFlash({ side: 'right', color: currentAIChar?.color || '#ef4444' });
      }

      if (result.synergyFound) {
        setActiveSynergy({ ...result.synergyFound, side: 'left' });
        const synLines = SOPHIA_INTERVENTIONS.synergie;
          push({
            text:     synLines[Math.floor(Math.random() * synLines.length)]
                      + ` [${result.synergyFound.name}]`,
            actor:    'synergie',
            color:    result.synergyFound.color,
            duration: 2000,
            side: 'center',
          });
      }
    };

    const cleanupEffects = () => {
      setState(prev => ({
        ...prev,
        playerHit: false, oppHit: false,
        playerAtk: false, oppAtk: false,
        lastDamageLabel: null,
      }));
      setScreenShake(false);
      setHitFlash(null);
      setSlashEffect(null);
      setConceptAnnounce(null);
      

    };

    switch (turnPhase) {
      case 'PLAYER_QUEUED':
        if (!queuedResult) { setTurnPhase('IDLE'); break; }

        const playerConcept = playerDeck.find(c => c.id === queuedResult.concept?.id);
        if (playerConcept) {
          setConceptAnnounce({
            text: playerConcept.label || playerConcept.id,
            color: playerConcept.color || '#facc15',
          });
        }
        sounds.conceptCall?.();

        timer1 = setTimeout(() => {
          setConceptAnnounce(null);
          setTurnPhase('PLAYER_ANIMATING');
        }, 500);
        break;

      case 'PLAYER_ANIMATING':
        setState(prev => ({ ...prev, playerAtk: true }));
        setSlashEffect({ side: 'right', actor: 'player' });
        sounds.slash?.();

        timer1 = setTimeout(() => {
          setSlashEffect(null);
          setTurnPhase('PLAYER_IMPACT');
        }, 700);
        break;

      case 'PLAYER_IMPACT':
        if (!queuedResult) { setTurnPhase('IDLE'); break; }
        

        sounds.hit?.();
        applyPlayerImpact(queuedResult);

        timer1 = setTimeout(() => { cleanupEffects(); }, 400);
        timer2 = setTimeout(() => {
          setTurnPhase('IDLE');
        }, 600);
        break;       
        
        default:
        break;
    }

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [turnPhase]);

  useEffect(() => {
  if (
    state.phase === 'battle' &&
    state.activePlayer === 'opponent' &&
    turnPhase === 'IDLE'
  ) {
    // Petit délai pour laisser le cleanup se faire
    const delay = 300;
    const timer = setTimeout(() => {
      executeOpponentTurn(state);
    }, delay);
    return () => clearTimeout(timer);
  }
}, [state.activePlayer, state.phase, turnPhase, state.turn]);

  useEffect(() => {
  prevDagueRef.current = state.dagueEclats;
}, [state.dagueEclats]);

  useEffect(() => {
  if (state.dagueEclats === undefined) return;
  if (state.phase !== 'battle') return;
  const eclats = state.dagueEclats;
  

  if (eclats === 2) {
    const lines = SOPHIA_INTERVENTIONS.dague_3;
    const text = lines[Math.floor(Math.random() * lines.length)];
    setSophiaInterruption({ text, urgency: 'high' });
    // ── AJOUTER ──
    push({ text, actor: 'sophia', color: '#e9d5ff', duration: 4500, side: 'center', });
    
  } else if (eclats === 1) {
    const lines = SOPHIA_INTERVENTIONS.dague_4;
    const text = lines[Math.floor(Math.random() * lines.length)];
    setSophiaInterruption({ text, urgency: 'high' });
    // ── AJOUTER ──
    push({ text, actor: 'sophia', color: '#e9d5ff', duration: 4000, side: 'center',  });
  }
}, [push, state.dagueEclats, state.phase]);

  useEffect(() => {
  if (state.phase !== 'roundEnd') return;

  setState(prev => {
    if (state.phase !== 'roundEnd') return;
    if (prev.punchlinePlayedThisRound) return prev;
    const newEclats = Math.max(0, (prev.dagueEclats ?? 5) - 1);
    const lines = SOPHIA_INTERVENTIONS.round_end_no_punchline;
    setTimeout(() => {
      const text = lines[Math.floor(Math.random() * lines.length)];
      setSophiaInterruption({ text, urgency: 'low' });
      push({ text, actor: 'sophia', color: '#e9d5ff', duration: 4000, side: 'center', });
    }, 400);
    return { ...prev, dagueEclats: newEclats };
  });
}, [push, state.phase]);

  useEffect(() => {
  if (state.round !== 2 || state.phase !== 'intro' || state.subThemeRevealed) return;
  const subTheme = THEME_SUBTHEMES[state.theme?.id];
  if (!subTheme) return;
  setState(prev => ({ ...prev, subThemeRevealed: true }));
  // Légère temporisation pour laisser l'intro s'afficher d'abord
  setTimeout(() => {
    setSubThemeReveal(subTheme);
    push({
      text:     subTheme.sophiaLine,
      actor:    'sophia',
      color:    '#e9d5ff',
      duration: 4000,
    });
  }, 1200);
}, [state.round, state.phase, state.subThemeRevealed, state.theme?.id, push]);

  useEffect(() => {
  if (state.dagueEclats !== 0 || state.phase !== 'battle') return;
  setState(prev => {
    if (prev.phase !== 'battle') return prev;
    const newRoundsWon = [...prev.roundsWon];
    newRoundsWon[1] = 2; // Force la victoire adverse (défaite narrative)
    return {
      ...prev,
      phase: 'victory',
      roundsWon: newRoundsWon,
      log: [...prev.log, {
        id: Date.now(),
        actor: 'system',
        text: '🌑 Le fragment s\'est dissous dans le silence. La Dague s\'est brisée.',
        color: '#ef4444',
        ts: prev.turn,
      }].slice(-30),
    };
  });
}, [state.dagueEclats, state.phase]);


  
  return (
    <>
    <div style={{
        height: '100vh',
        background: '#020617',
        color: '#e2e8f0',
        fontFamily: 'monospace',
        display: 'flex',
        overflow: 'hidden',
        flexDirection: 'column',
        userSelect: 'none',
        //filter: showTutorial ? 'blur(6px)' : 'none',
        transition: 'filter 0.3s',
      }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;500;700&family=Share+Tech+Mono&display=swap');
        @keyframes glow-pulse{0%,100%{opacity:0.5}50%{opacity:1}}
        .cinzel{font-family:'Cinzel',serif}
        .mono{font-family:'Share Tech Mono',monospace}
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        input[type=range]{height:4px}        
        @media(min-width:769px){ .arena-container{ max-height:340px!important } }
        .concepts-grid {
          display: grid;
          gap: 0.4rem;
          grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
        }
        @media (max-width: 768px) {
          .concepts-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 0.35rem;
          }
        }        
        @media (max-width: 400px) {
          .concepts-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 0.28rem;
          }
        }
        @media(min-width:769px){.concepts-grid{grid-template-columns:repeat(auto-fill,minmax(175px,1fr))!important}}
        .livret-tabs::-webkit-scrollbar {
          height: 5px;
        }
        .livret-tabs::-webkit-scrollbar-track {
          background: rgba(0,0,0,0.3);
          border-radius: 4px;
        }
        .livret-tabs::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #a78bfa, #f59e0b);
          border-radius: 4px;
          box-shadow: 0 0 6px rgba(167,139,250,0.4);
        }
        .livret-tabs {
          scrollbar-width: thin;
          scrollbar-color: #a78bfa rgba(0,0,0,0.3);
        }
        @keyframes attackSlashL {
        0%   { transform: translateX(-25%) skew(-12deg); opacity: 0.9; }
        40%  { opacity: 1; }
        100% { transform: translateX(35%) skew(-12deg); opacity: 0; }
        }

        @keyframes attackSlashR {
        0%   { transform: translateX(25%) skew(12deg); opacity: 0.9; }
        40%  { opacity: 1; }
        100% { transform: translateX(-35%) skew(12deg); opacity: 0; }
        }

        @keyframes attackBurstL {
        0%   { transform: scale(0.6); opacity: 0.9; }
        30%  { transform: scale(1.15); }
        100% { transform: scale(1.4); opacity: 0; }
        }

        @keyframes attackBurstR {
        0%   { transform: scale(0.6); opacity: 0.9; }
        30%  { transform: scale(1.12); }
        100% { transform: scale(1.45); opacity: 0; }
        }

        @keyframes hitImpact {
        0%   { opacity: 0.85; transform: scale(0.95); }
        25%  { opacity: 1; transform: scale(1.02); }
        100% { opacity: 0; transform: scale(1.15); }
        }

        @keyframes centralClash {
        from { opacity: 0.65; transform: scale(0.92); }
        to   { opacity: 1; transform: scale(1.08); }
        }

        @keyframes glow-pulse {
        0%, 100% { opacity: 0.85; }
        50% { opacity: 1; }
        }
      `}</style>

        {/* ── TITLE SCREEN ─────────────────────────────────────────────────── */}

        {showOpening && (
          <CinematicOpeningScreen characters={TOUS_PERSONNAGES} onComplete={() => {
            try { localStorage.setItem('jvb_opening_seen', '1'); } catch {}
            setShowOpening(false); music.playMenu();
          }}/>
        )}

        {screen === "blocked" && !isSuperAdmin && (
          <div style={{
            minHeight: '100vh',
            background: '#020617',
            color: '#e2e8f0',
            fontFamily: 'monospace',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            userSelect: 'none'
          }}>
            <div style={{ textAlign: "center", maxWidth: 460, padding: "0 24px" }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
              
              <h2 style={{ fontSize: 28, marginBottom: 12 }}>
                Accès restreint
              </h2>

              {user ? (
                <>
                  <p style={{ color: "#aaa", marginBottom: 16 }}>
                    Tu n'as pas assez de pesas pour jouer.
                  </p>
                  <p style={{ fontSize: 36, fontWeight: 800, color: "#facc15", margin: "12px 0" }}>
                    {userPesas}
                    <span style={{ fontSize: 18, marginLeft: 8 }}>pesas</span>
                  </p>
                  <p style={{ color: "#777", marginBottom: 32 }}>
                    Minimum requis : <strong>2200 pesas</strong>
                  </p>
                </>
              ) : (
                <p style={{ color: "#aaa", marginBottom: 24 }}>
                  Tu dois être connecté pour lancer THAI MEZRA.
                </p>
              )}

              <button
                onClick={() => {window.location.href = "/centre-ajustement"}}
                style={{
                  padding: "14px 32px",
                  fontSize: 15,
                  fontWeight: 700,
                  background: "linear-gradient(135deg, #4ade80, #22d3ee)",
                  border: "none",
                  borderRadius: 10,
                  color: "#fff",
                  cursor: "pointer"
                }}
              >
                {user ? "Obtenir des pesas" : "Se connecter"}
              </button>
            </div>
          </div>
        )}
      
        {screen === 'title' && (
          <TitleScreen
            selectedChar={selectedChar}
            onSelect={c => setSelectedChar(c)}
            onStart={handleTitleStart}
            onContinue={continueMatch}
            hasSavedMatch={hasSavedMatch}
            globalStats={globalStats}
            onReplayOpening={replayOpening} 
            onShowLivret={() => setShowLivret(true)}
            onShowGuilde={() => setShowGuilde(true)}
          />
        )}   
      
        {screen === 'introduction' && (
          <IntroductionScreen
            theme={state.theme}
            selectedChar={selectedChar}
            onBegin={handleIntroBegin}
            currentAIChar={currentAIChar}
          />
        )}
      
        {/* ── ARÈNE DE COMBAT ──────────────────────────────────────────────── */}
        {screen === 'battle' && (
            <>
              {/* HUD */}
              <div style={{ background: 'rgba(2,6,23,0.98)', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '0.35rem 1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', flexShrink: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: selectedChar.avatarColor, animation: 'glow-pulse 2s infinite', flexShrink: 0 }}/>
                        <div>
                          <div className="mono" style={{ fontSize: 'clamp(0.42rem, 2.5vw, 0.65rem)', letterSpacing: '0.18em', color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase' }}>{selectedChar.nom} · {selectedChar.verbe}</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.1rem' }}>
                            <span className="mono" style={{ fontSize: '0.55rem', color: rang.color, fontWeight: 700 }}>{rang.kanji} {rang.label}</span>
                            <span className="mono" style={{ fontSize: '0.5rem', color: 'rgba(255,255,255,0.4)' }}>NIV.{progression.niveau}</span>
                            <div style={{ width: '45px', height: '2px', background: 'rgba(255,255,255,0.15)' }}>
                              <motion.div animate={{ width: `${xpPct}%` }} style={{ height: '100%', background: rang.color }}/>
                            </div>
                          </div>
                        </div>
                      </div>
                      <DagueMemoireHUD
                        eclats={state.dagueEclats ?? 5}
                        prevEclats={prevDagueRef.current}
                        charColor={selectedChar.avatarColor}
                      />
                      <div style={{ textAlign: 'center', flex: 1, minWidth: 0 }}>
                        <div className="mono" style={{ fontSize: 'clamp(0.38rem, 2vw, 0.5rem)', letterSpacing: '0.2em', color: 'rgba(255,255,255,0.2)', textTransform: 'uppercase' }}>THÈME DU DUEL</div>
                        <div className="cinzel" style={{ fontSize: 'clamp(0.6rem, 3.5vw, 0.85rem)', color: 'rgba(255,255,255,0.75)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{state.theme.theme}</div>
                      </div>
                      
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.3rem',
                        marginLeft: '0.5rem',
                      }}>
                        <motion.span
                          animate={{ opacity: [0.4, 0.8, 0.4] }}
                          transition={{ duration: 2, repeat: Infinity }}
                          style={{
                            fontFamily: 'monospace',
                            fontSize: '0.42rem',
                            color: '#e9d5ff88',
                            letterSpacing: '0.15em',
                          }}
                        >
                          TRACE
                        </motion.span>
                        <div style={{
                          width: '30px',
                          height: '2px',
                          background: 'rgba(255,255,255,0.1)',
                          borderRadius: '1px',
                        }}>
                          <motion.div
                            animate={{ width: `${Math.min(100, (state.playerNoHitStreak / 10) * 100)}%` }}
                            style={{
                              height: '100%',
                              background: 'linear-gradient(90deg, #e9d5ff, #c084fc)',
                              borderRadius: '1px',
                            }}
                          />
                        </div>
                      </div>
        
                      
                      
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', flexShrink: 0 }}>
                        <div className="mono" style={{ fontSize: 'clamp(0.42rem, 2.5vw, 0.6rem)', letterSpacing: '0.18em', color: 'rgba(255,255,255,0.28)', textTransform: 'uppercase' }}>R{state.round}/3</div>
                        <MusicControl track={musicTrack}/>
                        <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                          onClick={() => setShowGuilde(true)}
                          style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)', color: 'rgba(245,158,11,0.7)', fontFamily: 'monospace', fontSize: 'clamp(0.42rem, 2vw, 0.58rem)', letterSpacing: '0.1em', padding: '0.28rem 0.65rem', cursor: 'pointer', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <span>道場</span> Guilde
                        </motion.button>
                        <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                          onClick={() => setShowLivret(true)}
                          style={{ background: 'rgba(167,139,250,0.06)', border: '1px solid rgba(167,139,250,0.22)', color: 'rgba(167,139,250,0.7)', fontFamily: 'monospace', fontSize: 'clamp(0.42rem, 2vw, 0.58rem)', letterSpacing: '0.1em', padding: '0.28rem 0.65rem', cursor: 'pointer', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <span>📖</span> Livret
                        </motion.button>
                        <motion.button whileHover={{ scale:1.05 }} whileTap={{ scale:0.95 }}
                          onClick={() => setScreen('title')}
                          style={{ background:'rgba(167,139,250,0.06)', border:'1px solid rgba(167,139,250,0.22)', color:'rgba(167,139,250,0.7)', fontFamily:'monospace', fontSize:'clamp(0.42rem,2vw,0.58rem)', letterSpacing:'0.1em', padding:'0.28rem 0.65rem', cursor:'pointer', textTransform:'uppercase' }}>
                          ← MENU
                        </motion.button>
                      </div>
              </div>
        
              {/* ARÈNE */}
              <div ref={arenaRef} className="arena-container" style={{ 
                position: 'relative', 
                flex: '1 1 0',
                minHeight: '200px',
                maxHeight: '380px',   // ← plafonne l'arène sur desktop
                overflow: 'hidden', 
                background: '#04060e' 
              }}>
                  {/* Stats */}
                  <div style={{ position: 'absolute', top: '0.8rem', left: '0.8rem', right: '0.8rem', display: 'flex', gap: '0.8rem', alignItems: 'flex-start', zIndex: 10 }}>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', marginBottom: '0.1rem' }}>
                    {[0,1].map(i => <div key={i} style={{ width: 'clamp(9px,3.5vw,14px)', height: 'clamp(9px,3.5vw,14px)', borderRadius: '50%', background: state.roundsWon[0] > i ? '#f59e0b' : 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }}/>)}
                      <span className="mono" style={{ fontSize: 'clamp(0.48rem,3vw,0.65rem)', letterSpacing: '0.1em', color: selectedChar.avatarColor, textTransform: 'uppercase', marginLeft: '0.2rem' }}>{selectedChar.nom}</span>
                      </div>
                  <EnergyBar value={state.playerEnergy} side="left" color="#22d3ee" label="ÉNERGIE"/>
                  <GripMeter value={state.playerGrip} side="left"/>
                  <DisorderMeter value={state.playerDisorder} side="left"/>
                  </div>
                  <div style={{ flexShrink: 0, paddingTop: '0.3rem' }}>
                    <span className="cinzel" style={{ fontSize: 'clamp(0.7rem,4vw,1.1rem)', color: 'rgba(255,255,255,0.12)', fontWeight: 700 }}>VS</span>
                  </div>
                  <div style={{ flex:1, display:'flex', flexDirection:'column', gap:'0.35rem', alignItems:'flex-end' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'0.3rem', marginBottom:'0.1rem', flexDirection:'row-reverse' }}>
                      {[0,1].map(i=><div key={i} style={{ width:'clamp(9px,3.5vw,14px)', height:'clamp(9px,3.5vw,14px)', borderRadius:'50%', background:state.roundsWon[1]>i?'#f59e0b':'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.12)' }}/>)}
                      <motion.span key={currentAIChar.id} initial={{ opacity:0, x:10 }} animate={{ opacity:1, x:0 }} className="mono" style={{ fontSize:'clamp(0.48rem,3vw,0.65rem)', letterSpacing:'0.1em', color:currentAIChar.color, textTransform:'uppercase', marginRight:'0.2rem' }}>{currentAIChar.nom}</motion.span>
                    </div>
                      <EnergyBar value={state.opponentEnergy} side="right" color={currentAIChar.color} label="ÉNERGIE"/>
                      <GripMeter value={state.opponentGrip} side="right"/>
                      <DisorderMeter value={state.opponentDisorder} side="right"/>
                  </div>
                </div>
              <Arena 
                playerColor={selectedChar.avatarColor}
                opponentColor={currentAIChar.color}
                arenaIndex={arenaIndex}           // état local ou global
                intensity={arenaIntensity}        // 0 → 1, monte pendant les phases tendues
                playerHit={state.playerHit}    // true pendant l'animation d'attaque
                oppHit={state.oppHit}     // true pendant l'animation d'attaque adv.
              />
              <ParticleArena 
                playerColor={selectedChar.avatarColor}
                opponentColor={currentAIChar.color}
                intensity={arenaIntensity}
                playerHit={state.playerHit}
                oppHit={state.oppHit}
              />

              <ScreenShake key= "screen-shake" active={screenShake}>                          
                      
                      {/* Scanlines */}
                      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'repeating-linear-gradient(0deg,transparent,transparent 3px,rgba(0,0,0,0.05) 3px,rgba(0,0,0,0.05) 4px)', pointerEvents: 'none', opacity: 0.4 }} />
                    
                      {/* Avatars */}
                      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'repeating-linear-gradient(0deg,transparent,transparent 3px,rgba(0,0,0,0.05) 3px,rgba(0,0,0,0.05) 4px)', pointerEvents: 'none', opacity: 0.4 }}/>
                      <div style={{ position:'absolute', bottom:'0', left:'4%', width: isMobile ? '22%' : '18%', height:'80%', zIndex:5,  }}>
                        <KimonoAvatar side="left" isHit={state.playerHit} isAttacking={state.playerAtk} grip={state.playerGrip} isBlocking={state.playerBlockNext} char={selectedChar} inFlow={playerInFlow} flowStreak={state.playerNoHitStreak}/>
                      </div>
                      <div style={{ position:'absolute', bottom:'0', right:'4%', width:'18%', height:'80%', zIndex:5,  }}>
                        <motion.div key={resolvedAdversary.id} initial={{ opacity:0 }} animate={{ opacity:1 }} style={{ width:'100%', height:'100%' }}>
                          <KimonoAvatar
                            side="right"
                            isHit={state.oppHit}
                            isAttacking={state.oppAtk}
                            grip={state.opponentGrip}
                            isBlocking={state.opponentBlockNext}
                            char={resolvedAdversary}  
                            inFlow={oppenentInFlow}
                            flowStreak={state.opponentNoHitStreak}
                          />
                        </motion.div>
                    </div>
        
                      {/* Dernière citation */}
                      {state.log.length > 0 && (
                        <motion.div key={state.log[state.log.length - 1]?.id}
                          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                          style={{ position: 'absolute', bottom: '22px', left: '23%', right: '23%', textAlign: 'center', zIndex: 8 }}>
                          <span className="mono" style={{ fontSize: 'clamp(0.5rem,3vw,0.75rem)', letterSpacing: '0.04em', lineHeight: 1.5, color: state.log[state.log.length - 1]?.color || 'rgba(255,255,255,0.55)', background: 'rgba(2,6,23,0.82)', padding: '0.25rem 0.7rem', backdropFilter: 'blur(4px)', display: 'inline-block', maxWidth: '90%' }}>
                            "{state.log[state.log.length - 1]?.text}"
                          </span>
                        </motion.div>
                      )}
        
                      {/* Indicateur de tour */}
                      {state.phase==='battle' && !state.analysisTimer && (
                        <motion.div animate={{ opacity:[0.5,1,0.5] }} transition={{ repeat:Infinity, duration:1.5 }}
                          style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', fontFamily:'monospace', fontSize:'clamp(0.4rem,2.5vw,0.6rem)', letterSpacing:'0.25em', color:state.activePlayer==='player'?'#22d3ee':currentAIChar.color, textTransform:'uppercase', zIndex:8, pointerEvents:'none' }}>
                          {state.activePlayer==='player'?'▶ VOTRE TOUR':`◀ ${currentAIChar.nom}`}
                        </motion.div>
                      )}
        
                      {/* Timer d'analyse */}
                      {state.phase==='battle' && (
                        <AnalysisTimer duration={5} active={state.analysisTimer} color={selectedChar.avatarColor}/>
                      )}
        
                      {/* Mémoire adversaire */}
                      {state.opponentMemory.length > 0 && state.phase === 'battle' && (
                        <div style={{ position:'absolute', top:'8%', right:'1rem', zIndex:12, display:'flex', flexDirection:'column', gap:'0.2rem', alignItems:'flex-end' }}>
                          <span style={{ fontFamily:'monospace', fontSize:'0.38rem', letterSpacing:'0.2em', color:'rgba(245,158,11,0.4)', textTransform:'uppercase' }}>MÉMOIRE DU PRECEPTEUR</span>
                          {state.opponentMemory.map((id, i) => {
                            const c = DECK_ADVERSAIRE_BASE.find(x => x.id === id);
                            return c ? <span key={i} style={{ fontFamily:'monospace', fontSize:'0.42rem', color:c.color+'88', letterSpacing:'0.1em' }}>{c.label}</span> : null;
                          })}
                        </div>
                      )}
        
                      <HitFlash flash={hitFlash} />
                      <SlashEffect slash={slashEffect} />
                      <ConceptAnnounce announce={conceptAnnounce} />              
        
                      {/* Effet de scan en lignes horizontales sur l'adversaire */}
                      <AnimatePresence>
                        {opponentScanning && (
                          <motion.div
                            key="scan-line"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: [0, 0.6, 0.3, 0.6, 0] }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 1.2, repeat: Infinity }}
                            style={{
                              position: 'absolute',
                              top: '15%',
                              right: '5%',
                              width: '22%',
                              height: '2px',
                              background: currentAIChar?.color || '#ef4444',
                              boxShadow: `0 0 12px ${currentAIChar?.color || '#ef4444'}`,
                              pointerEvents: 'none',
                              zIndex: 7,
                            }}
                          />
                        )}
                      </AnimatePresence>
              </ScreenShake>
        
                <SophiaWhisper interruption={sophiaInterruption} onDone={() => setSophiaInterruption(null)} />
                <DuelNarrative messages={messages.left} onDone={(id) => dismiss('left', id)}  />
                <DuelNarrative messages={messages.right} onDone={(id) => dismiss('right', id)}  />
                <DuelNarrative messages={messages.center} onDone={(id) => dismiss('center', id)}  />             
                <SubThemeReveal subTheme={subThemeReveal} onDone={() => setSubThemeReveal(null)} />  
        
                {/* Indicateur réflexion IA */}
                <AnimatePresence>
                {state.phase==='battle'&&state.activePlayer==='opponent'&&!state.analysisTimer && (
                  <AIThinkingIndicator key="ai-thinking" aiChar={currentAIChar} visible/>
                )}
                </AnimatePresence>
                
                <AnimatePresence>
                  {state.showWisdomFlash && (
                    <WisdomFlash
                      key="wisdom"
                      fragment={state.showWisdomFlash}
                      onDone={() => setState(p => ({ ...p, showWisdomFlash: null }))}
                    />
                  )}
                </AnimatePresence>
                <AnimatePresence>{state.lastDamageLabel && <DamagePopup key={state.turn} label={state.lastDamageLabel} color={state.lastDamageSide === 'right' ? '#ef4444' : '#f59e0b'} side={state.lastDamageSide}/>}</AnimatePresence>
                <AnimatePresence>{state.phase === 'intro' && <RoundIntro key={`intro-${state.round}`} round={state.round} onDone={() => setState(p => ({ ...p, phase: 'battle', activePlayer: 'player' }))}/>}</AnimatePresence>
                <AnimatePresence>{state.phase === 'victory' && <VictoryScreen key="victory-screen" winner={winner} energyLeft={winner === 'player' ? state.playerEnergy : state.opponentEnergy} roundsWon={state.roundsWon} onRematch={resetGame} onExit={() => setScreen('title')} selectedChar={selectedChar} dagueEclats={state.dagueEclats} narrativeDefeat={state.dagueEclats === 0}/>}</AnimatePresence>
              
              </div>
        
              
        
              
              {/* Panneau inférieur */}
              <BottomPanel
                state={state}
                playerDeck={playerDeck}
                selectedChar={selectedChar}
                progression={progression}
                playerInFlow={playerInFlow}
                logRef={logRef}
                playMove={playMove}
                onSelectConcept={handleConceptSelect}
                lastPlayerConceptId={state.lastPlayerConceptId}              
                hasSeen={hasSeen}
                onOpenKnowledge={handleOpenKnowledge}
                comboState={state.comboState}
              />            
            </>
                )}

               
        

        <AnimatePresence>{activeSynergy && <SynergyBanner key="syn" synergy={activeSynergy} onDone={() => setActiveSynergy(null)}/>}</AnimatePresence>
        <AnimatePresence>
        {state.showFlowModal && (
            <FlowModal
                key={`flow-${state.playerNoHitStreak}`}
                streak={state.playerNoHitStreak}
                        charId={selectedChar.id}
                onDone={() => setState(p => ({ ...p, showFlowModal: false }))}
            />
            )}    
        </AnimatePresence>    


        
        </div>

    

       <AnimatePresence>
          {pendingConcept && (
            <PunchlineWheel
              key="punchline-wheel"
              options={wheelOptions}
              conceptColor={pendingConcept.color}
              conceptLabel={pendingConcept.label}
              onSelect={handlePunchlineChosen}
              onSkip={handleWheelSkip}
              playerGrip={state.playerGrip}
              opponentGrip={state.opponentGrip}
              playerEnergy={state.playerEnergy}
              themeId={state.theme?.id || null}
              characterId={selectedChar.id}
              roundPhase={
                state.playerEnergy <= 25 ? 'critical' :
                state.round === 3 ? 'final' :
                state.round === 1 && state.turn <= 2 ? 'early' :
                'normal'
              }
              opponentName={state.currentAIChar?.nom || 'Le Précepteur'}
            />
          )}
        </AnimatePresence>  

      {knowledgeModal && createPortal(
        <EchoLibrary
          key="echo-library"
          knowledgeId={knowledgeModal.id}
          type={knowledgeModal.type}
          onClose={() => setKnowledgeModal(null)}
        />,
        document.body
      )} 
      
      <AnimatePresence>
          {aiCharSwitch && 
            <AICharSwitchBanner 
              key="aiswitch" 
              oldChar={aiCharSwitch.old} 
              newChar={aiCharSwitch.new} 
              onDone={()=>setAiCharSwitch(null)}/>
          }
        </AnimatePresence>
      <AnimatePresence>{showLivret && <Livret onClose={() => setShowLivret(false)}/>}</AnimatePresence>
      <AnimatePresence>{showGuilde && <GuildePanel onClose={() => setShowGuilde(false)} playerNom={selectedChar.fullNom}/>}</AnimatePresence>
      <AnimatePresence>
       
      {newUnlockAnim && (
        <UnlockRevealAnim
          key="unlock"
          char={newUnlockAnim}
          onDone={() => setNewUnlockAnim(null)}
        />
      )}
      </AnimatePresence>
    </>
  );
}